import { buildConnectionActions } from "./prototype_connect_matcher.mjs"

// 프로토타입 결정론 연결 — 프레임 HTML 에서 클릭 요소를 뽑을 때 쓰는 셀렉터/상수.
// ElementInventory.js(렌더된 iframe용)와 동일 규칙을 DOMParser(HTML 문자열)에 적용한다.
const PROTO_CLICKABLE_SELECTOR = [
  "button",
  "a[href]",
  '[role="button"]',
  "[onclick]",
  "[data-prototype-clickable]",
  ".btn",
  ".cta",
  "nav a",
  '[class*="button"]',
  '[class*="btn"]'
].join(",")
const PROTO_MAX_ELEMENTS_PER_FRAME = 24
const PROTO_MAX_LABEL_LEN = 60

// ── i18n 헬퍼 ────────────────────────────────────────────────────────────────
// window.KLAVY_I18N 전역 사전에서 점(.) 구분 경로로 번역 값을 가져옵니다.
// 키가 없으면 fallback 문자열을 반환합니다.
const _t = (path, fallback = "") => {
  try {
    const parts = ("klavy_agent." + path).split(".")
    let obj = (typeof window !== "undefined" && window.KLAVY_I18N) || {}
    for (const p of parts) {
      if (obj == null || typeof obj !== "object") return fallback
      obj = obj[p]
    }
    return (obj != null && typeof obj === "string") ? obj : fallback
  } catch (_) {
    return fallback
  }
}

export function withSearchParams(path, params = {}) {
  const raw = String(path || "")
  const qIndex = raw.indexOf("?")
  const base = qIndex === -1 ? raw : raw.slice(0, qIndex)
  const search = new URLSearchParams(qIndex === -1 ? "" : raw.slice(qIndex + 1))
  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === "") return
    search.set(key, String(value))
  })
  const qs = search.toString()
  return qs ? `${base}?${qs}` : base
}

export function withPathSuffix(path, suffix) {
  const raw = String(path || "")
  const qIndex = raw.indexOf("?")
  const base = qIndex === -1 ? raw : raw.slice(0, qIndex)
  const query = qIndex === -1 ? "" : raw.slice(qIndex)
  return `${base.replace(/\/$/, "")}${suffix}${query}`
}

export class KlavyAgentCore {
  constructor(config) {
    this.config = {
      panelId: "ba-chat-panel",
      aiBasePath: "/admin/ai",
      chatApiPath: null,
      rulesApiPath: null,
      formSelector: null,
      fieldPrefix: null,
      sectionTypeAttr: "baro-section-type",
      sectionRepeaterAttr: "baro-section-repeater",
      contextType: "page",
      resourceType: null,
      resourceId: 0,
      sectionId: 0,
      sectionType: null,
      sectionTitle: null,
      systemPrompt: "",
      welcomeMessage: _t("welcome.default", "안녕하세요! 무엇을 도와드릴까요?"),
      quickStarts: [],
      existingSections: [],
      pageTitle: "",
      pageSlug: "",
      metaDescription: "",
      storagePrefix: "baAgent",
      designerBridge: false,
      fromPlanning: false,
      credentialStatusPath: null,
      settingsUrl: null,
      draftApiPath: null,
      imageApiPath: null,
      draftWriterKind: null,
      threadsDraftApiPath: null,
      threadsStrategyApiPath: null,
      threadsProfileApiPath: null,
      kakaoTalkDraftApiPath: null,
      kakaoTalkStrategyApiPath: null,
      kakaoTalkProfileApiPath: null,
      kmongSellerProfileApiPath: null,
      kmongWorkspaceTab: "service",
      comparisonReportApiPath: null,
      comparisonPatchApiPath: null,
      comparisonGeneratedImagesApiPath: null,
      comparisonDetailImagesApiPath: null,
      promptToolsEnabled: false,
      promptFetchUrlPath: null,
      promptExtractDocumentPath: null,
      iaGenerateFromUrlPath: null,
      planningGenerateFromUrlPath: null,
      planningGenerateFromUrlTab: "",
      planningInterviewFromUrlPath: null,
      promptUrlAddLabel: "",
      promptUrlBusyLabel: "",
      voiceModelPicker: false,
      voiceModelMode: "realtime_voice",
      voiceModelRuntime: "",
      kakaoTalkFetchUrlPath: "/kakao-talk/ai/fetch_url",
      kakaoTalkExtractDocumentPath: "/kakao-talk/ai/extract_document",
      ...config
    }
    this._init()
  }

  // ── DOM 접근자 (Stimulus targets → querySelector) ──────────────────────────

  _t(name) { return this.config.container.querySelector(`[data-klavy-agent-target="${name}"]`) }

  get _panel()                { return this._t("panel") }
  get _messages()             { return this._t("messages") }
  get _input()                { return this._t("input") }
  get _toggleBtn()            { return document.getElementById("ka-toggle-btn") }
  get _modelBtn()             { return this._t("modelBtn") }
  get _modelLabel()           { return this._t("modelLabel") }
  get _modelRecommendedChip() { return this._t("modelRecommendedChip") }
  get _modelLabelChip()       { return this._t("modelLabelChip") }
  get _modelLabelChipSecondary() { return this._t("modelLabelChipSecondary") }
  get _modelSourceChip()      { return this._t("modelSourceChip") }
  get _modelWeightChip()       { return this._t("modelWeightChip") }
  get _threadsImageModelBtn() { return this._t("threadsImageModelBtn") }
  get _threadsImageModelLabel() { return this._t("threadsImageModelLabel") }
  get _threadsImageModelWeightChip() { return this._t("threadsImageModelWeightChip") }
  get _threadsImageModelSourceChip() { return this._t("threadsImageModelSourceChip") }
  get _threadsImageModelDropdown() { return this._t("threadsImageModelDropdown") }
  get _threadsImageModelNotice() { return this._t("threadsImageModelNotice") }
  get _threadsImageModelSection() { return this._t("threadsImageModelSection") }
  get _threadsDraftModeTab() { return this._t("threadsDraftModeTab") }
  get _threadsStrategyModeTab() { return this._t("threadsStrategyModeTab") }
  get _threadsProfileModeTab() { return this._t("threadsProfileModeTab") }
  get _inputExpandBtn() { return this._t("inputExpandBtn") }
  get _modelImageChip()        { return this._t("modelImageChip") }
  get _rcModelImageChip()      { return this._t("rcModelImageChip") }
  get _rcModelRecommendedChip(){ return this._t("rcModelRecommendedChip") }
  get _rcModelLabelChip()      { return this._t("rcModelLabelChip") }
  get _rcModelLabelChipSecondary(){ return this._t("rcModelLabelChipSecondary") }
  get _rcModelWeightChip()     { return this._t("rcModelWeightChip") }
  get _modelDropdown()        { return this._t("modelDropdown") }
  get _modeWrapper()          { return this._t("modeWrapper") }
  get _modeBtn()              { return this._t("modeBtn") }
  get _modeLabel()            { return this._t("modeLabel") }
  get _modeDropdown()         { return this._t("modeDropdown") }
  get _iaReadDepthBtn()       { return this._t("iaReadDepthBtn") }
  get _iaReadDepthLabel()     { return this._t("iaReadDepthLabel") }
  get _iaReadDepthDropdown()  { return this._t("iaReadDepthDropdown") }
  get _imageInput()           { return this._t("imageInput") }
  get _imagePreview()         { return this._t("imagePreview") }
  get _imagePreviewImg()      { return this._t("imagePreviewImg") }
  get _imageFileName()        { return this._t("imageFileName") }
  get _promptUrlPanel()       { return this._t("promptUrlPanel") || this._t("kakaoUrlPanel") }
  get _promptUrlInput()       { return this._t("promptUrlInput") || this._t("kakaoUrlInput") }
  get _promptUrlAddBtn()      { return this._t("promptUrlAddBtn") || this._t("kakaoUrlAddBtn") }
  get _promptAttachmentPreview() { return this._t("promptAttachmentPreview") || this._t("kakaoAttachmentPreview") }
  get _promptDocumentInput()  { return this._t("promptDocumentInput") || this._t("kakaoDocumentInput") }
  get _kakaoUrlPanel()        { return this._promptUrlPanel }
  get _kakaoUrlInput()        { return this._promptUrlInput }
  get _kakaoUrlAddBtn()       { return this._promptUrlAddBtn }
  get _kakaoAttachmentPreview() { return this._promptAttachmentPreview }
  get _kakaoDocumentInput()   { return this._promptDocumentInput }
  get _rulesBtn()             { return this._t("rulesBtn") }
  get _rulesDot()             { return this._t("rulesDot") }
  get _rulesPanel()           { return this._t("rulesPanel") }
  get _rulesList()            { return this._t("rulesList") }
  get _rulesListView()        { return this._t("rulesListView") }
  get _rulesEditView()        { return this._t("rulesEditView") }
  get _rulesEditLabel()       { return this._t("rulesEditLabel") }
  get _ruleEditTitle()        { return this._t("ruleEditTitle") }
  get _ruleEditContent()      { return this._t("ruleEditContent") }
  get _rulesSaveBtn()         { return this._t("rulesSaveBtn") }
  get _rulesBatchBar()        { return this._t("rulesBatchBar") }
  get _rulesSelectAll()       { return this._t("rulesSelectAll") }
  get _rulesBatchCount()      { return this._t("rulesBatchCount") }
  get _ruleEditActive()       { return this._t("ruleEditActive") }
  get _rulesDeleteBtn()       { return this._t("rulesDeleteBtn") }
  get _rulesPaginationEl()    { return this._t("rulesPaginationEl") }
  get _rulesChatView()        { return this._t("rulesChatView") }
  get _rulesChatMessages()    { return this._t("rulesChatMessages") }
  get _rulesChatInput()       { return this._t("rulesChatInput") }
  get _rcModelBtn()           { return this._t("rcModelBtn") }
  get _rcModelLabel()         { return this._t("rcModelLabel") }
  get _rcModelSourceChip()    { return this._t("rcModelSourceChip") }
  get _rcModelDropdown()      { return this._t("rcModelDropdown") }
  get _rcModeWrapper()        { return this._t("rcModeWrapper") }
  get _rcModeBtn()            { return this._t("rcModeBtn") }
  get _rcModeLabel()          { return this._t("rcModeLabel") }
  get _rcModeDropdown()       { return this._t("rcModeDropdown") }
  get _rcInputWrapper()       { return this._t("rcInputWrapper") }
  get _rcImageInput()         { return this._t("rcImageInput") }
  get _rcImagePreview()       { return this._t("rcImagePreview") }
  get _rcImagePreviewImg()    { return this._t("rcImagePreviewImg") }
  get _rcImageFileName()      { return this._t("rcImageFileName") }
  get _rulesHistoryPanel()    { return this._t("rulesHistoryPanel") }
  get _mainHistoryPanel()     { return this._t("mainHistoryPanel") }
  get _rulesToggleBtn()       { return this._t("rulesToggleBtn") }
  get _sendBtn()              { return this._t("sendBtn") }
  get _inputWrapper()         { return this._t("inputWrapper") }
  get _designerTargetValue()  { return this._t("designerTargetValue") }
  get _designerNewSectionButton() { return this._t("designerNewSectionButton") }
  get _wrapper()              { return this.config.container }

  // ── _init (기존 connect()) ────────────────────────────────────────────────

  _init() {
    if (this.config.agentSlug === "section-template") this._bdFlush()
    this.chatHistory = []
    this.isOpen = localStorage.getItem(`${this.config.storagePrefix}-open`) !== "0" // 기본값: 열림
    if (this.config.contextType === "ia_project" || this.config.contextType === "prompt_editor") this.isOpen = true
    this.isLoading = false
    this.selectedModel = localStorage.getItem(`${this.config.storagePrefix}-model`) || (this.config.voiceModelPicker ? "" : "claude-sonnet-4-6")
    this.selectedMode = localStorage.getItem(`${this.config.storagePrefix}-mode`) || "make"
    this.iaReadDepth = localStorage.getItem(`${this.config.storagePrefix}-ia-read-depth`) || "normal"
    this.iaReadDepthDropdownOpen = false
    this.imageBase64 = null
    this.imagePromptAttachmentName = null
    this.imagePromptAttachmentSize = 0
    this.imagePromptAttachmentMediaType = null
    this.kakaoPromptAttachments = []
    this.kmongNewServiceBrief = null
    this.lastFormData = null
    this.modelDropdownOpen = false
    this.modeDropdownOpen = false
    this.currentMainSessionId = localStorage.getItem(`${this.config.storagePrefix}-main-session`) || null
    this._lastInterviewQNum = null
    this.mainHistoryOpen = false
    this.designerSelection = null
    this.designerSelectionActivated = false
    this.designerPromptScope = null
    this.credentialStatusByProvider = {}
    this.threadsImageCredentialStatusByProvider = {}
    this.credentialStatusLoaded = false
    this.availableModels = []
    this.threadsImageModels = []
    this.selectedThreadsImageModel = localStorage.getItem(`${this.config.storagePrefix}-threads-image-model`) || ""
    this.threadsImageModelDropdownOpen = false
    this.threadsAgentMode = localStorage.getItem(`${this.config.storagePrefix}-threads-mode`) || "draft"
    if (this.isKmongComparisonReportAgent()) {
      this.threadsAgentMode = "draft"
    } else if (this.isKmongServiceDraftAgent()) {
      this.threadsAgentMode = this.config.kmongWorkspaceTab === "profile" && this.config.kmongSellerProfileApiPath ? "profile" : "draft"
    }
    this.inputExpanded = false
    this.creditPackBalance = 0
    this.imageAssetServerDefaultModel = null
    this.imageAssetModelExplicitlySelected = false
    this.credentialStatusPath = this.config.credentialStatusPath || this.config.container?.dataset?.aiCredentialStatusUrl || null
    this.credentialStatusRefreshPromise = null
    this.credentialStatusRefreshNeedsRerender = false
    this.credentialStatusPollTimer = null
    this.credentialStatusPollIntervalMs = null
    this.settingsUrl = this.config.settingsUrl || this.config.container?.dataset?.aiSettingsUrl || "/dashboard/models"
    this._handleCredentialVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        this.stopCredentialStatusPolling()
        return
      }
      this.refreshCredentialStatus({ force: true, rerenderModels: true })
    }
    document.addEventListener("visibilitychange", this._handleCredentialVisibilityChange)
    this._handleKmongWorkspaceTabChanged = (event) => {
      const tab = event.detail?.tab
      if (!this.isKmongServiceDraftAgent()) return
      if (this.isKmongComparisonReportAgent()) return
      this.setKmongWorkspaceTab(tab === "profile" ? "profile" : "service")
    }
    window.addEventListener("kmong-service-registration:workspace-tab-change", this._handleKmongWorkspaceTabChanged)
    this._handleKmongComparisonDetailImagesProgress = (event) => this.handleKmongComparisonDetailImagesProgress(event)
    window.addEventListener("kmong-comparison-detail-images-progress", this._handleKmongComparisonDetailImagesProgress)
    this._handleDesignerSelectionChanged = (event) => {
      const previousSummary = this.designerSelectionSummary(this.designerSelection)
      const previousActivated = this.designerSelectionActivated
      this.designerSelection = this.normalizeDesignerSelection(event.detail)
      this.updateDesignerSelectionActivation(event.detail)
      this.renderDesignerTargetIndicator()
      this.syncDesignerSelectionUI(
        previousSummary,
        this.designerSelectionSummary(this.designerSelection),
        previousActivated,
        this.designerSelectionActivated
      )
    }

    // 저장된 모드 복원
    const modeLabels = {
      make: _t("modes.make", "생성"),
      ask: _t("modes.ask", "질문"),
      think: _t("modes.think", "계획"),
      auto: _t("modes.auto", "자동")
    }
    if (this._modeLabel) {
      this._modeLabel.textContent = modeLabels[this.selectedMode] || _t("modes.make", "생성")
    }
    this._syncIaReadDepthUi()

    this.renderThreadsAgentMode()

    // loadProjectRules 완료 후 웰컴 메시지가 룰 상태를 정확히 반영하도록 순서 보장
    this.loadProjectRules().then(() => this.loadChatHistory())

    // 헤더 팝업에서 룰 변경 시 동기화
    this._handleExternalRulesChanged = (e) => {
      if (e.detail?.source === 'klavy-agent') return
      this.loadProjectRules()
    }
    document.addEventListener('project-rules-changed', this._handleExternalRulesChanged)


    // 사이드바 열림 상태 초기화
    if (!this.isOpen && this._wrapper) {
      this._wrapper.style.width = "0"
      this._wrapper.style.overflow = "hidden"
      this._wrapper.classList.add("ka-closed")
    }
    if (this._toggleBtn) {
      this._toggleBtn.style.display = this.isOpen ? "none" : "flex"
      this._toggleBtn.classList.toggle("ka-panel-open", this.isOpen)
    }

    // 외부 클릭 시 드롭다운 닫기
    this._closeDropdown = (e) => {
      if (this._modelDropdown && this._modelBtn && !this._modelBtn.contains(e.target) && !this._modelDropdown.contains(e.target)) {
        this._modelDropdown.style.display = "none"
        this.modelDropdownOpen = false
      }
      if (this._modeDropdown && this._modeBtn && !this._modeBtn.contains(e.target) && !this._modeDropdown.contains(e.target)) {
        this._modeDropdown.style.display = "none"
        this.modeDropdownOpen = false
      }
      if (this._iaReadDepthDropdown && this._iaReadDepthBtn && !this._iaReadDepthBtn.contains(e.target) && !this._iaReadDepthDropdown.contains(e.target)) {
        this._iaReadDepthDropdown.style.display = "none"
        this.iaReadDepthDropdownOpen = false
      }
      if (this._rcModelDropdown && this._rcModelBtn && !this._rcModelBtn.contains(e.target) && !this._rcModelDropdown.contains(e.target)) {
        this._rcModelDropdown.style.display = "none"
        this.rcModelDropdownOpen = false
      }
      if (this._rcModeDropdown && this._rcModeBtn && !this._rcModeBtn.contains(e.target) && !this._rcModeDropdown.contains(e.target)) {
        this._rcModeDropdown.style.display = "none"
        this.rcModeDropdownOpen = false
      }
      if (this._rulesHistoryPanel && this.rulesHistoryOpen && !this._rulesHistoryPanel.contains(e.target)) {
        this._rulesHistoryPanel.style.display = "none"
        this.rulesHistoryOpen = false
      }
      if (this._mainHistoryPanel && this.mainHistoryOpen && !this._mainHistoryPanel.contains(e.target)) {
        this._mainHistoryPanel.style.display = "none"
        this.mainHistoryOpen = false
      }
    }
    document.addEventListener("click", this._closeDropdown)
    if (this.config.designerBridge) {
      window.addEventListener("profile-page-designer:selection-changed", this._handleDesignerSelectionChanged)
      this._handleHeroTemplateApplied = (e) => this._showHeroTemplateSuggestion(e.detail?.templateName)
      window.addEventListener("profile-page-designer:hero-template-applied", this._handleHeroTemplateApplied)
      this.renderDesignerTargetIndicator()
      window.dispatchEvent(new CustomEvent("profile-page-designer:request-selection"))
    }

    this.projectRules = []
    this.editingRuleIndex = -1
    this.rulesOpen = localStorage.getItem(`${this.config.storagePrefix}-rules-open`) === "1"
    this.selectedRuleIndices = new Set()
    this.rulesCurrentPage = 1
    this.rulesGenerationMode = localStorage.getItem(`${this.config.storagePrefix}-rules-gen`) === "1"
    this.rulesGenerationRetryLimit = 1
    this.currentRulesSessionId = localStorage.getItem(`${this.config.storagePrefix}-rules-session`) || null
    this.rulesHistoryOpen = false
    this.rulesEnabled = localStorage.getItem(`${this.config.storagePrefix}-rules-enabled`) !== "0" // 기본 ON
    this.rcModelDropdownOpen = false
    this.rcModeDropdownOpen = false
    this.rcImageBase64 = null
    this.rulesPanelMode = this.rulesGenerationMode ? "chat_split" : "list"

    // 저장된 모델 선택 복원
    if (this.selectedModel !== "claude-sonnet-4-6" && this._modelDropdown) {
      const savedOpt = this._modelDropdown.querySelector(`.ba-model-opt[data-value="${this.selectedModel}"]`)
      if (savedOpt) {
        const savedLabel = savedOpt.querySelector(".ba-model-opt__name")?.textContent.trim() || savedOpt.dataset.value
        if (this._modelLabel) this._modelLabel.textContent = savedLabel
        this._modelDropdown.querySelectorAll(".ba-model-opt").forEach(el => {
          el.style.background = el.dataset.value === this.selectedModel ? "#EEF2FF" : ""
        })
      }
    }

    // 작업 규칙 열림 상태 복원
    if (this.rulesOpen && this._rulesBtn) {
      this._rulesBtn.style.borderColor = "#6366F1"
      this._rulesBtn.style.color = "#6366F1"
    }

    if (this.rulesGenerationMode) {
      // 채팅 패널 열기
      if (!this.isOpen) {
        this.isOpen = true
        localStorage.setItem(`${this.config.storagePrefix}-open`, "1")
        if (this._panel) this._panel.style.display = "flex"
        if (this._wrapper) this._wrapper.classList.remove("ka-closed")
        if (this._toggleBtn) { this._toggleBtn.style.display = "none"; this._toggleBtn.classList.add("ka-panel-open") }
      }
      // 룰 패널 열기
      if (!this.rulesOpen) {
        this.rulesOpen = true
        localStorage.setItem(`${this.config.storagePrefix}-rules-open`, "1")
        if (this._rulesBtn) {
          this._rulesBtn.style.borderColor = "#6366F1"
          this._rulesBtn.style.color = "#6366F1"
        }
      }
      // rc 레이블 동기화
      if (this._rcModelLabel) this._rcModelLabel.textContent = this._modelLabel ? this._modelLabel.textContent : "Claude Sonnet 4.6"
      this.updateCredentialSourceUI()
      if (this._rcModeLabel) this._rcModeLabel.textContent = this._modeLabel ? this._modeLabel.textContent : _t("modes.make", "생성")
      // 세션 복원 or 가이드 표시
      this.rulesChatHistory = []
      if (this.currentRulesSessionId) {
        this.loadRulesSession(this.currentRulesSessionId)
      } else {
        this.showRulesChatGuide()
      }
    }

    this.applyRulesPanelLayout()

    this.injectStyles()
    this.loadModels()
    this.refreshCredentialStatus()
  }

  debugLog(message, payload = undefined) {
    if (payload === undefined) {
      console.debug(`[KlavyAgentCore] ${message}`)
      return
    }
    console.debug(`[KlavyAgentCore] ${message}`, payload)
  }

  // 리로드 후에도 로그를 볼 수 있도록 localStorage에 저장하는 디버그 헬퍼
  _bdLog(...args) {
    const line = args.map(a => (typeof a === "object" ? JSON.stringify(a) : String(a))).join(" ")
    const ts = new Date().toISOString().slice(11, 23)
    const entry = `[${ts}] ${line}`
    console.log("[BD]", entry)
    try {
      const prev = JSON.parse(localStorage.getItem("_bd_logs") || "[]")
      prev.push(entry)
      localStorage.setItem("_bd_logs", JSON.stringify(prev.slice(-50)))
    } catch (_) {}
  }

  // 페이지 로드 시 저장된 로그 출력 (connect에서 호출)
  _bdFlush() {
    try {
      const logs = JSON.parse(localStorage.getItem("_bd_logs") || "[]")
      if (!logs.length) return
      console.group("=== [BATCH-DEBUG] 이전 페이지 로그 ===")
      logs.forEach(l => console.log(l))
      console.groupEnd()
      localStorage.removeItem("_bd_logs")
    } catch (_) {}
  }

  // ── destroy (기존 disconnect()) ───────────────────────────────────────────

  destroy() {
    document.removeEventListener("click", this._closeDropdown)
    if (this._handleCredentialVisibilityChange) document.removeEventListener("visibilitychange", this._handleCredentialVisibilityChange)
    this.stopCredentialStatusPolling()
    if (this._handleExternalRulesChanged) document.removeEventListener('project-rules-changed', this._handleExternalRulesChanged)
    if (this._handleKmongWorkspaceTabChanged) window.removeEventListener("kmong-service-registration:workspace-tab-change", this._handleKmongWorkspaceTabChanged)
    if (this._handleKmongComparisonDetailImagesProgress) window.removeEventListener("kmong-comparison-detail-images-progress", this._handleKmongComparisonDetailImagesProgress)
    if (this.config.designerBridge && this._handleDesignerSelectionChanged) {
      window.removeEventListener("profile-page-designer:selection-changed", this._handleDesignerSelectionChanged)
    }
    if (this.config.designerBridge && this._handleHeroTemplateApplied) {
      window.removeEventListener("profile-page-designer:hero-template-applied", this._handleHeroTemplateApplied)
    }
  }

  // 이미지 에셋 관리/UI·UX 디자인 패널은 드롭다운이 ImageGenerationModel 목록이라
  // 채팅 플래닝용 텍스트 LLM을 내부에서 고정 (selectedModel에는 이미지 모델명이 들어있음)
  get isImageAssetAgent() {
    return this.config.agentSlug === "image-asset" ||
      (this.config.resourceType === "UiUxDesignProject" && this.config.agentSlug === "prototype-designer")
  }
  get planningTextLlm()   { return "claude-haiku-4-5" }

  _resolveModel(msg) {
    if (this.isImageAssetAgent) return this.planningTextLlm
    if ((this.config.contextType === "user_story_project" || this.config.contextType === "planning_main_target")
        && this._isImageGenRequest(msg)) {
      const geminiPro = this.availableModels.find(m => m.name?.startsWith("gemini") && m.name?.includes("pro"))
      const geminiAny = this.availableModels.find(m => m.name?.startsWith("gemini"))
      const fallback = "gemini-2.5-pro"
      const model = (geminiPro || geminiAny)?.name || fallback
      if (model !== this.selectedModel) {
        const prev = this.selectedModel
        this.selectedModel = model
        this.updateCredentialSourceUI()
        this._updateModelDropdownUI(model)
        // 요청 완료 후 원래 모델로 복원
        setTimeout(() => {
          this.selectedModel = prev
          this.updateCredentialSourceUI()
          this._updateModelDropdownUI(prev)
        }, 3000)
        this.appendSystemNote(_t("system_notes.image_gen_req", "이미지 생성 요청 — Gemini Pro 모델로 전환합니다."))
      }
      return model
    }
    return this.selectedModel
  }

  _updateModelDropdownUI(modelName) {
    const dropdowns = [this._modelDropdown, this._rcModelDropdown].filter(Boolean)
    dropdowns.forEach(dd => {
      dd.querySelectorAll("[data-value]").forEach(el => {
        el.style.background = el.dataset.value === modelName ? "#EEF2FF" : ""
      })
    })
    const currentModel = this.availableModels.find(m => m.name === modelName)
    const label = currentModel?.display_name || currentModel?.label || modelName
    const labelEls = this.config.container.querySelectorAll(".ba-model-label")
    labelEls.forEach(el => { el.textContent = label })
  }

  _isImageGenRequest(msg) {
    const keywords = ["이미지 생성", "프로필 이미지", "프로필 사진", "사진 생성", "사진 만들어", "이미지 만들어"]
    return keywords.some(k => msg.includes(k))
  }

  isPlanningWorkspaceContext() {
    return String(this.config.contextType || "").startsWith("planning_")
      || this.config.resourceType === "PlanningWorkspaceProject"
  }

  isPlanningMainTargetGenerateRequest(msg) {
    const text = String(msg || "")
    if (!text) return false
    const wantsGenerate = text.includes("만들어") || text.includes("생성") || text.includes("저장해")
    if (!wantsGenerate) return false
    return text.includes("메인 타겟") || text.includes("페르소나")
  }

  async generatePlanningMainTargetFromServer(msg) {
    const last = this.chatHistory[this.chatHistory.length - 1]
    if (!last || last.role !== "user") {
      this.chatHistory.push({ role: "user", content: msg })
      await this.persistMessage("user", String(msg || ""))
    }

    this.setLoading(true)
    try {
      const projectId = this.config.resourceId
      if (!projectId) {
        throw new Error("프로젝트 정보를 찾지 못했습니다. 화면을 새로고침한 뒤 다시 시도해주세요.")
      }

      const body = {}
      try {
        const model = this._resolveModel(msg)
        if (model) body.model = model
      } catch (_) {}

      const response = await fetch(`/planning-workspace/projects/${projectId}/generate_main_target`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify(body)
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok || payload.success === false) {
        throw new Error(payload.error || payload.message || `HTTP ${response.status}`)
      }

      const summary = payload.summary || "메인 타겟 유저 3명을 저장했습니다."
      this.chatHistory.push({ role: "assistant", content: summary })
      this.appendMessage("assistant", summary)
      await this.persistMessage("assistant", summary)
      window.setTimeout(() => window.location.reload(), 200)
    } catch (e) {
      this.appendMessage("assistant", e.message || "메인 타겟 유저 생성에 실패했습니다.")
    } finally {
      this.setLoading(false)
    }
  }

  isQuotationWorkspaceContext() {
    return String(this.config.contextType || "").startsWith("quotation_")
      || this.config.resourceType === "QuotationWorkspaceProject"
  }

  isPromptDesignerContext() {
    return String(this.config.contextType || "").startsWith("prompt_")
      || this.config.resourceType === "Prompt"
      || this.config.agentSlug === "prompt-designer"
  }

  getProviderForModel(model) {
    if (!model) return "claude"
    if (model.startsWith("gemini")) return "gemini"
    if (model.toLowerCase().startsWith("deepseek")) return "deepseek"
    if (model.startsWith("kimi")) return "kimi"
    if (model.toLowerCase().startsWith("minimax")) return "minimax"
    if (model.startsWith("glm")) return "glm"
    if (model.startsWith("gpt") || model.startsWith("o")) return "openai"
    return "claude"
  }

  normalizeCredentialStatusPayload(payload) {
    const result = {}
    const assignEntry = (provider, value) => {
      if (!provider) return
      const key = String(provider).toLowerCase()
      result[key] = {
        source: value?.credential_source || value?.source || value?.credentialSource || null,
        authMode: value?.auth_mode || value?.authMode || null,
        connected: value?.connected ?? value?.available ?? value?.is_connected ?? null,
        fallbackUsed: value?.fallback_used ?? value?.fallbackUsed ?? null,
        label: value?.credential_label || value?.label || value?.display_label || value?.displayLabel || null,
        localBridgeUnsupported: value?.local_bridge_unsupported
          ?? value?.localBridgeUnsupported
          ?? value?.local_bridge_task_unsupported
          ?? value?.localBridgeTaskUnsupported
          ?? value?.metadata?.local_bridge_unsupported
          ?? value?.metadata?.localBridgeUnsupported
          ?? value?.metadata?.local_bridge_task_unsupported
          ?? value?.metadata?.localBridgeTaskUnsupported
          ?? (value?.bridge?.status === "unsupported_task")
          ?? false,
        localBridgePending: value?.local_bridge_pending
          ?? value?.localBridgePending
          ?? value?.metadata?.local_bridge_pending
          ?? value?.metadata?.localBridgePending
          ?? false,
        developmentSystemKeyOverride: value?.development_system_key_override
          ?? value?.developmentSystemKeyOverride
          ?? value?.metadata?.development_system_key_override
          ?? value?.metadata?.developmentSystemKeyOverride
          ?? false
      }
    }

    if (!payload) return result
    if (Array.isArray(payload.data)) {
      payload.data.forEach((entry) => assignEntry(entry?.provider, entry))
    } else if (payload.data && typeof payload.data === "object") {
      Object.entries(payload.data).forEach(([provider, value]) => assignEntry(provider, value))
    } else if (payload.providers && typeof payload.providers === "object") {
      Object.entries(payload.providers).forEach(([provider, value]) => assignEntry(provider, value))
    } else if (payload.openai || payload.claude || payload.gemini || payload.kimi) {
      ;["openai", "claude", "gemini", "kimi"].forEach((provider) => {
        if (payload[provider]) assignEntry(provider, payload[provider])
      })
    }

    return result
  }

  credentialStatusForProvider(provider) {
    return this.credentialStatusByProvider?.[provider] || null
  }

  threadsImageCredentialStatusForProvider(provider) {
    return this.threadsImageCredentialStatusByProvider?.[provider] || this.credentialStatusForProvider(provider)
  }

  modelUsesLocalBridge(model) {
    if (!model) return false
    const stateInfo = this.credentialChipStateForProvider(model.provider)
    return stateInfo?.state === "user" && stateInfo?.label === "L"
  }

  modelUsesDevelopmentSystemKey(model) {
    if (!model) return false
    const status = this.credentialStatusForProvider(model.provider)
    return status?.developmentSystemKeyOverride === true && status?.connected !== false
  }

  modelIsAvailableByCredential(model) {
    return this.modelUsesLocalBridge(model) || this.modelUsesDevelopmentSystemKey(model)
  }

  modelIsLocked(model) {
    return this.planSlug === "free" &&
      model &&
      !model.enabled_for_free &&
      !this.modelIsAvailableByCredential(model)
  }

  credentialChipStateForProvider(provider) {
    return this.credentialChipStateForStatus(this.credentialStatusForProvider(provider))
  }

  threadsImageCredentialChipStateForProvider(provider) {
    return this.credentialChipStateForStatus(this.threadsImageCredentialStatusForProvider(provider))
  }

  credentialChipStateForStatus(status) {
    if (!status) {
      return { label: _t("credentials.shared", "공용"), state: "system", title: _t("credentials.title_shared", "전역 기본 키 사용 중") }
    }

    const source = String(status.source || "").toLowerCase()
    const authMode = String(status.authMode || "").toLowerCase()
    const connected = status.connected
    const fallbackUsed = status.fallbackUsed
    const label = status.label
    const isLocal = label === "L"
    const localBridgeUnsupported = status.localBridgeUnsupported === true
    const localBridgePending = status.localBridgePending === true

    if (connected === false) {
      return { label: label || _t("credentials.missing", "연결 필요"), state: "missing", title: _t("credentials.title_missing", "AI 연결이 필요합니다") }
    }

    if (localBridgeUnsupported) {
      return {
        label: _t("credentials.shared", "공용"),
        state: "system",
        title: "로컬 브리지가 이 작업을 지원하지 않아 공용 API로 호출됩니다."
      }
    }

    if (source === "user") {
      if (authMode === "oauth") {
        return { label: label || _t("credentials.oauth", "OAuth"), state: "oauth", title: _t("credentials.title_oauth", "개인 OAuth 연결 사용 중") }
      }
      if (isLocal) {
        return { label, state: "user", title: "" }
      }
      return { label: label || _t("credentials.personal", "개인"), state: "user", title: _t("credentials.title_user", "개인 API key 연결 사용 중") }
    }

    if (source === "system") {
      return { label: label || _t("credentials.shared", "공용"), state: "system", title: _t("credentials.title_shared", "전역 기본 키 사용 중") }
    }

    if (localBridgePending) {
      return { label: _t("credentials.connect_required", "연결 필요"), state: "missing", pendingLocalBridge: true, title: _t("credentials.title_missing", "AI 연결이 필요합니다") }
    }

    if (connected === false || fallbackUsed === false) {
      return { label: label || _t("credentials.connect_required", "연결 필요"), state: "missing", title: _t("credentials.title_missing", "AI 연결이 필요합니다") }
    }

    return { label: label || _t("credentials.shared", "공용"), state: "system", title: _t("credentials.title_shared", "전역 기본 키 사용 중") }
  }

  paintCredentialChip(el, stateInfo, options = {}) {
    if (!el) return
    if (stateInfo?.state === "system" && !options.showSystem) {
      el.style.display = "none"
      return
    }
    const presets = {
      user:        { bg: "#EEF2FF", border: "#C7D2FE", color: "#4338CA" },
      oauth:       { bg: "#ECFDF5", border: "#A7F3D0", color: "#047857" },
      system:      { bg: "#F9FAFB", border: "#E5E7EB", color: "#6B7280" },
      missing:     { bg: "#FEF2F2", border: "#FECACA", color: "#B91C1C" }
    }
    const preset = presets[stateInfo?.state] || presets.system
    el.textContent = stateInfo?.label || _t("credentials.shared", "공용")
    el.title = stateInfo?.title || ""
    el.style.display = "inline-flex"
    el.style.alignItems = "center"
    el.style.height = "18px"
    el.style.padding = "0 6px"
    el.style.border = `1px solid ${preset.border}`
    el.style.background = preset.bg
    el.style.color = preset.color
    el.style.fontSize = "11px"
    el.style.fontWeight = "600"
    el.style.letterSpacing = "-0.01em"
    el.style.whiteSpace = "nowrap"
  }

  updateCredentialSourceUI() {
    const currentModel = this.availableModels?.find(m => m.name === this.selectedModel)
    const provider = currentModel?.provider || this.getProviderForModel(this.selectedModel)
    const stateInfo = this.credentialChipStateForProvider(provider)
    this.paintCredentialChip(this._modelSourceChip, stateInfo)
    this.paintCredentialChip(this._rcModelSourceChip, stateInfo)
  }

  rerenderModelDropdownsPreservingScroll() {
    const dropdowns = [this._modelDropdown, this._rcModelDropdown].filter(Boolean)
    const scrollPositions = dropdowns.map((dropdown) => dropdown.scrollTop || 0)
    this._renderModelDropdown()
    dropdowns.forEach((dropdown, index) => {
      dropdown.scrollTop = scrollPositions[index] || 0
    })
    this.updateCredentialSourceUI()
  }

  credentialStatusPollDelay() {
    let hasPending = false
    let hasLocal = false
    Object.values(this.credentialStatusByProvider || {}).forEach((status) => {
      if (!status || status.localBridgeUnsupported === true) return
      if (status.localBridgePending === true) hasPending = true
      if (String(status.source || "").toLowerCase() === "user" && status.label === "L") hasLocal = true
    })

    if (hasPending) return 15000
    if (hasLocal) return 30000
    return null
  }

  updateCredentialStatusPolling() {
    if (!this.credentialStatusPath || document.visibilityState === "hidden") {
      this.stopCredentialStatusPolling()
      return
    }

    const delay = this.credentialStatusPollDelay()
    if (!delay) {
      this.stopCredentialStatusPolling()
      return
    }
    if (this.credentialStatusPollTimer && this.credentialStatusPollIntervalMs === delay) return

    this.stopCredentialStatusPolling()
    this.credentialStatusPollIntervalMs = delay
    this.credentialStatusPollTimer = window.setInterval(() => {
      this.refreshCredentialStatus({ force: true, rerenderModels: true })
    }, delay)
  }

  stopCredentialStatusPolling() {
    if (this.credentialStatusPollTimer) {
      window.clearInterval(this.credentialStatusPollTimer)
      this.credentialStatusPollTimer = null
    }
    this.credentialStatusPollIntervalMs = null
  }

  async refreshCredentialStatus({ force = false, rerenderModels = false } = {}) {
    const url = this.credentialStatusRequestUrl()
    if (!url) {
      this.updateCredentialSourceUI()
      return
    }
    if (rerenderModels) this.credentialStatusRefreshNeedsRerender = true
    if (this.credentialStatusRefreshPromise) return this.credentialStatusRefreshPromise

    this.credentialStatusRefreshPromise = (async () => {
      try {
        const res = await fetch(url, {
          headers: {
            "Accept": "application/json",
            "X-Requested-With": "XMLHttpRequest"
          }
        })
        if (res.status === 404) {
          this.credentialStatusPath = null
          this.credentialStatusLoaded = true
          this.credentialStatusRefreshNeedsRerender = false
          this.stopCredentialStatusPolling()
          this.updateCredentialSourceUI()
          return
        }
        if (res.ok) {
          const payload = await res.json().catch(() => ({}))
          this.credentialStatusByProvider = this.normalizeCredentialStatusPayload(payload)
        }
        this.credentialStatusLoaded = true
      } catch (e) {
        this.credentialStatusLoaded = true
      }

      const shouldRerenderModels = this.credentialStatusRefreshNeedsRerender
      this.credentialStatusRefreshNeedsRerender = false
      if (shouldRerenderModels) {
        this.rerenderModelDropdownsPreservingScroll()
      } else {
        this.updateCredentialSourceUI()
      }
      this.updateCredentialStatusPolling()
    })()

    try {
      await this.credentialStatusRefreshPromise
    } finally {
      this.credentialStatusRefreshPromise = null
    }
  }

  credentialStatusRequestUrl() {
    const url = this.credentialStatusPath
    if (!url || !this.isImageAssetAgent) return url
    if (/[?&]task=/.test(url)) return url

    const separator = url.includes("?") ? "&" : "?"
    return `${url}${separator}task=image_generation`
  }

  // ── 토글 / 열기 / 닫기 ───────────────────────────────────────────────────

  toggle() {
    this.isOpen = !this.isOpen
    localStorage.setItem(`${this.config.storagePrefix}-open`, this.isOpen ? "1" : "0")
    if (this._wrapper) {
      this._wrapper.style.width = this.isOpen ? "380px" : "0"
      this._wrapper.style.overflow = this.isOpen ? "" : "hidden"
      this._wrapper.classList.toggle("ka-closed", !this.isOpen)
    }
    if (this._toggleBtn) {
      this._toggleBtn.style.display = this.isOpen ? "none" : "flex"
      this._toggleBtn.classList.toggle("ka-panel-open", this.isOpen)
    }
    if (this.isOpen && this._input) this._input.focus()
  }

  open() {
    if (this.isOpen) return
    this.isOpen = true
    localStorage.setItem(`${this.config.storagePrefix}-open`, "1")
    if (this._wrapper) {
      this._wrapper.style.width = "380px"
      this._wrapper.style.overflow = ""
      this._wrapper.classList.remove("ka-closed")
    }
    if (this._toggleBtn) {
      this._toggleBtn.style.display = "none"
      this._toggleBtn.classList.add("ka-panel-open")
    }
    if (this._input) this._input.focus()
  }

  close() {
    if (!this.isOpen) return
    this.isOpen = false
    localStorage.setItem(`${this.config.storagePrefix}-open`, "0")
    if (this._wrapper) {
      this._wrapper.style.width = "0"
      this._wrapper.style.overflow = "hidden"
      this._wrapper.classList.add("ka-closed")
    }
    if (this._toggleBtn) {
      this._toggleBtn.style.display = "flex"
      this._toggleBtn.classList.remove("ka-panel-open")
    }
  }

  // ── 채팅 히스토리 로드 ────────────────────────────────────────────────────

  async loadChatHistory() {
    if (!this.config.chatApiPath || !this.config.resourceId || this.config.resourceId === 0) {
      // /new 페이지에서도 sessionId를 초기화해야 저장 시 사용 가능
      if (!this.currentMainSessionId) {
        this.currentMainSessionId = this.generateSessionId()
        localStorage.setItem(`${this.config.storagePrefix}-main-session`, this.currentMainSessionId)
      }
      this.showWelcomeMessage()
      return
    }

    // 세션 ID 초기화 (없으면 새로 생성)
    if (!this.currentMainSessionId) {
      this.currentMainSessionId = this.generateSessionId()
      localStorage.setItem(`${this.config.storagePrefix}-main-session`, this.currentMainSessionId)
    }

    // /new 페이지에서 이어진 채팅 히스토리가 있으면 복원
    const hadPending = await this._loadAndPersistPendingHistory()
    if (hadPending) {
      if (this.config.contextType === "site" && this.config.resourceId > 0) {
        this._appendSiteCreationNextSteps()
      } else if (this.config.contextType === "page" && this.config.resourceId > 0
                 && this.config.parentSiteId > 0) {
        this._appendPageCreationNextSteps()
      }
      return
    }

    try {
      const res = await fetch(withSearchParams(this.config.chatApiPath, {
        context_type: this.config.contextType,
        section_id: this.config.sectionId || null,
        session_id: this.currentMainSessionId
      }), {
        headers: { "X-Requested-With": "XMLHttpRequest" }
      })
      const data = await res.json()
      const chats = data.data || []

      chats.forEach(chat => {
        this.chatHistory.push({ role: chat.role, content: chat.content })
        this.appendMessage(chat.role, chat.content)
      })

      if (this.isKmongComparisonReportAgent() && chats.length > 0) {
        this.appendKmongComparisonReportFollowUps()
      }

      if (this.chatHistory.length === 0 || this.isEnglishSpeakingTopicWriterAgent()) {
        this.showWelcomeMessage()
      }
    } catch (e) {
      console.error("[KlavyAgentCore] loadChatHistory failed:", e)
      this.showWelcomeMessage()
    }

    this._restoreIaUrlGenCompletionNote()
    this._restorePwUrlGenCompletionNote()
  }

  renderMessages(messages) {
    if (!this._messages) return
    this._messages.innerHTML = ""
    messages.forEach(msg => this.appendMessage(msg.role, msg.content))
  }

  // ── 메시지 전송 ──────────────────────────────────────────────────────────

  async send() {
    const input = this._input
    if (!input) return
    let msg = input.value.trim()
    if (!msg || this.isLoading) return
    const originalMsg = msg

    if (this.isIaUrlGenerationAgent()) {
      const iaUrl = this.extractFirstUrl(originalMsg)
      if (iaUrl && this.isIaGenerationPrompt(originalMsg)) {
        input.value = ""
        this.autoResize()
        await this.generateIaFromUrl(iaUrl, { prompt: originalMsg })
        return
      }
    }

    this._messages?.querySelectorAll(".ba-followup-buttons").forEach(el => el.remove())
    this._messages?.querySelectorAll(".ba-hero-template-suggestion").forEach(el => el.remove())
    input.value = ""
    this.autoResize()

    const isKakaoPromptAttachmentSend = this.isKakaoTalkDraftAgent()
    const promptAttachments = this.isPromptAttachmentAgent() ? this.buildPromptAttachments() : null

    if (this.config.promptToolsEnabled && this.selectedMode === "think") {
      msg = await this.enhancePromptForPromptTools(msg)
    }

    const promptAugmentedMsg = this.composePromptAttachmentMessage(msg, promptAttachments)

    // 이미지 첨부 시 content를 멀티파트로 구성
    const userContent = this.imageBase64 && !isKakaoPromptAttachmentSend
      ? [
          { type: "image", source: { type: "base64", media_type: this.imageBase64.substring(5, this.imageBase64.indexOf(";base64,")), data: this.imageBase64.replace(/^data:image\/[^;]+;base64,/, "") } },
          { type: "text", text: originalMsg }
        ]
      : originalMsg
    const aiUserContent = this.imageBase64 && !isKakaoPromptAttachmentSend
      ? userContent.map(part => part.type === "text" ? { ...part, text: promptAugmentedMsg } : part)
      : promptAugmentedMsg

    this.appendMessage("user", userContent)
    this.config.container.dispatchEvent(new CustomEvent("klavy-agent:user-message", { bubbles: true, detail: { text: originalMsg } }))
    await this.persistMessage("user", originalMsg)

    if (this.isKmongComparisonReportAgent() && this.isKmongComparisonPatchRevisionRequest(msg)) {
      this.chatHistory.push({ role: "user", content: msg })
      await this.reviseKmongComparisonPatch(msg)
      return
    }

    if (this.isKmongServiceDraftAgent()) {
      if (this.kmongNewServiceBrief?.active) {
        this.chatHistory.push({ role: "user", content: msg })
        await this.handleKmongNewServiceBriefInput(msg)
        return
      }
      if (this.isKmongComparisonReportAgent() && this.isKmongComparisonReportCreateRequest(msg)) {
        this.chatHistory.push({ role: "user", content: msg })
        await this.createKmongComparisonReport(msg, { analysisMode: this.pendingKmongComparisonAnalysisMode })
        this.pendingKmongComparisonAnalysisMode = null
        return
      }
      if (this.isKmongComparisonReportAgent() && this.isKmongComparisonGeneratedImageDeleteRequest(msg)) {
        this.chatHistory.push({ role: "user", content: msg })
        await this.deleteKmongComparisonGeneratedImages(this.kmongComparisonGeneratedImageDeletePayload(msg))
        return
      }
      if (this.isKmongComparisonReportAgent() && this.isKmongComparisonMainImageGenerateRequest(msg)) {
        this.chatHistory.push({ role: "user", content: msg })
        await this.createKmongComparisonMainImage({ instructions: msg, prompt: msg })
        return
      }
      if (this.isKmongComparisonReportAgent() && this.isKmongComparisonDetailImageRefineRequest(msg)) {
        this.chatHistory.push({ role: "user", content: msg })
        await this.createKmongComparisonDetailImages(this.kmongComparisonDetailImageRefinePayload(msg))
        return
      }
      if (!this.isKmongComparisonReportAgent() && this.isKmongServiceRegistrationDetailImageRequest(msg)) {
        this.chatHistory.push({ role: "user", content: msg })
        await this.createKmongServiceRegistrationDetailImage(this.kmongServiceRegistrationDetailImagePayload(msg))
        return
      }
      if (!this.isKmongComparisonReportAgent() && this.isKmongNewServiceBriefStartRequest(msg)) {
        this.chatHistory.push({ role: "user", content: msg })
        await this.startKmongNewServiceBrief()
        return
      }
    }

    const shouldAutoStartPrecisePrototype =
      this.config.agentSlug === "prototype-designer" &&
      this.config.resourceType === "UiPlanningProject" &&
      this.config.contextType === "ui_planning_prototype" &&
      typeof msg === "string" &&
      (msg.includes("요소 단위로 정밀 연결") || msg.includes("100% 요소 단위로 전체 연결") || msg.includes("요소 선택 연결"))

    const shouldAutoStartSequentialConnect =
      this.config.agentSlug === "prototype-designer" &&
      this.config.resourceType === "UiPlanningProject" &&
      this.config.contextType === "ui_planning_prototype" &&
      typeof msg === "string" &&
      msg.includes("순서대로 자동 연결")

    const shouldAutoStartFullConnect =
      shouldAutoStartPrecisePrototype && msg.includes("100% 요소 단위로 전체 연결")

    const shouldAutoStartSelectiveConnect =
      shouldAutoStartPrecisePrototype && msg.includes("요소 선택 연결")

    // 프로토타입 "요소 단위로 정밀 연결" 전용 — 클릭 요소 인벤토리를 AI 호출 메시지에만 주입
    // (UI/저장 로그에는 깔끔한 원문 msg 가 남고, chatHistory 의 AI 호출 페이로드에만 JSON 이 붙는다)
    let historyContent = aiUserContent
    try {
      if (
        shouldAutoStartPrecisePrototype &&
        !shouldAutoStartFullConnect &&
        !shouldAutoStartSelectiveConnect &&
        typeof window !== "undefined" &&
        typeof window.buildPrototypeElementsContext === "function" &&
        window.__prototypeApp
      ) {
        const ctx = window.buildPrototypeElementsContext(window.__prototypeApp)
        const totalElements = (ctx?.frames || []).reduce((s, f) => s + (f.elements?.length || 0), 0)
        console.log(`[prototype] ELEMENTS_CONTEXT 인벤토리: ${ctx?.frames?.length || 0} 프레임, 총 ${totalElements} 요소`, ctx)
        if (ctx && Array.isArray(ctx.frames) && totalElements > 0) {
          const augmented = `${msg}\n\n<ELEMENTS_CONTEXT>\n${JSON.stringify(ctx)}\n</ELEMENTS_CONTEXT>`
          historyContent = this.imageBase64
            ? userContent.map(p => p.type === "text" ? { ...p, text: augmented } : p)
            : augmented
          console.log("[prototype] ELEMENTS_CONTEXT 주입 완료 — AI 호출 페이로드에 포함됨")
        } else {
          console.warn("[prototype] 요소 인벤토리가 비어 있어 ELEMENTS_CONTEXT 주입을 생략. iframe 미렌더 또는 클릭 가능한 요소 없음")
        }
      }
    } catch (ctxErr) {
      console.warn("[prototype] ELEMENTS_CONTEXT 주입 실패 — 원문 프롬프트로 전송:", ctxErr)
    }

    this.chatHistory.push({ role: "user", content: historyContent })

    if (shouldAutoStartSequentialConnect) {
      await this._runSequentialConnectFlow()
      return
    }

    if (shouldAutoStartFullConnect) {
      // 결정론 경로(렌더 불필요, AI 비용 0)로 요소 단위 전체 연결.
      // 수동 MCP 방식과 동일: 프레임 HTML 에서 클릭 요소 추출 → 이름 매칭 → 일괄 핫스팟 생성.
      await this._runDeterministicElementConnectFlow()
      return
    }

    if (shouldAutoStartSelectiveConnect) {
      await this._runSelectiveConnectFlow()
      return
    }

    // 이미지 전송 후 프리뷰 제거
    const hadImage = !!this.imageBase64
    if (hadImage && !isKakaoPromptAttachmentSend) this.removeImage()

    // section-template: UI기획 가져오기 또는 시안 병렬 생성
    if (this.config.agentSlug === "section-template") {
      if (userContent === "__ui_planning_import__") {
        this._openUiPlanningImportWizard()
        return
      }
      const selected = await this._pickSectionTemplateVariants()
      if (!selected || !selected.length) return
      await this._sendSectionTemplateVariants(msg, userContent, selected)
      return
    }

    if (this.isImageAssetAgent && !hadImage && this.isDirectImageAssetGenerationRequest(msg)) {
      this.setLoading(true)
      try {
        const formData = this.buildDirectImageAssetFormData(msg)
        this.lastFormData = formData
        await this.createImageAssets(formData.data)
      } catch (e) {
        console.error("[KlavyAgentCore] direct image asset generation failed", e)
        this.appendSystemNote(e.message || _t("system_notes.image_asset_request_parse_failed", "생성 요청을 정리하지 못했어요"))
      } finally {
        this.setLoading(false)
      }
      return
    }

    if (this.isThreadsDraftAgent()) {
      if (this.threadsAgentMode === "profile" || this.isThreadsProfileRequest(msg)) {
        await this.updateThreadsAgentProfile(msg)
      } else if (this.threadsAgentMode === "strategy" || this.isThreadsStrategyRequest(msg)) {
        await this.updateThreadsAgentStrategy(msg)
      } else {
        await this.createThreadsAgentDraft(msg)
      }
      return
    }

    if (this.isFacebookAdsDraftAgent()) {
      await this.createFacebookAdsAgentDraft(msg)
      return
    }

    if (this.isKakaoTalkDraftAgent()) {
      if (this.threadsAgentMode === "profile" || this.isKakaoTalkProfileRequest(msg)) {
        await this.updateKakaoTalkAgentProfile(msg, promptAttachments)
      } else if (this.threadsAgentMode === "strategy" || this.isKakaoTalkStrategyRequest(msg)) {
        await this.updateKakaoTalkAgentStrategy(msg, promptAttachments)
      } else {
        await this.createKakaoTalkAgentDraft(msg, promptAttachments)
      }
      return
    }

    if (this.isKmongServiceDraftAgent()) {
      if (this.isKmongComparisonReportAgent()) {
        // 비교 리포트 화면의 일반 질문은 아래의 일반 Agent 대화 흐름으로 보낸다.
      } else {
        const kmongServiceRegistrationSource = this.isKmongServiceRegistrationSourceRequest(msg)
        if ((this.threadsAgentMode === "profile" && !kmongServiceRegistrationSource) || this.isKmongSellerProfileRequest(msg)) {
          await this.updateKmongSellerProfile(msg)
          return
        }
        await this.createKmongServiceDraft(msg)
        return
      }
    }

    if (
      this.isPlanningWorkspaceContext() &&
      this.config.contextType === "planning_main_target" &&
      this.isPlanningMainTargetGenerateRequest(msg)
    ) {
      await this.generatePlanningMainTargetFromServer(msg)
      return
    }

    this.setLoading(true)

    try {
      const model = this._resolveModel(msg)
      const provider = this.getProvider(model)
      const systemPrompt = this.buildSystemPrompt()
      let streamEl = null
      const requestOptions = {
        provider,
        endpointPrefix: this.config.aiBasePath,
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        payload: {
          model: model,
          system: systemPrompt,
          messages: this.chatHistory,
          max_tokens: this.planningWorkspaceMaxTokens(),
          context: this._buildUsageContext()
        },
        onFirstChunk: () => {
          this.setLoading(false)
          streamEl = document.createElement("div")
          streamEl.className = "ba-msg-assistant ba-streaming"
          if (this._messages) this._messages.appendChild(streamEl)
          this.scrollToBottom()
        },
        onContent: (fullText) => {
          if (!streamEl) return
          const streamJsonLabel = _t("loading.form_data", "폼 데이터 생성 중...")
          const displayText = this.cleanAssistantDisplayText(fullText, { streaming: true, replacement: streamJsonLabel })
          streamEl.innerHTML = this._renderMarkdown(displayText)
          this.scrollToBottom()
          this.config.container.dispatchEvent(new CustomEvent("klavy-agent:stream-chunk", { bubbles: true, detail: { fullText } }))
        }
      }
      let aiText = await this.requestAiResponse(requestOptions)

      if (streamEl) streamEl.remove()

      let formData = this.extractFormData(aiText)
      const coldmailQuality = await this.ensureColdmailSequenceQuality({
        aiText,
        formData,
        requestOptions,
        userMessage: msg
      })
      aiText = coldmailQuality.aiText
      formData = coldmailQuality.formData
      const qualityBlocked = coldmailQuality.blocked
      const qualityIssues = coldmailQuality.issues || []
      const isImageAssetCreateAction = this.isImageAssetAgent && formData?._action === "create_image_assets"

      if (!isImageAssetCreateAction) {
        this.chatHistory.push({ role: "assistant", content: aiText })
      }
      // 이전 메시지에서 이미지 데이터 제거 (메모리 최적화)
      this.chatHistory.forEach((msg, idx) => {
        if (idx >= this.chatHistory.length - 2) return
        if (Array.isArray(msg.content)) {
          msg.content = msg.content.filter(p => p.type === "text")
        }
      })
      if (shouldAutoStartPrecisePrototype && formData?._action === "apply_prototype_actions") {
        formData.data = {
          ...(formData.data || {}),
          auto_start_from_first_connection: true
        }
      }
      if (!isImageAssetCreateAction) {
        this.appendMessage("assistant", aiText)
        this.config.container.dispatchEvent(new CustomEvent("klavy-agent:message-complete", { bubbles: true, detail: { text: aiText } }))
        await this.persistMessage("assistant", aiText)
      }

      if (qualityBlocked) {
        this.appendSystemNote(`콜드메일 품질 기준 미달로 자동 적용하지 않았습니다: ${qualityIssues.slice(0, 3).join(", ")}`)
        return
      }

      if (formData) {
        if (formData._action === "add_menu_item" || formData._action === "add_menu_items") {
          this._appendMenuItemButton(formData)
        } else if (isImageAssetCreateAction) {
          this.lastFormData = formData
          await this.createImageAssets(formData.data)
        } else {
          this.lastFormData = formData
          this._appendApplyButton(formData)
        }
      }
      if (!isImageAssetCreateAction) {
        this._appendFollowUpButtons(aiText)
      }
    } catch (e) {
      console.error("[KlavyAgentCore]", e)
      if (e.message === "quota_exceeded") {
        const upgradeUrl = e.payload?.upgrade_url || "/pricing"
        if (e.reason === "model_not_available") {
          this.appendMessage("assistant", _t("system_notes.model_not_available", "이 AI 모델은 현재 요금제에서 사용할 수 없어요. 다른 모델을 선택하거나 요금제를 확인해주세요."))
        } else {
          this.appendMessage("assistant", _t("system_notes.usage_limit", "오늘 AI 사용량을 다 썼어요. 더 쓰려면 요금제를 업그레이드해보세요."))
        }
        this._appendUpgradeButton(upgradeUrl)
      } else {
        this.appendMessage("assistant", _t("system_notes.api_error", "오류가 발생했습니다: %{message}").replace("%{message}", e.message))
      }
    } finally {
      this.setLoading(false)
    }
  }

  isThreadsDraftAgent() {
    return this.config.agentSlug === "threads-writer" &&
      this.config.resourceType === "ThreadsCampaign" &&
      !!this.threadsDraftApiPath()
  }

  isFacebookAdsDraftAgent() {
    return this.config.agentSlug === "facebook-ad-writer" &&
      this.config.resourceType === "FacebookAdsCampaign" &&
      !!this.draftApiPath()
  }

  isKakaoTalkDraftAgent() {
    return this.config.agentSlug === "kakao-talk-writer" &&
      this.config.resourceType === "KakaoTalkChatRoom" &&
      !!this.kakaoTalkDraftApiPath()
  }

  isKmongServiceDraftAgent() {
    return this.config.agentSlug === "kmong-service-writer" &&
      (this.config.resourceType === "KmongServiceRegistrationDraft" || this.config.resourceType === "KmongServiceRegistration" || this.config.resourceType === "Kmong::ServiceRegistration") &&
      !!this.draftApiPath()
  }

  isKmongComparisonReportAgent() {
    return this.config.agentSlug === "kmong-service-writer" &&
      (this.config.resourceType === "KmongServiceRegistrationDraft" || this.config.resourceType === "KmongServiceRegistration" || this.config.resourceType === "Kmong::ServiceRegistration") &&
      this.config.contextType === "kmong_comparison_report" &&
      !!this.config.comparisonReportApiPath
  }

  isKmongComparisonPatchRevisionRequest(prompt) {
    if (!this.isKmongComparisonReportAgent()) return false
    return String(prompt || "").includes(">>")
  }

  isKmongComparisonReportCreateRequest(prompt) {
    if (!this.isKmongComparisonReportAgent()) return false
    const compact = String(prompt || "").replace(/\s+/g, "")
    const target = /(개선리포트|비교리포트|AI평가|경쟁력평가|리포트|진단리포트)/i.test(compact)
    const action = /(생성|만들|작성|평가|진단|실행|시작)/i.test(compact)
    return target && action
  }

  isKmongComparisonDetailImageRefineRequest(prompt) {
    if (!this.isKmongComparisonReportAgent()) return false
    const text = String(prompt || "")
    const compact = text.replace(/\s+/g, "")
    const hasDetailImageTarget = /(상세이미지|상세image|detailimage)/i.test(compact)
    const hasActiveStorageUrl = /https?:\/\/\S*\/rails\/active_storage\/blobs\/redirect\/\S+/i.test(text)
    const action = /(생성|만들|요청|뽑아|수정|고쳐|바꿔|교체|다시생성|재생성|새로생성|빼|제거|삭제|반영)/i.test(compact)
    return (hasDetailImageTarget || hasActiveStorageUrl) && action
  }

  isKmongServiceRegistrationDetailImageRequest(prompt) {
    if (!this.isKmongServiceDraftAgent() || this.isKmongComparisonReportAgent()) return false
    const compact = String(prompt || "").replace(/\s+/g, "")
    const hasDetailImageTarget = /(상세이미지|상세image|detailimage)/i.test(compact)
    const action = /(생성|만들|요청|뽑아|다시생성|재생성|새로생성|교체)/i.test(compact)
    return hasDetailImageTarget && action
  }

  isKmongComparisonGeneratedImageDeleteRequest(prompt) {
    if (!this.isKmongComparisonReportAgent()) return false
    const text = String(prompt || "")
    const compact = text.replace(/\s+/g, "")
    const hasImageTarget = /(대표이미지|메인이미지|mainimage|상세이미지|상세image|detailimage)/i.test(compact) ||
      /https?:\/\/\S*\/rails\/active_storage\/blobs\/redirect\/\S+/i.test(text)
    const deleteAction = /(삭제|제거|지워|없애|비워)/i.test(compact)
    return hasImageTarget && deleteAction
  }

  isKmongComparisonMainImageGenerateRequest(prompt) {
    if (!this.isKmongComparisonReportAgent()) return false
    const compact = String(prompt || "").replace(/\s+/g, "")
    const hasMainTarget = /(대표이미지|메인이미지|mainimage|커버이미지)/i.test(compact)
    const action = /(생성|만들|수정|고쳐|바꿔|교체|다시생성|재생성|새로생성)/i.test(compact)
    return hasMainTarget && action
  }

  kmongComparisonDetailImageRefinePayload(prompt) {
    const text = String(prompt || "")
    const urlMatch = text.match(/https?:\/\/\S*\/rails\/active_storage\/blobs\/redirect\/\S+/i)
    const positions = this.extractKmongComparisonDetailImagePositions(text)
    const count = this.extractKmongComparisonDetailImageCount(text)

    return {
      instructions: text,
      count: count || undefined,
      positions: !count && positions.length > 1 ? positions : undefined,
      target_position: positions.length === 1 && !count ? positions[0] : undefined,
      target_image_url: urlMatch ? urlMatch[0] : undefined
    }
  }

  kmongServiceRegistrationDetailImagePayload(prompt) {
    const text = String(prompt || "")
    const count = this.extractKmongComparisonDetailImageCount(text)
    return {
      instructions: text,
      count: count || undefined
    }
  }

  kmongComparisonGeneratedImageDeletePayload(prompt) {
    const text = String(prompt || "")
    const compact = text.replace(/\s+/g, "")
    const urlMatch = text.match(/https?:\/\/\S*\/rails\/active_storage\/blobs\/redirect\/\S+/i)
    const roles = []
    if (/(대표이미지|메인이미지|mainimage|커버이미지)/i.test(compact)) roles.push("main")
    if (/(상세이미지|상세image|detailimage)/i.test(compact) || urlMatch) roles.push("detail")
    const positions = this.extractKmongComparisonDetailImagePositions(text)
    return {
      roles: roles.length > 0 ? roles : ["detail"],
      positions: positions.length > 0 ? positions : undefined,
      target_image_url: urlMatch ? urlMatch[0] : undefined
    }
  }

  extractKmongComparisonDetailImageCount(prompt) {
    const compact = String(prompt || "").replace(/\s+/g, "")
    if (!/(상세이미지|상세image|detailimage)/i.test(compact)) return null

    const numberToken = "(\\d{1,2}|한|하나|두|둘|세|셋|네|넷|다섯|여섯|일곱|여덟|아홉)"
    const detailTarget = "(?:상세이미지|상세image|detailimage)"
    const patterns = [
      new RegExp(`${detailTarget}${numberToken}(?:개|장)만?`, "i"),
      new RegExp(`${numberToken}(?:개|장)만?${detailTarget}`, "i")
    ]

    for (const pattern of patterns) {
      const match = compact.match(pattern)
      if (match) {
        const count = this.kmongComparisonDetailImageNumberValue(match[1])
        return Number.isFinite(count) && count > 0 ? Math.min(count, 9) : null
      }
    }

    return null
  }

  kmongComparisonDetailImageNumberValue(value) {
    const token = String(value || "")
    const koreanNumbers = {
      "한": 1,
      "하나": 1,
      "두": 2,
      "둘": 2,
      "세": 3,
      "셋": 3,
      "네": 4,
      "넷": 4,
      "다섯": 5,
      "여섯": 6,
      "일곱": 7,
      "여덟": 8,
      "아홉": 9
    }
    if (Object.prototype.hasOwnProperty.call(koreanNumbers, token)) return koreanNumbers[token]

    const count = Number(token)
    return Number.isFinite(count) ? count : null
  }

  extractKmongComparisonDetailImagePositions(prompt) {
    const compact = String(prompt || "").replace(/\s+/g, "")
    if (!/(상세이미지|상세image|detailimage)/i.test(compact)) return []

    const values = new Set()
    const patterns = [
      /상세이미지(?:번호)?((?:\d{1,2}(?:,|，|및|와|과|번|번째)?)+)번?/g,
      /((?:\d{1,2}(?:,|，|및|와|과)?)+)(?:번|번째)상세이미지/g,
      /상세image(?:번호)?((?:\d{1,2}(?:,|，|및|와|과|번|번째)?)+)번?/gi
    ]
    patterns.forEach((pattern) => {
      let match
      while ((match = pattern.exec(compact)) !== null) {
        String(match[1] || "").match(/\d{1,2}/g)?.forEach((num) => {
          const position = Number(num)
          if (position >= 1 && position <= 9) values.add(position)
        })
      }
    })
    return Array.from(values).sort((a, b) => a - b)
  }

  isKmongSellerProfileRequest(prompt) {
    if (!this.config.kmongSellerProfileApiPath) return false
    if (this.isKmongServiceRegistrationSourceRequest(prompt)) return false
    const text = String(prompt || "").trim()
    const maxProfileCommandLength = 120
    if (text.length > maxProfileCommandLength) return false
    const compact = String(prompt || "").replace(/\s+/g, "")
    const profileTarget = /(전문가프로필|판매자프로필|크몽프로필|프로필관리|전문가정보|판매자소개|전문가소개|활동정보|경력|학력|자격증|전문분야|보유기술)/.test(compact)
    const profileAction = /(수정|바꿔|바꾸|업데이트|저장|입력|작성|생성|만들|정리|보완|개선|다듬|삭제|비워|초기화|지워|없애|제거)/.test(compact)
    return profileTarget && profileAction
  }

  isKmongServiceRegistrationSourceRequest(prompt) {
    const text = String(prompt || "").trim()
    if (!text) return false

    const compact = text.replace(/\s+/g, "")
    const markers = [
      /상세\s*정보/,
      /가격\s*정보/,
      /판매\s*핵심\s*정보/,
      /서비스\s*설명/,
      /주요\s*특징/,
      /서비스\s*제공\s*절차/,
      /의뢰인\s*준비사항/,
      /취소\s*및\s*환불\s*규정/,
      /상품\s*정보\s*고시|상품정보고시/,
      /작업\s*전\s*요청사항/,
      /검색\s*키워드/,
      /자주\s*묻는\s*질문|FAQ/i,
      /메인\s*이미지/,
      /상세\s*이미지/,
      /STANDARD/i,
      /DELUXE/i,
      /PREMIUM/i
    ]
    const markerCount = markers.reduce((count, pattern) => count + (pattern.test(text) ? 1 : 0), 0)
    const hasPackageMarkers = /STANDARD/i.test(text) && /DELUXE/i.test(text) && /PREMIUM/i.test(text)
    const draftIntent = /(서비스초안|새서비스초안|신규서비스초안|상품정보등록|상품등록|등록글|서비스등록).{0,20}(작성|생성|만들|등록|적용|반영|채워|옮겨)?/.test(compact) ||
      /(작성|생성|만들|등록|적용|반영|채워|옮겨).{0,20}(서비스초안|상품정보|상품등록|등록글|서비스등록)/.test(compact)

    return markerCount >= 4 ||
      (markerCount >= 3 && hasPackageMarkers) ||
      (text.length >= 500 && markerCount >= 2) ||
      (text.length >= 250 && draftIntent && markerCount >= 1)
  }

  isKmongPastedListingApplyRequest(prompt) {
    const text = String(prompt || "")
    if (text.length < 300) return false
    if (!this.isKmongServiceRegistrationSourceRequest(text)) return false

    const applyIntent = /(이\s*내용|위\s*내용|붙여넣은\s*내용|원문|각\s*항목|100%|최대한).{0,80}(적용|반영|채워|입력|옮겨|맞춰)|(적용|반영|채워|입력|옮겨|맞춰).{0,80}(각\s*항목|100%|그대로|최대한)/m
    return applyIntent.test(text)
  }

  draftApiPath() {
    return this.config.draftApiPath || this.config.threadsDraftApiPath || this.config.kakaoTalkDraftApiPath || null
  }

  comparisonPatchApiPath() {
    return this.config.comparisonPatchApiPath || null
  }

  threadsDraftApiPath() {
    return this.config.threadsDraftApiPath || (this.config.draftWriterKind === "threads" ? this.config.draftApiPath : null)
  }

  kakaoTalkDraftApiPath() {
    return this.config.kakaoTalkDraftApiPath || (this.config.draftWriterKind === "kakao_talk" ? this.config.draftApiPath : null)
  }

  isDraftWriterAgent() {
    return this.isThreadsDraftAgent() ||
      this.isFacebookAdsDraftAgent() ||
      this.isKakaoTalkDraftAgent() ||
      this.isKmongServiceDraftAgent() ||
      this.isColdmailCampaignContext()
  }

  draftWriterCopy() {
    if (this.isKmongServiceDraftAgent()) {
      return {
        draftPlaceholder: "어떤 크몽 서비스를 등록할까요?",
        strategyPlaceholder: "서비스 판매 전략을 어떻게 정리할까요?",
        profilePlaceholder: "판매자 소개를 어떻게 정리할까요?"
      }
    }

    if (this.isKakaoTalkDraftAgent()) {
      return {
        draftPlaceholder: "어떤 카카오톡 문구를 만들어 드릴까요?",
        strategyPlaceholder: "채팅방 핵심 전략을 어떻게 생성하거나 수정할까요?",
        profilePlaceholder: "카카오톡 프로필을 어떻게 생성하거나 수정할까요?"
      }
    }

    if (this.isFacebookAdsDraftAgent()) {
      return {
        draftPlaceholder: "어떤 Facebook 광고 소재를 만들까요?",
        strategyPlaceholder: "광고 캠페인 전략을 어떻게 정리할까요?",
        profilePlaceholder: "브랜드 정보를 어떻게 정리할까요?"
      }
    }

    if (this.isColdmailCampaignContext()) {
      return {
        draftPlaceholder: "어떤 이메일 시퀀스를 만들까요?",
        strategyPlaceholder: "이메일 캠페인 전략을 어떻게 정리할까요?",
        profilePlaceholder: "발신자/브랜드 정보를 어떻게 정리할까요?"
      }
    }

    return {
      draftPlaceholder: "어떤 Threads 초안을 만들어 드릴까요?",
      strategyPlaceholder: "핵심 전략을 어떻게 생성하거나 수정할까요?",
      profilePlaceholder: "쓰레드 프로필을 어떻게 생성하거나 수정할까요?"
    }
  }

  draftWriterContentLabel() {
    if (this.config.designerBridge) return "텍스트"
    if (this.isKakaoTalkDraftAgent()) return "문구"
    if (this.isKmongServiceDraftAgent()) return "서비스"
    if (this.isFacebookAdsDraftAgent()) return "광고"
    if (this.isColdmailCampaignContext()) return "메일"
    return "글"
  }

  kmongNewServiceBriefQuestions() {
    return [
      {
        key: "service_type",
        title: "어떤 분야의 서비스를 등록할까요?",
        hint: "가장 가까운 분야를 고르면 카테고리와 키워드 방향을 맞춥니다.",
        type: "single",
        options: ["웹·앱 기획", "디자인", "마케팅", "문서·글쓰기", "상담·컨설팅", "AI가 제안"]
      },
      {
        key: "buyer",
        title: "주요 구매자는 누구인가요?",
        hint: "구매자 관점이 잡히면 제목과 상세 설명이 덜 모호해집니다.",
        type: "single",
        options: ["초기 스타트업", "소상공인", "기업 담당자", "프리랜서·1인 사업자", "예비 창업자", "AI가 제안"]
      },
      {
        key: "deliverables",
        title: "구매자가 받는 결과물은 무엇인가요?",
        hint: "여러 개를 선택하거나 입력창에 직접 적어도 됩니다.",
        type: "multi",
        options: ["문서", "Figma 파일", "PDF", "원본 파일", "상담 리포트", "체크리스트", "이미지 시안", "AI가 제안"]
      },
      {
        key: "strengths",
        title: "강점이나 신뢰 요소는 무엇인가요?",
        hint: "없는 항목은 고르지 않아도 됩니다. 확인되지 않은 성과는 만들지 않습니다.",
        type: "multi",
        options: ["실무 경력", "성과 사례", "빠른 납품", "꼼꼼한 커뮤니케이션", "AI 활용", "전문 툴 활용", "AI가 제안"]
      },
      {
        key: "package_direction",
        title: "가격과 패키지는 어떤 방향이 좋을까요?",
        hint: "정확한 가격을 모르면 방향만 골라도 됩니다.",
        type: "single",
        options: ["저가 진입형", "표준형", "고가 전문형", "기존 가격 유지", "AI가 제안"]
      },
      {
        key: "cautions",
        title: "작업 제외 범위나 유의사항이 있나요?",
        hint: "분쟁을 줄이는 항목입니다. 모르면 버튼으로 넘기세요.",
        type: "text",
        options: ["특별한 유의사항 없음", "AI가 제안"]
      }
    ]
  }

  isKmongNewServiceBriefStartRequest(prompt) {
    const text = String(prompt || "").trim()
    const compact = text.replace(/\s+/g, "").replace(/[.!?。]+$/g, "")
    const maxBriefCommandLength = 40
    if (text.length > maxBriefCommandLength || compact.length > maxBriefCommandLength) return false

    const briefCommands = [
      "판매할서비스와대상고객을설명하면등록초안을작성해줘",
      "상품정보등록",
      "상품등록",
      "상품정보등록해",
      "상품정보등록해줘",
      "상품정보등록해주세요",
      "상품등록해",
      "상품등록해줘",
      "상품등록해주세요",
      "새서비스초안",
      "새서비스초안작성",
      "새서비스초안작성해",
      "새서비스초안만들어줘",
      "신규서비스초안",
      "신규서비스초안작성",
      "신규서비스초안작성해",
      "신규서비스초안만들어줘"
    ]
    return briefCommands.includes(compact)
  }

  async startKmongNewServiceBrief({ echo = false } = {}) {
    this.kmongNewServiceBrief = { active: true, index: 0, answers: {}, selections: {} }
    if (echo) {
      this.chatHistory.push({ role: "user", content: "상품 정보 등록" })
      this.appendMessage("user", "상품 정보 등록")
      await this.persistMessage("user", "상품 정보 등록")
    }
    await this.appendAndPersistAgentSummary("상품 정보 등록에 필요한 내용을 먼저 짧게 확인하겠습니다. 아는 것은 버튼으로 고르고, 애매한 항목은 AI가 제안하도록 넘기면 됩니다.")
    await this.renderKmongNewServiceBriefQuestion()
  }

  async handleKmongNewServiceBriefInput(message) {
    const state = this.kmongNewServiceBrief
    if (!state?.active) return

    const question = this.kmongNewServiceBriefQuestions()[state.index]
    if (!question) {
      await this.finishKmongNewServiceBrief()
      return
    }

    const answer = question.type === "multi"
      ? String(message || "").split(/[,\n]/).map(item => item.trim()).filter(Boolean)
      : String(message || "").trim()
    await this.commitKmongNewServiceBriefAnswer(question, answer, { echo: false })
  }

  async renderKmongNewServiceBriefQuestion() {
    const state = this.kmongNewServiceBrief
    if (!state?.active) return

    this.disableKmongBriefCards()
    const questions = this.kmongNewServiceBriefQuestions()
    const question = questions[state.index]
    if (!question) {
      await this.finishKmongNewServiceBrief()
      return
    }

    const selected = new Set(Array.from(state.selections?.[question.key] || []))
    const msgEl = document.createElement("div")
    msgEl.className = "ba-msg-assistant ba-kmong-brief-card"
    msgEl.dataset.active = "true"
    msgEl.innerHTML = `
      <div style="border:1px solid #E5E7EB;background:#FFFFFF;padding:14px;">
        <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;">
          <strong style="font-size:14px;color:#111827;">${this.escapeHtml(question.title)}</strong>
          <span style="font-size:12px;color:#6B7280;">${state.index + 1}/${questions.length}</span>
        </div>
        <p style="margin:8px 0 0;font-size:12px;line-height:1.5;color:#6B7280;">${this.escapeHtml(question.hint || "")}</p>
        <div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:12px;">
          ${question.options.map(option => {
            const active = selected.has(option)
            return `<button type="button" class="ba-quick-btn ba-kmong-brief-option${active ? " is-selected" : ""}" data-brief-value="${this.escapeHtml(option)}" style="${active ? "background:#EEF2FF;color:#4F46E5;border-color:#A5B4FC;" : ""}">${this.escapeHtml(option)}</button>`
          }).join("")}
        </div>
        ${question.type === "multi" ? `<button type="button" class="ba-quick-btn ba-kmong-brief-next" style="margin-top:12px;background:#4F46E5;color:#FFFFFF;border-color:#4F46E5;">선택 완료</button>` : ""}
        <p style="margin:10px 0 0;font-size:11px;line-height:1.5;color:#9CA3AF;">직접 쓰려면 아래 입력창에 답을 입력해 주세요.</p>
      </div>
    `

    if (this._messages) this._messages.appendChild(msgEl)
    if (this._input) this._input.placeholder = "직접 답변을 입력해도 됩니다."
    this.scrollToBottom()
    const questionSummary = `${question.title}\n${question.hint || ""}`
    this.chatHistory.push({ role: "assistant", content: questionSummary })
    await this.persistMessage("assistant", questionSummary)

    msgEl.querySelectorAll("[data-brief-value]").forEach(button => {
      button.addEventListener("click", async () => {
        const value = button.dataset.briefValue
        if (question.type === "multi") {
          const next = new Set(Array.from(this.kmongNewServiceBrief?.selections?.[question.key] || []))
          if (next.has(value)) next.delete(value)
          else next.add(value)
          this.kmongNewServiceBrief.selections[question.key] = Array.from(next)
          button.classList.toggle("is-selected", next.has(value))
          button.style.background = next.has(value) ? "#EEF2FF" : ""
          button.style.color = next.has(value) ? "#4F46E5" : ""
          button.style.borderColor = next.has(value) ? "#A5B4FC" : ""
        } else {
          await this.commitKmongNewServiceBriefAnswer(question, value)
        }
      })
    })

    msgEl.querySelector(".ba-kmong-brief-next")?.addEventListener("click", async () => {
      const values = Array.from(this.kmongNewServiceBrief?.selections?.[question.key] || [])
      await this.commitKmongNewServiceBriefAnswer(question, values.length > 0 ? values : ["AI가 제안"])
    })
  }

  disableKmongBriefCards() {
    this._messages?.querySelectorAll(".ba-kmong-brief-card[data-active='true']").forEach(card => {
      card.dataset.active = "false"
      card.querySelectorAll("button").forEach(button => { button.disabled = true })
    })
  }

  async commitKmongNewServiceBriefAnswer(question, answer, { echo = true } = {}) {
    const state = this.kmongNewServiceBrief
    if (!state?.active) return

    const normalized = Array.isArray(answer)
      ? answer.map(item => String(item || "").trim()).filter(Boolean)
      : String(answer || "").trim()
    const display = Array.isArray(normalized) ? normalized.join(", ") : normalized
    state.answers[question.key] = Array.isArray(normalized) ? normalized : display

    if (echo) {
      this.chatHistory.push({ role: "user", content: display || "AI가 제안" })
      this.appendMessage("user", display || "AI가 제안")
      await this.persistMessage("user", display || "AI가 제안")
    }

    state.index += 1
    if (state.index >= this.kmongNewServiceBriefQuestions().length) {
      await this.finishKmongNewServiceBrief()
    } else {
      await this.renderKmongNewServiceBriefQuestion()
    }
  }

  async finishKmongNewServiceBrief() {
    const state = this.kmongNewServiceBrief
    if (!state) return

    state.active = false
    if (this._input) this._input.placeholder = this.draftWriterCopy().draftPlaceholder
    this.disableKmongBriefCards()
    await this.appendAndPersistAgentSummary("브리핑을 바탕으로 신규 크몽 서비스 등록 초안을 작성하겠습니다.")
    await this.createKmongServiceDraft(this.buildKmongNewServiceBriefPrompt(state.answers || {}))
  }

  buildKmongNewServiceBriefPrompt(answers) {
    const questions = this.kmongNewServiceBriefQuestions()
    const answerLine = (question) => {
      const value = answers[question.key]
      const rendered = Array.isArray(value) ? value.join(", ") : String(value || "").trim()
      return `- ${question.title}: ${rendered || "AI가 제안"}`
    }

    return [
      "[신규 서비스 브리핑]",
      ...questions.map(answerLine),
      "",
      "[작성 지시]",
      "- 위 브리핑을 바탕으로 신규 크몽 등록 초안을 작성한다.",
      "- 버튼 선택값을 그대로 나열하지 말고 구매자가 이해할 수 있는 구체적인 문장으로 재구성한다.",
      "- 모르는 항목은 보수적으로 제안하되, 확인되지 않은 경력·성과·보장 표현은 만들지 않는다."
    ].join("\n")
  }

  isThreadsStrategyRequest(prompt) {
    if (!this.config.threadsStrategyApiPath) return false
    const text = String(prompt || "").trim()
    const compact = text.replace(/\s+/g, "")
    const strategyTarget = /(핵심전략|전략문서|캠페인전략|콘텐츠전략|운영전략)/.test(compact)
    const strategyAction = /(수정|바꿔|바꾸|업데이트|저장|입력|작성|생성|만들|정리|보완|개선|다듬)/.test(compact)
    return strategyTarget && strategyAction
  }

  isThreadsProfileRequest(prompt) {
    if (!this.config.threadsProfileApiPath) return false
    const text = String(prompt || "").trim()
    const compact = text.replace(/\s+/g, "")
    const profileTarget = /(프로필|소개|정체성|바이오|bio|계정소개|쓰레드프로필|Threads프로필)/i.test(compact)
    const profileAction = /(수정|바꿔|바꾸|업데이트|저장|입력|작성|생성|만들|정리|보완|개선|다듬|삭제|비워|초기화|지워|없애|제거)/.test(compact)
    return profileTarget && profileAction
  }

  isKakaoTalkStrategyRequest(prompt) {
    if (!this.config.kakaoTalkStrategyApiPath) return false
    const text = String(prompt || "").trim()
    const compact = text.replace(/\s+/g, "")
    const strategyTarget = /(핵심전략|전략문서|채팅방전략|방전략|대화전략|카카오톡전략|카톡전략)/.test(compact)
    const strategyAction = /(수정|바꿔|바꾸|업데이트|저장|입력|작성|생성|만들|정리|보완|개선|다듬)/.test(compact)
    return strategyTarget && strategyAction
  }

  isKakaoTalkProfileRequest(prompt) {
    if (!this.config.kakaoTalkProfileApiPath) return false
    const text = String(prompt || "").trim()
    const compact = text.replace(/\s+/g, "")
    const profileTarget = /(프로필|프로필보정|방별프로필|소개|정체성|말투|톤|배경|기본작성규칙|카카오톡프로필|카톡프로필)/.test(compact)
    const profileAction = /(수정|바꿔|바꾸|업데이트|저장|입력|작성|생성|만들|정리|보완|개선|다듬|삭제|비워|초기화|지워|없애|제거)/.test(compact)
    return profileTarget && profileAction
  }

  selectThreadsAgentMode(e) {
    const mode = e?.currentTarget?.dataset?.threadsMode || "draft"
    this.threadsAgentMode = ["draft", "strategy", "profile"].includes(mode) ? mode : "draft"
    localStorage.setItem(`${this.config.storagePrefix}-threads-mode`, this.threadsAgentMode)
    this.renderThreadsAgentMode()
  }

  setKmongWorkspaceTab(tab) {
    if (!this.isKmongServiceDraftAgent()) return
    if (this.isKmongComparisonReportAgent()) return

    this.threadsAgentMode = tab === "profile" ? "profile" : "draft"
    localStorage.setItem(`${this.config.storagePrefix}-threads-mode`, this.threadsAgentMode)
    this.renderThreadsAgentMode()
    this.renderKmongWelcomeMessage()
  }

  renderThreadsAgentMode() {
    if (!this.isDraftWriterAgent()) return

    if (!["draft", "strategy", "profile"].includes(this.threadsAgentMode)) this.threadsAgentMode = "draft"
    if (this.isKmongComparisonReportAgent()) this.threadsAgentMode = "draft"
    const hasDocumentModes = this.isThreadsDraftAgent() || this.isKakaoTalkDraftAgent() || this.isKmongServiceDraftAgent()
    if (!hasDocumentModes) this.threadsAgentMode = "draft"
    if (this.isKmongServiceDraftAgent() && this.threadsAgentMode === "strategy") this.threadsAgentMode = "draft"

    const strategyMode = this.threadsAgentMode === "strategy"
    const profileMode = this.threadsAgentMode === "profile"
    const documentMode = strategyMode || profileMode
    this._threadsDraftModeTab?.classList.toggle("is-active", !documentMode)
    this._threadsStrategyModeTab?.classList.toggle("is-active", strategyMode)
    this._threadsProfileModeTab?.classList.toggle("is-active", profileMode)

    if (this._threadsImageModelSection) {
      this._threadsImageModelSection.style.display = documentMode ? "none" : ""
    }
    if (documentMode && this._threadsImageModelDropdown) {
      this._threadsImageModelDropdown.style.display = "none"
      this.threadsImageModelDropdownOpen = false
    }

    if (this._input) {
      const copy = this.draftWriterCopy()
      this._input.placeholder = profileMode
        ? copy.profilePlaceholder
        : strategyMode
          ? copy.strategyPlaceholder
          : copy.draftPlaceholder
    }
  }

  renderKmongWelcomeMessage() {
    if (!this.isKmongServiceDraftAgent() || !this._messages || this.chatHistory.length > 0) return

    this._messages.innerHTML = ""
    const msgEl = document.createElement("div")
    msgEl.className = "ka-message ka-message--assistant"
    const quickBtns = this.kmongWelcomeQuickButtons()
    if (this.isKmongComparisonReportAgent()) {
      const welcomeMsg = this.config.welcomeMessage || "내 상품과 경쟁상품을 AI 경쟁력 기준으로 평가합니다."
      msgEl.innerHTML = `<p><strong>AI 경쟁력 리포트를 생성할까요?</strong></p><p style="margin-top:8px; font-size:13px; color:#6B7280;">${this.escapeHtml(welcomeMsg)}</p><p style="margin-top:6px; font-size:13px; color:#6B7280;">서비스 생성 모델은 평가 리포트 품질에 반영됩니다. 대표 이미지 생성 모델은 선택하지 않아도 이미지 포함 리포트 분석이 가능하며, 새 대표 이미지를 만들 때만 사용됩니다.</p><div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:10px;">${quickBtns.map(t => this.quickButtonHtml(t)).join("")}</div>`
    } else if (this.threadsAgentMode === "profile") {
      msgEl.innerHTML = `<p><strong>전문가 프로필을 어떻게 정리할까요?</strong></p><p style="margin-top:8px; font-size:13px; color:#6B7280;">크몽 상세 페이지와 등록 초안에 공통으로 쓰는 판매자 정보를 정리합니다. 경력, 전문분야, 보유 기술, 신뢰 포인트를 사실 기반으로 보내주세요.</p><p style="margin-top:6px; font-size:13px; color:#6B7280;">생성된 내용은 전문가 프로필 탭에 저장되고 현재 초안의 판매자 프로필에도 반영됩니다.</p><div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:10px;">${quickBtns.map(t => this.quickButtonHtml(t)).join("")}</div>`
    } else {
      const welcomeMsg = this.config.welcomeMessage || "크몽 서비스 등록 초안을 만들어드릴게요."
      msgEl.innerHTML = `<p><strong>어떤 서비스를 등록할까요?</strong></p><p style="margin-top:8px; font-size:13px; color:#6B7280;">${this.escapeHtml(welcomeMsg)}</p><p style="margin-top:6px; font-size:13px; color:#6B7280;">생성된 내용은 서비스 등록 초안으로 저장됩니다. 이미지 모델을 선택하면 첫 번째 이미지 방향으로 대표 이미지도 생성합니다.</p><div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:10px;">${quickBtns.map(t => this.quickButtonHtml(t)).join("")}</div>`
    }
    this._messages.appendChild(msgEl)
  }

  kmongWelcomeQuickButtons() {
    if (this.isKmongComparisonReportAgent()) {
      return this.config.quickStarts
    }

    if (this.threadsAgentMode === "profile") {
      return [
        { text: "프로필 개선", prompt: "현재 전문가 프로필을 크몽 상세 페이지에 맞게 더 신뢰감 있게 다듬어줘." },
        { text: "경력 정리", prompt: "경력, 학력, 자격증 정보를 전문가 프로필에 맞게 정리해줘." },
        { text: "전문분야 정리", prompt: "전문분야와 보유 기술을 크몽 프로필에 맞게 정리해줘." },
        { text: "신뢰 포인트", prompt: "상세 페이지에 보여줄 신뢰 포인트를 정리해줘." },
        { text: "프로필 초기화", prompt: "전문가 프로필 내용을 초기화해줘." }
      ]
    }

    const baseQuickStarts = this.config.quickStarts.length > 0
      ? this.config.quickStarts
      : [
          { text: "기존 등록글 개선", prompt: "기존 크몽 등록글을 붙여넣고, 같은 서비스를 더 잘 팔리게 재작성해줘." },
          { text: "상품 정보 등록", prompt: "판매할 서비스와 대상 고객을 설명하면 등록 초안을 작성해줘.", actionType: "kmong-new-service-brief-start" },
          { text: "패키지 구성", prompt: "베이직/스탠다드/프리미엄 패키지를 제안해줘." },
          { text: "FAQ 작성", prompt: "구매자가 등록 전에 확인할 FAQ를 작성해줘." },
          { text: "대표 이미지 기획", prompt: "서비스 대표 이미지와 상세 이미지 방향을 제안해줘." }
        ]

    return this.appendUniqueQuickButtons(baseQuickStarts, this.kmongServiceRegistrationAssetQuickButtons())
  }

  kmongServiceRegistrationAssetQuickButtons() {
    return [
      { text: "상품명형 대표 이미지", prompt: "현재 등록 초안을 기준으로 상품명이 크게 보이는 크몽 대표 이미지를 생성해줘", actionType: "kmong-comparison-main-image", mainImageMode: "product" },
      { text: "문제제기형 대표 이미지", prompt: "현재 등록 초안을 기준으로 구매자 문제가 먼저 보이는 크몽 대표 이미지를 생성해줘", actionType: "kmong-comparison-main-image", mainImageMode: "problem" },
      { text: "상세 이미지 생성", prompt: "현재 등록 초안을 기준으로 구매 설득에 필요한 크몽 상세 이미지를 생성해줘", actionType: "kmong-comparison-detail-images" },
      { text: "전문가 이미지 생성", prompt: "현재 전문가 프로필을 바탕으로 크몽 프로필 이미지를 생성해줘", actionType: "kmong-seller-profile-image" },
      { text: "전문가 정보 생성", prompt: "현재 등록 초안과 서비스 맥락을 기준으로 타겟 고객이 문의하고 싶어지는 전문가 소개를 생성해줘", actionType: "kmong-seller-profile-generate" }
    ]
  }

  appendUniqueQuickButtons(baseButtons, extraButtons) {
    const seen = new Set()
    return [...Array.from(baseButtons || []), ...Array.from(extraButtons || [])].filter(button => {
      const label = typeof button === "object" && button !== null ? (button.text || button.label || "") : String(button || "")
      const key = label.trim()
      if (!key || seen.has(key)) return false
      seen.add(key)
      return true
    })
  }

  toggleThreadInputExpand(e) {
    e?.preventDefault?.()
    this.inputExpanded = !this.inputExpanded
    this.renderThreadInputExpandedState()
  }

  renderThreadInputExpandedState() {
    if (!this.isThreadsDraftAgent()) return
    if (this._inputExpandBtn) this._inputExpandBtn.textContent = this.inputExpanded ? "접기" : "전체 보기"
    if (this._inputWrapper) this._inputWrapper.classList.toggle("ka-input-expanded", this.inputExpanded)
    this.autoResize()
  }

  setDraftWriterFieldValue(id, value) {
    if (value == null) return
    const field = document.getElementById(id)
    if (field) field.value = value
  }

  async appendAndPersistAgentSummary(summary, { dispatchComplete = false } = {}) {
    this.chatHistory.push({ role: "assistant", content: summary })
    this.appendMessage("assistant", summary)
    if (dispatchComplete) {
      this.config.container.dispatchEvent(new CustomEvent("klavy-agent:message-complete", { bubbles: true, detail: { text: summary } }))
    }
    await this.persistMessage("assistant", summary)
  }

  async updateThreadsAgentStrategy(prompt) {
    this.setLoading(true)
    try {
      const response = await fetch(this.config.threadsStrategyApiPath, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({
          prompt,
          text_model_id: this.selectedModel
        })
      })

      const result = await response.json().catch(() => ({}))
      if (!response.ok || result.ok === false) {
        const error = new Error(result.error || result.message || `HTTP ${response.status}`)
        error.reason = result.reason
        throw error
      }

      const strategy = result.data?.strategy || ""
      const strategyEditor = document.getElementById("thStrategyEditor")
      if (strategyEditor) {
        strategyEditor.value = strategy
        if (document.getElementById("thStrategyPanel")?.classList.contains("is-expanded")) {
          window.thResizeCampaignContextEditorToContent?.("strategy")
        }
      }

      const summary = [
        "핵심 전략을 업데이트했습니다.",
        "이번 요청은 새 회차 초안을 만들지 않고 상단 핵심 전략 문서만 수정했습니다.",
        "이후 AI 회차 생성과 Agent 초안 작성에서 이 전략을 참조합니다."
      ].join("\n")

      this.chatHistory.push({ role: "assistant", content: summary })
      this.appendMessage("assistant", summary)
      this.config.container.dispatchEvent(new CustomEvent("klavy-agent:message-complete", { bubbles: true, detail: { text: summary } }))
      await this.persistMessage("assistant", summary)
    } catch (e) {
      console.error("[KlavyAgentCore] threads strategy update failed", e)
      if (e.reason === "model_not_available") {
        this.appendMessage("assistant", _t("system_notes.model_not_available", "이 AI 모델은 현재 요금제에서 사용할 수 없어요. 다른 모델을 선택하거나 요금제를 확인해주세요."))
      } else {
        this.appendMessage("assistant", _t("system_notes.api_error", "오류가 발생했습니다: %{message}").replace("%{message}", e.message))
      }
    } finally {
      this.setLoading(false)
    }
  }

  async updateThreadsAgentProfile(prompt) {
    this.setLoading(true)
    try {
      const response = await fetch(this.config.threadsProfileApiPath, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({
          prompt,
          text_model_id: this.selectedModel
        })
      })

      const result = await response.json().catch(() => ({}))
      if (!response.ok || result.ok === false) {
        const error = new Error(result.error || result.message || `HTTP ${response.status}`)
        error.reason = result.reason
        throw error
      }

      const data = result.data || {}
      if (data.awaiting_selection) {
        const suggestions = Array.isArray(data.suggestions) ? data.suggestions : []
        const suggestionLines = suggestions.map((item, index) => {
          const name = item?.name || ""
          const handle = item?.handle || ""
          const reason = item?.reason || ""
          return `${index + 1}. ${name} / ${handle}${reason ? `\n   - ${reason}` : ""}`
        })
        const summary = [
          "닉네임과 아이디 후보를 추천했습니다.",
          "아직 프로필에는 저장하지 않았습니다.",
          "",
          ...suggestionLines,
          "",
          "원하는 후보를 `1번으로 적용`, `2번으로 적용`처럼 입력하면 쓰레드 프로필에 반영합니다.",
          "참고: 실제 Threads 아이디 사용 가능 여부는 Threads에서 최종 확인이 필요합니다."
        ].join("\n")

        this.chatHistory.push({ role: "assistant", content: summary })
        this.appendMessage("assistant", summary)
        this.config.container.dispatchEvent(new CustomEvent("klavy-agent:message-complete", { bubbles: true, detail: { text: summary } }))
        await this.persistMessage("assistant", summary)
        return
      }

      const profile = data.profile || ""
      const profileEditor = document.getElementById("thProfileEditor")
      if (profileEditor) {
        profileEditor.value = profile
        if (document.getElementById("thProfilePanel")?.classList.contains("is-expanded")) {
          window.thResizeCampaignContextEditorToContent?.("profile")
        }
      }

      const summary = data.deleted
        ? [
            "쓰레드 프로필을 삭제했습니다.",
            "이후 AI 회차 생성과 Agent 초안 작성에서는 프로필 문서를 제외합니다."
          ].join("\n")
        : data.applied_selection
          ? [
              "선택한 닉네임과 아이디를 쓰레드 프로필에 반영했습니다.",
              "이번 요청은 새 회차 초안을 만들지 않고 상단 쓰레드 프로필 문안만 수정했습니다.",
              "실제 Threads 아이디 사용 가능 여부는 Threads 프로필 편집 화면에서 최종 확인해 주세요."
            ].join("\n")
        : [
            "쓰레드 프로필을 업데이트했습니다.",
            "이번 요청은 새 회차 초안을 만들지 않고 상단 쓰레드 프로필 문안만 수정했습니다.",
            "이후 AI 회차 생성과 Agent 초안 작성에서 이 프로필을 기본 정체성으로 참조합니다."
          ].join("\n")

      this.chatHistory.push({ role: "assistant", content: summary })
      this.appendMessage("assistant", summary)
      this.config.container.dispatchEvent(new CustomEvent("klavy-agent:message-complete", { bubbles: true, detail: { text: summary } }))
      await this.persistMessage("assistant", summary)
    } catch (e) {
      console.error("[KlavyAgentCore] threads profile update failed", e)
      if (e.reason === "model_not_available") {
        this.appendMessage("assistant", _t("system_notes.model_not_available", "이 AI 모델은 현재 요금제에서 사용할 수 없어요. 다른 모델을 선택하거나 요금제를 확인해주세요."))
      } else {
        this.appendMessage("assistant", _t("system_notes.api_error", "오류가 발생했습니다: %{message}").replace("%{message}", e.message))
      }
    } finally {
      this.setLoading(false)
    }
  }

  async createThreadsAgentDraft(prompt) {
    this.setLoading(true)
    try {
      const response = await fetch(this.threadsDraftApiPath(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({
          prompt,
          text_model_id: this.selectedModel,
          image_model_id: this.selectedThreadsImageModel || null
        })
      })

      const result = await response.json().catch(() => ({}))
      if (!response.ok || result.ok === false) {
        const error = new Error(result.error || result.message || `HTTP ${response.status}`)
        error.reason = result.reason
        throw error
      }

      const data = result.data || {}
      const post = data.post || {}
      const mediaCount = Array.isArray(data.media_asset_ids) ? data.media_asset_ids.length : 0
      const replyDraftCount = Number(data.reply_draft_count || 0)
      const action = data.action || "created"
      const roundLabel = `R${post.round_number || data.round_number || ""}`
      const summaryLines = action === "updated" ? [
        `초안 ${roundLabel}을 수정했습니다.`,
        "기존 미디어와 답글 초안은 그대로 유지했습니다.",
        "카드에서 수정된 본문을 확인할 수 있습니다."
      ] : [
        `초안 ${roundLabel}을 등록했습니다.`,
        replyDraftCount > 0 ? `답글 초안 ${replyDraftCount}개를 함께 등록했습니다.` : null,
        mediaCount > 0 ? `첨부 미디어 ${mediaCount}개를 연결했습니다.` : "미디어는 첨부하지 않았습니다.",
        "카드의 발행 버튼으로 승인 후 Threads에 발행할 수 있습니다."
      ].filter(Boolean)
      const summary = summaryLines.join("\n")

      this.chatHistory.push({ role: "assistant", content: summary })
      this.appendMessage("assistant", summary)
      this.config.container.dispatchEvent(new CustomEvent("klavy-agent:message-complete", { bubbles: true, detail: { text: summary } }))
      await this.persistMessage("assistant", summary)
      window.setTimeout(() => window.location.reload(), 700)
    } catch (e) {
      console.error("[KlavyAgentCore] threads draft failed", e)
      if (e.reason === "model_not_available") {
        this.appendMessage("assistant", _t("system_notes.model_not_available", "이 AI 모델은 현재 요금제에서 사용할 수 없어요. 다른 모델을 선택하거나 요금제를 확인해주세요."))
      } else {
        this.appendMessage("assistant", _t("system_notes.api_error", "오류가 발생했습니다: %{message}").replace("%{message}", e.message))
      }
    } finally {
      this.setLoading(false)
    }
  }

  async createFacebookAdsAgentDraft(prompt, options = {}) {
    this.setLoading(true)
    try {
      const imageOnly = !!options.imageOnly
      const imageMode = options.imageMode || null
      const response = await fetch(this.draftApiPath(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({
          prompt,
          text_model_id: this.selectedModel,
          image_model_id: this.selectedThreadsImageModel || null,
          image_mode: imageMode,
          image_only: imageOnly
        })
      })

      const result = await response.json().catch(() => ({}))
      if (!response.ok || result.ok === false) {
        const error = new Error(result.error || result.message || `HTTP ${response.status}`)
        error.reason = result.reason
        throw error
      }

      const data = result.data || {}
      const creative = data.creative || {}
      const roundLabel = `R${creative.round_number || ""}`
      const imageStatus = data.action === "image_updated"
        ? "기존 광고 카피는 유지하고 대표 이미지만 새로 생성했습니다."
        : data.image_generated
        ? "단일 이미지 광고용 이미지도 함께 생성했습니다."
        : data.image_error
          ? `광고 카피는 저장했지만 이미지는 생성하지 못했습니다: ${data.image_error}`
          : "이미지는 생성하지 않았습니다. 이미지 모델을 선택하면 다음 요청부터 함께 생성됩니다."
      const summary = [
        data.action === "image_updated"
          ? `Facebook 광고 소재 ${roundLabel}의 대표 이미지를 교체했습니다.`
          : `Facebook 광고 소재 ${roundLabel}을 등록했습니다.`,
        imageStatus,
        "카드에서 본문, 제목, 설명, CTA, 랜딩 URL을 복사해 Ads Manager에 수동 등록할 수 있습니다."
      ].join("\n")

      this.chatHistory.push({ role: "assistant", content: summary })
      this.appendMessage("assistant", summary)
      this.config.container.dispatchEvent(new CustomEvent("klavy-agent:message-complete", { bubbles: true, detail: { text: summary } }))
      await this.persistMessage("assistant", summary)
      window.setTimeout(() => window.location.reload(), 700)
    } catch (e) {
      console.error("[KlavyAgentCore] facebook ads creative failed", e)
      if (e.reason === "model_not_available") {
        this.appendMessage("assistant", _t("system_notes.model_not_available", "이 AI 모델은 현재 요금제에서 사용할 수 없어요. 다른 모델을 선택하거나 요금제를 확인해주세요."))
      } else {
        this.appendMessage("assistant", _t("system_notes.api_error", "오류가 발생했습니다: %{message}").replace("%{message}", e.message))
      }
    } finally {
      this.setLoading(false)
    }
  }

  async updateKakaoTalkAgentStrategy(prompt, promptAttachments = null) {
    this.setLoading(true)
    try {
      const response = await fetch(this.config.kakaoTalkStrategyApiPath, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({
          prompt,
          text_model_id: this.selectedModel,
          prompt_attachments: promptAttachments
        })
      })

      const result = await response.json().catch(() => ({}))
      if (!response.ok || result.ok === false) {
        const error = new Error(result.error || result.message || `HTTP ${response.status}`)
        error.reason = result.reason
        throw error
      }

      const data = result.data || {}
      const chatRoom = data.chat_room || data.room || {}
      this.setDraftWriterFieldValue("ktRoomName", chatRoom.name)
      this.setDraftWriterFieldValue("ktRoomRelationship", chatRoom.relationship)
      this.setDraftWriterFieldValue("ktRoomContext", chatRoom.room_context)
      this.setDraftWriterFieldValue("ktRoomStrategy", data.strategy ?? chatRoom.strategy)
      this.setDraftWriterFieldValue("ktRoomProfileOverride", chatRoom.profile_override)

      const summary = [
        "채팅방 핵심 전략을 업데이트했습니다.",
        "이번 요청은 새 카카오톡 문구를 만들지 않고 채팅방 전략 문서만 수정했습니다.",
        "이후 AI 생성과 Agent 문구 작성에서 이 전략을 참조합니다."
      ].join("\n")

      await this.appendAndPersistAgentSummary(summary)
      this.clearKakaoPromptAttachments()
    } catch (e) {
      console.error("[KlavyAgentCore] kakao talk strategy update failed", e)
      if (e.reason === "model_not_available") {
        this.appendMessage("assistant", _t("system_notes.model_not_available", "이 AI 모델은 현재 요금제에서 사용할 수 없어요. 다른 모델을 선택하거나 요금제를 확인해주세요."))
      } else {
        this.appendMessage("assistant", _t("system_notes.api_error", "오류가 발생했습니다: %{message}").replace("%{message}", e.message))
      }
    } finally {
      this.setLoading(false)
    }
  }

  async updateKakaoTalkAgentProfile(prompt, promptAttachments = null) {
    this.setLoading(true)
    try {
      const response = await fetch(this.config.kakaoTalkProfileApiPath, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({
          prompt,
          text_model_id: this.selectedModel,
          prompt_attachments: promptAttachments
        })
      })

      const result = await response.json().catch(() => ({}))
      if (!response.ok || result.ok === false) {
        const error = new Error(result.error || result.message || `HTTP ${response.status}`)
        error.reason = result.reason
        throw error
      }

      const data = result.data || {}
      const profile = data.kakao_talk_profile || data.profile_data || {}
      this.setDraftWriterFieldValue("ktProfileDisplayName", profile.display_name)
      this.setDraftWriterFieldValue("ktProfileTone", profile.tone)
      this.setDraftWriterFieldValue("ktProfileIdentity", profile.identity || data.profile || "")
      this.setDraftWriterFieldValue("ktProfileBackground", profile.background)
      this.setDraftWriterFieldValue("ktProfileRules", profile.default_rules)

      const summary = data.deleted
        ? [
            "카카오톡 프로필을 삭제했습니다.",
            "이후 Agent 문구 작성에서는 공통 카카오톡 프로필을 제외합니다."
          ].join("\n")
        : [
            "카카오톡 프로필을 업데이트했습니다.",
            "이번 요청은 새 카카오톡 문구를 만들지 않고 상단 카카오톡 프로필만 수정했습니다.",
            "이후 모든 채팅방의 Agent 문구 작성에서 이 정체성과 말투를 참조합니다."
          ].join("\n")

      await this.appendAndPersistAgentSummary(summary)
      this.clearKakaoPromptAttachments()
    } catch (e) {
      console.error("[KlavyAgentCore] kakao talk profile update failed", e)
      if (e.reason === "model_not_available") {
        this.appendMessage("assistant", _t("system_notes.model_not_available", "이 AI 모델은 현재 요금제에서 사용할 수 없어요. 다른 모델을 선택하거나 요금제를 확인해주세요."))
      } else {
        this.appendMessage("assistant", _t("system_notes.api_error", "오류가 발생했습니다: %{message}").replace("%{message}", e.message))
      }
    } finally {
      this.setLoading(false)
    }
  }

  async createKakaoTalkAgentDraft(prompt, promptAttachments = null) {
    this.setLoading(true)
    try {
      const response = await fetch(this.kakaoTalkDraftApiPath(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({
          prompt,
          text_model_id: this.selectedModel,
          image_model_id: this.selectedThreadsImageModel || null,
          prompt_attachments: promptAttachments
        })
      })

      const result = await response.json().catch(() => ({}))
      if (!response.ok || result.ok === false) {
        const error = new Error(result.error || result.message || `HTTP ${response.status}`)
        error.reason = result.reason
        throw error
      }

      const data = result.data || {}
      const message = data.message || data.kakao_talk_message || data.post || {}
      const mediaCount = Array.isArray(data.media_asset_ids) ? data.media_asset_ids.length : 0
      const action = data.action || "created"
      const roundNumber = message.round_number || data.round_number
      const roundLabel = roundNumber ? `R${roundNumber}` : "새 문구"
      const summaryLines = action === "updated" ? [
        `카카오톡 문구 ${roundLabel}을 수정했습니다.`,
        "카드에서 수정된 본문을 확인할 수 있습니다."
      ] : [
        `카카오톡 문구 ${roundLabel}을 등록했습니다.`,
        mediaCount > 0 ? `첨부 미디어 ${mediaCount}개를 연결했습니다.` : "이미지는 첨부하지 않았습니다.",
        "카드의 복사 버튼으로 카카오톡에 붙여넣을 수 있습니다."
      ]
      const summary = summaryLines.join("\n")

      await this.appendAndPersistAgentSummary(summary)
      this.clearKakaoPromptAttachments()
      window.setTimeout(() => window.location.reload(), 700)
    } catch (e) {
      console.error("[KlavyAgentCore] kakao talk draft failed", e)
      if (e.reason === "model_not_available") {
        this.appendMessage("assistant", _t("system_notes.model_not_available", "이 AI 모델은 현재 요금제에서 사용할 수 없어요. 다른 모델을 선택하거나 요금제를 확인해주세요."))
      } else {
        this.appendMessage("assistant", _t("system_notes.api_error", "오류가 발생했습니다: %{message}").replace("%{message}", e.message))
      }
    } finally {
      this.setLoading(false)
    }
  }

  async createKmongServiceDraft(prompt) {
    if (this.isKmongPastedListingApplyRequest(prompt)) {
      await this.importKmongPastedListing(prompt)
      return
    }

    const stages = [
      { key: "basic", label: "1/3 기본 정보", loading: "1/3 기본 정보를 작성하고 있어요", done: "1/3 기본 정보를 저장했습니다." },
      { key: "details", label: "2/3 상세 설명", loading: "2/3 상세 설명과 정책을 정리하고 있어요", done: "2/3 상세 설명과 정책을 저장했습니다." },
      { key: "commerce", label: "3/3 패키지와 FAQ", loading: "3/3 패키지와 FAQ를 구성하고 있어요", done: "3/3 패키지와 FAQ를 저장했습니다." }
    ]
    let finalData = null
    let improvementNotes = []
    const savedStages = []

    this.setLoadingMessage(stages[0].loading)
    this.setLoading(true)
    try {
      for (const stage of stages) {
        this.setLoadingMessage(stage.loading)
        try {
          finalData = await this.createKmongServiceDraftStage(prompt, stage)
        } catch (stageError) {
          if (!this.shouldRetryKmongServiceDraftStage(stageError)) throw stageError

          this.setLoadingMessage(`${stage.label} 단계를 다시 시도하고 있어요`)
          finalData = await this.createKmongServiceDraftStage(prompt, stage)
          finalData.retried = true
        }
        const stageNotes = this.extractKmongImprovementNotes(finalData?.draft_payload)
        if (stageNotes.length > 0) improvementNotes = stageNotes
        savedStages.push(stage.label)
        this.appendMessage("assistant", finalData.retried ? `${stage.done} 재시도 후 완료했습니다.` : stage.done)
      }

      const data = finalData || {}
      const draft = data.draft || {}
      const draftPayload = data.draft_payload || {}
      const mediaIds = Array.isArray(data.media_asset_ids) ? data.media_asset_ids : []
      const mediaCount = mediaIds.length

      const title = draft.title || draftPayload.title || "서비스 등록 초안"
      const packageCount = Number(draft.package_count || 0) || (Array.isArray(draftPayload.packages) ? draftPayload.packages.length : 0)
      const faqCount = Number(draft.faq_count || 0) || (Array.isArray(draftPayload.faq) ? draftPayload.faq.length : Array.isArray(draftPayload.faqs) ? draftPayload.faqs.length : 0)
      const summary = [
        "크몽 서비스 등록 초안을 저장했습니다.",
        `제목: ${title}`,
        packageCount > 0 ? `패키지 ${packageCount}개를 구성했습니다.` : null,
        faqCount > 0 ? `FAQ ${faqCount}개를 작성했습니다.` : null,
        mediaCount > 0 ? `대표 이미지 ${mediaCount}개를 생성했습니다.` : null,
        improvementNotes.length > 0 ? `개선 방향:\n- ${improvementNotes.join("\n- ")}` : null,
        "화면의 등록 폼에서 저장된 내용을 확인할 수 있습니다."
      ].filter(Boolean).join("\n")

      await this.appendAndPersistAgentSummary(summary, { dispatchComplete: true })
      window.setTimeout(() => window.location.reload(), 700)
    } catch (e) {
      console.error("[KlavyAgentCore] kmong service draft failed", e)
      if (e.reason === "model_not_available") {
        this.appendMessage("assistant", _t("system_notes.model_not_available", "이 AI 모델은 현재 요금제에서 사용할 수 없어요. 다른 모델을 선택하거나 요금제를 확인해주세요."))
      } else if (e.reason === "needs_service_context") {
        this.appendMessage("assistant", e.message || "판매할 서비스 설명이나 기존 크몽 등록글을 붙여넣어 주세요.")
      } else {
        const savedText = savedStages.length > 0 ? `\n저장 완료: ${savedStages.join(", ")}` : ""
        const prefix = e.stageLabel ? `${e.stageLabel} 단계에서 오류가 발생했습니다.` : "오류가 발생했습니다."
        this.appendMessage("assistant", `${prefix}: ${e.message}${savedText}\n이전 단계까지 저장된 내용은 유지됩니다. 같은 요청을 다시 보내면 저장된 내용을 바탕으로 다시 작성합니다.`)
      }
    } finally {
      this.setLoading(false)
      this.setLoadingMessage("")
    }
  }

  async importKmongPastedListing(prompt) {
    this.setLoadingMessage("붙여넣은 크몽 원문을 항목별로 적용하고 있어요")
    this.setLoading(true)

    try {
      const response = await fetch(this.draftApiPath(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({
          prompt,
          import_pasted_listing: true,
          text_model_id: this.selectedModel
        })
      })

      const result = await response.json().catch(() => ({}))
      if (!response.ok || result.ok === false) {
        const error = new Error(result.error || result.message || `HTTP ${response.status}`)
        error.reason = result.reason
        error.status = response.status
        throw error
      }

      const data = result.data || {}
      const draft = data.draft || {}
      const draftPayload = data.draft_payload || {}
      const title = draft.title || draftPayload.title || "서비스 등록 초안"
      const importer = data.importer || {}
      const packageCount = Number(importer.packages || draft.package_count || 0)
      const faqCount = Number(importer.faqs || draft.faq_count || 0)
      const warnings = Array.isArray(data.warnings) ? data.warnings : []
      const summary = [
        "붙여넣은 크몽 원문을 서비스 등록 초안에 적용했습니다.",
        `제목: ${title}`,
        packageCount > 0 ? `패키지 ${packageCount}개를 반영했습니다.` : null,
        faqCount > 0 ? `FAQ ${faqCount}개를 반영했습니다.` : null,
        warnings.length > 0 ? `확인 필요:\n- ${warnings.join("\n- ")}` : null,
        "이미지와 동영상 파일은 원본 파일명 단서만 보존했습니다."
      ].filter(Boolean).join("\n")

      await this.appendAndPersistAgentSummary(summary, { dispatchComplete: true })
      window.setTimeout(() => window.location.reload(), 700)
    } catch (e) {
      console.error("[KlavyAgentCore] kmong pasted listing import failed", e)
      this.appendMessage("assistant", e.message || "붙여넣은 원문을 적용하지 못했습니다.")
    } finally {
      this.setLoading(false)
      this.setLoadingMessage("")
    }
  }

  inferKmongComparisonAnalysisMode(prompt) {
    const text = String(prompt || "")
    if (/이미지|OCR|비전|상세 이미지|대표 이미지/.test(text)) return "with_images"
    return "text_only"
  }

  async createKmongComparisonReport(prompt, options = {}) {
    if (!this.config.comparisonReportApiPath) {
      this.appendMessage("assistant", "개선 리포트 생성 경로가 연결되지 않았습니다.")
      return
    }

    this.setLoadingMessage("AI 경쟁력 리포트를 생성하고 있어요")
    this.setLoading(true)
    const analysisMode = options.analysisMode || this.inferKmongComparisonAnalysisMode(prompt)
    const analyzeImages = analysisMode === "with_images"
    const autoApplyImprovement = options.autoApplyImprovement === true
    const controller = new AbortController()
    const timeout = window.setTimeout(() => controller.abort(), 540000)
    try {
      const response = await fetch(this.config.comparisonReportApiPath, {
        method: "POST",
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({
          prompt,
          model_name: this.selectedModel,
          text_model_id: this.selectedModel,
          image_model_id: this.selectedThreadsImageModel || null,
          analyze_images: analyzeImages,
          image_analysis: analyzeImages ? "with_images" : "text_only",
          auto_apply_improvement: autoApplyImprovement
        })
      })

      const result = await response.json().catch(() => ({}))
      let data = result.data || {}
      const report = data.report || {}
      const reportUrl = data.report_url || data.reportUrl
      if (!response.ok || result.ok === false) {
        const error = new Error(result.error || result.message || `HTTP ${response.status}`)
        error.reason = result.reason
        error.reportUrl = reportUrl
        throw error
      }
      if (data.status_url && ["queued", "generating"].includes(String(report.status || ""))) {
        data = await this.pollKmongComparisonReport(data.status_url, data.poll_interval_ms || 2000)
      }

      const finalReport = data.report || report
      const score = finalReport?.report_payload?.summary?.score
      const analysisText = analyzeImages
        ? "분석 범위: 텍스트 + 대표/상세 이미지 OCR/비전 분석"
        : "분석 범위: 텍스트만 분석"
      const imageText = data.generated_image
        ? `대표 이미지도 ${data.generated_image.image_model || this.selectedThreadsImageModel} 모델로 생성했습니다.`
        : this.selectedThreadsImageModel
          ? (data.image_warning || "대표 이미지 생성은 완료되지 않았습니다.")
          : "대표 이미지 생성 모델을 선택하지 않아 새 대표 이미지는 만들지 않았습니다."
      const applyText = data.auto_applied
        ? `개선안을 현재 초안에 저장 반영했습니다.${this.kmongComparisonAppliedSectionsText(data.applied_sections)}`
        : data.apply_warning
          ? `개선 리포트는 생성됐지만 초안 저장 반영은 실패했습니다: ${data.apply_warning}`
          : null
      const summary = [
        "AI 경쟁력 리포트를 생성했습니다.",
        score != null ? `경쟁력 점수: ${score}점` : null,
        analysisText,
        `평가 모델: ${finalReport.ai_model_name || this.selectedModel}`,
        imageText,
        applyText,
        data.auto_applied ? "저장 반영된 초안과 생성된 리포트 화면으로 이동합니다." : "생성된 리포트 화면으로 이동합니다."
      ].filter(Boolean).join("\n")

      await this.appendAndPersistAgentSummary(summary, { dispatchComplete: true })
      this.appendKmongComparisonReportFollowUps()
      if (data.report_url || reportUrl) window.setTimeout(() => { window.location.href = data.report_url || reportUrl }, 500)
    } catch (e) {
      console.error("[KlavyAgentCore] kmong comparison report failed", e)
      const message = e.name === "AbortError"
        ? "AI 경쟁력 리포트 생성 시간이 초과되었습니다. 저장되지 않았으니 잠시 후 다시 시도해주세요."
        : e.reason === "model_not_available"
          ? _t("system_notes.model_not_available", "이 AI 모델은 현재 요금제에서 사용할 수 없어요. 다른 모델을 선택하거나 요금제를 확인해주세요.")
          : _t("system_notes.api_error", "오류가 발생했습니다: %{message}").replace("%{message}", e.message)
      this.appendMessage("assistant", message)
      if (e.reportUrl) window.setTimeout(() => { window.location.href = e.reportUrl }, 800)
    } finally {
      window.clearTimeout(timeout)
      this.setLoading(false)
      this.setLoadingMessage("")
    }
  }

  async reviseKmongComparisonPatch(prompt, options = {}) {
    const patchApiPath = this.comparisonPatchApiPath()
    if (!patchApiPath) {
      this.appendMessage("assistant", "개선 상품을 수정할 수 있는 리포트 상태가 아닙니다. 개선 리포트 생성이 끝난 뒤 새로고침하고 다시 시도해주세요.")
      return
    }

    this.setLoadingMessage("개선 상품 문안을 수정하고 있어요")
    this.setLoading(true)
    try {
      const response = await fetch(patchApiPath, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({
          prompt,
          instructions: prompt,
          text_model_id: this.selectedModel,
          model_name: this.selectedModel,
          sections: Array.isArray(options.sections) ? options.sections : [],
          mode: options.mode || "all"
        })
      })

      const result = await response.json().catch(() => ({}))
      if (!response.ok || result.ok === false) {
        throw new Error(result.error || result.message || `HTTP ${response.status}`)
      }

      const data = result.data || {}
      const fields = Array.isArray(data.changed_fields) ? data.changed_fields : []
      const summary = [
        data.change_summary || "개선 상품 문안을 수정했습니다.",
        fields.length > 0 ? `수정 항목: ${fields.join(", ")}` : null,
        "개선 상품 미리보기를 갱신합니다. 실제 초안 반영은 기존 저장 버튼으로 진행하세요."
      ].filter(Boolean).join("\n")

      await this.appendAndPersistAgentSummary(summary, { dispatchComplete: true })
      window.dispatchEvent(new CustomEvent("kmong-comparison-preview-request", {
        detail: { sections: [], mode: "all" }
      }))
      this.appendKmongComparisonReportFollowUps()
    } catch (e) {
      console.error("[KlavyAgentCore] kmong comparison patch revision failed", e)
      this.appendMessage("assistant", _t("system_notes.api_error", "오류가 발생했습니다: %{message}").replace("%{message}", e.message))
    } finally {
      this.setLoading(false)
      this.setLoadingMessage("")
    }
  }

  async pollKmongComparisonReport(statusUrl, pollIntervalMs = 2000) {
    const startedAt = Date.now()
    const timeoutMs = 30 * 60 * 1000
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ""

    while (Date.now() - startedAt < timeoutMs) {
      await new Promise((resolve) => window.setTimeout(resolve, pollIntervalMs))
      const response = await fetch(statusUrl, {
        method: "GET",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken
        }
      })
      const result = await response.json().catch(() => ({}))
      const data = result.data || {}
      const progress = data.progress || {}
      if (progress.message) {
        const percent = progress.percent != null ? ` ${progress.percent}%` : ""
        this.setLoadingMessage(`${progress.message}${percent}`)
      }

      if (!response.ok || result.ok === false) {
        const error = new Error(result.error || result.message || `HTTP ${response.status}`)
        error.reportUrl = data.report_url
        throw error
      }

      const reportStatus = data.report?.status
      if (reportStatus === "failed") {
        const error = new Error(data.report?.error_message || progress.message || "AI 경쟁력 리포트를 생성하지 못했습니다.")
        error.reportUrl = data.report_url
        throw error
      }
      if (data.terminal && ["ready", "applied"].includes(String(reportStatus))) {
        return data
      }
    }

    throw new Error("AI 경쟁력 리포트 생성 상태 확인 시간이 초과되었습니다. 잠시 후 리포트 화면을 새로고침해 주세요.")
  }

  kmongComparisonAppliedSectionsText(fields = []) {
    const labels = {
      title: "제목",
      summary: "요약",
      description: "서비스 설명",
      target_customer: "타깃 고객",
      requirements: "의뢰인 준비사항",
      deliverables: "산출물",
      service_process: "서비스 제공 절차",
      revision_policy: "수정 안내",
      work_request: "작업 요청사항",
      portfolio_notes: "포트폴리오 안내",
      search_keywords: "검색 키워드",
      service_features: "서비스 속성",
      extra_options: "추가 옵션",
      pre_work_requests: "사전 질문",
      packages: "패키지",
      faqs: "FAQ"
    }
    const names = Array.isArray(fields)
      ? fields.map(field => labels[String(field)] || String(field)).filter(Boolean)
      : []
    return names.length > 0 ? `\n반영 항목: ${names.join(", ")}` : ""
  }

  appendKmongComparisonReportFollowUps() {
    if (!this.isKmongComparisonReportAgent()) return
    this._messages?.querySelectorAll(".ba-followup-buttons").forEach(el => el.remove())
    this._renderFollowUpButtons([
      {
        label: "이미지 포함 재평가",
        action: "send",
        message: "현재 선택한 개선 상품 재평가 리포트를 기준으로 상세 이미지까지 OCR/비전 분석해서 AI 경쟁력 개선 리포트를 다시 생성하고, 개선안을 초안에 바로 저장 반영해줘."
      },
      {
        label: "텍스트만 재평가",
        action: "send",
        message: "현재 선택한 개선 상품 재평가 리포트를 기준으로 텍스트만 분석해서 AI 경쟁력 개선 리포트를 다시 생성하고, 개선안을 초안에 바로 저장 반영해줘."
      },
      {
        label: "상품명형 대표 이미지",
        action: "kmong-comparison-main-image",
        message: "현재 개선 리포트 기준으로 상품명이 크게 보이는 크몽 대표 이미지 후보를 생성합니다.",
        mainImageMode: "product"
      },
      {
        label: "문제제기형 대표 이미지",
        action: "kmong-comparison-main-image",
        message: "현재 개선 리포트 기준으로 구매자 문제를 먼저 제기하는 크몽 대표 이미지 후보를 생성합니다.",
        mainImageMode: "problem"
      },
      {
        label: "상세 이미지 생성",
        action: "kmong-comparison-detail-images",
        message: "현재 개선 리포트의 To-be 개선 내용을 기준으로 의미 있는 상세 이미지만 생성합니다."
      },
      {
        label: "전문가 이미지 생성",
        action: "kmong-seller-profile-image",
        message: "기획·웹사이트 제작·아임웹/노코드 제작 전반에 공통으로 쓸 수 있는 비식별 45도 측후면 전문가 프로필 이미지를 생성합니다."
      },
      {
        label: "전문가 정보 생성",
        action: "kmong-seller-profile-generate",
        message: "현재 리포트와 서비스 맥락을 기준으로 타겟 고객이 문의하고 싶어지는 전문가 소개를 생성해줘."
      },
      {
        label: "점수 근거 설명",
        action: "send",
        message: "현재 선택한 개선 상품 재평가 리포트의 경쟁력 점수 근거를 항목별로 설명해줘."
      },
      {
        label: "To-be 문구",
        action: "kmong-comparison-preview",
        sections: ["title_summary", "description", "process"],
        message: "To-be 문구 개선 미리보기를 표시합니다."
      },
      {
        label: "패키지 개선",
        action: "kmong-comparison-preview",
        sections: ["commerce"],
        message: "가격·패키지와 FAQ 개선 미리보기를 표시합니다."
      }
    ])
  }

  previewKmongComparisonPatchFromButton(button) {
    const sections = this.comparisonSectionsFromDataset(button?.dataset?.comparisonSections)
    window.dispatchEvent(new CustomEvent("kmong-comparison-preview-request", {
      detail: {
        sections,
        mode: sections.length > 0 ? "selected" : "all"
      }
    }))
    this.appendSystemNote(sections.includes("commerce") ? "가격·패키지 개선 미리보기를 요청했습니다." : "To-be 개선 미리보기를 요청했습니다.")
  }

  async requestKmongComparisonDetailImages() {
    if (!this.kmongComparisonDetailImagesProgressNote?.isConnected) {
      this.kmongComparisonDetailImagesProgressNote = this.appendSystemNote("상세 이미지 생성 요청을 전달하고 있습니다.")
    } else {
      this.updateSystemNote(this.kmongComparisonDetailImagesProgressNote, "상세 이미지 생성 요청을 전달하고 있습니다.")
      this.scrollToBottom()
    }

    const detailImagesApiPath = this.kmongComparisonDetailImagesApiPath()
    if (detailImagesApiPath) {
      this.config.comparisonDetailImagesApiPath = detailImagesApiPath
      await this.createKmongComparisonDetailImages()
      return
    }

    if (this.isKmongServiceDraftAgent() && !this.isKmongComparisonReportAgent()) {
      this.kmongComparisonDetailImagesProgressNote?.remove?.()
      this.kmongComparisonDetailImagesProgressNote = null
      await this.createKmongServiceRegistrationDetailImage()
      return
    }

    window.dispatchEvent(new CustomEvent("kmong-comparison-detail-images-request", {
      detail: { source: "klavy-agent" }
    }))
  }

  async createKmongComparisonDetailImages(options = {}) {
    const detailImagesApiPath = this.kmongComparisonDetailImagesApiPath()
    if (!detailImagesApiPath) {
      this.handleKmongComparisonDetailImagesProgress({
        detail: {
          status: "failed",
          message: "상세 이미지 생성 주소를 찾지 못했습니다. 화면을 새로고침한 뒤 다시 시도해주세요."
        }
      })
      return
    }

    const imageModel = this.selectedThreadsImageModel
    if (!imageModel) {
      this.handleKmongComparisonDetailImagesProgress({
        detail: {
          status: "failed",
          message: "이미지 생성 모델을 선택해주세요."
        }
      })
      return
    }

    this.handleKmongComparisonDetailImagesProgress({
      detail: {
        status: "running",
        message: "상세 이미지 생성 요청을 보내고 있습니다.",
        details: { phase: "request", elapsed_seconds: 0 }
      }
    })

    const requestStartedAt = Date.now()
    const requestWaitingTimer = window.setInterval(() => {
      const elapsedSeconds = Math.max(1, Math.floor((Date.now() - requestStartedAt) / 1000))
      this.handleKmongComparisonDetailImagesProgress({
        detail: {
          status: "running",
          message: `상세 이미지 생성 요청 전송 중 · 서버 응답 대기 ${elapsedSeconds}초`,
          details: { phase: "request", elapsed_seconds: elapsedSeconds }
        }
      })
    }, 1000)

    try {
      const response = await fetch(detailImagesApiPath, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({
          image_model_id: imageModel,
          ...Object.fromEntries(Object.entries(options || {}).filter(([, value]) => value != null && value !== ""))
        })
      })
      const result = await response.json().catch(() => ({}))
      window.clearInterval(requestWaitingTimer)
      if (!response.ok || result.ok === false) {
        throw new Error(result.error || result.message || `HTTP ${response.status}`)
      }

      const data = result.data || {}
      this.handleKmongComparisonDetailImagesProgress({ detail: data.detail_image_generation_progress })
      const finalData = await this.pollKmongComparisonDetailImages(data.status_url, data.poll_interval_ms || 2000)
      const progress = finalData.detail_image_generation_progress || {}
      const generatedCount = Array.isArray(finalData.generated_images?.details) ? finalData.generated_images.details.length : 0
      const summary = finalData.background
        ? (this.kmongComparisonDetailImagesProgressText(progress) || "상세 이미지가 백그라운드에서 계속 생성 중입니다. 잠시 후 새로고침하면 이어서 확인할 수 있습니다.")
        : generatedCount > 0
        ? `${this.kmongComparisonDetailImagesProgressText(progress)}\n생성된 상세 이미지를 확인하려면 화면을 새로고침합니다.`
        : this.kmongComparisonDetailImagesProgressText(progress)
      if (summary) this.updateSystemNote(this.kmongComparisonDetailImagesProgressNote, summary)
      if (finalData.background) return
      window.setTimeout(() => window.location.reload(), 700)
    } catch (e) {
      this.handleKmongComparisonDetailImagesProgress({
        detail: {
          status: "failed",
          message: e.message || "상세 이미지 생성에 실패했습니다."
        }
      })
    } finally {
      window.clearInterval(requestWaitingTimer)
    }
  }

  async createKmongComparisonMainImage(options = {}) {
    const generatedImagesApiPath = this.kmongComparisonGeneratedImagesApiPath()
    if (!generatedImagesApiPath) {
      if (this.isKmongServiceDraftAgent() && !this.isKmongComparisonReportAgent()) {
        await this.createKmongServiceRegistrationMainImage(options)
        return
      }

      this.appendSystemNote("대표 이미지 생성 주소를 찾지 못했습니다. 화면을 새로고침한 뒤 다시 시도해주세요.")
      return
    }

    const imageModel = this.selectedThreadsImageModel
    if (!imageModel) {
      this.appendSystemNote("이미지 생성 모델을 선택해주세요.")
      return
    }

    const note = this.appendSystemNote("대표 이미지를 생성하고 있습니다.")
    try {
      const response = await fetch(generatedImagesApiPath, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({
          image_model_id: imageModel,
          role: "main",
          ...Object.fromEntries(Object.entries(options || {}).filter(([, value]) => value != null && value !== ""))
        })
      })
      const result = await response.json().catch(() => ({}))
      if (!response.ok || result.ok === false) {
        throw new Error(result.error || result.message || `HTTP ${response.status}`)
      }
      this.updateSystemNote(note, "대표 이미지 후보를 생성했습니다. 화면을 새로고침합니다.")
      window.setTimeout(() => window.location.reload(), 700)
    } catch (e) {
      this.updateSystemNote(note, e.message || "대표 이미지 생성에 실패했습니다.")
    }
  }

  async deleteKmongComparisonGeneratedImages(options = {}) {
    const generatedImagesApiPath = this.kmongComparisonGeneratedImagesApiPath()
    if (!generatedImagesApiPath) {
      this.appendSystemNote("생성 이미지 삭제 주소를 찾지 못했습니다. 화면을 새로고침한 뒤 다시 시도해주세요.")
      return
    }

    const note = this.appendSystemNote("생성 이미지 후보를 삭제하고 있습니다.")
    try {
      const response = await fetch(generatedImagesApiPath, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify(Object.fromEntries(Object.entries(options || {}).filter(([, value]) => value != null && value !== "")))
      })
      const result = await response.json().catch(() => ({}))
      if (!response.ok || result.ok === false) {
        throw new Error(result.error || result.message || `HTTP ${response.status}`)
      }
      this.updateSystemNote(note, result.data?.message || "생성 이미지 후보를 삭제했습니다. 화면을 새로고침합니다.")
      window.setTimeout(() => window.location.reload(), 500)
    } catch (e) {
      this.updateSystemNote(note, e.message || "생성 이미지 후보 삭제에 실패했습니다.")
    }
  }

  kmongComparisonDetailImagesApiPath() {
    if (this.config.comparisonDetailImagesApiPath) return this.config.comparisonDetailImagesApiPath

    const buttonPath = document.querySelector("[data-comparison-detail-images-url]")?.dataset?.comparisonDetailImagesUrl
    if (buttonPath) return buttonPath

    try {
      const url = new URL(window.location.href)
      if (!url.pathname.match(/^\/kmong\/service-registrations\/\d+\/comparison-reports\/\d+\/?$/)) return ""

      const apiUrl = new URL(`${url.pathname.replace(/\/$/, "")}/detail-generated-images`, url.origin)
      const source = url.searchParams.get("source")
      if (source) apiUrl.searchParams.set("source", source)
      return `${apiUrl.pathname}${apiUrl.search}`
    } catch (_error) {
      return ""
    }
  }

  kmongComparisonGeneratedImagesApiPath() {
    if (this.config.comparisonGeneratedImagesApiPath) return this.config.comparisonGeneratedImagesApiPath
    if (this.config.generatedImagesApiPath) return this.config.generatedImagesApiPath

    try {
      const url = new URL(window.location.href)
      if (!url.pathname.match(/^\/kmong\/service-registrations\/\d+\/comparison-reports\/\d+\/?$/)) return ""

      const apiUrl = new URL(`${url.pathname.replace(/\/$/, "")}/generated-images`, url.origin)
      const source = url.searchParams.get("source")
      if (source) apiUrl.searchParams.set("source", source)
      return `${apiUrl.pathname}${apiUrl.search}`
    } catch (_error) {
      return ""
    }
  }

  kmongComparisonReportId() {
    try {
      const match = window.location.pathname.match(/\/kmong\/service-registrations\/\d+\/comparison-reports\/(\d+)\/?$/)
      return match ? match[1] : ""
    } catch (_error) {
      return ""
    }
  }

  async pollKmongComparisonDetailImages(statusUrl, pollIntervalMs = 2000) {
    if (!statusUrl) throw new Error("상세 이미지 상태 확인 주소를 찾지 못했습니다.")

    const startedAt = Date.now()
    const timeoutMs = 30 * 60 * 1000
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ""
    let lastData = {}
    let lastProgress = {}

    while (Date.now() - startedAt < timeoutMs) {
      await new Promise((resolve) => window.setTimeout(resolve, pollIntervalMs))
      const response = await fetch(statusUrl, {
        method: "GET",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken
        }
      })
      const result = await response.json().catch(() => ({}))
      if (!response.ok || result.ok === false) {
        throw new Error(result.error || result.message || `HTTP ${response.status}`)
      }

      const data = result.data || {}
      const progress = data.detail_image_generation_progress || {}
      lastData = data
      lastProgress = progress
      this.handleKmongComparisonDetailImagesProgress({ detail: progress })

      const status = String(progress.status || "")
      if (["done", "partial"].includes(status)) return data
      if (status === "failed") throw new Error(progress.message || "상세 이미지 생성에 실패했습니다.")
    }

    return {
      ...lastData,
      background: true,
      detail_image_generation_progress: {
        ...lastProgress,
        status: "running",
        message: "상세 이미지가 백그라운드에서 계속 생성 중입니다. 잠시 후 새로고침하면 이어서 확인할 수 있습니다.",
        details: {
          ...(lastProgress.details || {}),
          background: true
        }
      }
    }
  }

  handleKmongComparisonDetailImagesProgress(event) {
    if (!this.isKmongComparisonReportAgent()) return

    const progress = event?.detail || {}
    const text = this.kmongComparisonDetailImagesProgressText(progress)
    if (!text) return

    if (String(progress.status || "") === "queued" || !this.kmongComparisonDetailImagesProgressNote?.isConnected) {
      this.kmongComparisonDetailImagesProgressNote = this.appendSystemNote(text)
      return
    }

    this.updateSystemNote(this.kmongComparisonDetailImagesProgressNote, text)
    this.scrollToBottom()
  }

  kmongComparisonDetailImagesProgressText(progress = {}) {
    const details = progress.details || {}
    const status = String(progress.status || "")
    const current = Number(details.current || ((Number(details.generated || 0) + Number(details.failed || 0) + Number(details.running || 0)) || details.generated || 0))
    const total = Number(details.total || 0)
    const percent = progress.percent != null ? ` · ${progress.percent}%` : ""
    const title = details.title ? ` · ${details.title}` : ""

    if (details.phase === "request") {
      const elapsed = Number(details.elapsed_seconds || 0)
      return elapsed > 0
        ? `상세 이미지 생성 요청 전송 중 · 서버 응답 대기 ${elapsed}초`
        : "상세 이미지 생성 요청을 보내고 있습니다."
    }
    if (status === "queued") {
      return total > 0 ? `상세 이미지 생성 대기 중 · 총 ${total}장` : "상세 이미지 생성 대기 중"
    }
    if (status === "running") {
      return total > 0 && current > 0
        ? `상세 이미지 생성 중 · ${current}/${total}장${percent}${title}`
        : `${progress.message || "상세 이미지를 생성하고 있습니다."}${percent}`
    }
    if (status === "partial") {
      return total > 0 ? `상세 이미지 일부 생성 완료 · ${current}/${total}장` : (progress.message || "상세 이미지 일부 생성 완료")
    }
    if (status === "done") {
      return total > 0 ? `상세 이미지 생성 완료 · ${current || total}/${total}장` : (progress.message || "상세 이미지 생성 완료")
    }
    if (status === "failed") {
      return progress.message || "상세 이미지 생성에 실패했습니다."
    }

    return progress.message || ""
  }

  comparisonSectionsFromDataset(value) {
    if (!value) return []
    try {
      const parsed = JSON.parse(value)
      return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : []
    } catch (_error) {
      return String(value).split(",").map(item => item.trim()).filter(Boolean)
    }
  }

  async updateKmongSellerProfile(prompt, options = {}) {
    this.setLoading(true)
    try {
      const body = {
        prompt,
        mode: options.mode || "profile",
        text_model_id: this.selectedModel,
        apply_to_draft: true
      }
      const comparisonReportId = options.comparisonReportId || this.kmongComparisonReportId()
      if (comparisonReportId) body.comparison_report_id = comparisonReportId

      const response = await fetch(this.config.kmongSellerProfileApiPath, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify(body)
      })

      const result = await response.json().catch(() => ({}))
      if (!response.ok || result.ok === false) {
        const error = new Error(result.error || result.message || `HTTP ${response.status}`)
        error.reason = result.reason
        throw error
      }

      const data = result.data || {}
      const summary = data.deleted
        ? [
            "크몽 전문가 프로필을 초기화했습니다.",
            "현재 초안의 판매자 프로필에도 반영했습니다."
          ].join("\n")
        : [
            "크몽 전문가 프로필을 업데이트했습니다.",
            "현재 초안의 판매자 프로필에도 반영했습니다.",
            "상단의 전문가 프로필 탭에서 저장된 내용을 확인할 수 있습니다."
          ].join("\n")

      await this.appendAndPersistAgentSummary(summary, { dispatchComplete: true })
      window.setTimeout(() => window.location.reload(), 700)
    } catch (e) {
      console.error("[KlavyAgentCore] kmong seller profile update failed", e)
      if (e.reason === "model_not_available") {
        this.appendMessage("assistant", _t("system_notes.model_not_available", "이 AI 모델은 현재 요금제에서 사용할 수 없어요. 다른 모델을 선택하거나 요금제를 확인해주세요."))
      } else {
        this.appendMessage("assistant", _t("system_notes.api_error", "오류가 발생했습니다: %{message}").replace("%{message}", e.message))
      }
    } finally {
      this.setLoading(false)
    }
  }

  async createKmongSellerProfileImage(prompt) {
    if (!this.config.kmongSellerProfileApiPath) {
      this.appendSystemNote("전문가 프로필 생성 주소를 찾지 못했습니다. 화면을 새로고침한 뒤 다시 시도해주세요.")
      return
    }

    const imageModel = this.selectedThreadsImageModel
    if (!imageModel) {
      this.appendSystemNote("이미지 생성 모델을 선택해주세요.")
      return
    }

    const note = this.appendSystemNote("전문가 프로필 이미지를 생성하고 있습니다.")
    try {
      const body = {
        mode: "profile_image",
        prompt: prompt || "기획·웹사이트 제작·아임웹/노코드 제작 전반에 공통으로 쓸 수 있는 비식별 45도 측후면 전문가 프로필 이미지를 생성해줘.",
        image_model_id: imageModel,
        apply_to_draft: true
      }
      const comparisonReportId = this.kmongComparisonReportId()
      if (comparisonReportId) body.comparison_report_id = comparisonReportId

      const response = await fetch(this.config.kmongSellerProfileApiPath, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify(body)
      })
      const result = await response.json().catch(() => ({}))
      if (!response.ok || result.ok === false) {
        const error = new Error(result.error || result.message || `HTTP ${response.status}`)
        error.reason = result.reason
        throw error
      }

      this.updateSystemNote(note, "전문가 프로필 이미지를 생성해 적용했습니다. 화면을 새로고침합니다.")
      window.setTimeout(() => window.location.reload(), 700)
    } catch (e) {
      console.error("[KlavyAgentCore] kmong seller profile image failed", e)
      if (e.reason === "model_not_available") {
        this.updateSystemNote(note, _t("system_notes.model_not_available", "이 AI 모델은 현재 요금제에서 사용할 수 없어요. 다른 모델을 선택하거나 요금제를 확인해주세요."))
      } else {
        this.updateSystemNote(note, e.message || "전문가 프로필 이미지 생성에 실패했습니다.")
      }
    }
  }

  extractKmongImprovementNotes(payload) {
    const notes = payload?.improvement_notes || payload?.conversion_notes || payload?.diagnosis || []
    if (Array.isArray(notes)) {
      return notes.map(note => String(note || "").trim()).filter(Boolean).slice(0, 5)
    }
    const text = String(notes || "").trim()
    if (!text) return []
    return text.split(/\n+/).map(note => note.replace(/^[-*\d.\s]+/, "").trim()).filter(Boolean).slice(0, 5)
  }

  async createKmongServiceDraftStage(prompt, stage) {
    const response = await fetch(this.draftApiPath(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
      },
      body: JSON.stringify({
        prompt,
        stage: stage.key,
        text_model_id: this.selectedModel,
        image_model_id: stage.key === "commerce" ? (this.selectedThreadsImageModel || null) : null
      })
    })

    const result = await response.json().catch(() => ({}))
    if (!response.ok || result.ok === false) {
      const error = new Error(result.error || result.message || `HTTP ${response.status}`)
      error.reason = result.reason
      error.status = response.status
      error.stage = stage.key
      error.stageLabel = stage.label
      throw error
    }

    return result.data || {}
  }

  shouldRetryKmongServiceDraftStage(error) {
    const status = Number(error?.status || 0)
    return status === 422 || status >= 500
  }

  async createKmongServiceImage(imagePrompt, options = {}) {
    const response = await fetch(this.config.imageApiPath, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
      },
      body: JSON.stringify({
        prompt: imagePrompt,
        image_model_id: this.selectedThreadsImageModel,
        role: options.role || undefined,
        aspect_ratio: options.aspectRatio || options.aspect_ratio || undefined,
        detail_page_text: options.detailPageText || options.detail_page_text || undefined,
        allow_photo_hybrid: options.allowPhotoHybrid || options.allow_photo_hybrid || undefined
      })
    })

    const result = await response.json().catch(() => ({}))
    if (!response.ok || result.ok === false) {
      const error = new Error(result.error || result.message || `HTTP ${response.status}`)
      error.reason = result.reason
      throw error
    }

    const mediaIds = result.data?.media_asset_ids
    if (Array.isArray(mediaIds)) return mediaIds.length
    return result.data?.media_asset_id ? 1 : 0
  }

  kmongServiceMainImagePrompt(mode = "product") {
    if (mode === "problem") {
      return "현재 등록 초안을 기준으로 구매자가 겪는 문제를 먼저 제기하는 크몽 대표 이미지를 생성해줘. 큰 한국어 문제 문구, 서비스 결과물 힌트, 전문적인 B2B 톤으로 구성해줘."
    }

    return "현재 등록 초안을 기준으로 상품명이 크게 보이는 크몽 대표 이미지를 생성해줘. 서비스 제목을 중심으로 산출물과 구매 혜택이 한눈에 보이게 구성해줘."
  }

  kmongServiceDetailImagePrompt() {
    return "현재 등록 초안을 기준으로 구매 설득에 필요한 크몽 상세 이미지를 생성해줘. 문제, 제공 범위, 산출물, 진행 방식 중 가장 중요한 메시지 하나를 큰 한국어 카피와 서비스 맥락이 보이는 시각 요소로 정리해줘."
  }

  async createKmongServiceRegistrationMainImage(options = {}) {
    if (!this.config.imageApiPath) {
      this.appendSystemNote("대표 이미지 생성 주소를 찾지 못했습니다. 화면을 새로고침한 뒤 다시 시도해주세요.")
      return
    }

    if (!this.selectedThreadsImageModel) {
      this.appendSystemNote("이미지 생성 모델을 선택해주세요.")
      return
    }

    const mode = options.main_image_mode || options.mainImageMode || "product"
    const note = this.appendSystemNote(mode === "problem" ? "문제제기형 대표 이미지를 생성하고 있습니다." : "상품명형 대표 이미지를 생성하고 있습니다.")
    try {
      const count = await this.createKmongServiceImage(this.kmongServiceMainImagePrompt(mode), {
        role: "main",
        aspectRatio: "4:3"
      })
      this.updateSystemNote(note, count > 0 ? "대표 이미지를 생성해 현재 초안에 적용했습니다. 화면을 새로고침합니다." : "대표 이미지 생성 요청은 완료됐지만 저장된 이미지를 확인하지 못했습니다.")
      window.setTimeout(() => window.location.reload(), 700)
    } catch (e) {
      this.updateSystemNote(note, e.message || "대표 이미지 생성에 실패했습니다.")
    }
  }

  async createKmongServiceRegistrationDetailImage(options = {}) {
    if (!this.config.imageApiPath) {
      this.appendSystemNote("상세 이미지 생성 주소를 찾지 못했습니다. 화면을 새로고침한 뒤 다시 시도해주세요.")
      return
    }

    if (!this.selectedThreadsImageModel) {
      this.appendSystemNote("이미지 생성 모델을 선택해주세요.")
      return
    }

    const count = Number(options.count || options.detail_count || 9)
    const normalizedCount = Number.isFinite(count) && count > 0 ? Math.min(count, 9) : 9
    const note = this.appendSystemNote(`상세 이미지 생성 요청을 보내고 있습니다. 총 ${normalizedCount}장`)
    const requestStartedAt = Date.now()
    const requestWaitingTimer = window.setInterval(() => {
      const elapsedSeconds = Math.max(1, Math.floor((Date.now() - requestStartedAt) / 1000))
      this.updateSystemNote(note, `상세 이미지 생성 요청 전송 중 · 서버 응답 대기 ${elapsedSeconds}초`)
    }, 1000)

    try {
      const response = await fetch(this.config.imageApiPath, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({
          prompt: options.instructions || options.prompt || this.kmongServiceDetailImagePrompt(),
          instructions: options.instructions || options.prompt || undefined,
          image_model_id: this.selectedThreadsImageModel,
          role: "detail",
          count: normalizedCount,
          detail_set: true,
          aspect_ratio: "1:1",
          detail_page_text: true,
          allow_photo_hybrid: false
        })
      })

      const result = await response.json().catch(() => ({}))
      window.clearInterval(requestWaitingTimer)
      if (!response.ok || result.ok === false) {
        const error = new Error(result.error || result.message || `HTTP ${response.status}`)
        error.reason = result.reason
        throw error
      }

      const data = result.data || {}
      const progress = data.detail_image_generation_progress || {}
      this.updateSystemNote(note, this.kmongComparisonDetailImagesProgressText(progress) || `상세 이미지 생성 대기 중 · 총 ${normalizedCount}장`)
      const finalData = await this.pollKmongServiceRegistrationDetailImages(data.status_url, data.poll_interval_ms || 2000, note)
      const finalProgress = finalData.detail_image_generation_progress || {}
      const generatedCount = Array.isArray(finalData.generated_images?.details) ? finalData.generated_images.details.length : 0
      const summary = finalData.background
        ? (this.kmongComparisonDetailImagesProgressText(finalProgress) || "상세 이미지가 백그라운드에서 계속 생성 중입니다. 잠시 후 새로고침하면 이어서 확인할 수 있습니다.")
        : generatedCount > 0
        ? `${this.kmongComparisonDetailImagesProgressText(finalProgress)}\n생성된 상세 이미지를 확인하려면 화면을 새로고침합니다.`
        : (this.kmongComparisonDetailImagesProgressText(finalProgress) || "상세 이미지 생성 요청은 완료됐지만 저장된 이미지를 확인하지 못했습니다.")
      this.updateSystemNote(note, summary)
      if (finalData.background) return
      window.setTimeout(() => window.location.reload(), 700)
    } catch (e) {
      this.updateSystemNote(note, e.message || "상세 이미지 생성에 실패했습니다.")
    } finally {
      window.clearInterval(requestWaitingTimer)
    }
  }

  async pollKmongServiceRegistrationDetailImages(statusUrl, pollIntervalMs = 2000, note = null) {
    if (!statusUrl) throw new Error("상세 이미지 상태 확인 주소를 찾지 못했습니다.")

    const startedAt = Date.now()
    const timeoutMs = 30 * 60 * 1000
    let lastData = {}
    let lastProgress = {}
    while (Date.now() - startedAt < timeoutMs) {
      await this.sleep(pollIntervalMs)
      const response = await fetch(statusUrl, {
        method: "GET",
        headers: { "Accept": "application/json" }
      })
      const result = await response.json().catch(() => ({}))
      if (!response.ok || result.ok === false) {
        throw new Error(result.error || result.message || `HTTP ${response.status}`)
      }

      const data = result.data || {}
      const progress = data.detail_image_generation_progress || {}
      lastData = data
      lastProgress = progress
      const text = this.kmongComparisonDetailImagesProgressText(progress)
      if (text && note) this.updateSystemNote(note, text)

      const status = String(progress.status || "")
      if (["done", "partial"].includes(status)) return data
      if (status === "failed") throw new Error(progress.message || "상세 이미지 생성에 실패했습니다.")
    }

    return {
      ...lastData,
      background: true,
      detail_image_generation_progress: {
        ...lastProgress,
        status: "running",
        message: "상세 이미지가 백그라운드에서 계속 생성 중입니다. 잠시 후 새로고침하면 이어서 확인할 수 있습니다.",
        details: {
          ...(lastProgress.details || {}),
          background: true
        }
      }
    }
  }

  sleep(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms))
  }

  async requestAiResponse({ provider, endpointPrefix, headers, payload, onFirstChunk, onContent, stream = true }) {
    const streamUrl = this.getStreamPath(provider, endpointPrefix, stream)
    const fetchUrl = streamUrl || `${endpointPrefix}/${provider}`
    const response = await fetch(fetchUrl, {
      method: "POST",
      headers,
      body: JSON.stringify(payload)
    })

    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      const requestError = new Error(err.error || err.message || `HTTP ${response.status}`)
      requestError.status = response.status
      requestError.reason = err.reason
      requestError.payload = err
      throw requestError
    }

    if (!streamUrl) {
      const data = await response.json()
      return data.content?.[0]?.text || data.error || _t("system_notes.no_response", "응답을 받지 못했습니다.")
    }

    return this.readSseStreamResponse(response, { onFirstChunk, onContent })
  }

  getStreamPath(provider, endpointPrefix, stream = true) {
    if (stream && ["claude", "gemini", "openai", "kimi", "minimax", "glm", "deepseek", "xai"].includes(provider)) {
      return `${endpointPrefix}/${provider}_stream`
    }
    return null
  }

  async readSseStreamResponse(response, { onFirstChunk, onContent } = {}) {
    const reader = response.body?.getReader()
    if (!reader) throw new Error(_t("system_notes.no_streaming", "스트리밍 응답을 읽을 수 없습니다."))

    const decoder = new TextDecoder()
    let sseBuffer = ""
    let aiText = ""
    let started = false
    const processLine = (line) => {
      const trimmed = line.trim()
      if (!trimmed || trimmed.startsWith("event:") || !trimmed.startsWith("data:")) return

      const jsonStr = trimmed.slice(5).trim()
      if (!jsonStr) return

      try {
        const parsed = JSON.parse(jsonStr)
        if (parsed.type === "content" && parsed.content) {
          aiText += parsed.content
          if (!started) {
            started = true
            onFirstChunk?.()
          }
          onContent?.(aiText)
        } else if (parsed.type === "error") {
          throw new Error(parsed.content || "AI 스트리밍 오류")
        }
      } catch (e) {
        if (!(e instanceof SyntaxError)) throw e
      }
    }

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      sseBuffer += decoder.decode(value, { stream: true })
      const lines = sseBuffer.split("\n")
      sseBuffer = lines.pop() || ""

      for (const line of lines) processLine(line)
    }

    if (sseBuffer.trim()) processLine(sseBuffer)

    return aiText || _t("system_notes.no_response", "응답을 받지 못했습니다.")
  }

  // ── 입력 UI ───────────────────────────────────────────────────────────────

  autoResize() {
    const el = this._input
    if (!el) return
    el.style.height = "auto"
    const maxHeight = this.inputExpanded
      ? Math.max(260, Math.floor(window.innerHeight * 0.55))
      : 120
    const height = this.inputExpanded
      ? Math.max(260, Math.min(el.scrollHeight, maxHeight))
      : Math.min(el.scrollHeight, maxHeight)
    el.style.height = height + "px"
    el.style.maxHeight = maxHeight + "px"
    el.style.overflowY = el.scrollHeight > maxHeight ? "auto" : "hidden"
  }

  focusInput() {
    if (this._inputWrapper) {
      this._inputWrapper.style.borderColor = "#6366F1"
      this._inputWrapper.style.boxShadow = "0 0 0 2px rgba(99,102,241,0.1)"
    }
  }

  blurInput() {
    if (this._inputWrapper) {
      this._inputWrapper.style.borderColor = "#e0e0e0"
      this._inputWrapper.style.boxShadow = "none"
    }
  }

  handleKeydown(e) {
    if (e.key === "Enter" && !e.shiftKey && !e.isComposing) {
      e.preventDefault()
      this.send()
    }
  }

  // ── 모델 드롭다운 ─────────────────────────────────────────────────────────

  toggleModelDropdown(e) {
    e.stopPropagation()
    this.modelDropdownOpen = !this.modelDropdownOpen
    this.rcModelDropdownOpen = false
    if (this._rcModelDropdown) this._rcModelDropdown.style.display = "none"
    if (this._modelDropdown) {
      this._modelDropdown.style.display = this.modelDropdownOpen ? "block" : "none"
      this._modelDropdown.style.zIndex = "2000"
    }
    if (this.modelDropdownOpen) this.refreshCredentialStatus({ force: true, rerenderModels: true })
  }

  selectModel(e) {
    const opt = e.currentTarget

    // 잠긴(유료 전용) 모델: 선택 대신 업그레이드 모달 오픈
    if (opt.dataset.locked === "true") {
      if (this._modelDropdown) this._modelDropdown.style.display = "none"
      if (this._rcModelDropdown) this._rcModelDropdown.style.display = "none"
      this.modelDropdownOpen = false
      window.dispatchEvent(new CustomEvent("klavy:upgrade-required", {
        detail: {
          reason: "model_not_available",
          provider: opt.dataset.provider,
          model: opt.dataset.value
        }
      }))
      return
    }

    this.selectedModel = opt.dataset.value
    if (this.isImageAssetAgent) this.imageAssetModelExplicitlySelected = true
    localStorage.setItem(`${this.config.storagePrefix}-model`, this.selectedModel)
    if (this.config.voiceModelPicker && opt.dataset.provider) {
      localStorage.setItem(`${this.config.storagePrefix}-provider`, opt.dataset.provider)
    }
    const label = opt.querySelector(".ba-model-opt__name")?.textContent.trim() || opt.dataset.value
    const weight = opt.querySelector(".ka-model-badge--weight")?.textContent.trim()
    if (this._modelLabel) this._modelLabel.textContent = label
    if (this._rcModelLabel) this._rcModelLabel.textContent = label
    if (weight && this._modelWeightChip) this._modelWeightChip.textContent = weight
    if (weight && this._rcModelWeightChip) this._rcModelWeightChip.textContent = weight
    this.updateSelectedModelBadgeChips()

    // 선택 상태 하이라이트
    if (this._modelDropdown) {
      this._modelDropdown.querySelectorAll(".ba-model-opt").forEach(el => {
        el.style.background = el.dataset.value === this.selectedModel ? "#EEF2FF" : ""
      })
    }
    if (this._rcModelDropdown) {
      this._rcModelDropdown.querySelectorAll(".ba-model-opt").forEach(el => {
        el.style.background = el.dataset.value === this.selectedModel ? "#EEF2FF" : ""
      })
    }

    if (this._modelDropdown) this._modelDropdown.style.display = "none"
    this.modelDropdownOpen = false
    this.updateCredentialSourceUI()
  }

  updateModelLabel() {
    if (!this._modelLabel) return
    const model = this.selectedModel
    if (this._modelDropdown) {
      const opt = this._modelDropdown.querySelector(`.ba-model-opt[data-value="${model}"]`)
      if (opt) {
        const nameEl = opt.querySelector(".ba-model-opt__name")
        this._modelLabel.textContent = nameEl ? nameEl.textContent.trim() : opt.textContent.trim()
      }
    }
  }

  // ── 모드 드롭다운 ─────────────────────────────────────────────────────────

  toggleModeDropdown(e) {
    e.stopPropagation()
    this.modeDropdownOpen = !this.modeDropdownOpen
    if (this._modeDropdown) this._modeDropdown.style.display = this.modeDropdownOpen ? "block" : "none"
  }

  selectMode(e) {
    const mode = e.currentTarget.dataset.mode
    const label = e.currentTarget.dataset.label || mode
    this.selectedMode = mode
    localStorage.setItem(`${this.config.storagePrefix}-mode`, mode)
    if (this._modeLabel) this._modeLabel.textContent = label
    if (this._rcModeLabel) this._rcModeLabel.textContent = label
    if (this._modeDropdown) this._modeDropdown.style.display = "none"
    this.modeDropdownOpen = false
  }

  // ── 읽기 깊이 드롭다운 (IA URL 분석 전용) ─────────────────────────────────

  iaReadDepthMaxPages() {
    const pages = { fast: 12, normal: 20, deep: 30 }
    return pages[this.iaReadDepth] || pages.normal
  }

  toggleIaReadDepthDropdown(e) {
    e.stopPropagation()
    this.iaReadDepthDropdownOpen = !this.iaReadDepthDropdownOpen
    if (this._iaReadDepthDropdown) this._iaReadDepthDropdown.style.display = this.iaReadDepthDropdownOpen ? "block" : "none"
  }

  selectIaReadDepth(e) {
    const depth = e.currentTarget.dataset.depth
    if (depth) {
      this.iaReadDepth = depth
      try { localStorage.setItem(`${this.config.storagePrefix}-ia-read-depth`, depth) } catch (err) {}
    }
    if (this._iaReadDepthDropdown) this._iaReadDepthDropdown.style.display = "none"
    this.iaReadDepthDropdownOpen = false
    this._syncIaReadDepthUi()
  }

  _syncIaReadDepthUi() {
    const dropdown = this._iaReadDepthDropdown
    if (!dropdown) return
    let label = ""
    dropdown.querySelectorAll("[data-depth]").forEach((opt) => {
      const selected = opt.dataset.depth === this.iaReadDepth
      opt.style.background = selected ? "#EEF2FF" : "transparent"
      if (selected) label = opt.dataset.label || ""
    })
    if (this._iaReadDepthLabel && label) this._iaReadDepthLabel.textContent = label
  }

  // ── 프롬프트 첨부 ───────────────────────────────────────────────────────

  isIaProjectAgent() {
    return this.config.resourceType === "IaProject" || this.config.contextType === "ia_project"
  }

  iaGenerateFromUrlPath() {
    if (this.config.iaGenerateFromUrlPath) return this.config.iaGenerateFromUrlPath
    if (this.config.resourceType === "IaProject" && this.config.resourceId) {
      return `/ia/projects/${this.config.resourceId}/generate_from_url`
    }
    return null
  }

  isIaUrlGenerationAgent() {
    return this.isIaProjectAgent() && !!this.iaGenerateFromUrlPath()
  }

  planningInterviewFromUrlPath() {
    return this.config.planningInterviewFromUrlPath || null
  }

  planningGenerateFromUrlPath() {
    return this.config.planningGenerateFromUrlPath || this.planningInterviewFromUrlPath()
  }

  planningGenerateFromUrlTab() {
    if (this.config.planningGenerateFromUrlTab) return this.config.planningGenerateFromUrlTab
    const match = String(this.config.contextType || "").match(/^planning_(.+)$/)
    return match ? match[1] : "idea_interview"
  }

  isPlanningInterviewUrlGenerationAgent() {
    return !!this.planningGenerateFromUrlPath()
  }

  extractFirstUrl(text) {
    const match = String(text || "").match(/https?:\/\/[^\s<>"')]+/i)
    return match ? match[0].replace(/[.,;!?]+$/, "") : ""
  }

  isIaGenerationPrompt(text) {
    return /IA|정보\s*구조|구조|사이트맵|메뉴|화면|페이지|생성|만들|정리|읽고|분석/i.test(String(text || ""))
  }

  isPromptAttachmentAgent() {
    return this.config.promptToolsEnabled || this.isKakaoTalkDraftAgent()
  }

  canUsePromptUrlPanel() {
    return this.isIaUrlGenerationAgent() || this.isPlanningInterviewUrlGenerationAgent() || this.isPromptAttachmentAgent()
  }

  promptUrlAddLabel() {
    if (this.config.promptUrlAddLabel) return this.config.promptUrlAddLabel
    return this.isIaUrlGenerationAgent() ? "IA 생성" : "추가"
  }

  promptUrlBusyLabel() {
    if (this.config.promptUrlBusyLabel) return this.config.promptUrlBusyLabel
    return this.isIaUrlGenerationAgent() ? "생성 중" : "추가 중"
  }

  promptAttachmentCount() {
    return this.kakaoPromptAttachments.length + (this.imageBase64 ? 1 : 0)
  }

  canAddPromptAttachment({ replacingImage = false } = {}) {
    if (!this.isPromptAttachmentAgent()) return false
    const count = this.promptAttachmentCount()
    if (count < 5 || replacingImage) return true

    alert("첨부 자료는 최대 5개까지 추가할 수 있습니다.")
    return false
  }

  promptAttachmentId() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID()
    }
    return `ka-${Date.now()}-${Math.random().toString(16).slice(2)}`
  }

  promptAttachmentEndpoint(path, fallback) {
    return path || fallback
  }

  promptFetchUrlPath() {
    return this.promptAttachmentEndpoint(this.config.promptFetchUrlPath || this.config.kakaoTalkFetchUrlPath, "/kakao-talk/ai/fetch_url")
  }

  promptExtractDocumentPath() {
    return this.promptAttachmentEndpoint(this.config.promptExtractDocumentPath || this.config.kakaoTalkExtractDocumentPath, "/kakao-talk/ai/extract_document")
  }

  togglePromptUrlPanel(e) {
    e?.preventDefault?.()
    if (!this.canUsePromptUrlPanel()) return
    if (!this.isIaUrlGenerationAgent() && !this.isPlanningInterviewUrlGenerationAgent() && this.isPromptAttachmentAgent() && !this.canAddPromptAttachment()) return

    const panel = this._promptUrlPanel
    if (!panel) return

    const nextVisible = panel.style.display === "none" || panel.style.display === ""
    panel.style.display = nextVisible ? "flex" : "none"
    if (nextVisible) window.setTimeout(() => this._promptUrlInput?.focus(), 0)
  }

  handlePromptUrlKeydown(e) {
    if (e.key === "Enter") {
      e.preventDefault()
      this.submitPromptUrl(e)
      return
    }
    if (e.key === "Escape" && this._promptUrlPanel) {
      e.preventDefault()
      this._promptUrlPanel.style.display = "none"
    }
  }

  async submitPromptUrl(e) {
    e?.preventDefault?.()
    if (this.isIaUrlGenerationAgent()) {
      await this.generateIaFromUrl(this._promptUrlInput?.value)
      return
    }
    if (this.isPlanningInterviewUrlGenerationAgent()) {
      await this.generatePlanningTabFromUrl(this._promptUrlInput?.value)
      return
    }

    await this.attachPromptUrl(this._promptUrlInput?.value)
  }

  async attachPromptUrl(input) {
    if (!this.canAddPromptAttachment()) return

    const url = String(input || "").trim()
    if (!url) return
    if (!/^https?:\/\//i.test(url)) {
      alert("http:// 또는 https:// 로 시작하는 URL만 지원합니다.")
      return
    }
    if (this.kakaoPromptAttachments.some((item) => item.type === "url" && item.url === url)) {
      alert("이미 첨부한 URL입니다.")
      return
    }

    try {
      if (this._promptUrlAddBtn) {
        this._promptUrlAddBtn.disabled = true
        this._promptUrlAddBtn.textContent = this.promptUrlBusyLabel()
      }
      const response = await fetch(this.promptFetchUrlPath(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({ url })
      })
      const result = await response.json().catch(() => ({}))
      if (!response.ok || result.success === false) {
        throw new Error(result.error || result.message || `HTTP ${response.status}`)
      }

      let domain = ""
      try { domain = new URL(result.url || url).hostname } catch (_) {}
      const content = String(result.content || "").trim()
      if (!content) throw new Error("URL에서 읽을 수 있는 본문을 찾지 못했습니다.")

      this.kakaoPromptAttachments.push({
        id: this.promptAttachmentId(),
        type: "url",
        url: result.url || url,
        title: result.title || "",
        domain,
        content,
        size: content.length
      })
      this.renderPromptAttachments()
      if (this._promptUrlInput) this._promptUrlInput.value = ""
      if (this._promptUrlPanel) this._promptUrlPanel.style.display = "none"
    } catch (error) {
      alert(error.message || "URL을 첨부하지 못했습니다.")
    } finally {
      if (this._promptUrlAddBtn) {
        this._promptUrlAddBtn.disabled = false
        this._promptUrlAddBtn.textContent = this.promptUrlAddLabel()
      }
    }
  }

  async generateIaFromUrl(input, options = {}) {
    const url = String(input || "").trim()
    if (!url) return
    if (!/^https?:\/\//i.test(url)) {
      alert("http:// 또는 https:// 로 시작하는 URL만 지원합니다.")
      return
    }

    const endpoint = this.iaGenerateFromUrlPath()
    if (!endpoint) {
      alert("IA 생성 주소를 찾지 못했습니다. 화면을 새로고침한 뒤 다시 시도해주세요.")
      return
    }

    try {
      if (this._promptUrlAddBtn) {
        this._promptUrlAddBtn.disabled = true
        this._promptUrlAddBtn.textContent = this.promptUrlBusyLabel()
      }
      // 진행 상태는 별도 시스템 노트 없이 작업 카드 문구 하나로만 표시한다 (중복 안내 제거)
      this.setLoadingMessage("URL을 분석해 IA를 생성하고 있습니다...")
      this.setLoading(true)

      const maxPages = this.iaReadDepthMaxPages()

      // 1단계: 잡 생성 (202 응답 + job_id)
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({
          url,
          max_pages: maxPages,
          include_admin: true,
          mode: "replace",
          prompt: options.prompt || "",
          text_model_id: this.selectedModel,
          model_name: this.selectedModel,
          provider: this.getProvider(this.selectedModel)
        })
      })
      const enqueueResult = await response.json().catch(() => ({}))

      // 동기 응답(200 success) — 레거시 경로 호환
      if (response.ok && enqueueResult.success !== false && !enqueueResult.job_id) {
        if (this._promptUrlInput) this._promptUrlInput.value = ""
        if (this._promptUrlPanel) this._promptUrlPanel.style.display = "none"
        this.appendSystemNote(enqueueResult.message || "URL 기반 IA를 생성했습니다. 화면을 새로고침합니다.")
        window.setTimeout(() => window.location.reload(), 600)
        return
      }

      if (!response.ok || enqueueResult.success === false) {
        throw new Error(enqueueResult.error || enqueueResult.message || `HTTP ${response.status}`)
      }

      const jobId = enqueueResult.job_id
      if (!jobId) throw new Error("잡 ID를 받지 못했습니다. 잠시 후 다시 시도해주세요.")

      // 2단계: 폴링 (비동기 내성 패턴 — 일시 장애 600s 재시도)
      const projectId = this.config.resourceId
      const statusBase = `/ia/projects/${projectId}/url_generation_jobs/${encodeURIComponent(jobId)}`
      const startedAt = Date.now()
      const TIMEOUT_MS = 600000 // 600s
      const POLL_INTERVAL_MS = 2000
      let consecutiveFails = 0

      this.setLoadingMessage("URL을 크롤링하고 IA를 생성하고 있습니다. 잠시 기다려 주세요...")

      while (true) {
        let job = null
        try {
          const pollRes = await fetch(statusBase, {
            method: "GET",
            headers: { "Accept": "application/json" }
          })
          if (!pollRes.ok) {
            const errData = await pollRes.json().catch(() => ({}))
            // 즉시 실패 조건: 401/402/404
            if (pollRes.status === 401 || pollRes.status === 419) {
              throw new Error("인증이 만료되었습니다. 페이지를 새로고침해 주세요.")
            }
            if (pollRes.status === 404) {
              throw new Error("생성 작업을 찾을 수 없습니다. 다시 시도해 주세요.")
            }
            if (pollRes.status === 402 && errData?.error === 'quota_exceeded') {
              throw new Error(errData.message || "AI 사용량 한도를 초과했습니다.")
            }
            // 5xx, 408, 429 → 일시 장애 → 재시도
            if (pollRes.status >= 500 || pollRes.status === 408 || pollRes.status === 429) {
              throw Object.assign(new Error(`서버 일시 오류 (${pollRes.status})`), { retryable: true })
            }
            throw new Error(errData?.error || errData?.message || `HTTP ${pollRes.status}`)
          }
          job = await pollRes.json()
          consecutiveFails = 0
        } catch (pollError) {
          if (this._isIaUrlPollTransient(pollError)) {
            consecutiveFails++
            if (consecutiveFails === 2) {
              this.setLoadingMessage("서버 연결이 잠시 불안정합니다. 계속 시도 중...")
            }
          } else {
            throw pollError
          }
        }

        if (job) {
          if (job.status === "succeeded") {
            if (this._promptUrlInput) this._promptUrlInput.value = ""
            if (this._promptUrlPanel) this._promptUrlPanel.style.display = "none"
            const generatedCount = job.workspace_payload?.nodes?.length || 0
            const elapsedText = this._formatElapsedKo(Date.now() - startedAt)
            const completionNote = generatedCount > 0
              ? `완료: 총 ${generatedCount}개 페이지를 생성했습니다. (${elapsedText} 소요)`
              : `완료: IA가 생성되었습니다. (${elapsedText} 소요)`
            this.appendSystemNote(completionNote)
            // 600ms 뒤 새로고침으로 사라지므로 1회용 키로 보존 → 로드 시 복원
            try { sessionStorage.setItem(`${this.config.storagePrefix}-ia-url-gen-note`, completionNote) } catch (e) { /* 저장 불가 시 무시 */ }
            window.setTimeout(() => window.location.reload(), 600)
            return
          }

          if (job.status === "failed" || job.status === "cancelled") {
            throw new Error(job.error_message || "IA 생성에 실패했습니다. 다시 시도해 주세요.")
          }

          // queued/running — 계속 폴링
          if (job.status === "running") {
            const crawledCount = job.crawled_pages_count
            if (job.phase === "generating") {
              this.setLoadingMessage("사이트를 다 읽었어요! 이제 구조를 정리하고 있어요...")
            } else if (crawledCount && crawledCount > 0) {
              let message = `사이트를 읽고 있어요... (${crawledCount}/${maxPages}페이지)`
              // 90초 넘게 크롤 중이면 멈춘 게 아니라 무거운 사이트임을 안내
              if (Date.now() - startedAt > 90000) {
                message += " · 내용이 많은 사이트라 시간이 조금 더 걸려요"
              }
              this.setLoadingMessage(message)
            } else {
              this.setLoadingMessage("URL을 분석하고 IA를 생성하고 있습니다...")
            }
          }
        }

        if (Date.now() - startedAt > TIMEOUT_MS) {
          throw new Error("IA 생성 응답 시간이 초과되었습니다. 다시 시도해 주세요.")
        }

        const delay = consecutiveFails > 0
          ? Math.min(3000 * consecutiveFails, 10000)
          : POLL_INTERVAL_MS
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    } catch (error) {
      const message = error.message || "URL 기반 IA 생성에 실패했습니다."
      this.appendSystemNote(message)
      alert(message)
    } finally {
      this.setLoadingMessage("")
      this.setLoading(false)
      if (this._promptUrlAddBtn) {
        this._promptUrlAddBtn.disabled = false
        this._promptUrlAddBtn.textContent = this.promptUrlAddLabel()
      }
    }
  }

  // IA URL 생성 폴링 일시 장애 판정 (PlanClientAPI._isTransientPollError 동일 로직)
  _isIaUrlPollTransient(error) {
    if (!error) return false
    if (error.quotaExceeded === true || error.fatal === true) return false
    if (error.name === 'TimeoutError' || error.name === 'RequestTimeoutError') return true
    if (error.name === 'TypeError') return true // fetch 네트워크 단절
    if (error.retryable === true) return true   // 5xx/408/429
    if (error instanceof SyntaxError) return true // 비정상 본문
    return /timed out|network|failed to fetch|load failed/i.test(String(error.message || ''))
  }

  // 경과 시간을 "21분" / "1분 30초" / "45초" 형태로 표기
  _formatElapsedKo(ms) {
    const totalSec = Math.max(1, Math.round(ms / 1000))
    if (totalSec < 60) return `${totalSec}초`
    const min = Math.floor(totalSec / 60)
    const sec = totalSec % 60
    return sec > 0 ? `${min}분 ${sec}초` : `${min}분`
  }

  // URL 생성 완료 안내가 600ms 새로고침에 휩쓸려 사라지지 않도록 1회 복원
  _restoreIaUrlGenCompletionNote() {
    let note = null
    try {
      const key = `${this.config.storagePrefix}-ia-url-gen-note`
      note = sessionStorage.getItem(key)
      if (note) sessionStorage.removeItem(key)
    } catch (e) { return }
    if (note) this.appendSystemNote(note)
  }

  // 기획 워크스페이스 URL 생성 완료 안내가 600ms 이동에 휩쓸려 사라지지 않도록 1회 복원
  _restorePwUrlGenCompletionNote() {
    let note = null
    try {
      const key = `${this.config.storagePrefix}-pw-url-gen-note`
      note = sessionStorage.getItem(key)
      if (note) sessionStorage.removeItem(key)
    } catch (e) { return }
    if (note) this.appendSystemNote(note)
  }

  async generatePlanningTabFromUrl(input, options = {}) {
    const url = String(input || "").trim()
    if (!url) return
    if (!/^https?:\/\//i.test(url)) {
      alert("http:// 또는 https:// 로 시작하는 URL만 지원합니다.")
      return
    }

    const endpoint = this.planningGenerateFromUrlPath()
    if (!endpoint) {
      alert("기획 생성 주소를 찾지 못했습니다. 화면을 새로고침한 뒤 다시 시도해주세요.")
      return
    }

    const tab = options.tab || this.planningGenerateFromUrlTab()
    const maxPages = this.iaReadDepthMaxPages()

    try {
      if (this._promptUrlAddBtn) {
        this._promptUrlAddBtn.disabled = true
        this._promptUrlAddBtn.textContent = this.promptUrlBusyLabel()
      }
      // 진행 상태는 별도 시스템 노트 없이 작업 카드 문구 하나로만 표시한다 (중복 안내 제거)
      this.setLoadingMessage("URL을 분석해 기획 탭을 작성하고 있습니다...")
      this.setLoading(true)

      // 1단계: 잡 생성 (202 응답 + job_id)
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({
          url,
          tab,
          prompt: options.prompt || "",
          max_pages: maxPages,
          text_model_id: this.selectedModel,
          model_name: this.selectedModel,
          provider: this.getProvider(this.selectedModel)
        })
      })
      const enqueueResult = await response.json().catch(() => ({}))

      if (!response.ok || enqueueResult.success === false) {
        throw new Error(enqueueResult.error || enqueueResult.message || `HTTP ${response.status}`)
      }

      const jobId = enqueueResult?.data?.job_id
      if (!jobId) throw new Error("잡 ID를 받지 못했습니다. 잠시 후 다시 시도해주세요.")

      // 2단계: 폴링 (비동기 내성 패턴 — 일시 장애 600s 재시도)
      // 폴링 경로: endpoint에서 generate_from_url 부분을 url_generation_jobs/:id 로 대체
      const statusBase = endpoint.replace(/\/generate_from_url$/, `/url_generation_jobs/${encodeURIComponent(jobId)}`)
      const startedAt = Date.now()
      const TIMEOUT_MS = 600000 // 600s
      const POLL_INTERVAL_MS = 2000
      let consecutiveFails = 0

      while (true) {
        let job = null
        try {
          const pollRes = await fetch(statusBase, {
            method: "GET",
            headers: { "Accept": "application/json" }
          })
          if (!pollRes.ok) {
            const errData = await pollRes.json().catch(() => ({}))
            // 즉시 실패 조건: 401/419/404
            if (pollRes.status === 401 || pollRes.status === 419) {
              throw new Error("인증이 만료되었습니다. 페이지를 새로고침해 주세요.")
            }
            if (pollRes.status === 404) {
              throw new Error("생성 작업을 찾을 수 없습니다. 다시 시도해 주세요.")
            }
            if (pollRes.status === 402 && errData?.error === 'quota_exceeded') {
              throw new Error(errData.message || "AI 사용량 한도를 초과했습니다.")
            }
            // 5xx, 408, 429 → 일시 장애 → 재시도
            if (pollRes.status >= 500 || pollRes.status === 408 || pollRes.status === 429) {
              throw Object.assign(new Error(`서버 일시 오류 (${pollRes.status})`), { retryable: true })
            }
            throw new Error(errData?.error || errData?.message || `HTTP ${pollRes.status}`)
          }
          job = await pollRes.json()
          consecutiveFails = 0
        } catch (pollError) {
          if (this._isIaUrlPollTransient(pollError)) {
            consecutiveFails++
            if (consecutiveFails === 2) {
              this.setLoadingMessage("서버 연결이 잠시 불안정합니다. 계속 시도 중...")
            }
          } else {
            throw pollError
          }
        }

        if (job) {
          const jobData = job?.data || job
          if (jobData.status === "succeeded") {
            if (this._promptUrlInput) this._promptUrlInput.value = ""
            if (this._promptUrlPanel) this._promptUrlPanel.style.display = "none"
            const elapsedText = this._formatElapsedKo(Date.now() - startedAt)
            const summary = jobData.message || "기획 탭을 작성했습니다"
            const completionNote = `완료: ${summary} (${elapsedText} 소요)`
            this.appendSystemNote(completionNote)
            // 600ms 뒤 이동으로 사라지므로 1회용 키로 보존 → 로드 시 복원
            try { sessionStorage.setItem(`${this.config.storagePrefix}-pw-url-gen-note`, completionNote) } catch (e) { /* 저장 불가 시 무시 */ }
            const redirectUrl = jobData.redirect_url
            window.setTimeout(() => {
              if (redirectUrl) window.location.href = redirectUrl
              else window.location.reload()
            }, 600)
            return
          }

          if (jobData.status === "failed" || jobData.status === "cancelled") {
            throw new Error(jobData.error_message || "기획 탭 생성에 실패했습니다. 다시 시도해 주세요.")
          }

          // queued/running — 계속 폴링
          if (jobData.status === "running") {
            const crawledCount = jobData.crawled_pages_count
            if (jobData.phase === "generating") {
              this.setLoadingMessage("사이트를 다 읽었어요! 이제 기획 내용을 작성하고 있어요...")
            } else if (crawledCount && crawledCount > 0) {
              let message = `사이트를 읽고 있어요... (${crawledCount}/${maxPages}페이지)`
              // 90초 넘게 크롤 중이면 멈춘 게 아니라 무거운 사이트임을 안내
              if (Date.now() - startedAt > 90000) {
                message += " · 내용이 많은 사이트라 시간이 조금 더 걸려요"
              }
              this.setLoadingMessage(message)
            } else {
              this.setLoadingMessage("URL을 분석해 기획 탭을 작성하고 있습니다...")
            }
          }
        }

        if (Date.now() - startedAt > TIMEOUT_MS) {
          throw new Error("기획 탭 생성 응답 시간이 초과되었습니다. 다시 시도해 주세요.")
        }

        const delay = consecutiveFails > 0
          ? Math.min(3000 * consecutiveFails, 10000)
          : POLL_INTERVAL_MS
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    } catch (error) {
      const message = error.message || "URL 기반 기획 탭 생성에 실패했습니다."
      this.appendSystemNote(message)
      alert(message)
    } finally {
      this.setLoadingMessage("")
      this.setLoading(false)
      if (this._promptUrlAddBtn) {
        this._promptUrlAddBtn.disabled = false
        this._promptUrlAddBtn.textContent = this.promptUrlAddLabel()
      }
    }
  }

  triggerPromptDocumentUpload(e) {
    e?.preventDefault?.()
    if (!this.canAddPromptAttachment()) return
    if (this._promptDocumentInput) this._promptDocumentInput.click()
  }

  async handlePromptDocumentUpload(e) {
    const files = Array.from(e.target.files || [])
    if (!files.length) return

    const remainingSlots = 5 - this.promptAttachmentCount()
    if (remainingSlots <= 0) {
      this.canAddPromptAttachment()
      e.target.value = ""
      return
    }

    try {
      for (const file of files.slice(0, remainingSlots)) {
        const ext = `.${(file.name.split(".").pop() || "").toLowerCase()}`
        if (![".md", ".txt", ".markdown", ".mdx"].includes(ext)) {
          alert(`${file.name}: 지원하지 않는 형식이에요. .md 또는 .txt로 저장해서 올려주세요`)
          continue
        }
        if (file.size > 10 * 1024 * 1024) {
          alert(`${file.name}: 파일이 너무 커요 (최대 10MB)`)
          continue
        }
        if (!this.canAddPromptAttachment()) break

        const formData = new FormData()
        formData.append("file", file)
        const response = await fetch(this.promptExtractDocumentPath(), {
          method: "POST",
          headers: {
            "Accept": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
          },
          body: formData
        })
        const result = await response.json().catch(() => ({}))
        if (!response.ok || result.success === false) {
          throw new Error(result.error || result.message || `HTTP ${response.status}`)
        }

        const text = String(result.text || "").trim()
        if (!text) throw new Error("문서에서 읽을 수 있는 내용을 찾지 못했습니다.")

        this.kakaoPromptAttachments.push({
          id: this.promptAttachmentId(),
          type: "document",
          filename: result.filename || file.name,
          source_type: result.source_type || ext.replace(".", ""),
          text,
          size: Number(result.size || file.size || text.length)
        })
      }
      this.renderPromptAttachments()
    } catch (error) {
      alert(error.message || "문서를 첨부하지 못했습니다.")
    } finally {
      e.target.value = ""
    }
  }

  removePromptAttachment(e) {
    const id = e?.currentTarget?.dataset?.attachmentId
    if (!id) return
    this.kakaoPromptAttachments = this.kakaoPromptAttachments.filter((item) => item.id !== id)
    this.renderPromptAttachments()
  }

  renderPromptAttachments() {
    const preview = this._promptAttachmentPreview
    if (!preview) return

    preview.innerHTML = ""
    if (!this.kakaoPromptAttachments.length) {
      preview.style.display = "none"
      return
    }

    preview.style.display = "block"
    const list = document.createElement("div")
    list.style.display = "flex"
    list.style.flexDirection = "column"
    list.style.gap = "6px"

    this.kakaoPromptAttachments.forEach((attachment) => {
      const row = document.createElement("div")
      row.style.display = "flex"
      row.style.alignItems = "center"
      row.style.gap = "8px"
      row.style.minHeight = "28px"

      const label = document.createElement("span")
      label.textContent = attachment.type === "url" ? "URL" : "문서"
      label.style.flexShrink = "0"
      label.style.fontSize = "10px"
      label.style.fontWeight = "800"
      label.style.color = "#6366F1"
      label.style.border = "1px solid #C7D2FE"
      label.style.padding = "2px 5px"

      const title = document.createElement("span")
      title.textContent = attachment.type === "url"
        ? (attachment.title || attachment.url)
        : (attachment.filename || "첨부 문서")
      title.style.minWidth = "0"
      title.style.flex = "1"
      title.style.fontSize = "12px"
      title.style.color = "#374151"
      title.style.overflow = "hidden"
      title.style.textOverflow = "ellipsis"
      title.style.whiteSpace = "nowrap"

      const remove = document.createElement("button")
      remove.type = "button"
      remove.dataset.attachmentId = attachment.id
      remove.textContent = "삭제"
      remove.style.height = "24px"
      remove.style.padding = "0 8px"
      remove.style.background = "transparent"
      remove.style.border = "1px solid #E5E7EB"
      remove.style.color = "#6B7280"
      remove.style.fontSize = "11px"
      remove.style.fontWeight = "600"
      remove.style.cursor = "pointer"
      remove.style.fontFamily = "inherit"
      remove.addEventListener("click", (event) => this.removePromptAttachment(event))

      row.appendChild(label)
      row.appendChild(title)
      row.appendChild(remove)
      list.appendChild(row)
    })

    preview.appendChild(list)
  }

  buildPromptAttachments() {
    const urls = this.kakaoPromptAttachments
      .filter((item) => item.type === "url")
      .map((item) => ({ url: item.url, title: item.title, domain: item.domain, content: item.content, size: item.size }))
    const documents = this.kakaoPromptAttachments
      .filter((item) => item.type === "document")
      .map((item) => ({ filename: item.filename, source_type: item.source_type, text: item.text, size: item.size }))
    const images = []
    if (this.imageBase64 && this.isKakaoTalkDraftAgent()) {
      const mediaType = this.imagePromptAttachmentMediaType || this.imageBase64.substring(5, this.imageBase64.indexOf(";base64,")) || "image/png"
      images.push({
        filename: this.imagePromptAttachmentName || "attached-image",
        media_type: mediaType,
        data: this.imageBase64.replace(/^data:image\/[^;]+;base64,/, ""),
        size: this.imagePromptAttachmentSize || 0
      })
    }
    return urls.length + documents.length + images.length ? { urls, documents, images } : null
  }

  composePromptAttachmentMessage(message, attachments) {
    if (!attachments) return message
    const sections = [message]
    if (attachments.urls?.length) {
      sections.push(`\n\n[첨부 URL 자료]\n${attachments.urls.map((item, index) => `#${index + 1} ${item.title || item.url}\nURL: ${item.url}\n${item.content}`).join("\n\n")}`)
    }
    if (attachments.documents?.length) {
      sections.push(`\n\n[첨부 문서 자료]\n${attachments.documents.map((item, index) => `#${index + 1} ${item.filename || "문서"}\n${item.text}`).join("\n\n")}`)
    }
    return sections.join("")
  }

  async enhancePromptForPromptTools(message) {
    return message
  }

  // ── 카카오톡 프롬프트 첨부 호환 메서드 ─────────────────────────────────

  kakaoPromptAttachmentCount() {
    return this.kakaoPromptAttachments.length + (this.imageBase64 ? 1 : 0)
  }

  canAddKakaoPromptAttachment({ replacingImage = false } = {}) {
    if (!this.isKakaoTalkDraftAgent()) return false
    const count = this.kakaoPromptAttachmentCount()
    if (count < 5 || replacingImage) return true

    alert("첨부 자료는 최대 5개까지 추가할 수 있습니다.")
    return false
  }

  kakaoAttachmentId() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID()
    }
    return `ka-${Date.now()}-${Math.random().toString(16).slice(2)}`
  }

  kakaoAttachmentEndpoint(path, fallback) {
    return path || fallback
  }

  toggleKakaoPromptUrlPanel(e) {
    e?.preventDefault?.()
    if (!this.canAddKakaoPromptAttachment()) return

    const panel = this._kakaoUrlPanel
    if (!panel) return

    const nextVisible = panel.style.display === "none" || panel.style.display === ""
    panel.style.display = nextVisible ? "flex" : "none"
    if (nextVisible) window.setTimeout(() => this._kakaoUrlInput?.focus(), 0)
  }

  handleKakaoPromptUrlKeydown(e) {
    if (e.key === "Enter") {
      e.preventDefault()
      this.submitKakaoPromptUrl(e)
      return
    }
    if (e.key === "Escape" && this._kakaoUrlPanel) {
      e.preventDefault()
      this._kakaoUrlPanel.style.display = "none"
    }
  }

  async submitKakaoPromptUrl(e) {
    e?.preventDefault?.()
    await this.attachKakaoPromptUrl(this._kakaoUrlInput?.value)
  }

  async attachKakaoPromptUrl(input) {
    if (!this.canAddKakaoPromptAttachment()) return

    const url = String(input || "").trim()
    if (!url) return
    if (!/^https?:\/\//i.test(url)) {
      alert("http:// 또는 https:// 로 시작하는 URL만 지원합니다.")
      return
    }
    if (this.kakaoPromptAttachments.some((item) => item.type === "url" && item.url === url)) {
      alert("이미 첨부한 URL입니다.")
      return
    }

    try {
      if (this._kakaoUrlAddBtn) {
        this._kakaoUrlAddBtn.disabled = true
        this._kakaoUrlAddBtn.textContent = "추가 중"
      }
      const response = await fetch(this.kakaoAttachmentEndpoint(this.config.kakaoTalkFetchUrlPath, "/kakao-talk/ai/fetch_url"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({ url })
      })
      const result = await response.json().catch(() => ({}))
      if (!response.ok || result.success === false) {
        throw new Error(result.error || result.message || `HTTP ${response.status}`)
      }

      let domain = ""
      try { domain = new URL(result.url || url).hostname } catch (_) {}
      const content = String(result.content || "").trim()
      if (!content) throw new Error("URL에서 읽을 수 있는 본문을 찾지 못했습니다.")

      this.kakaoPromptAttachments.push({
        id: this.kakaoAttachmentId(),
        type: "url",
        url: result.url || url,
        title: result.title || "",
        domain,
        content,
        size: content.length
      })
      this.renderKakaoPromptAttachments()
      if (this._kakaoUrlInput) this._kakaoUrlInput.value = ""
      if (this._kakaoUrlPanel) this._kakaoUrlPanel.style.display = "none"
    } catch (error) {
      alert(error.message || "URL을 첨부하지 못했습니다.")
    } finally {
      if (this._kakaoUrlAddBtn) {
        this._kakaoUrlAddBtn.disabled = false
        this._kakaoUrlAddBtn.textContent = "추가"
      }
    }
  }

  triggerKakaoDocumentUpload(e) {
    e?.preventDefault?.()
    if (!this.canAddKakaoPromptAttachment()) return
    if (this._kakaoDocumentInput) this._kakaoDocumentInput.click()
  }

  async handleKakaoDocumentUpload(e) {
    const files = Array.from(e.target.files || [])
    if (!files.length) return

    const remainingSlots = 5 - this.kakaoPromptAttachmentCount()
    if (remainingSlots <= 0) {
      this.canAddKakaoPromptAttachment()
      e.target.value = ""
      return
    }
    if (files.length > remainingSlots) {
      alert(`첨부 자료는 최대 5개까지 추가할 수 있습니다. ${remainingSlots}개만 첨부합니다.`)
    }

    try {
      for (const file of files.slice(0, remainingSlots)) {
        const ext = `.${(file.name.split(".").pop() || "").toLowerCase()}`
        if (![".md", ".txt", ".markdown", ".mdx"].includes(ext)) {
          alert(`${file.name}: 지원하지 않는 형식이에요. .md 또는 .txt로 저장해서 올려주세요`)
          continue
        }
        if (file.size > 10 * 1024 * 1024) {
          alert(`${file.name}: 파일이 너무 커요 (최대 10MB)`)
          continue
        }
        if (!this.canAddKakaoPromptAttachment()) break

        const formData = new FormData()
        formData.append("file", file)
        const response = await fetch(this.kakaoAttachmentEndpoint(this.config.kakaoTalkExtractDocumentPath, "/kakao-talk/ai/extract_document"), {
          method: "POST",
          headers: {
            "Accept": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
          },
          body: formData
        })
        const result = await response.json().catch(() => ({}))
        if (!response.ok || result.success === false) {
          throw new Error(result.error || result.message || `HTTP ${response.status}`)
        }

        const text = String(result.text || "").trim()
        if (!text) throw new Error("문서에서 읽을 수 있는 내용을 찾지 못했습니다.")

        this.kakaoPromptAttachments.push({
          id: this.kakaoAttachmentId(),
          type: "document",
          filename: result.filename || file.name,
          source_type: result.source_type || ext.replace(".", ""),
          text,
          size: Number(result.size || file.size || text.length)
        })
      }
      this.renderKakaoPromptAttachments()
    } catch (error) {
      alert(error.message || "문서를 첨부하지 못했습니다.")
    } finally {
      e.target.value = ""
    }
  }

  removeKakaoPromptAttachment(e) {
    const id = e?.currentTarget?.dataset?.attachmentId
    if (!id) return
    this.kakaoPromptAttachments = this.kakaoPromptAttachments.filter((item) => item.id !== id)
    this.renderKakaoPromptAttachments()
  }

  renderKakaoPromptAttachments() {
    const preview = this._kakaoAttachmentPreview
    if (!preview) return

    preview.innerHTML = ""
    if (!this.kakaoPromptAttachments.length) {
      preview.style.display = "none"
      return
    }

    preview.style.display = "block"
    const list = document.createElement("div")
    list.style.display = "flex"
    list.style.flexDirection = "column"
    list.style.gap = "6px"

    this.kakaoPromptAttachments.forEach((attachment) => {
      const row = document.createElement("div")
      row.style.display = "flex"
      row.style.alignItems = "center"
      row.style.gap = "8px"
      row.style.minHeight = "28px"

      const label = document.createElement("span")
      label.textContent = attachment.type === "url" ? "URL" : "문서"
      label.style.flexShrink = "0"
      label.style.fontSize = "10px"
      label.style.fontWeight = "800"
      label.style.color = "#6366F1"
      label.style.border = "1px solid #C7D2FE"
      label.style.padding = "2px 5px"

      const title = document.createElement("span")
      title.textContent = attachment.type === "url"
        ? (attachment.title || attachment.url)
        : (attachment.filename || "첨부 문서")
      title.style.minWidth = "0"
      title.style.flex = "1"
      title.style.fontSize = "12px"
      title.style.color = "#374151"
      title.style.overflow = "hidden"
      title.style.textOverflow = "ellipsis"
      title.style.whiteSpace = "nowrap"

      const remove = document.createElement("button")
      remove.type = "button"
      remove.dataset.attachmentId = attachment.id
      remove.textContent = "삭제"
      remove.style.height = "24px"
      remove.style.padding = "0 8px"
      remove.style.background = "transparent"
      remove.style.border = "1px solid #E5E7EB"
      remove.style.color = "#6B7280"
      remove.style.fontSize = "11px"
      remove.style.fontWeight = "600"
      remove.style.cursor = "pointer"
      remove.style.fontFamily = "inherit"
      remove.addEventListener("click", (event) => this.removeKakaoPromptAttachment(event))

      row.appendChild(label)
      row.appendChild(title)
      row.appendChild(remove)
      list.appendChild(row)
    })

    preview.appendChild(list)
  }

  buildKakaoPromptAttachments() {
    if (!this.isKakaoTalkDraftAgent()) return null

    const urls = this.kakaoPromptAttachments
      .filter((item) => item.type === "url")
      .map((item) => ({
        url: item.url,
        title: item.title,
        domain: item.domain,
        content: item.content,
        size: item.size
      }))

    const documents = this.kakaoPromptAttachments
      .filter((item) => item.type === "document")
      .map((item) => ({
        filename: item.filename,
        source_type: item.source_type,
        text: item.text,
        size: item.size
      }))

    const images = []
    if (this.imageBase64) {
      const mediaType = this.imagePromptAttachmentMediaType || this.imageBase64.substring(5, this.imageBase64.indexOf(";base64,")) || "image/png"
      images.push({
        filename: this.imagePromptAttachmentName || "attached-image",
        media_type: mediaType,
        data: this.imageBase64.replace(/^data:image\/[^;]+;base64,/, ""),
        size: this.imagePromptAttachmentSize || 0
      })
    }

    if (urls.length + documents.length + images.length === 0) return null
    return { urls, documents, images }
  }

  clearKakaoPromptAttachments() {
    this.kakaoPromptAttachments = []
    this.renderKakaoPromptAttachments()
    if (this._kakaoUrlInput) this._kakaoUrlInput.value = ""
    if (this._kakaoUrlPanel) this._kakaoUrlPanel.style.display = "none"
    if (this.isKakaoTalkDraftAgent()) this.removeImage()
  }

  // ── 이미지 업로드 ─────────────────────────────────────────────────────────

  triggerImageUpload() {
    if (this.isPromptAttachmentAgent() && !this.canAddPromptAttachment({ replacingImage: !!this.imageBase64 })) return
    if (this._imageInput) this._imageInput.click()
  }

  handleImageUpload(e) {
    const file = e.target.files[0]
    if (!file) return
    if (this.isPromptAttachmentAgent() && !this.canAddPromptAttachment({ replacingImage: !!this.imageBase64 })) {
      e.target.value = ""
      return
    }
    // 파일 크기 체크 (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert(_t("alerts.image_too_large", "이미지 크기는 5MB 이하여야 합니다."))
      e.target.value = ""
      return
    }
    const reader = new FileReader()
    reader.onload = (event) => {
      this.imageBase64 = event.target.result
      this.imagePromptAttachmentName = file.name
      this.imagePromptAttachmentSize = file.size
      this.imagePromptAttachmentMediaType = file.type || this.imageBase64.substring(5, this.imageBase64.indexOf(";base64,"))
      if (this._imagePreviewImg) this._imagePreviewImg.src = this.imageBase64
      if (this._imagePreview) this._imagePreview.style.display = "block"
      if (this._imageFileName) this._imageFileName.textContent = file.name
    }
    reader.readAsDataURL(file)
    // 같은 파일 재선택 허용
    e.target.value = ""
  }

  removeImage() {
    this.imageBase64 = null
    this.imagePromptAttachmentName = null
    this.imagePromptAttachmentSize = 0
    this.imagePromptAttachmentMediaType = null
    if (this._imagePreview) this._imagePreview.style.display = "none"
    if (this._imagePreviewImg) this._imagePreviewImg.src = ""
    if (this._imageFileName) this._imageFileName.textContent = _t("image_upload.attached", "이미지 첨부됨")
  }

  handlePaste(e) {
    const items = e.clipboardData?.items
    if (!items) return
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        e.preventDefault()
        const file = item.getAsFile()
        if (!file) return
        if (this.isPromptAttachmentAgent() && !this.canAddPromptAttachment({ replacingImage: !!this.imageBase64 })) {
          return
        }
        if (file.size > 5 * 1024 * 1024) {
          alert(_t("alerts.image_too_large", "이미지 크기는 5MB 이하여야 합니다."))
          return
        }
        const reader = new FileReader()
        reader.onload = (event) => {
          this.imageBase64 = event.target.result
          this.imagePromptAttachmentName = file.name || _t("image_upload.clipboard", "클립보드 이미지")
          this.imagePromptAttachmentSize = file.size || 0
          this.imagePromptAttachmentMediaType = file.type || this.imageBase64.substring(5, this.imageBase64.indexOf(";base64,"))
          if (this._imagePreviewImg) this._imagePreviewImg.src = this.imageBase64
          if (this._imagePreview) this._imagePreview.style.display = "block"
          if (this._imageFileName) this._imageFileName.textContent = _t("image_upload.clipboard", "클립보드 이미지")
        }
        reader.readAsDataURL(file)
        return
      }
    }
  }

  // ── 메시지 저장/불러오기 ──────────────────────────────────────────────────

  async persistMessage(role, content) {
    if (!this.config.resourceId || this.config.resourceId === 0 || !this.config.chatApiPath) return
    try {
      await fetch(this.config.chatApiPath, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({
          role: role,
          content: content,
          context_type: this.config.contextType,
          section_id: this.config.sectionId || null,
          session_id: this.currentMainSessionId,
          resource_type: this.config.resourceType,
          resource_id: this.config.resourceId
        })
      })
    } catch (e) {
      // silent fail
    }
  }

  _saveChatHistoryToSession() {
    if (this.chatHistory.length === 0) return
    const key = `${this.config.storagePrefix}-pending-history`
    sessionStorage.setItem(key, JSON.stringify({
      sessionId: this.currentMainSessionId,
      messages: this.chatHistory
    }))
  }

  async _loadAndPersistPendingHistory() {
    const key = `${this.config.storagePrefix}-pending-history`
    const raw = sessionStorage.getItem(key)
    if (!raw) return false
    try {
      const { sessionId, messages } = JSON.parse(raw)
      if (!messages?.length) return false
      this.currentMainSessionId = sessionId
      localStorage.setItem(`${this.config.storagePrefix}-main-session`, sessionId)
      for (const msg of messages) {
        this.chatHistory.push({ role: msg.role, content: msg.content })
        const text = typeof msg.content === "string"
          ? msg.content
          : msg.content?.[msg.content.length - 1]?.text || ""
        this.appendMessage(msg.role, text)
        await this.persistMessage(msg.role, msg.content)
      }
      sessionStorage.removeItem(key)
      return true
    } catch (e) {
      console.error("[KlavyAgentCore] _loadAndPersistPendingHistory failed:", e)
      sessionStorage.removeItem(key)
      return false
    }
  }

  _appendSiteCreationNextSteps() {
    this.appendSystemNote(_t("system_notes.site_created", "사이트가 생성되었습니다! 다음 단계로 페이지를 추가해보세요."))
    const resourceId = this.config.resourceId
    this._renderFollowUpButtons([
      { label: "홈 페이지 만들기", action: "navigate",
        url: `/admin/profile_pages/new?profile_site_id=${resourceId}` },
      { label: "필요한 페이지 추천받기", action: "send",
        message: "이 사이트에 맞는 페이지 구성을 추천해줘. 포트폴리오에 필요한 페이지 목록과 각 페이지별 역할, 포함할 섹션을 알려줘." }
    ])
  }

  _appendPageCreationNextSteps() {
    this.appendSystemNote(_t("system_notes.page_created", "페이지가 생성되었습니다!"))
    const siteId = this.config.parentSiteId
    this._renderFollowUpButtons([
      { label: "섹션 추가하기", action: "send",
        message: "이 페이지에 필요한 섹션 구성을 추천해줘. 각 섹션의 역할과 내용을 설명해줘." },
      { label: "다음 페이지 만들기", action: "navigate",
        url: `/admin/profile_pages/new?profile_site_id=${siteId}` },
      { label: "사이트로 돌아가기", action: "navigate",
        url: `/admin/profile_sites/${siteId}/edit` }
    ])
  }

  _renderFollowUpButtons(buttons) {
    const el = document.createElement("div")
    el.className = "ba-followup-buttons"
    el.innerHTML = buttons.map(b =>
      `<button type="button" class="ba-followup-btn">${this.escapeHtml(b.label)}</button>`
    ).join("")
    el.querySelectorAll(".ba-followup-btn").forEach((btn, i) => {
      btn.addEventListener("click", async () => {
        el.remove()
        const b = buttons[i]
        if (b.action === "navigate") {
          window.location.href = b.url
        } else if (b.action === "send") {
          if (this._input) this._input.value = b.message
          this.send()
        } else if (b.action === "kmong-comparison-preview") {
          window.dispatchEvent(new CustomEvent("kmong-comparison-preview-request", {
            detail: {
              sections: Array.isArray(b.sections) ? b.sections : [],
              mode: Array.isArray(b.sections) && b.sections.length > 0 ? "selected" : "all"
            }
          }))
          this.appendSystemNote(b.message || "개선 미리보기를 요청했습니다.")
        } else if (b.action === "kmong-comparison-detail-images") {
          this.requestKmongComparisonDetailImages()
        } else if (b.action === "kmong-comparison-main-image") {
          this.createKmongComparisonMainImage({ main_image_mode: b.mainImageMode || b.main_image_mode || "product" })
        } else if (b.action === "kmong-seller-profile-image") {
          this.createKmongSellerProfileImage(b.message)
        } else if (b.action === "kmong-seller-profile-generate") {
          this.updateKmongSellerProfile(b.message, { comparisonReportId: this.kmongComparisonReportId() })
        }
      })
    })
    if (this._messages) this._messages.appendChild(el)
    this.scrollToBottom()
  }

  async loadSessions() {
    if (!this.config.chatApiPath || !this.config.resourceId || this.config.resourceId === 0) return []
    try {
      const res = await fetch(withSearchParams(withPathSuffix(this.config.chatApiPath, "/main_sessions"), {
        context_type: this.config.contextType || "page",
        section_id: this.config.sectionId || null
      }))
      const data = await res.json()
      return data.data || []
    } catch (e) {
      return []
    }
  }

  async switchSession(sessionId) {
    this.currentMainSessionId = sessionId
    localStorage.setItem(`${this.config.storagePrefix}-main-session`, sessionId)
    this.chatHistory = []
    if (this._messages) this._messages.innerHTML = ""

    if (!this.config.resourceId || this.config.resourceId === 0) { this.showWelcomeMessage(); return }

    try {
      const res = await fetch(withSearchParams(this.config.chatApiPath, {
        context_type: this.config.contextType,
        session_id: sessionId,
        section_id: this.config.sectionId || null
      }))
      const data = await res.json()
      if (data.data && data.data.length > 0) {
        data.data.forEach(msg => {
          this.chatHistory.push({ role: msg.role, content: msg.content })
          this.appendMessage(msg.role, msg.content)
        })
        if (this._messages) this._messages.scrollTop = this._messages.scrollHeight
      } else {
        this.showWelcomeMessage()
      }
    } catch (e) {
      console.error("[KlavyAgentCore] switchSession error:", e)
      this.showWelcomeMessage()
    }
  }

  historySessionDeleteUrl(sessionId) {
    if (!this.config.chatApiPath || !sessionId) return null
    if (this.config.chatApiPath.includes("/admin/klavy_agents/")) {
      return withPathSuffix(this.config.chatApiPath, `/${encodeURIComponent(sessionId)}`)
    }
    return withPathSuffix(this.config.chatApiPath, `/session/${encodeURIComponent(sessionId)}`)
  }

  async deleteChatSession(sessionId) {
    const url = this.historySessionDeleteUrl(sessionId)
    if (!url) return false

    try {
      const res = await fetch(url, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
          "X-Requested-With": "XMLHttpRequest"
        }
      })
      return res.ok
    } catch (_e) {
      return false
    }
  }

  async handleMainSessionDelete(sessionId) {
    const deleted = await this.deleteChatSession(sessionId)
    if (!deleted) return false

    if (sessionId === this.currentMainSessionId) {
      this.newSession()
    }

    return true
  }

  async handleRulesSessionDelete(sessionId) {
    const deleted = await this.deleteChatSession(sessionId)
    if (!deleted) return false

    if (sessionId === this.currentRulesSessionId) {
      this.currentRulesSessionId = this.generateSessionId()
      localStorage.setItem(`${this.config.storagePrefix}-rules-session`, this.currentRulesSessionId)
      this.rulesChatHistory = []
      this.showRulesChatGuide()
      if (this._rulesChatInput) this._rulesChatInput.focus()
    }

    return true
  }

  newSession() {
    this.currentMainSessionId = this.generateSessionId()
    localStorage.setItem(`${this.config.storagePrefix}-main-session`, this.currentMainSessionId)
    this.chatHistory = []
    this._lastInterviewQNum = null
    if (this._messages) this._messages.innerHTML = ""
    this.showWelcomeMessage()
    if (this._input) this._input.focus()
  }

  newMainSession() { this.newSession() }

  updateSectionContext(sectionType, systemPrompt, options = {}) {
    this.config.sectionType = sectionType
    if (systemPrompt) this.config.systemPrompt = systemPrompt
    if (Object.prototype.hasOwnProperty.call(options, "sectionTitle")) this.config.sectionTitle = options.sectionTitle
    if (Object.prototype.hasOwnProperty.call(options, "quickStarts")) this.config.quickStarts = options.quickStarts || []
    if (this._messages) {
      // 환영 메시지만 교체 (채팅 히스토리는 유지)
      const welcomeEl = this._messages.querySelector(".ba-welcome-message")
      if (welcomeEl) welcomeEl.remove()
      this.showWelcomeMessage()
    }
  }

  // ── 메인 히스토리 패널 ────────────────────────────────────────────────────

  async toggleMainHistory(e) {
    if (e) e.stopPropagation()
    this.mainHistoryOpen = !this.mainHistoryOpen
    if (!this._mainHistoryPanel) return

    if (!this.mainHistoryOpen) {
      this._mainHistoryPanel.style.display = "none"
      return
    }

    const id = this.config.resourceId
    if (!id || id === 0) {
      this._mainHistoryPanel.innerHTML = `<div style="padding: 16px; text-align: center; color: #9CA3AF; font-size: 12px;">${_t("history.empty_after_save", "저장 후 대화 내역을 확인할 수 있습니다")}</div>`
      this._mainHistoryPanel.style.display = "block"
      return
    }

    this._mainHistoryPanel.innerHTML = `<div style="padding: 12px; text-align: center; color: #9CA3AF; font-size: 12px;">${_t("history.loading", "로딩 중...")}</div>`
    this._mainHistoryPanel.style.display = "block"

    try {
      const res = await fetch(withSearchParams(withPathSuffix(this.config.chatApiPath, "/main_sessions"), {
        context_type: this.config.contextType || "page",
        section_id: this.config.sectionId || null
      }))
      const data = await res.json()

      if (!data.data || data.data.length === 0) {
        this._mainHistoryPanel.innerHTML = `<div style="padding: 16px; text-align: center; color: #9CA3AF; font-size: 12px;">${_t("history.no_history", "대화 내역이 없습니다")}</div>`
        return
      }

      this._mainHistoryPanel.innerHTML = data.data.map(s => {
        const isActive = s.session_id === this.currentMainSessionId
        const date = new Date(s.created_at)
        const dateStr = `${date.getMonth()+1}/${date.getDate()} ${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`
        return `<div class="ba-history-item${isActive ? ' ba-history-item--active' : ''}"
                     data-session-id="${s.session_id}"
                     style="padding: 10px 14px; cursor: pointer; border-bottom: 1px solid #F3F4F6; font-size: 12px;${isActive ? ' background: #EEF2FF;' : ''}">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <span style="color: #111827; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 200px;">${this.escapeHtml(s.first_message)}</span>
            <span style="display:flex; align-items:center; gap:4px; flex-shrink:0; margin-left:8px;">
              <span style="color: #9CA3AF; font-size: 11px;">${dateStr}</span>
              <button type="button" class="ba-history-download-btn" data-session-id="${s.session_id}" title="${_t("history.download_title", "다운로드")}" style="width:18px;height:18px;display:inline-flex;align-items:center;justify-content:center;border:1px solid #E5E7EB;background:#fff;color:#9CA3AF;cursor:pointer;font-size:11px;">↓</button>
              <button type="button" class="ba-history-delete-btn" data-session-id="${s.session_id}" title="${_t("history.delete_title", "대화 삭제")}" style="width:18px;height:18px;display:inline-flex;align-items:center;justify-content:center;border:1px solid #E5E7EB;background:#fff;color:#9CA3AF;cursor:pointer;">×</button>
            </span>
          </div>
          <div style="color: #6B7280; font-size: 11px; margin-top: 2px;">${_t("history.message_count", "%{count}개 메시지").replace("%{count}", s.message_count)}</div>
        </div>`
      }).join("")

      this._mainHistoryPanel.querySelectorAll(".ba-history-download-btn").forEach(btn => {
        btn.addEventListener("click", async (event) => {
          event.stopPropagation()
          const sid = btn.dataset.sessionId
          if (!sid) return
          await this.downloadSessionConversation(sid)
        })
      })
      this._mainHistoryPanel.querySelectorAll(".ba-history-item").forEach(el => {
        el.addEventListener("click", () => {
          const sid = el.dataset.sessionId
          this.mainHistoryOpen = false
          this._mainHistoryPanel.style.display = "none"
          this.switchSession(sid)
        })
      })
      this._mainHistoryPanel.querySelectorAll(".ba-history-delete-btn").forEach(btn => {
        btn.addEventListener("click", async (event) => {
          event.stopPropagation()
          const sid = btn.dataset.sessionId
          if (!sid) return
          btn.disabled = true
          const deleted = await this.handleMainSessionDelete(sid)
          if (!deleted) {
            btn.disabled = false
            return
          }
          const sessionsAfterDelete = await this.loadSessions()
          this.renderMainHistory(sessionsAfterDelete)
        })
      })
    } catch (e) {
      this._mainHistoryPanel.innerHTML = `<div style="padding: 16px; text-align: center; color: #EF4444; font-size: 12px;">${_t("history.load_failed", "로딩 실패")}</div>`
    }
  }

  renderMainHistory(sessions) {
    if (!this._mainHistoryPanel) return
    if (!sessions || sessions.length === 0) {
      this._mainHistoryPanel.innerHTML = '<div style="padding: 16px; text-align: center; color: #9CA3AF; font-size: 12px;">대화 내역이 없습니다</div>'
      return
    }
    this._mainHistoryPanel.innerHTML = sessions.map(s => {
      const isActive = s.session_id === this.currentMainSessionId
      const date = new Date(s.created_at)
      const dateStr = `${date.getMonth()+1}/${date.getDate()} ${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`
      return `<div class="ba-history-item${isActive ? ' ba-history-item--active' : ''}" data-session-id="${s.session_id}"
                   style="padding:10px 14px;cursor:pointer;border-bottom:1px solid #F3F4F6;font-size:12px;${isActive ? 'background:#EEF2FF;' : ''}">
        <div style="display:flex;justify-content:space-between;align-items:center;">
          <span style="color:#111827;font-weight:500;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:200px;">${this.escapeHtml(s.first_message)}</span>
          <span style="display:flex;align-items:center;gap:4px;flex-shrink:0;margin-left:8px;">
            <span style="color:#9CA3AF;font-size:11px;">${dateStr}</span>
            <button type="button" class="ba-history-download-btn" data-session-id="${s.session_id}" title="${_t("history.download_title", "다운로드")}" style="width:18px;height:18px;display:inline-flex;align-items:center;justify-content:center;border:1px solid #E5E7EB;background:#fff;color:#9CA3AF;cursor:pointer;font-size:11px;">↓</button>
            <button type="button" class="ba-history-delete-btn" data-session-id="${s.session_id}" title="${_t("history.delete_title", "대화 삭제")}" style="width:18px;height:18px;display:inline-flex;align-items:center;justify-content:center;border:1px solid #E5E7EB;background:#fff;color:#9CA3AF;cursor:pointer;">×</button>
          </span>
        </div>
        <div style="color:#6B7280;font-size:11px;margin-top:2px;">${_t("history.message_count", "%{count}개 메시지").replace("%{count}", s.message_count)}</div>
      </div>`
    }).join("")
    this._mainHistoryPanel.querySelectorAll(".ba-history-download-btn").forEach(btn => {
      btn.addEventListener("click", async (event) => {
        event.stopPropagation()
        const sid = btn.dataset.sessionId
        if (!sid) return
        await this.downloadSessionConversation(sid)
      })
    })
    this._mainHistoryPanel.querySelectorAll(".ba-history-item").forEach(el => {
      el.addEventListener("click", () => {
        const sid = el.dataset.sessionId
        this.mainHistoryOpen = false
        if (this._mainHistoryPanel) this._mainHistoryPanel.style.display = "none"
        this.switchSession(sid)
      })
    })
    this._mainHistoryPanel.querySelectorAll(".ba-history-delete-btn").forEach(btn => {
      btn.addEventListener("click", async (event) => {
        event.stopPropagation()
        const sid = btn.dataset.sessionId
        if (!sid) return
        btn.disabled = true
        const deleted = await this.handleMainSessionDelete(sid)
        if (!deleted) {
          btn.disabled = false
          return
        }
        const sessionsAfterDelete = await this.loadSessions()
        this.renderMainHistory(sessionsAfterDelete)
      })
    })
  }

  // ── 대화 다운로드 ─────────────────────────────────────────────────────────

  _buildMarkdown(messages) {
    const projectTitle = this.config.pageTitle || this.config.parentSiteTitle || "대화"
    const dateStr = new Date().toLocaleString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })
    const header = `# 대화 내역 — ${projectTitle}\n날짜: ${dateStr}\n\n`
    const body = messages
      .filter(m => m.role === "user" || m.role === "assistant")
      .map(m => {
        const clean = m.content
          .replace(/```json[\s\S]*?```/g, "")
          .replace(/<think>[\s\S]*?<\/think>/gi, "")
          .trim()
        if (!clean) return null
        const label = m.role === "user" ? "**Q.** " : "**A.** "
        return `${label}${clean}`
      })
      .filter(Boolean)
      .join("\n\n---\n\n")
    return header + body
  }

  _triggerDownload(content, filename) {
    const blob = new Blob([content], { type: "text/markdown;charset=utf-8" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  }

  downloadConversation() {
    if (!this.chatHistory || this.chatHistory.length === 0) {
      alert(_t("alerts.no_chat_to_download", "다운로드할 대화 내역이 없습니다."))
      return
    }
    const md = this._buildMarkdown(this.chatHistory)
    const dateTag = new Date().toISOString().slice(0, 10)
    const title = (this.config.pageTitle || "대화").replace(/[^\w가-힣]/g, "_")
    this._triggerDownload(md, `대화내역-${title}-${dateTag}.md`)
  }

  async downloadSessionConversation(sessionId) {
    try {
      const res = await fetch(withSearchParams(this.config.chatApiPath, {
        context_type: this.config.contextType || "page",
        session_id: sessionId
      }))
      const data = await res.json()
      const messages = data.data || []
      if (messages.length === 0) { alert(_t("alerts.no_messages", "대화 내역이 없습니다.")); return }
      const md = this._buildMarkdown(messages)
      const dateTag = new Date().toISOString().slice(0, 10)
      const title = (this.config.pageTitle || "대화").replace(/[^\w가-힣]/g, "_")
      this._triggerDownload(md, `대화내역-${title}-${dateTag}.md`)
    } catch (e) {
      alert(_t("alerts.download_failed", "다운로드에 실패했습니다."))
    }
  }

  // ── 시스템 프롬프트 ───────────────────────────────────────────────────────

  buildProjectRulesPrompt() {
    if (!this.rulesEnabled) return ""
    const rules = this.activeProjectRules()
    if (rules.length === 0) return ""
    const sections = rules.map(r => `### ${r.title}\n${r.content}`).join("\n\n")
    const scopeLabel = this.isPlanningWorkspaceContext()
      ? "이 기획 워크스페이스에 대해 설정된 규칙입니다. 콘텐츠 생성 시 반드시 따르세요."
      : (this.config.contextType === "ia_project" || this.config.resourceType === "IaProject")
        ? "이 IA 프로젝트에 대해 설정된 규칙입니다. 콘텐츠 생성 시 반드시 따르세요."
        : "이 페이지에 대해 설정된 규칙입니다. 콘텐츠 생성 시 반드시 따르세요."
    return `\n## 작업 규칙\n${scopeLabel}\n\n${sections}\n`
  }

  activeProjectRules() {
    return (this.projectRules || []).filter(r => r.active !== false)
  }

  sectionQuickPrompts(sectionLabel, options = {}) {
    if (this.config.contextType === "section" && ["profile-page", "profile-site"].includes(this.config.agentSlug)) {
      return this.profileSectionQuickPrompts(this.config.sectionType, sectionLabel)
    }

    const activeRules = this.activeProjectRules()
    const normalizedLabel = sectionLabel || this.config.sectionTitle || this.config.sectionType || "섹션"
    if (activeRules.length === 0) {
      const fallbackPrompts = options.designer
        ? ["이 섹션 내용 생성해줘", "현재 페이지 톤에 맞게 다듬어줘", "더 설득력 있게 고쳐줘"]
        : ["이 섹션 내용 생성해줘", "페이지에 맞게 작성해줘"]
      return {
        prompts: fallbackPrompts,
        helperText: ""
      }
    }

    return {
      prompts: [
        `등록된 작업 규칙을 기준으로 현재 ${normalizedLabel} 내용을 페이지 맥락에 맞게 수정해줘`,
        "현재 페이지 톤에 맞게 다듬어줘",
        "이 섹션 내용 새로 제안해줘"
      ],
      helperText: "등록된 작업 규칙을 바탕으로 현재 섹션 내용을 바로 다듬을 수 있어요."
    }
  }

  profileSectionQuickPrompts(sectionType, sectionLabel) {
    const label = sectionLabel || "섹션"
    const map = {
      hero: ["강한 첫인상 히어로 문구", "신뢰감 있는 자기소개", "문의 유도 CTA 포함"],
      about: ["소개 문단 작성", "전문성과 강점 정리", "브랜드 톤으로 다듬기"],
      experience: ["경력 항목 작성", "성과 중심으로 정리", "프리랜서 이력 구성"],
      projects: ["대표 프로젝트 3개 작성", "성과 수치 포함", "프로젝트 설명 다듬기"],
      skills: ["기술 스택 정리", "핵심 역량 태그 구성", "전문 분야별로 묶기"],
      contact: ["문의 섹션 문구", "협업 제안 문구", "연락 CTA 작성"],
      education: ["학력/교육 이력 작성", "수료 과정 정리", "전문성 중심으로 구성"],
      services: ["제공 서비스 3개 작성", "패키지형 서비스 구성", "고객 혜택 중심으로 작성"],
      testimonials: ["추천글 예시 작성", "클라이언트 후기 톤", "신뢰감 있게 다듬기"],
      stats: ["성과 수치 3개 제안", "포트폴리오 지표 구성", "임팩트 있는 숫자 문구"],
      gallery: ["갤러리 설명 작성", "작업 이미지 캡션", "작품 소개 문구"],
      clients: ["클라이언트 목록 구성", "협업 브랜드 설명", "신뢰 요소 정리"],
      awards: ["수상/인증 항목 작성", "이력 강조 문구", "권위 요소 정리"],
      faq: ["자주 묻는 질문 5개", "협업 전 FAQ", "가격/일정 FAQ"],
      cta: ["상담 유도 문구", "프로젝트 문의 CTA", "전환율 높은 CTA"],
      block_text: ["텍스트 블록 작성", "핵심 메시지 한 단락", "CTA 포함 문구"],
      block_image: ["이미지 캡션 작성", "작업 이미지 설명", "비주얼 섹션 문구"],
      block_image_text: ["이미지+텍스트 내용", "작업 과정 소개", "강점 설명 블록"],
      block_image_top: ["이미지 상단 블록", "케이스 소개 문구", "스토리형 설명"],
      block_columns: ["3단 컬럼 내용", "핵심 강점 3개", "프로세스 3단계"],
      block_cards: ["카드 3개 작성", "서비스 카드 구성", "프로젝트 카드 구성"],
      block_banner: ["배너 문구 작성", "프로모션 CTA", "짧은 안내 배너"],
      block_spacer: ["여백 용도 추천", "섹션 간 호흡 조정", "간격 설정 제안"],
      block_divider: ["구분선 스타일 추천", "섹션 분리 방식", "간결한 구분선 설정"]
    }
    const prompts = map[sectionType] || [`${label} 내용 생성`, `${label} 페이지 톤에 맞게 작성`, `${label} 더 구체적으로`]
    return {
      prompts: prompts.map((text) => ({ text, prompt: `${label}에 들어갈 ${text}을(를) 현재 페이지 맥락에 맞게 작성해서 폼에 반영해줘.` })),
      helperText: `${label}에 들어갈 내용만 생성해 좌측 섹션 폼에 먼저 반영합니다.`
    }
  }

  quickButtonHtml(text) {
    const label = typeof text === "object" && text !== null ? (text.text || text.label || "") : String(text || "")
    const prompt = typeof text === "object" && text !== null ? (text.prompt || text.message || label) : label
    const actionType = typeof text === "object" && text !== null ? (text.actionType || text.action_type || "") : ""
    const analysisMode = typeof text === "object" && text !== null ? (text.comparisonAnalysisMode || text.comparison_analysis_mode || "") : ""
    const comparisonSections = typeof text === "object" && text !== null ? (text.comparisonSections || text.comparison_sections || []) : []
    const mainImageMode = typeof text === "object" && text !== null ? (text.mainImageMode || text.main_image_mode || "") : ""
    const facebookImageMode = typeof text === "object" && text !== null ? (text.facebookImageMode || text.facebook_image_mode || "") : ""
    const isRuleFocusedPrompt = label.startsWith("등록된 작업 규칙을 기준으로")
    const buttonClass = isRuleFocusedPrompt ? "ba-quick-btn ba-quick-btn--long" : "ba-quick-btn"
    const actionAttr = actionType ? ` data-action-type="${this.escapeHtml(actionType)}"` : ""
    const analysisAttr = analysisMode ? ` data-comparison-analysis-mode="${this.escapeHtml(analysisMode)}"` : ""
    const mainImageModeAttr = mainImageMode ? ` data-main-image-mode="${this.escapeHtml(mainImageMode)}"` : ""
    const facebookImageModeAttr = facebookImageMode ? ` data-facebook-image-mode="${this.escapeHtml(facebookImageMode)}"` : ""
    const sectionsAttr = comparisonSections.length ? ` data-comparison-sections="${this.escapeHtml(JSON.stringify(comparisonSections))}"` : ""
    return `<button type="button" class="${buttonClass}" data-text="${this.escapeHtml(prompt)}"${actionAttr}${analysisAttr}${mainImageModeAttr}${facebookImageModeAttr}${sectionsAttr}>${this.escapeHtml(label)}</button>`
  }

  normalizeDesignerSelection(detail) {
    const selectedSurface = detail?.selectedSurface || "section"
    return {
      selectedSurface,
      clientKey: detail?.clientKey || null,
      id: detail?.id || null,
      sectionType: detail?.sectionType || null,
      title: detail?.title || null,
      contentData: detail?.contentData && typeof detail.contentData === "object" ? detail.contentData : {},
      fieldSchema: Array.isArray(detail?.fieldSchema) ? detail.fieldSchema : [],
      label: detail?.label || null,
      visible: detail?.visible ?? null,
      styleVariant: detail?.styleVariant || null,
      interactionSource: detail?.interactionSource || "sync"
    }
  }

  designerSelectionSummary(selection) {
    if (!selection) return "none"
    if (selection.selectedSurface !== "section") return selection.selectedSurface || "none"
    return `${selection.selectedSurface}:${selection.clientKey || ""}:${selection.sectionType || ""}:${selection.title || ""}`
  }

  updateDesignerSelectionActivation(detail) {
    const interactionSource = detail?.interactionSource || "sync"
    if (["user", "picker", "assistant", "request"].includes(interactionSource)) {
      this.designerSelectionActivated = true
      return
    }

    if (!this.designerSelection) {
      this.designerSelectionActivated = false
    }
  }

  designerTargetText() {
    if (!this.designerSelection) return _t("designer.no_section_selected", "선택된 섹션이 없습니다")

    if (this.designerSelection.selectedSurface === "section") {
      return this.designerSelection.title ||
        this.designerSelection.label ||
        this.designerSelection.sectionType ||
        _t("designer.no_section_selected", "선택된 섹션이 없습니다")
    }

    if (this.designerSelection.selectedSurface === "header") return _t("designer.header", "헤더")
    if (this.designerSelection.selectedSurface === "footer") return _t("designer.footer", "푸터")
    return _t("designer.no_section_selected", "선택된 섹션이 없습니다")
  }

  renderDesignerTargetIndicator() {
    if (!this.config.designerBridge || !this._designerTargetValue) return

    const nextText = this.designerTargetText()
    this._designerTargetValue.textContent = nextText
    this._designerTargetValue.style.color = this.designerSelection ? "#111827" : "#6B7280"
  }

  openDesignerSectionPicker(event) {
    event?.preventDefault()
    if (!this.config.designerBridge) return

    window.dispatchEvent(new CustomEvent("profile-page-designer:open-picker", {
      detail: { insertIndex: this.designerSectionInsertIndex() }
    }))
  }

  designerSectionInsertIndex() {
    const controllerElement = document.querySelector('[data-controller~="profile-page-designer"]')
    const designerController = window.Stimulus?.getControllerForElementAndIdentifier(
      controllerElement,
      "profile-page-designer"
    )
    if (!designerController) return 0

    const visibleSections = typeof designerController.visibleSections === "function"
      ? designerController.visibleSections()
      : []

    if (this.designerSelection?.selectedSurface === "section" && this.designerSelection?.clientKey) {
      const currentIndex = visibleSections.findIndex((section) => section.client_key === this.designerSelection.clientKey)
      if (currentIndex >= 0) return currentIndex + 1
    }

    return visibleSections.length
  }


  designerSelectionNoticeText() {
    if (!this.designerSelection) return null
    if (this.designerSelection.selectedSurface === "section") {
      const label = this.designerSelection.label || this.designerSelection.sectionType || "섹션"
      return `${label} 섹션을 수정 대상으로 인식했어요.`
    }
    if (this.designerSelection.selectedSurface === "header") {
      return _t("designer.header_notice", "지금은 헤더가 선택되어 있어요. 섹션을 선택하면 그 섹션 기준으로 도와드릴게요.")
    }
    if (this.designerSelection.selectedSurface === "footer") {
      return _t("designer.footer_notice", "지금은 푸터가 선택되어 있어요. 섹션을 선택하면 그 섹션 기준으로 도와드릴게요.")
    }
    return null
  }

  syncDesignerSelectionUI(previousSummary, nextSummary, previousActivated = false, nextActivated = false) {
    if (!this.config.designerBridge) return
    if (previousSummary === nextSummary && previousActivated === nextActivated) return
    if (this.chatHistory.length === 0 &&
        !this._messages?.querySelector(".ba-welcome-message") &&
        !this._messages?.querySelector(".ba-context-guide")) return

    this.renderContextGuide()
  }

  _showHeroTemplateSuggestion(templateName) {
    if (!this._messages) return
    // 이전 제안 제거 (중복 방지)
    this._messages.querySelectorAll(".ba-hero-template-suggestion").forEach(el => el.remove())

    const displayName = templateName || _t("designer.new_template", "새 템플릿")
    const prompt = `히어로 섹션의 템플릿이 "${displayName}"(으)로 변경됐어. 이 템플릿 레이아웃에 맞게 히어로 섹션의 내용(이름, 직함, 태그라인, CTA 텍스트 등)을 작업 규칙에 맞춰서 새로 작성해줘.`

    const el = document.createElement("div")
    el.className = "ba-msg-assistant ba-hero-template-suggestion"
    el.style.cssText = "border-left:3px solid #6366F1; padding-left:12px;"
    el.innerHTML = `
      <p style="font-size:14px; color:#111827;">${_t("designer.template_changed", "히어로 템플릿이 %{name}(으)로 변경되었습니다.").replace("%{name}", `<strong>${this.escapeHtml(displayName)}</strong>`)}</p>
      <p style="margin-top:4px; font-size:13px; color:#6B7280;">${_t("designer.template_rewrite_helper", "이 템플릿에 맞는 내용을 작업 규칙에 맞춰서 다시 작성해 드릴까요?")}</p>
      <div style="margin-top:10px;">
        <button type="button" class="ba-followup-btn ba-hero-rewrite-btn">
          ${_t("designer.rewrite_confirm", "템플릿에 맞게 내용 다시 작성하기")}
        </button>
      </div>
    `

    this._messages.appendChild(el)
    this.scrollToBottom()

    el.querySelector(".ba-hero-rewrite-btn")?.addEventListener("click", () => {
      el.remove()
      if (this._input) this._input.value = prompt
      this.send()
    })
  }

  hasDesignerSectionSelection() {
    return this.config.designerBridge &&
      this.designerPromptScope !== "page" &&
      this.designerSelectionActivated &&
      this.designerSelection?.selectedSurface === "section" &&
      !!this.designerSelection?.sectionType
  }

  buildDesignerSectionPrompt() {
    if (!this.hasDesignerSectionSelection()) return ""

    const selection = this.designerSelection
    const schema = selection.fieldSchema || []
    const labelMap = {}
    const fieldLines = schema.map((field) => {
      const typeLabel = field.type === "repeater" ? "목록" : (field.type || "text")
      const label = field.label || field.name
      labelMap[field.name] = label
      return `- ${label}(${field.name}) [${typeLabel}]`
    })

    const contentLines = Object.entries(selection.contentData || {}).map(([key, value]) => {
      const label = labelMap[key] || key
      const display = typeof value === "string" ? value : JSON.stringify(value)
      return `- **${label}**: ${display}`
    })

    return [
      "## 현재 선택된 디자이너 섹션",
      `- 섹션 타입: ${selection.sectionType}`,
      `- 섹션 라벨: ${selection.label || selection.sectionType}`,
      `- 섹션 이름: ${selection.title || "(미설정)"}`,
      `- 노출 여부: ${selection.visible === false ? "숨김" : "노출"}`,
      "",
      "### 현재 내용",
      contentLines.join("\n") || "- 없음",
      "",
      "### 수정 가능한 필드",
      fieldLines.join("\n") || "- 없음",
      "",
      "## 응답 규칙",
      "- 이 선택된 섹션만 수정하세요.",
      "- 페이지 전체 필드(title, slug, meta_description 등)는 절대 포함하지 마세요.",
      "- 사용자에게 설명할 때 반드시 한국어 레이블을 사용하세요(예: tagline → '한 줄 소개', bio_short → '짧은 소개', badge_text → '배지 문구'). 영문 필드명을 절대 노출하지 마세요.",
      "### 이미지 사용 규칙",
      '- image_url, photo_url, bg_image_url 필드에 새 이미지가 필요하면 "__generate__" 값과 "image_prompt" 필드를 함께 사용하세요.',
      '- 예시: {"photo_url":"__generate__","image_prompt":"프리미엄 헤어 살롱 인테리어, 모던한 분위기"}',
      "- 생성 프롬프트는 구체적으로(스타일, 분위기, 구도 포함). 시스템이 자동으로 이미지를 생성합니다.",
      "- 외부 URL(https://...)이나 placeholder URL을 절대 사용하지 마세요.",
      "- 기존 이미지를 유지하려면 해당 필드를 생략하세요.",
      "- 마지막에 반드시 아래 JSON만 포함하세요(JSON 내부에서는 영문 키 사용).",
      "```json",
      '{"_action":"fill_designer_section","data":{"title":"선택 사항","content_data":{"field":"value"}}}',
      "```",
      "- `data.title`은 필요할 때만 포함하세요.",
      "- `data.content_data`에는 수정 가능한 필드만 포함하세요."
    ].join("\n")
  }

  buildSystemPrompt() {
    if (this.rulesGenerationMode) {
      return this._buildRulesGenPrompt()
    }
    let prompt = this.config.systemPrompt || ""
    if (this.projectRules?.length) {
      prompt += "\n\n" + this.buildProjectRulesPrompt()
    }
    const formSnapshot = this._getFormSnapshot()
    if (formSnapshot) {
      prompt += "\n\n" + formSnapshot
    }
    const availablePages = this._getAvailablePagesSnapshot()
    if (availablePages) {
      prompt += "\n\n" + availablePages
    }
    if (this.designerPromptScope === "all_sections") {
      const allSectionsPrompt = this.buildDesignerAllSectionsPrompt()
      if (allSectionsPrompt) prompt += "\n\n" + allSectionsPrompt
    } else {
      const designerSectionPrompt = this.buildDesignerSectionPrompt()
      if (designerSectionPrompt) prompt += "\n\n" + designerSectionPrompt
    }
    return prompt
  }

  buildDesignerAllSectionsPrompt() {
    if (!this.config.designerBridge) return null
    let sections
    if (Array.isArray(this.config.existingSections)) {
      sections = this.config.existingSections
    } else {
      try { sections = JSON.parse(this.config.existingSections || "[]") } catch(e) { return null }
    }
    if (sections.length === 0) return null

    const sectionLines = sections.map((section, index) => {
      const clientKey = section.client_key || `section-${index + 1}`
      const position = Number.isFinite(Number(section.position)) ? Number(section.position) : index
      const label = section.title || section.type || "섹션"
      const contentSummary = section.content_data ? JSON.stringify(section.content_data) : "{}"
      return `- position=${position}, client_key=${clientKey}, type=${section.type}, title=${label}, content_data=${contentSummary}`
    })

    const templateLines = []
    sections.forEach((section) => {
      const templates = Array.isArray(section.available_templates) ? section.available_templates : []
      if (templates.length === 0) return

      if (section.type === "hero") {
        templateLines.push(`- hero (${section.client_key || section.title || "hero"}): 유지 전용, 사용 가능한 style_variant:`)
      } else {
        templateLines.push(`- ${section.client_key || section.type} (${section.type}): 교체/추가에 사용할 수 있는 템플릿:`)
      }

      templates.slice(0, 8).forEach((template) => {
        const sectionType = template.section_type || section.type
        const styleVariant = template.style_variant ? `, style_variant=${template.style_variant}` : ""
        templateLines.push(`  - section_type=${sectionType}${styleVariant}, name=${template.name}`)
      })
    })

    const templateSection = templateLines.length > 0
      ? ["", "### 사용 가능한 템플릿 풀", ...templateLines, ""]
      : []

    const lines = [
      "## 전체 섹션 일괄 수정 모드",
      "",
      "현재 페이지의 구조와 내용을 한번에 재편성합니다.",
      "현재 섹션 목록:",
      ...sectionLines,
      ...templateSection,
      "### 응답 형식",
      "반드시 아래 JSON 형식으로 응답하세요:",
      "```json",
      '{',
      '  "_action": "restructure_designer_page",',
      '  "data": {',
      '    "sections": [',
      '      { "op": "keep", "client_key": "persisted-12", "section_type": "hero", "style_variant": "stats_lead", "content_data": { ... } },',
      '      { "op": "replace", "client_key": "persisted-13", "new_section_type": "projects", "title": "주요 프로젝트", "content_data": { ... } },',
      '      { "op": "remove", "client_key": "persisted-14" },',
      '      { "op": "add", "section_type": "cta", "position": 3, "title": "상담 유도", "content_data": { ... } }',
      '    ],',
      '    "header": { "template_id": 5 },',
      '    "footer": { "template_id": 3 }',
      '  }',
      '}',
      "```",
      "",
      "### 중요 규칙",
      "- 구조 변경은 반드시 client_key 기준으로 타겟팅",
      "- 히어로(hero)는 타입 유지. keep만 사용하고 style_variant 변경만 허용",
      "- keep 시에도 content_data에 모든 필드를 포함하세요 (repeater 포함). 생략하면 기존 값이 유지됩니다.",
      "- non-hero 섹션만 replace/remove/add 대상",
      "- header.template_id: 시스템 프롬프트의 '사용 가능한 헤더 템플릿' 목록에서 페이지에 가장 어울리는 template_id를 선택",
      "- footer.template_id: 시스템 프롬프트의 '사용 가능한 푸터 템플릿' 목록에서 페이지에 가장 어울리는 template_id를 선택",
      "- 헤더/푸터를 변경하지 않으려면 header/footer 키를 생략",
      "- add.position은 현재 보이는 섹션 순서 기준 0-based",
      "- 같은 client_key를 두 번 사용하지 마세요",
      "- replace는 new_section_type을 사용하세요",
      "- style_variant가 실제로 의미 있는 템플릿일 때만 포함하세요",
      "- content_data는 각 섹션 타입의 정확한 필드명을 사용 (틀리면 적용 안 됨!)",
      "- 히어로(hero): name, title, tagline, bio_short, badge_text, cta_text, cta_url, secondary_cta_text, secondary_cta_url",
      "- 소개(about): headline, subheadline, bio, vision, photo_url, bg_image_url",
      "- 서비스(services): headline, subheadline, items[{icon, title, description, price}]",
      "- 프로젝트(projects): headline, subheadline, items[{title, period, role, tools, description, outcomes, featured, image_url}]",
      "- 연락처(contact): headline, subheadline, email, phone",
      "- 스킬(skills): headline, subheadline, items[{icon, title, description}]",
      "- 성과(stats): headline, items[{value, suffix, label, icon}]",
      "- FAQ(faq): headline, items[{question, answer}]",
      "- CTA(cta): headline, subheadline, description, bg_image_url, cta_text, cta_url, secondary_cta_text, secondary_cta_url, links[{label, title, description, url, accent}]",
      "- 텍스트 블록(block_text): heading, subheading, body_text, cta_text, cta_url, bg_image_url",
      "- 카드 블록(block_cards): items[{title, description, image_url, link_url}]",
      "- 이미지 블록(block_image): image_url, caption",
      "- 이미지+텍스트(block_image_text): image_url, heading, body_text, cta_text, cta_url",
      "- 상단이미지(block_image_top): image_url, heading, body_text",
      "- 컬럼 블록(block_columns): items[{icon, heading, body_text, image_url}]",
      "- 갤러리(gallery): headline, subheadline, items[{image_url, caption}]",
      "- 추천사(testimonials): headline, testimonials[{name, role, quote, photo_url}]",
      "- 배너 블록(block_banner): heading, subheading, cta_text, cta_url, bg_image_url",
      "",
      "### 이미지 사용 규칙",
      "- image_url, photo_url, bg_image_url 필드에는 두 가지 방식을 사용할 수 있습니다:",
      "  방식1: 시스템 프롬프트의 '사용 가능한 이미지 에셋' 목록에서 기존 URL 선택",
      "  방식2: 새 이미지 생성 - 필드값을 \"__generate__\"로 설정하고, 같은 객체에 \"image_prompt\" 필드 추가",
      "- 이미지 생성 예시: { \"image_url\": \"__generate__\", \"image_prompt\": \"모던한 카페 인테리어, 따뜻한 조명, 사진 스타일\" }",
      "- 기존 에셋이 내용에 잘 맞으면 사용하고, 맞는 것이 없으면 __generate__로 생성하세요.",
      "- image_prompt는 영어로 구체적으로 작성 (스타일, 분위기, 구도 포함).",
      "- 외부 URL(https://...)이나 placeholder URL은 절대 사용하지 마세요.",
      "- 작업 규칙에 등록된 정보를 적극 활용하여 실제 비즈니스에 맞는 내용 작성"
    ]
    return lines.join("\n")
  }

  _getFormSnapshot() {
    const form = this.config.formSelector ? document.querySelector(this.config.formSelector) : null
    if (!form) return null
    const prefix = this.config.fieldPrefix || ""
    const lines = ["## 현재 폼 입력 상태"]
    const labels = {}
    form.querySelectorAll("label").forEach(label => {
      const forAttr = label.getAttribute("for")
      const text = label.textContent.trim().replace(/\s*\*\s*/, "").split("\n")[0].trim()
      if (forAttr && text) labels[forAttr] = text
    })

    const inputs = form.querySelectorAll("input, select, textarea")
    let hasData = false
    inputs.forEach(el => {
      if (el.type === "hidden" || el.type === "file" || !el.name) return
      if (!el.name.startsWith(`${prefix}[`)) return
      const fieldName = el.name.replace(`${prefix}[`, "").replace("]", "")
      const labelText = labels[el.id] || fieldName.replace(/_/g, " ")
      let value
      if (el.type === "checkbox") {
        value = el.checked ? "ON" : "OFF"
      } else {
        value = el.value?.trim() || "(비어 있음)"
      }
      lines.push(`- ${labelText}: ${value}`)
      if (el.type !== "checkbox" && value !== "(비어 있음)") hasData = true
      if (el.type === "checkbox" && el.checked) hasData = true
    })

    if (lines.length <= 1) return null
    lines.push("")
    lines.push(hasData
      ? "위 내용을 참고하여 비어 있는 항목 위주로 추천해주세요."
      : "폼이 비어 있습니다. 사용자의 의도를 파악하고 적절한 값을 추천해주세요.")
    return lines.join("\n")
  }

_getAvailablePagesSnapshot() {
    if (this.config.contextType !== "site") return null
    const select = document.querySelector("select[name*='profile_page_id']")
    if (!select) return null
    const options = [...select.options].filter(o => o.value && o.value !== "")
    if (options.length === 0) return null
    const lines = [
      "## 메뉴에 추가 가능한 페이지 목록",
      "아래 페이지를 사이트 메뉴에 추가할 수 있습니다. 사용자가 페이지 추가를 요청하면 `_action: \"add_menu_item\"` JSON으로 응답하세요.",
      "```json",
      `{"_action": "add_menu_item", "data": {"profile_page_id": "ID값", "label": "메뉴라벨", "is_home": false}}`,
      "```",
      "여러 페이지를 한 번에 추가하려면 `_action: \"add_menu_items\"` (복수형)를 사용하세요:",
      "```json",
      `{"_action": "add_menu_items", "data": {"items": [{"profile_page_id": "ID값", "label": "라벨", "is_home": true}, ...]}}`,
      "```",
      ""
    ]
    options.forEach(o => {
      lines.push(`- ID: ${o.value}, 제목: ${o.textContent.trim()}`)
    })
    return lines.join("\n")
  }

  _buildRulesGenPrompt() {
    const existingTitles = (this.projectRules || []).map(r => r.title).join(", ")
    const isIaProjectContext = this.config.contextType === "ia_project" || this.config.resourceType === "IaProject"
    const isPlanningWorkspaceContext = this.isPlanningWorkspaceContext()
    const isSectionTemplateContext = this.config.agentSlug === "section-template"

    const jsonFormat = `반드시 JSON 형식으로만 응답하세요:
{"_action": "preview_rules", "data": [{"title": "...", "content": "...", "active": true}]}

JSON 외 텍스트를 포함하지 마세요. 5~6개의 룰을 생성하세요. 빈 template이 아닌 실제 내용을 담은 룰을 생성하세요.
각 content는 220자 이내의 짧은 실무 규칙 요약으로 작성하세요.
content 문자열 안에서는 쌍따옴표(")를 직접 쓰지 말고, 꼭 필요하면 작은따옴표로 바꾸세요.
줄바꿈은 실제 개행 대신 \\n 이스케이프 문자열로 표현하세요.
JSON 문법이 깨지지 않도록 마지막 쉼표, 닫히지 않은 대괄호/중괄호가 없게 확인하세요.`

    if (isIaProjectContext) {
      return `당신은 IA(Information Architecture) 설계와 UI기획 연결을 돕는 작업 규칙 작성 전문가입니다.

기존 등록된 룰: ${existingTitles || "없음"}

${jsonFormat}
룰은 메뉴 구조, 페이지 명명, 플로우 정리, 용어 통일, UI기획 동기화 기준 중심으로 작성하세요.`
    }

    if (isPlanningWorkspaceContext) {
      return `당신은 서비스 기획 산출물의 품질 기준을 정하는 작업 규칙 작성 전문가입니다.

기존 등록된 룰: ${existingTitles || "없음"}

${jsonFormat}
룰은 다음 기준 중심으로 작성하세요:
- 아이디어 인터뷰 답변을 이후 산출물에 반영하는 기준
- 아이디어 메모, 린 캔버스, 포지셔닝, 메인 타겟 유저, PRD의 일관성 기준
- 타겟 고객과 문제 정의를 비워두지 않는 기준
- PRD와 구조화 탭 저장 시 반드시 포함해야 할 항목
- 한국어 문체, 구체성, 범위 관리 기준
사용자가 입력한 서비스 맥락에 맞춰 구체적인 기획 작업 규칙을 만드세요.`
    }

    if (isSectionTemplateContext) {
      return `당신은 AI 블록 섹션 템플릿의 디자인/콘텐츠 생성 기준을 정하는 작업 규칙 전문가입니다.

기존 등록된 룰: ${existingTitles || "없음"}

${jsonFormat}
룰은 다음 기준 중심으로 작성하세요:
- 디자인 톤/무드 (미니멀, 모던, 감성적, 대담한 등)
- 색상 팔레트 (브랜드 컬러, 배경색, 강조색)
- 타이포그래피 스타일 (제목 크기, 본문 스타일, 한국어 카피 톤)
- 레이아웃 선호 (그리드 구조, 여백, 이미지 비율)
- 콘텐츠 작성 규칙 (한국어 카피 톤, CTA 문구 스타일)
- 금지 사항 (외부 이미지 금지, border-radius 금지 등)
사용자가 입력한 스타일/톤에 맞춰 구체적인 생성 기준 규칙을 만드세요.`
    }

    return `당신은 프리랜서/크리에이터의 포트폴리오 및 랜딩 페이지 작업 규칙 작성 전문가입니다.

기존 등록된 룰: ${existingTitles || "없음"}

${jsonFormat}`
  }

  // ── 제공자/세션 유틸 ──────────────────────────────────────────────────────

  getProvider(model) {
    if (model.startsWith("claude")) return "claude"
    if (model.startsWith("gemini")) return "gemini"
    if (model.toLowerCase().startsWith("kimi")) return "kimi"
    if (model.toLowerCase().startsWith("minimax")) return "minimax"
    if (model.toLowerCase().startsWith("glm")) return "glm"
    if (model.toLowerCase().startsWith("deepseek")) return "deepseek"
    return "openai"
  }

  generateSessionId() {
    return crypto.randomUUID ? crypto.randomUUID() :
      'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0
        return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16)
      })
  }

  parseExistingSections() {
    try {
      return Array.isArray(this.config.existingSections)
        ? this.config.existingSections
        : JSON.parse(JSON.stringify(this.config.existingSections) || "[]")
    } catch { return [] }
  }

  // ── AI 모델 목록 동적 로드 ────────────────────────────────────────────────

  async loadModels() {
    const endpoint = this.config.voiceModelPicker ? "/voice_models" : (this.isImageAssetAgent ? "/image_generation_models" : "/models")
    try {
      const query = this.voiceModelQueryString()
      const res = await fetch(`${this.config.aiBasePath}${endpoint}${query}`)
      const data = await res.json()
      this.availableModels = data.models || []
      if (this.config.voiceModelPicker) {
        this.availableModels = this.availableModels.filter(model => model.mode === this.config.voiceModelMode)
      }
      this.credentialStatuses = data.credential_statuses || {}
      this.credentialStatusByProvider = this.normalizeCredentialStatusPayload({ data: this.credentialStatuses })
      this.planSlug = data.plan_slug || null
      this.creditPackBalance = Number(data.credit_pack_balance || 0)

      // image-asset 패널은 이미지 모델 전용 — localStorage의 텍스트 LLM 값을 사용하면 안 됨
      if (this.config.voiceModelPicker) {
        const serverDefault = data.default_models?.[this.config.voiceModelMode] || data.default_model
        const validSelected = this.availableModels.find(m => m.name === this.selectedModel)
        if (!validSelected) {
          const fallbackModel = this.availableModels.find(m => m.name === serverDefault) || this.availableModels[0]
          this.selectedModel = fallbackModel?.name || serverDefault || ""
          try { localStorage.setItem(`${this.config.storagePrefix}-model`, this.selectedModel) } catch(e) {}
        }
      } else if (this.isImageAssetAgent) {
        const deprecatedNames = ["chatgpt-image-latest"]
        const serverDefault = deprecatedNames.includes(data.default_model) ? null : data.default_model
        this.imageAssetServerDefaultModel = serverDefault || null
        const validSelected = this.availableModels.find(m => m.name === this.selectedModel && !deprecatedNames.includes(m.name))
        if (!this.imageAssetModelExplicitlySelected || !validSelected) {
          const fallbackModel = this.availableModels.find(m => !deprecatedNames.includes(m.name))
          this.selectedModel = serverDefault || fallbackModel?.name || "gpt-image-2"
          try { localStorage.setItem(`${this.config.storagePrefix}-model`, this.selectedModel) } catch(e) {}
        }
      } else {
        const serverDefault = data.default_model
        const validSelected = this.availableModels.find(m => m.name === this.selectedModel)
        if (!validSelected) {
          const fallbackModel = this.availableModels.find(m => m.name === serverDefault) || this.availableModels[0]
          this.selectedModel = fallbackModel?.name || serverDefault || "claude-sonnet-4-6"
          try { localStorage.setItem(`${this.config.storagePrefix}-model`, this.selectedModel) } catch(e) {}
        }
      }

      // 무료 유저가 localStorage에 유료 모델을 갖고 있으면 무료 기본 모델로 리셋
      if (this.planSlug === "free") {
        const current = this.availableModels.find(m => m.name === this.selectedModel)
        if (!current || this.modelIsLocked(current)) {
          const serverDefault = this.availableModels.find(m => m.name === data.default_model)
          this.selectedModel = (serverDefault && !this.modelIsLocked(serverDefault) ? data.default_model : null) ||
            this.availableModels.find(m => this.modelIsAvailableByCredential(m))?.name ||
            this.availableModels.find(m => m.enabled_for_free)?.name ||
            this.selectedModel
          try { localStorage.setItem(`${this.config.storagePrefix}-model`, this.selectedModel) } catch(e) {}
        }
      }
    } catch(e) {
      this.availableModels = []
      this.creditPackBalance = 0
      this.selectedModel = this.selectedModel || (this.config.voiceModelPicker ? "" : (this.isImageAssetAgent ? "gemini-3-pro-image-preview" : "claude-sonnet-4-6"))
    }
    this._renderModelDropdown()
    if (this.isDraftWriterAgent() || this.config.designerBridge || this.config.imageModelPicker) this.loadThreadsImageModels()
    this.updateCredentialSourceUI()
    this.updateCredentialStatusPolling()
  }

  voiceModelQueryString() {
    if (!this.config.voiceModelPicker) return ""

    const params = new URLSearchParams()
    if (this.config.voiceModelMode) params.set("mode", this.config.voiceModelMode)
    if (this.config.voiceModelRuntime) params.set("runtime", this.config.voiceModelRuntime)
    const query = params.toString()
    return query ? `?${query}` : ""
  }

  async loadThreadsImageModels() {
    if (!this._threadsImageModelDropdown) return

    try {
      const res = await fetch(`${this.config.aiBasePath}/image_generation_models`)
      const data = await res.json()
      this.threadsImageModels = data.models || []
      this.threadsImageDefaultModel = data.default_model || ""
      this.threadsImageCredentialStatusByProvider = this.normalizeCredentialStatusPayload({ data: data.credential_statuses || {} })
      if (!this.threadsImageModels.find(m => m.name === this.selectedThreadsImageModel)) {
        this.selectedThreadsImageModel = ""
        localStorage.setItem(`${this.config.storagePrefix}-threads-image-model`, "")
      }
    } catch(e) {
      this.threadsImageModels = []
      this.threadsImageCredentialStatusByProvider = {}
      this.selectedThreadsImageModel = ""
    }

    this._renderThreadsImageModelDropdown()
  }

  preferredImageAssetModel(defaultModel) {
    const preferredNames = ["gpt-image-2"]
    const deprecatedNames = ["chatgpt-image-latest"]
    const serverDefault = this.availableModels.find(m => m.name === defaultModel)
    if (serverDefault && preferredNames.includes(serverDefault.name)) return serverDefault.name

    const preferred = preferredNames
      .map(name => this.availableModels.find(m => m.name === name))
      .find(model => model && this.imageAssetProviderConnected(model.provider))
    return preferred?.name || (serverDefault && !deprecatedNames.includes(serverDefault.name) ? serverDefault.name : null)
  }

  imageAssetProviderConnected(provider) {
    const status = this.credentialStatusForProvider(provider)
    if (!status) return false
    return status.connected === true || status.available === true
  }

  visibleModelBadgeLabels(model) {
    if (!model || !Array.isArray(model.badge_labels)) return []
    return model.badge_labels
      .map(label => String(label || "").trim())
      .filter(Boolean)
      .slice(0, 2)
  }

  createImageCapableBadge() {
    const badge = document.createElement("span")
    badge.className = "ka-model-badge ka-model-badge--image"
    badge.title = "이미지 첨부 가능"
    badge.innerHTML = '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="9" cy="9" r="2"></circle><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path></svg>'
    return badge
  }

  addOperationalModelBadges(container, model) {
    if (!container || !model) return

    if (model.recommended) {
      const recommendedBadge = document.createElement("span")
      recommendedBadge.className = "ka-model-badge ka-model-badge--recommended"
      recommendedBadge.textContent = "추천"
      container.appendChild(recommendedBadge)
    }

    if (model.image_capable) {
      container.appendChild(this.createImageCapableBadge())
    }

    this.visibleModelBadgeLabels(model).forEach((label) => {
      const labelBadge = document.createElement("span")
      labelBadge.className = "ka-model-badge ka-model-badge--label"
      labelBadge.textContent = label
      container.appendChild(labelBadge)
    })
  }

  updateModelBadgeChipPair(recommendedChip, labelChip, model, secondaryLabelChip = null) {
    const labels = this.visibleModelBadgeLabels(model)

    if (recommendedChip) {
      recommendedChip.textContent = "추천"
      recommendedChip.style.display = model?.recommended ? "" : "none"
    }

    if (labelChip) {
      labelChip.textContent = labels[0] || ""
      labelChip.style.display = labels[0] ? "" : "none"
    }

    if (secondaryLabelChip) {
      secondaryLabelChip.textContent = labels[1] || ""
      secondaryLabelChip.style.display = labels[1] ? "" : "none"
    }
  }

  updateSelectedModelBadgeChips() {
    const models = Array.isArray(this.availableModels) ? this.availableModels : []
    const currentModel = models.find(m => m.name === this.selectedModel)
    this.updateModelBadgeChipPair(this._modelRecommendedChip, this._modelLabelChip, currentModel, this._modelLabelChipSecondary)
    this.updateModelBadgeChipPair(this._rcModelRecommendedChip, this._rcModelLabelChip, currentModel, this._rcModelLabelChipSecondary)
    const imageCapable = currentModel?.image_capable ? true : false
    if (this._modelImageChip) this._modelImageChip.style.display = imageCapable ? "" : "none"
    if (this._rcModelImageChip) this._rcModelImageChip.style.display = imageCapable ? "" : "none"
  }

  providerGroupLabel(provider) {
    const key = String(provider || "other").toLowerCase()
    if (key === "deepseek") return "Klavy"
    return key.toUpperCase()
  }

  _renderModelDropdown() {
    const dropdowns = [this._modelDropdown, this._rcModelDropdown].filter(Boolean)
    const models = Array.isArray(this.availableModels) ? this.availableModels : []
    if (!dropdowns.length || !models.length) return

    // provider별 그룹핑
    const grouped = {}
    models.forEach((model) => {
      const provider = this.providerGroupLabel(model.provider)
      if (!grouped[provider]) grouped[provider] = []
      grouped[provider].push(model)
    })

    // 무료 모델을 포함한 provider 그룹을 상단에, 유료 전용 그룹을 하단에 배치
    const groupEntries = Object.entries(grouped).sort(([, a], [, b]) => {
      const aHasFree = a.some(m => m.enabled_for_free) ? 0 : 1
      const bHasFree = b.some(m => m.enabled_for_free) ? 0 : 1
      return aHasFree - bHasFree
    })

    dropdowns.forEach((dropdown) => {
      dropdown.innerHTML = ""
      let first = true
      groupEntries.forEach(([provider, models]) => {
        const header = document.createElement("div")
        header.className = "ka-model-group-header"
        if (first) { header.style.borderTop = "none"; first = false }
        header.textContent = provider
        dropdown.appendChild(header)

        models.forEach((model) => {
          const credState = this.credentialChipStateForProvider(model.provider)
          const weight = model.usage_weight ?? 1
          const isSelected = model.name === this.selectedModel
          const isLocked = this.modelIsLocked(model)

          const opt = document.createElement("button")
          opt.type = "button"
          opt.className = "ba-model-opt" + (isSelected ? " selected" : "") + (isLocked ? " is-locked" : "")
          opt.dataset.value = model.name
          opt.dataset.provider = model.provider
          if (isLocked) opt.dataset.locked = "true"

          const nameSpan = document.createElement("span")
          nameSpan.className = "ba-model-opt__name"
          nameSpan.textContent = model.display_name || model.name

          const metaSpan = document.createElement("span")
          metaSpan.className = "ba-model-opt__meta"

          const weightBadge = document.createElement("span")
          weightBadge.className = "ka-model-badge ka-model-badge--weight"
          weightBadge.textContent = `${weight}P`

          this.addOperationalModelBadges(metaSpan, model)
          metaSpan.appendChild(weightBadge)

          if (isLocked) {
            const lockBadge = document.createElement("span")
            lockBadge.className = "ka-model-badge ka-model-badge--locked"
            lockBadge.innerHTML = credState.pendingLocalBridge
              ? _t("credentials.connect_required", "연결 필요")
              : '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="margin-right:3px;"><rect x="3" y="11" width="18" height="11" rx="0"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>유료'
            metaSpan.appendChild(lockBadge)
          } else {
            if (credState.state !== "system") {
              const credBadge = document.createElement("span")
              credBadge.className = `ka-model-badge ka-model-badge--credential is-${credState.state}`
              credBadge.textContent = credState.label
              metaSpan.appendChild(credBadge)
            }
          }

          opt.appendChild(nameSpan)
          opt.appendChild(metaSpan)
          opt.addEventListener("click", (e) => this.selectModel(e))
          dropdown.appendChild(opt)
        })
      })
    })

    // 트리거 라벨 + weight chip 업데이트
    const currentModel = models.find(m => m.name === this.selectedModel)
    const label = currentModel?.display_name || currentModel?.name || this.selectedModel
    const weight = currentModel?.usage_weight ?? 1
    if (this._modelLabel) this._modelLabel.textContent = label
    if (this._rcModelLabel) this._rcModelLabel.textContent = label
    if (this._modelWeightChip) this._modelWeightChip.textContent = `${weight}P`
    if (this._rcModelWeightChip) this._rcModelWeightChip.textContent = `${weight}P`
    this.updateSelectedModelBadgeChips()
  }

  _renderThreadsImageModelDropdown() {
    const dropdown = this._threadsImageModelDropdown
    if (!dropdown) return

    dropdown.innerHTML = ""
    const contentLabel = this.draftWriterContentLabel()
    // 미선택 = 이미지 생성 안 함 (전 컨텍스트 통일). 기획 워크스페이스는 라벨을 "미선택"으로 노출.
    const isPlanningImagePicker = this.config.imageModelPicker && !this.isDraftWriterAgent() && !this.config.designerBridge
    const noneName = isPlanningImagePicker ? "미선택" : "선택하지 않음"
    const noneBadge = isPlanningImagePicker ? "이미지 생성 안 함" : `${contentLabel}만 생성`

    const noneOpt = document.createElement("button")
    noneOpt.type = "button"
    noneOpt.className = "ba-model-opt" + (this.selectedThreadsImageModel ? "" : " selected")
    noneOpt.dataset.value = ""
    noneOpt.innerHTML = `<span class="ba-model-opt__name">${noneName}</span><span class="ba-model-opt__meta"><span class="ka-model-badge ka-model-badge--label">${noneBadge}</span></span>`
    noneOpt.addEventListener("click", (e) => this.selectThreadsImageModel(e))
    dropdown.appendChild(noneOpt)

    const grouped = {}
    this.threadsImageModels.forEach((model) => {
      const provider = (model.provider || "other").toUpperCase()
      if (!grouped[provider]) grouped[provider] = []
      grouped[provider].push(model)
    })

    Object.entries(grouped).forEach(([provider, models]) => {
      const header = document.createElement("div")
      header.className = "ka-model-group-header"
      header.textContent = provider
      dropdown.appendChild(header)

      models.forEach((model) => {
        const credState = this.threadsImageCredentialChipStateForProvider(model.provider)
        const opt = document.createElement("button")
        opt.type = "button"
        opt.className = "ba-model-opt" + (model.name === this.selectedThreadsImageModel ? " selected" : "")
        opt.dataset.value = model.name

        const nameSpan = document.createElement("span")
        nameSpan.className = "ba-model-opt__name"
        nameSpan.textContent = model.display_name || model.name

        const metaSpan = document.createElement("span")
        metaSpan.className = "ba-model-opt__meta"
        this.addOperationalModelBadges(metaSpan, model)

        const weightBadge = document.createElement("span")
        weightBadge.className = "ka-model-badge ka-model-badge--weight"
        weightBadge.textContent = `${model.usage_weight ?? 1}P`
        metaSpan.appendChild(weightBadge)

        if (credState?.label) {
          const credBadge = document.createElement("span")
          credBadge.className = `ka-model-badge ka-model-badge--credential is-${credState.state}`
          credBadge.textContent = credState.label
          if (credState.title) credBadge.title = credState.title
          metaSpan.appendChild(credBadge)
        }

        opt.appendChild(nameSpan)
        opt.appendChild(metaSpan)
        opt.addEventListener("click", (e) => this.selectThreadsImageModel(e))
        dropdown.appendChild(opt)
      })
    })

    const current = this.threadsImageModels.find(m => m.name === this.selectedThreadsImageModel)
    if (this._threadsImageModelLabel) {
      this._threadsImageModelLabel.textContent = current?.display_name || current?.name || noneName
    }
    if (this._threadsImageModelWeightChip) {
      // 기획 피커에서 미선택일 땐 라벨이 이미 "미선택"이므로 중복 배지 숨김
      if (!current && isPlanningImagePicker) {
        this._threadsImageModelWeightChip.textContent = ""
        this._threadsImageModelWeightChip.style.display = "none"
      } else {
        this._threadsImageModelWeightChip.textContent = current ? `${current.usage_weight ?? 1}P` : `${contentLabel}만`
        this._threadsImageModelWeightChip.style.display = ""
      }
    }
    this.paintCredentialChip(
      this._threadsImageModelSourceChip,
      current ? this.threadsImageCredentialChipStateForProvider(current.provider) : { state: "system" },
      { showSystem: !!current }
    )
    this.updateThreadsImageModelNotice(current)
  }

  updateThreadsImageModelNotice(current) {
    const notice = this._threadsImageModelNotice
    if (!notice) return

    if (!current) {
      notice.style.display = "none"
      notice.textContent = ""
      return
    }

    const status = this.threadsImageCredentialStatusForProvider(current.provider)
    const state = this.threadsImageCredentialChipStateForProvider(current.provider)
    if (status?.localBridgeUnsupported === true) {
      notice.textContent = current.provider === "openai"
        ? "Codex L은 현재 이미지 생성 태스크를 지원하지 않아 공용 API로 호출됩니다. OpenAI 한도에 막히면 사용 가능한 다른 이미지 API 모델로 자동 재시도합니다."
        : "Codex L은 현재 이미지 생성 태스크를 지원하지 않아 이 이미지 모델은 공용 API로 호출됩니다."
      notice.style.display = "block"
      return
    }

    if (state?.state === "system") {
      // 기획 워크스페이스 피커에서는 공용 키 호출 안내 문구를 노출하지 않는다(불필요한 기술 노출)
      const isPlanningImagePicker = this.config.imageModelPicker && !this.isDraftWriterAgent() && !this.config.designerBridge
      if (isPlanningImagePicker) {
        notice.style.display = "none"
        notice.textContent = ""
        return
      }
      notice.textContent = "이 이미지 모델은 공용 API 키로 호출됩니다."
      notice.style.display = "block"
      return
    }

    notice.style.display = "none"
    notice.textContent = ""
  }

  toggleThreadsImageModelDropdown(e) {
    e.stopPropagation()
    this.threadsImageModelDropdownOpen = !this.threadsImageModelDropdownOpen
    if (this._threadsImageModelDropdown) this._threadsImageModelDropdown.style.display = this.threadsImageModelDropdownOpen ? "block" : "none"
  }

  selectThreadsImageModel(e) {
    const opt = e.currentTarget
    this.selectedThreadsImageModel = opt.dataset.value || ""
    localStorage.setItem(`${this.config.storagePrefix}-threads-image-model`, this.selectedThreadsImageModel)
    this._renderThreadsImageModelDropdown()
    if (this._threadsImageModelDropdown) this._threadsImageModelDropdown.style.display = "none"
    this.threadsImageModelDropdownOpen = false
  }

  // ── 메시지 렌더링 유틸 ────────────────────────────────────────────────────

  appendMessage(role, content) {
    const msgEl = document.createElement("div")
    msgEl.className = role === "user" ? "ba-msg-user" : "ba-msg-assistant"

    if (role === "assistant") {
      const displayText = this.cleanAssistantDisplayText(content).replace(/https?:\/\/\S+/g, "").trim()
      if (!displayText) return null
      msgEl.innerHTML = this._renderMarkdown(displayText)
    } else {
      if (Array.isArray(content)) {
        const imagePart = content.find(p => p.type === "image")
        const textPart = content.find(p => p.type === "text")
        if (imagePart?.source?.data) {
          const img = document.createElement("img")
          img.src = `data:${imagePart.source.media_type || "image/png"};base64,${imagePart.source.data}`
          img.alt = _t("image_upload.attached_image_alt", "첨부 이미지")
          msgEl.appendChild(img)
        }
        if (textPart?.text) {
          const span = document.createElement("span")
          span.textContent = textPart.text
          msgEl.appendChild(span)
        }
      } else {
        msgEl.textContent = content
      }
    }

    if (this._messages) this._messages.appendChild(msgEl)
    this.scrollToBottom()
    return msgEl
  }

  cleanAssistantDisplayText(content, { streaming = false, replacement = "" } = {}) {
    let text = String(content || "")
      .replace(/<think>[\s\S]*?(<\/think>|$)/gi, "")
      .replace(/```json[\s\S]*?```/g, replacement)
      .replace(/```[\s\S]*?```/g, "")

    if (text.includes("_action")) {
      const jsonReplacement = streaming ? replacement : ""
      const candidates = this.extractJsonCandidates(text)
        .filter(candidate => candidate.includes("_action"))
        .sort((a, b) => b.length - a.length)

      candidates.forEach((candidate) => {
        text = text.replace(candidate, jsonReplacement)
      })

      text = streaming
        ? text.replace(/\{[\s\S]*"_action"[\s\S]*$/g, jsonReplacement)
        : text.replace(/\{[\s\S]*"_action"[\s\S]*\}/g, "")
    }

    return text
      .replace(/(?:^|\n)\s*(?:JSON|json|Json)\s*:\s*(?:```(?:json)?\s*)?$/g, "")
      .replace(/(?:^|\n)\s*```(?:json)?\s*$/g, "")
      .replace(/\n{3,}/g, "\n\n")
      .trim()
  }

  planningWorkspaceApplySummary(data, fallbackSummary = "") {
    const actions = Array.isArray(data?.actions) ? data.actions : []
    const types = actions.map(action => action?.type).filter(Boolean)
    const explicitSummary = String(data?.summary || "").trim()

    if (types.includes("save_interview_schema")) return "질문지를 준비했어요"
    if (types.includes("apply_interview_answers") && types.includes("complete_interview")) return "답변을 정리하고 다음 단계로 넘겼어요"
    if (types.includes("apply_interview_answers")) return "답변을 반영했어요"
    if (types.includes("complete_interview")) return "아이디어 인터뷰를 완료했어요"
    if (types.includes("set_stage_document") || types.includes("set_tab_document")) return explicitSummary || "문서를 정리했어요"
    if (types.includes("set_persona_profiles")) return explicitSummary || "타겟 고객 정보를 정리했어요"
    if (types.includes("rename_project")) return "프로젝트 이름을 바꿨어요"
    if (types.includes("navigate_to_tab")) return "다음 단계로 이동할게요"
    if (types.includes("update_naming_phase")) return explicitSummary || "이름 후보를 정리했어요"

    return explicitSummary || fallbackSummary || "요청한 내용을 반영했어요"
  }

  _appendUpgradeButton(upgradeUrl = "/pricing") {
    if (!this._messages) return
    const el = document.createElement("div")
    el.className = "ba-msg-system ba-upgrade-cta"
    el.style.cssText = "margin:6px 0 4px;"
    const a = document.createElement("a")
    a.href = upgradeUrl
    a.target = "_blank"
    a.rel = "noopener"
    a.textContent = _t("system_notes.upgrade_button", "요금제 업그레이드")
    a.style.cssText = "display:inline-flex;align-items:center;padding:8px 16px;background:#6366F1;color:#fff;font-weight:600;font-size:13px;text-decoration:none;"
    a.addEventListener("mouseenter", () => { a.style.background = "#4F46E5" })
    a.addEventListener("mouseleave", () => { a.style.background = "#6366F1" })
    el.appendChild(a)
    this._messages.appendChild(el)
    this.scrollToBottom()
  }

  _appendApplyButton(formData) {
    const el = document.createElement("div")
    el.className = "ba-msg-system ba-apply-block"

    const fields = formData._action === "fill_designer_section"
      ? Object.keys(formData.data?.content_data || {})
      : formData._action === "apply_ia_actions"
        ? (Array.isArray(formData.data?.actions) ? formData.data.actions.map((action) => action.type || "action") : [])
        : formData._action === "apply_planning_workspace_actions"
          ? (Array.isArray(formData.data?.actions) ? formData.data.actions.map((action) => action.type || "action") : [])
        : formData._action === "apply_quotation_workspace_actions"
          ? ["items", "cells"]
        : (formData.data ? Object.keys(formData.data) : [])
    const fieldLabels = {
      title: _t("field_labels.title", "사이트 제목"), slug: _t("field_labels.slug", "URL 슬러그"), meta_description: _t("field_labels.meta_description", "메타 설명"),
      active: _t("field_labels.active", "게시 상태"), theme: _t("field_labels.theme", "테마"),
      sticky_cta: _t("field_labels.sticky_cta", "고정 CTA"), sticky_cta_text: _t("field_labels.sticky_cta_text", "CTA 텍스트"),
      sticky_cta_url: _t("field_labels.sticky_cta_url", "CTA URL"), sticky_cta_color: _t("field_labels.sticky_cta_color", "CTA 색상"),
      show_header: _t("field_labels.show_header", "헤더 표시"), header_style: _t("field_labels.header_style", "헤더 스타일"),
      show_footer: _t("field_labels.show_footer", "푸터 표시"), footer_style: _t("field_labels.footer_style", "푸터 스타일"),
      custom_css: _t("field_labels.custom_css", "커스텀 CSS"),
      add_node: _t("field_labels.add_node", "항목 추가"),
      update_node: _t("field_labels.update_node", "항목 수정"),
      move_node: _t("field_labels.move_node", "항목 이동"),
      reorder_node: _t("field_labels.reorder_node", "순서 변경"),
      delete_node: _t("field_labels.delete_node", "항목 삭제"),
      apply_template: _t("field_labels.apply_template", "템플릿 적용"),
      update_interview_answers: _t("field_labels.update_interview_answers", "인터뷰 답변 저장"),
      save_interview_schema: _t("field_labels.save_interview_schema", "질문지 준비"),
      apply_interview_answers: _t("field_labels.apply_interview_answers", "답변 반영"),
      complete_interview: _t("field_labels.complete_interview", "인터뷰 완료"),
      navigate_to_tab: _t("field_labels.navigate_to_tab", "다음 단계 이동"),
      update_naming_phase: _t("field_labels.update_naming_phase", "이름 후보 정리"),
      rename_project: _t("field_labels.rename_project", "프로젝트명 변경"),
      set_stage_document: _t("field_labels.set_stage_document", "문서 저장"),
      set_tab_document: _t("field_labels.set_tab_document", "탭 문서 저장"),
      set_persona_profiles: _t("field_labels.set_persona_profiles", "페르소나 프로필"),
      create_image_assets: _t("field_labels.create_image_assets", "이미지 에셋 생성"),
      cells: "견적서 정보",
      items: "견적 품목",
      sequences: "시퀀스 생성",
      sequence: "메일 단계"
    }
    // 디자이너 섹션 필드 스키마에서 한국어 레이블 추가
    ;(this.designerSelection?.fieldSchema || []).forEach((field) => {
      if (field.name && field.label) fieldLabels[field.name] = field.label
    })
    const summary = fields.map(f => fieldLabels[f] || f).join(", ")
    const itemLabel = formData._action === "fill_designer_section"
      ? _t("apply_button.section_recommend", "섹션 추천 항목")
      : formData._action === "restructure_designer_page"
        ? _t("apply_button.page_restructure", "페이지 구조 재편")
      : formData._action === "fill_designer_all_sections"
        ? _t("apply_button.all_sections", "전체 섹션 추천")
      : formData._action === "apply_ia_actions"
        ? _t("apply_button.ia_changes", "IA 변경 제안")
      : formData._action === "apply_planning_workspace_actions"
          ? _t("apply_button.planning_workspace", "기획 워크스페이스")
          : formData._action === "apply_quotation_workspace_actions"
            ? "견적서"
          : formData._action === "apply_user_story_actions"
              ? _t("apply_button.persona", "타겟 고객 페르소나")
              : formData._action === "create_image_assets"
                ? _t("apply_button.image_gen_request", "이미지 생성 요청")
                : formData._action === "fill_prompt"
                  ? _t("apply_button.prompt_recommend", "프롬프트 추천 항목")
                : formData._action === "create_coldmail_sequences"
                  ? "홍보 메일 시퀀스"
                : _t("apply_button.recommend", "추천 항목")
    const buttonLabel = formData._action === "fill_designer_section"
      ? _t("apply_button.apply_to_section", "섹션에 적용하기")
      : formData._action === "apply_ia_actions"
        ? _t("apply_button.apply_to_ia", "IA에 반영하기")
      : formData._action === "apply_planning_workspace_actions"
          ? _t("apply_button.apply_to_workspace", "워크스페이스에 반영하기")
        : formData._action === "apply_quotation_workspace_actions"
          ? "견적서에 반영하기"
          : formData._action === "create_image_assets"
            ? _t("apply_button.generate_images", "이미지 생성하기")
            : formData._action === "fill_prompt"
              ? _t("apply_button.apply_to_prompt", "프롬프트에 적용하기")
            : formData._action === "create_coldmail_sequences"
              ? "시퀀스 생성하기"
            : _t("apply_button.apply_to_form", "폼에 적용하기")

    const isDesignerSection = formData._action === "fill_designer_section"
    const isDesignerAllSections = formData._action === "fill_designer_all_sections"
    const isDesignerRestructure = formData._action === "restructure_designer_page"
    const isIaActions = formData._action === "apply_ia_actions"
    const isPlanningWorkspaceActions = formData._action === "apply_planning_workspace_actions"
    const isQuotationWorkspaceActions = formData._action === "apply_quotation_workspace_actions"
    const isUserStoryActions = formData._action === "apply_user_story_actions"
    const isCreateImageAssets = formData._action === "create_image_assets"
    const isPrototypeActions = formData._action === "apply_prototype_actions"
    const isAiBlockFill = formData._action === "fill_section" && this.config.sectionType === "ai_block"
    const isPromptForm = formData._action === "fill_prompt"
    const isCreateAiBlockTemplate = formData._action === "create_ai_block_template"
    const isCreateAiBlockTemplateBatch = formData._action === "create_ai_block_templates_batch"
    const isCreateProfilePageSections = formData._action === "create_profile_page_sections" || (formData._action === "fill_page" && this.config.contextType === "page_sections" && Array.isArray(formData?.data?.sections))
    const isCreateColdmailSequences = formData._action === "create_coldmail_sequences" && this.isColdmailCampaignContext()
    const isColdmailCampaignFormAutoApply = formData._action === "fill_coldmail_campaign" && this.isColdmailCampaignContext() && !!this.config.formSelector
    const isProfileFormAutoApply = formData._action === "fill_page" && ["profile-site", "profile-page", "english-speaking-topic-writer"].includes(this.config.agentSlug) && !!this.config.formSelector
    const isProfileSectionFormAutoApply = formData._action === "fill_section" && this.config.contextType === "section" && ["profile-site", "profile-page"].includes(this.config.agentSlug)
    // isInterviewContext 제거됨: 인터뷰는 이제 좌측 폼에서만 진행
    const isNamingContext = isPlanningWorkspaceActions && this.config.contextType === "planning_service_name"
    const isAdminPromptTemplateCreate = isPromptForm && this.shouldCreateAdminPromptTemplate()

    if (!isNamingContext && this._messages) this._messages.appendChild(el)

    if ((isDesignerSection && this.hasDesignerSectionSelection()) || isDesignerAllSections || isDesignerRestructure || isCreateProfilePageSections || isCreateColdmailSequences || isColdmailCampaignFormAutoApply || isIaActions || isPlanningWorkspaceActions || isQuotationWorkspaceActions || isUserStoryActions || isCreateImageAssets || isPrototypeActions || isAiBlockFill || isPromptForm || isCreateAiBlockTemplate || isCreateAiBlockTemplateBatch || isProfileFormAutoApply || isProfileSectionFormAutoApply) {
      // 자동 적용: 이전 상태 스냅샷 저장 후 즉시 적용
      const snapshot = this.designerSelection?.contentData
        ? JSON.parse(JSON.stringify(this.designerSelection.contentData))
        : null
      const prevTitle = this.designerSelection?.title || null
      const prevStyleVariant = this.designerSelection?.styleVariant || null

      // 프로토타입 액션: PrototypeApp 상태 스냅샷 (undo용)
      let prototypeSnapshot = null
      if (isPrototypeActions) {
        const app = window.__prototypeApp
        if (app) {
          prototypeSnapshot = {
            hotspotIds: new Set((app.hotspots || []).map(h => h.id)),
            startFrameId: app.startFrameId || null,
            mode: app.mode || "editing"
          }
        }
      }

      // 전체 섹션 적용 시 모든 섹션의 스냅샷을 미리 가져옴
      let allSectionsSnapshot = null
      if (isDesignerAllSections || isDesignerRestructure) {
        window.dispatchEvent(new CustomEvent("profile-page-designer:snapshot-all-sections", {
          detail: { callback: (snap) => { allSectionsSnapshot = snap } }
        }))
      }

      // 인터뷰는 이제 좌측 폼에서만 진행 — 채팅 경유 planning_idea_interview 분기 제거
      const isInterviewAutoApply = false // planning_idea_interview 분기 제거됨
      const isNamingAutoApply = isNamingContext

      if (isNamingAutoApply) {
        // 네이밍 컨텍스트는 항상 auto-apply 진행 (내용 체크 불필요)
      }

      const autoApplyLabel = isNamingAutoApply
        ? _t("apply_button.saved", "저장되었습니다")
        : isAdminPromptTemplateCreate
          ? "템플릿 등록 중..."
        : isPlanningWorkspaceActions
          ? _t("apply_button.workspace_applied", "작업에 반영했어요")
        : isQuotationWorkspaceActions
          ? "견적서에 반영했어요"
        : isCreateColdmailSequences
          ? _t("apply_button.applying", "적용 중...")
          : _t("apply_button.auto_applied", "자동 적용됨")
      const planningSummary = this.planningWorkspaceApplySummary(formData.data, summary)
      const autoApplySummary = isNamingAutoApply
        ? summary
        : isPlanningWorkspaceActions
          ? this.escapeHtml(planningSummary)
          : `${this.escapeHtml(itemLabel)}: ${this.escapeHtml(summary)}`
      if (!isNamingAutoApply) {
        el.innerHTML = `<div style="display: flex; flex-direction: column; gap: 6px; width: 100%;">
        <span style="font-size: 12px;">${autoApplySummary}</span>
        <span style="font-size: 12px; color: #6366F1;">${autoApplyLabel}</span>
      </div>`
      }

      // AI 블록: applyFormData 전에 이전 값 스냅샷 (undo용)
      let aiBlockPrevValue = ""
      if (isAiBlockFill) {
        const tf = Array.from(document.getElementById("section-form")?.querySelectorAll("textarea:not([disabled])") || [])
          .find(t => t.name === "profile_page_section[content_data][html_content]")
        aiBlockPrevValue = tf ? tf.value : ""
      }

      // 프로필 사이트/페이지/섹션 및 콜드메일 캠페인 생성·수정 폼: AI 응답 후 좌측 폼에 먼저 반영하고 사용자가 취소할 수 있게 함
      const configuredFormSnapshot = (isProfileFormAutoApply || isProfileSectionFormAutoApply || isColdmailCampaignFormAutoApply) ? this.snapshotConfiguredForm() : null

      ;(async () => {
        try {
          this.debugLog(
            isIaActions
              ? "auto-apply IA actions"
              : isPlanningWorkspaceActions
                ? "auto-apply planning workspace actions"
                : isQuotationWorkspaceActions
                  ? "auto-apply quotation workspace actions"
                : isCreateImageAssets
                  ? "auto-apply image assets"
              : isDesignerRestructure
                ? "auto-apply restructure page"
                : isCreateProfilePageSections
                  ? "auto-create profile page sections"
                : isDesignerAllSections
                  ? "auto-apply all sections"
                  : "auto-apply designer section",
            formData?.data || {}
          )
          const applyResult = await this.applyFormData(formData)

          if (isIaActions) {
            // IA 액션: 적용 취소 버튼 (서버 사이드 undo)
            const undoWrap = document.createElement("div")
            undoWrap.className = "ba-followup-buttons"
            undoWrap.style.cssText = "display:flex; gap:8px; margin-top:6px;"
            const undoBtn = document.createElement("button")
            undoBtn.type = "button"
            undoBtn.className = "ba-followup-btn"
            undoBtn.style.cssText = "color:#DC2626; border-color:#FECACA;"
            undoBtn.textContent = _t("buttons.undo", "적용 취소")
            undoBtn.addEventListener("click", async () => {
              undoBtn.disabled = true
              undoBtn.textContent = _t("buttons.undoing", "취소 중...")
              try {
                const projectId = window.location.pathname.match(/\/ia\/projects\/(\d+)/)?.[1]
                if (!projectId) return
                const token = document.querySelector('meta[name="csrf-token"]')?.content
                await fetch(`/ia/projects/${projectId}/undo_last_ai_action`, {
                  method: "POST",
                  headers: { "X-CSRF-Token": token }
                })
                const notice = el.querySelector("span[style*='6366F1']")
                if (notice) { notice.textContent = _t("buttons.undone", "적용 취소됨"); notice.style.color = "#6B7280" }
                undoBtn.remove()
                window.location.reload()
              } catch (err) {
                undoBtn.disabled = false
                undoBtn.textContent = _t("buttons.undo", "적용 취소")
              }
            })
            undoWrap.appendChild(undoBtn)
            el.querySelector("div").appendChild(undoWrap)
          } else if (isUserStoryActions) {
            // 유저 스토리 액션: 적용 취소 버튼 (서버 사이드 undo)
            const undoWrap = document.createElement("div")
            undoWrap.className = "ba-followup-buttons"
            undoWrap.style.cssText = "display:flex; gap:8px; margin-top:6px;"
            const undoBtn = document.createElement("button")
            undoBtn.type = "button"
            undoBtn.className = "ba-followup-btn"
            undoBtn.style.cssText = "color:#DC2626; border-color:#FECACA;"
            undoBtn.textContent = _t("buttons.undo", "적용 취소")
            undoBtn.addEventListener("click", async () => {
              undoBtn.disabled = true
              undoBtn.textContent = _t("buttons.undoing", "취소 중...")
              try {
                const projectId = window.location.pathname.match(/\/main-target\/projects\/(\d+)/)?.[1]
                if (!projectId) return
                const token = document.querySelector('meta[name="csrf-token"]')?.content
                await fetch(`/main-target/projects/${projectId}/undo_last_ai_action`, {
                  method: "POST",
                  headers: { "X-CSRF-Token": token }
                })
                const notice = el.querySelector("span[style*='6366F1']")
                if (notice) { notice.textContent = _t("buttons.undone", "적용 취소됨"); notice.style.color = "#6B7280" }
                undoBtn.remove()
                window.location.reload()
              } catch (err) {
                undoBtn.disabled = false
                undoBtn.textContent = _t("buttons.undo", "적용 취소")
              }
            })
            undoWrap.appendChild(undoBtn)
            el.querySelector("div").appendChild(undoWrap)
          } else if (isDesignerAllSections || isDesignerRestructure) {
            // 전체 섹션: 클라이언트 사이드 undo (스냅샷 복원)
            const undoWrap = document.createElement("div")
            undoWrap.className = "ba-followup-buttons"
            undoWrap.style.cssText = "display:flex; gap:8px; margin-top:6px;"
            if (allSectionsSnapshot && allSectionsSnapshot.length > 0) {
              const undoBtn = document.createElement("button")
              undoBtn.type = "button"
              undoBtn.className = "ba-followup-btn"
              undoBtn.style.cssText = "color:#DC2626; border-color:#FECACA;"
              undoBtn.textContent = _t("buttons.undo", "적용 취소")
              undoBtn.addEventListener("click", () => {
                window.dispatchEvent(new CustomEvent("profile-page-designer:restore-all-sections", {
                  detail: { snapshot: allSectionsSnapshot }
                }))
                undoBtn.remove()
                const notice = el.querySelector("span[style*='6366F1']")
                if (notice) { notice.textContent = _t("buttons.undone", "적용 취소됨"); notice.style.color = "#6B7280" }
              })
              undoWrap.appendChild(undoBtn)
            }
            const savePreviewBtn = document.createElement("button")
            savePreviewBtn.type = "button"
            savePreviewBtn.className = "ba-followup-btn"
            savePreviewBtn.style.cssText = "color:#4F46E5; border-color:#C7D2FE;"
            savePreviewBtn.textContent = _t("buttons.save_preview", "저장 후 미리보기")
            savePreviewBtn.addEventListener("click", () => {
              const form = document.getElementById("profile-page-designer-form")
              if (!form) return
              savePreviewBtn.disabled = true
              savePreviewBtn.textContent = _t("buttons.saving", "저장 중...")
              const previewUrl = form.action.replace(/\/designer$/, "/preview")
              form.addEventListener("turbo:submit-end", (e) => {
                if (e.detail?.success) {
                  window.open(previewUrl, "_blank")
                } else {
                  savePreviewBtn.disabled = false
                  savePreviewBtn.textContent = _t("buttons.save_preview", "저장 후 미리보기")
                }
              }, { once: true })
              form.requestSubmit()
            })
            undoWrap.appendChild(savePreviewBtn)
            el.querySelector("div").appendChild(undoWrap)
          } else if (isCreateColdmailSequences) {
            const notice = el.querySelector("span[style*='6366F1']")
            if (notice) {
              const requestedCount = applyResult?.requestedCount || applyResult?.appliedCount || 0
              const appliedCount = applyResult?.appliedCount || 0
              const failedCount = applyResult?.failedCount || 0
              if (applyResult?.status === "partial" || failedCount > 0) {
                notice.textContent = `${appliedCount}개 생성, ${failedCount}개 실패`
                notice.style.color = "#D97706"
              } else {
                notice.textContent = `${appliedCount || requestedCount}개 시퀀스 생성 완료`
                notice.style.color = "#6366F1"
              }
            }
            this.appendColdmailSequenceUndoButton(el, applyResult?.createdSequences || [])
          } else if (isCreateImageAssets) {
            const followUpWrap = document.createElement("div")
            followUpWrap.className = "ba-followup-buttons"
            followUpWrap.style.cssText = "display:flex; gap:8px; margin-top:6px;"
            const refreshBtn = document.createElement("button")
            refreshBtn.type = "button"
            refreshBtn.className = "ba-followup-btn"
            refreshBtn.style.cssText = "color:#4F46E5; border-color:#C7D2FE;"
            refreshBtn.textContent = _t("buttons.refresh_list", "목록 새로고침")
            refreshBtn.addEventListener("click", () => window.location.reload())
            followUpWrap.appendChild(refreshBtn)
            el.querySelector("div").appendChild(followUpWrap)
          } else if (isAiBlockFill) {
            // AI 블록: 폼 textarea 직접 적용 + 취소 버튼
            const sectionForm = document.getElementById("section-form")
            const textarea = Array.from(sectionForm?.querySelectorAll("textarea:not([disabled])") || []).find(t => t.name === "profile_page_section[content_data][html_content]") || null
            const prevValue = aiBlockPrevValue
            const undoWrap = document.createElement("div")
            undoWrap.className = "ba-followup-buttons"
            undoWrap.style.cssText = "display:flex; gap:8px; margin-top:6px;"
            const undoBtn = document.createElement("button")
            undoBtn.type = "button"
            undoBtn.className = "ba-followup-btn"
            undoBtn.style.cssText = "color:#DC2626; border-color:#FECACA;"
            undoBtn.textContent = _t("buttons.undo", "적용 취소")
            undoBtn.addEventListener("click", () => {
              if (textarea) {
                textarea.value = prevValue
                textarea.dispatchEvent(new Event("input", { bubbles: true }))
              }
              undoBtn.remove()
              const notice = el.querySelector("span[style*='6366F1']")
              if (notice) { notice.textContent = _t("buttons.undone", "적용 취소됨"); notice.style.color = "#6B7280" }
            })
            undoWrap.appendChild(undoBtn)
            // 저장 후 미리보기 버튼
            const savePreviewBtn = document.createElement("button")
            savePreviewBtn.type = "button"
            savePreviewBtn.className = "ba-followup-btn"
            savePreviewBtn.style.cssText = "color:#4F46E5; border-color:#C7D2FE;"
            savePreviewBtn.textContent = _t("buttons.save_preview", "저장 후 미리보기")
            savePreviewBtn.addEventListener("click", () => {
              if (!sectionForm) return
              savePreviewBtn.disabled = true
              savePreviewBtn.textContent = _t("buttons.saving", "저장 중...")
              // 숨김 필드로 컨트롤러에 preview redirect 요청
              let previewInput = sectionForm.querySelector("[name='preview_after_save']")
              if (!previewInput) {
                previewInput = document.createElement("input")
                previewInput.type = "hidden"
                previewInput.name = "preview_after_save"
                sectionForm.appendChild(previewInput)
              }
              previewInput.value = "1"
              sectionForm.requestSubmit()
            })
            undoWrap.appendChild(savePreviewBtn)
            el.querySelector("div").appendChild(undoWrap)

            // AI 블록 HTML 내 __generate__ 이미지 자동 생성
            if (textarea && textarea.value.includes("__generate__")) {
              this.processAiBlockImageGeneration(textarea)
            }
          } else if (isPrototypeActions) {
            // 프로토타입 액션: 클라이언트 사이드 undo (핫스팟 삭제 + 시작 프레임 복원)
            const undoWrap = document.createElement("div")
            undoWrap.className = "ba-followup-buttons"
            undoWrap.style.cssText = "display:flex; gap:8px; margin-top:6px;"
            const undoBtn = document.createElement("button")
            undoBtn.type = "button"
            undoBtn.className = "ba-followup-btn"
            undoBtn.style.cssText = "color:#DC2626; border-color:#FECACA;"
            undoBtn.textContent = _t("buttons.undo", "적용 취소")
            undoBtn.addEventListener("click", () => {
              undoBtn.disabled = true
              undoBtn.textContent = _t("buttons.undoing", "취소 중...")
              const onDone = () => {
                window.removeEventListener("prototype:undo-done", onDone)
                undoBtn.remove()
                const notice = el.querySelector("span[style*='6366F1']")
                if (notice) { notice.textContent = _t("buttons.undone", "적용 취소됨"); notice.style.color = "#6B7280" }
              }
              window.addEventListener("prototype:undo-done", onDone, { once: true })
              window.dispatchEvent(new CustomEvent("prototype:undo-actions", {
                detail: { snapshot: prototypeSnapshot }
              }))
            })
            undoWrap.appendChild(undoBtn)
            el.querySelector("div").appendChild(undoWrap)
          } else if (isCreateAiBlockTemplate || isCreateAiBlockTemplateBatch) {
            // createAiBlockTemplate(s)는 applyFormData 내에서 처리됨 (API 호출 + 페이지 리로드)
            // 추가 UI 작업 불필요
          } else if (isProfileFormAutoApply || isProfileSectionFormAutoApply || isColdmailCampaignFormAutoApply) {
            const undoWrap = document.createElement("div")
            undoWrap.className = "ba-followup-buttons"
            undoWrap.style.cssText = "display:flex; gap:8px; margin-top:6px;"
            const undoBtn = document.createElement("button")
            undoBtn.type = "button"
            undoBtn.className = "ba-followup-btn"
            undoBtn.style.cssText = "color:#DC2626; border-color:#FECACA;"
            undoBtn.textContent = _t("buttons.undo", "적용 취소")
            undoBtn.addEventListener("click", () => {
              this.restoreConfiguredFormSnapshot(configuredFormSnapshot)
              undoBtn.remove()
              const notice = el.querySelector("span[style*='6366F1']")
              if (notice) { notice.textContent = _t("buttons.undone", "적용 취소됨"); notice.style.color = "#6B7280" }
            })
            undoWrap.appendChild(undoBtn)
            el.querySelector("div")?.appendChild(undoWrap)
          } else {
            // 디자이너 섹션: 기존 follow-up + 클라이언트 사이드 undo
            this.appendNextDesignerSectionButton(el)

            const wrap = el.querySelector(".ba-followup-buttons")
            if (wrap && snapshot) {
              const undoBtn = document.createElement("button")
              undoBtn.type = "button"
              undoBtn.className = "ba-followup-btn"
              undoBtn.dataset.followupAction = "undo"
              undoBtn.style.cssText = "color:#DC2626; border-color:#FECACA;"
              undoBtn.textContent = _t("buttons.undo", "적용 취소")
              undoBtn.addEventListener("click", () => {
                const undoData = { content_data: snapshot }
                if (prevTitle) undoData.title = prevTitle
                if (prevStyleVariant) undoData.style_variant = prevStyleVariant
                window.dispatchEvent(new CustomEvent("profile-page-designer:apply-section-content", {
                  detail: { data: undoData }
                }))
                el.querySelector('[data-followup-action="save"]')?.remove()
                undoBtn.remove()
                const notice = el.querySelector("span[style*='6366F1']")
                if (notice) { notice.textContent = _t("buttons.undone", "적용 취소됨"); notice.style.color = "#6B7280" }
              })
              wrap.insertBefore(undoBtn, wrap.firstChild)
            }
          }
        } catch (error) {
          const notice = el.querySelector("span[style*='6366F1']")
          if (notice) {
            notice.textContent = isCreateColdmailSequences ? "시퀀스 생성 실패" : _t("apply_button.apply_failed", "적용 실패")
            notice.style.color = "#DC2626"
          }
          this.appendSystemNote(error?.message || _t("system_notes.apply_error", "적용 중 오류가 발생했습니다."))
        }
      })()
    } else {
      // 기존 방식: 수동 적용 버튼
      el.innerHTML = `<div style="display: flex; flex-direction: column; gap: 6px; width: 100%;">
        <span style="font-size: 12px;">${this.escapeHtml(itemLabel)}: ${this.escapeHtml(summary)}</span>
        <button type="button" class="ba-apply-btn">${this.escapeHtml(buttonLabel)}</button>
      </div>`

      const btn = el.querySelector(".ba-apply-btn")
      btn.addEventListener("click", async () => {
        this.debugLog("apply button clicked", {
          action: formData?._action,
          contextType: this.config.contextType,
          designerBridge: this.config.designerBridge,
          fields
        })
        this.debugLog("apply button payload", formData?.data || {})
        const isColdmailSequenceCreation = formData?._action === "create_coldmail_sequences"
        const originalButtonText = btn.textContent
        btn.disabled = true
        btn.textContent = isColdmailSequenceCreation ? "시퀀스 생성 중..." : _t("buttons.applying", "적용 중...")
        btn.style.opacity = "0.75"
        btn.style.cursor = "wait"
        let progressNote = null
        if (isColdmailSequenceCreation) {
          progressNote = this.appendSystemNote("메일 단계와 이미지를 생성하고 있습니다. 잠시만 기다려주세요.")
          this.setLoading(true)
        }
        try {
          const applyResult = await this.applyFormData(formData)
          if (this.isColdmailCampaignContext() && applyResult?.appliedCount === 0) {
            btn.disabled = false
            btn.textContent = originalButtonText
            btn.style.opacity = ""
            btn.style.cursor = ""
            if (progressNote) progressNote.remove()
            if (isColdmailSequenceCreation) this.setLoading(false)
            this.appendSystemNote("적용 가능한 캠페인 필드가 없습니다. 리드 리스트는 먼저 생성해야 연결할 수 있어요.")
            return
          }
          btn.textContent = _t("buttons.apply_done", "적용 완료")
          btn.style.background = "#D1D5DB"
          btn.style.borderColor = "#D1D5DB"
          btn.style.opacity = "1"
          btn.style.cursor = "default"
        } catch (error) {
          btn.disabled = false
          btn.textContent = originalButtonText
          btn.style.opacity = ""
          btn.style.cursor = ""
          if (progressNote) progressNote.remove()
          if (isColdmailSequenceCreation) this.setLoading(false)
          this.appendSystemNote(error?.message || _t("system_notes.apply_error", "적용 중 오류가 발생했습니다."))
        }
      })
    }

    this.scrollToBottom()
  }

  appendNextDesignerSectionButton(container) {
    if (!this.config.designerBridge) return

    const nextClientKey = this.getNextDesignerSectionClientKey()
    let wrap = container.querySelector(".ba-followup-buttons")
    if (!wrap) {
      wrap = document.createElement("div")
      wrap.className = "ba-followup-buttons"
      container.appendChild(wrap)
    }

    if (!wrap.querySelector('[data-followup-action="save"]')) {
      const saveBtn = document.createElement("button")
      saveBtn.type = "button"
      saveBtn.className = "ba-followup-btn"
      saveBtn.dataset.followupAction = "save"
      saveBtn.style.cssText = "background:#6366F1; color:#FFF; border-color:#6366F1;"
      saveBtn.textContent = _t("buttons.save", "저장하기")
      saveBtn.addEventListener("click", () => {
        const form = document.getElementById("profile-page-section-designer-form")
          || document.getElementById("profile-page-designer-form")
        if (form) form.requestSubmit()
      })
      wrap.appendChild(saveBtn)
    }

    const isSectionOnly = this.config.contextType === "section"

    if (!isSectionOnly && nextClientKey && !wrap.querySelector('[data-followup-action="next-section"]')) {
      const nextBtn = document.createElement("button")
      nextBtn.type = "button"
      nextBtn.className = "ba-followup-btn"
      nextBtn.dataset.followupAction = "next-section"
      nextBtn.textContent = _t("buttons.next_section", "다음 섹션 이어서 수정하기")
      nextBtn.addEventListener("click", () => {
        this.activateDesignerSection(nextClientKey)
      })
      wrap.appendChild(nextBtn)
    }

    if (!isSectionOnly && !wrap.querySelector('[data-followup-action="page-overall"]')) {
      const pageBtn = document.createElement("button")
      pageBtn.type = "button"
      pageBtn.className = "ba-followup-btn"
      pageBtn.dataset.followupAction = "page-overall"
      pageBtn.textContent = _t("buttons.edit_full_page", "페이지 전체 수정하기")
      pageBtn.addEventListener("click", () => {
        this.sendDesignerPageLevelPrompt("현재 페이지 전체 톤에 맞게 다듬어줘")
      })
      wrap.appendChild(pageBtn)
    }
  }

  getNextDesignerSectionClientKey() {
    if (!this.config.designerBridge || !this.designerSelection?.clientKey) return null

    const nodes = Array.from(document.querySelectorAll(".ppd-canvas-section[data-client-key]"))
    const keys = nodes.map((node) => node.dataset.clientKey).filter(Boolean)
    const currentIndex = keys.indexOf(this.designerSelection.clientKey)
    if (currentIndex === -1) return null

    return keys[currentIndex + 1] || null
  }

  _appendMenuItemButton(parsed) {
    const items = parsed._action === "add_menu_items"
      ? (parsed.data?.items || [])
      : [parsed.data]

    if (items.length === 0) return

    const el = document.createElement("div")
    el.className = "ba-msg-system ba-apply-block"

    const labels = items.map(i => i.label).join(", ")
    el.innerHTML = `<div style="display: flex; flex-direction: column; gap: 6px; width: 100%;">
      <span style="font-size: 12px;">메뉴에 추가할 페이지 (${items.length}개): ${this.escapeHtml(labels)}</span>
      <button type="button" class="ba-apply-btn">${_t("buttons.add_to_menu", "메뉴에 추가하기")}</button>
    </div>`

    const btn = el.querySelector(".ba-apply-btn")
    btn.addEventListener("click", async () => {
      btn.textContent = _t("buttons.adding", "추가 중...")
      btn.disabled = true
      await this._submitMenuItemsAjax(items, btn)
    })

    if (this._messages) this._messages.appendChild(el)
    this.scrollToBottom()
  }

  async _submitMenuItemsAjax(items, statusBtn) {
    // 메뉴 추가 폼의 action URL에서 엔드포인트 추출
    const form = document.querySelector("form[action*='menu_items']")
    if (!form) {
      this.appendSystemNote(_t("system_notes.menu_form_not_found", "메뉴 추가 폼을 찾을 수 없습니다."))
      return
    }
    const baseUrl = form.getAttribute("action")
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content")

    let successCount = 0
    let failCount = 0

    for (const item of items) {
      try {
        const res = await fetch(`${baseUrl}.json`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken
          },
          body: JSON.stringify({
            profile_site_menu_item: {
              profile_page_id: item.profile_page_id,
              label: item.label,
              is_home: item.is_home || false
            }
          })
        })
        if (res.ok) {
          successCount++
          if (statusBtn) statusBtn.textContent = _t("status.add_count_status", "추가 중... (%{done}/%{total})").replace("%{done}", successCount).replace("%{total}", items.length)
        } else {
          failCount++
          const err = await res.json().catch(() => ({}))
          console.warn("[KlavyAgent] menu item add failed:", err)
        }
      } catch (e) {
        failCount++
        console.error("[KlavyAgent] menu item add error:", e)
      }
    }

    if (statusBtn) {
      statusBtn.textContent = _t("status.add_complete", "%{count}개 추가 완료").replace("%{count}", successCount)
      statusBtn.style.background = "#D1D5DB"
      statusBtn.style.borderColor = "#D1D5DB"
      statusBtn.style.cursor = "default"
    }

    if (failCount > 0) {
      this.appendSystemNote(_t("system_notes.menu_add_result", "%{success}개 추가, %{fail}개 실패").replace("%{success}", successCount).replace("%{fail}", failCount))
    } else {
      this.appendSystemNote(_t("system_notes.menu_add_done", "메뉴 항목 %{count}개가 추가되었습니다. 페이지를 새로고침합니다.").replace("%{count}", successCount))
      setTimeout(() => window.location.reload(), 800)
    }
  }

  _appendFollowUpButtons(aiText) {
    const el = document.createElement("div")
    el.className = "ba-followup-buttons"

    const buttons = []
    const hasForm = !!this.config.formSelector && !!document.querySelector(this.config.formSelector)
    const hasFormData = !!this.extractFormData(aiText)
    const isEditPage = this.config.resourceId && this.config.resourceId > 0
    const isSiteContext = this.config.contextType === "site"
    const isIaProjectContext = this.config.contextType === "ia_project" || this.config.resourceType === "IaProject"
    const isUserStoryContext = this.config.contextType === "user_story_project"
    const isPlanningWorkspaceContext = this.isPlanningWorkspaceContext()
    const isPlanningWorkspaceAutoApply = isPlanningWorkspaceContext && hasFormData
    const isUiPlanningPrototypeContext = this.config.contextType === "ui_planning_prototype" && this.config.resourceType === "UiPlanningProject"
    // isInterviewContext 제거됨: 인터뷰는 이제 좌측 폼에서만 진행

    // 프로토타입 적용 후에는 자동 적용 카드/취소 버튼만 유지하고 일반 후속 버튼은 붙이지 않음
    if (isUiPlanningPrototypeContext) return

    // 이미지 에셋 에이전트는 제안형 답변에서 바로 생성하는 단일 CTA만 노출한다.
    // JSON 액션이 이미 있으면 위쪽 자동 생성 카드가 본 흐름이므로 후속 버튼은 생략한다.
    if (this.isImageAssetAgent) {
      if (!hasFormData) this._appendImageAssetGenerateButton(aiText)
      return
    }

    // 유저 스토리 컨텍스트에서는 팔로업 버튼 불필요
    if (isUserStoryContext) {
      if (el.children.length === 0) return
      if (this._messages) this._messages.appendChild(el)
      return
    }

    // 섹션 템플릿 에이전트: 맥락에 맞는 버튼만 표시
    if (this.config.agentSlug === "section-template") {
      const hasTemplateData = !!this.extractFormData(aiText)
      const sectionTemplateBtns = []
      if (hasTemplateData) {
        sectionTemplateBtns.push({ label: "다른 섹션 만들기", action: "send", message: "다른 주제의 섹션 템플릿을 하나 더 만들어줘" })
        sectionTemplateBtns.push({ label: "디자인 수정하기", action: "send", message: "방금 만든 섹션의 디자인을 더 세련되게 수정해줘" })
      } else {
        sectionTemplateBtns.push({ label: "섹션 만들어줘", action: "send", message: "이 내용으로 섹션 템플릿을 만들어줘" })
        sectionTemplateBtns.push({ label: "다른 스타일로", action: "send", message: "다른 스타일로 섹션 템플릿을 만들어줘" })
      }
      el.innerHTML = sectionTemplateBtns.map(b =>
        `<button type="button" class="ba-followup-btn">${this.escapeHtml(b.label)}</button>`
      ).join("")
      el.querySelectorAll(".ba-followup-btn").forEach((btn, i) => {
        btn.addEventListener("click", async () => {
          el.remove()
          if (this._input) this._input.value = sectionTemplateBtns[i].message
          await this.send()
        })
      })
      if (this._messages) this._messages.appendChild(el)
      this.scrollToBottom()
      return
    }

    // 네이밍 대화: 단계별 안내 버튼
    if (this.config.contextType === "planning_service_name" && isPlanningWorkspaceContext) {
      const el = document.createElement("div")
      el.className = "ba-followup-buttons"
      el.style.cssText = "display:flex;flex-direction:column;gap:6px;align-items:stretch;"

      // AI 응답에서 phase 파악
      const hasProposal = aiText.includes("## 후보 이름") || aiText.includes("## 네이밍 방향")

      // AI가 제시한 선택지를 파싱 (예: "영어 기반 — Notion, Slack...", "1. 영어 기반 — ...")
      const parseAiOptions = (text) => {
        const lines = text.split('\n')
        const opts = []
        for (const line of lines) {
          const trimmed = line.replace(/^[\s\-\*\d\.]+/, '').trim()
          if (!trimmed) continue
          // "선택지 — 설명" 또는 "선택지: 설명" 패턴
          const dashMatch = trimmed.match(/^(.+?)\s+[—\-–]\s+(.+)$/)
          if (dashMatch) {
            const label = dashMatch[1].trim()
            if (label.length > 0 && label.length < 30 && opts.length < 4) {
              opts.push({ label, message: trimmed })
            }
          }
        }
        return opts
      }

      // AI가 update_naming_phase 액션에 직접 담아 보낸 선택지를 우선 사용 (산문 질문이라 텍스트 파싱이 안 잡히는 케이스 복구)
      const namingFormData = this.extractFormData(aiText)
      const namingAction = (namingFormData?.data?.actions || []).find(a => a && a.type === "update_naming_phase")
      const aiChoices = Array.isArray(namingAction?.choices)
        ? namingAction.choices
            .map(c => (typeof c === "string" ? c : (c && (c.label || c.text)) || "").trim())
            .filter(c => c.length > 0 && c.length < 40 && !/^직접\s*입력/.test(c))
            .slice(0, 4)
            .map(label => ({ label, message: label }))
        : []

      // gathering 단계 선택지: (a) AI choices → (b) 텍스트 파싱 폴백 → (c) 기본값
      const gatheringOptions = aiChoices.length >= 1
        ? aiChoices
        : (() => {
            const parsed = parseAiOptions(aiText)
            if (parsed.length >= 2) return parsed
            return [
              { label: "이 방향으로 진행", message: "이 방향이 좋아요. 이대로 진행해줘." },
              { label: "다른 스타일로 제안", message: "다른 스타일로 새 방향을 제안해줘." }
            ]
          })()

      const buttons = hasProposal
        ? [
            { label: "다른 스타일로 후보를 더 제안해줘", message: "마음에 드는 이름이 없어요. 완전히 다른 스타일로 5개 더 제안해줘." },
            { label: "이 방향에서 조금 변형해줘", message: "방향은 좋은데, 후보 이름들을 좀 더 다듬어서 새 버전을 제안해줘." },
            { label: "직접 입력하기", message: null }
          ]
        : [...gatheringOptions, { label: "직접 입력하기", message: null }]

      const headerText = hasProposal
        ? '<div style="font-size:11px;color:#9CA3AF;margin-bottom:2px;font-weight:600;">후보를 검토하거나 대화를 이어가세요</div>'
        : '<div style="font-size:11px;color:#9CA3AF;margin-bottom:2px;font-weight:600;">선택지를 고르거나 직접 입력하세요</div>'

      el.innerHTML = headerText + buttons.map(b =>
        `<button type="button" class="ba-followup-btn ba-naming-btn" style="text-align:left;white-space:normal;line-height:1.4;width:100%;display:block;">${this.escapeHtml(b.label)}</button>`
      ).join("")

      el.querySelectorAll(".ba-naming-btn").forEach((btn, i) => {
        btn.addEventListener("click", async () => {
          el.remove()
          if (buttons[i].message === null) {
            // 직접 입력하기
            if (this._input) {
              this._input.focus()
              this._input.placeholder = _t("buttons.input_placeholder", "직접 입력하세요...")
            }
            return
          }
          if (this._input) this._input.value = buttons[i].message
          await this.send()
        })
      })

      if (this._messages) this._messages.appendChild(el)
      this.scrollToBottom()
      return
    }

    // 기획 워크스페이스 자동 적용 응답은 좌측 단계 진행이 본 흐름이므로 일반 후속 버튼을 붙이지 않음
    if (isPlanningWorkspaceAutoApply) return
    if (isPlanningWorkspaceContext) return

    // 인터뷰는 이제 좌측 폼에서만 진행 — planning_idea_interview 채팅 예시 버튼 분기 제거

    // 사이트 구조 파악
    const structure = this.config.siteStructure || {}
    const menuCount = structure.menu?.length || 0
    const resourceId = this.config.resourceId

    if (hasFormData && hasForm) {
      if (isEditPage) {
        buttons.push({ label: "저장", action: "applyAndSubmit" })
      } else {
        buttons.push({ label: "이 내용으로 등록", action: "applyAndSubmit" })
        buttons.push({
          label: "내용 수정 후 등록",
          action: "send",
          message: "폼 내용을 먼저 확인하고 수정할게요. 등록은 잠시 뒤에 할게요."
        })
      }
    } else if (hasForm && !hasFormData) {
      buttons.push({
        label: "추천 내용을 폼에 입력해줘",
        action: "send",
        message: "위 추천 내용을 폼에 자동 입력해줘"
      })
    }

    // 사이트 편집 페이지: 페이지 관리 관련 버튼
    if (isSiteContext && isEditPage) {
      const availableSelect = document.querySelector("select[name*='profile_page_id']")
      const availableCount = availableSelect
        ? [...availableSelect.options].filter(o => o.value && o.value !== "").length
        : 0

      if (availableCount > 0) {
        buttons.push({
          label: `페이지 추가 (${availableCount}개 가능)`,
          action: "send",
          message: "사이트 메뉴에 추가 가능한 페이지들을 모두 추가해줘. 드롭다운에 있는 페이지를 적절한 순서와 라벨로 메뉴에 등록해줘."
        })
      }
      if (menuCount === 0 && availableCount === 0) {
        buttons.push({
          label: "홈 페이지 만들기",
          action: "navigate",
          url: `/admin/profile_pages/new${resourceId ? `?profile_site_id=${resourceId}` : ""}`
        })
        buttons.push({
          label: "필요한 페이지 추천받기",
          action: "send",
          message: "이 사이트에 맞는 페이지 구성을 추천해줘. 포트폴리오에 필요한 페이지 목록과 각 페이지별 역할, 포함할 섹션을 알려줘."
        })
      } else if (menuCount > 0) {
        buttons.push({
          label: "다음 페이지 추천받기",
          action: "send",
          message: "현재 사이트 구조를 보고 추가로 만들면 좋을 페이지를 추천해줘."
        })
        buttons.push({
          label: "새 페이지 만들기",
          action: "navigate",
          url: `/admin/profile_pages/new${resourceId ? `?profile_site_id=${resourceId}` : ""}`
        })
        if (resourceId) {
          buttons.push({
            label: "사이트 미리보기",
            action: "navigate",
            url: `/admin/profile_sites/${resourceId}/preview`
          })
        }
      }
    }

    if (!hasFormData && isIaProjectContext) {
      buttons.push({
        label: "IA 구조 초안으로 정리해줘",
        action: "send",
        message: "현재 IA 프로젝트를 메뉴, 페이지, 플로우 기준으로 체계적으로 정리해줘."
      })
      buttons.push({
        label: "IA 규칙으로 등록해줘",
        action: "rules",
        context: aiText
      })
    } else if (!hasFormData && !isSiteContext) {
      buttons.push({
        label: "작업 규칙으로 등록해줘",
        action: "rules",
        context: aiText
      })
    }
    buttons.push({
      label: "더 구체적으로",
      action: "send",
      message: "좀 더 구체적으로 설명해줘"
    })

    el.innerHTML = buttons.map(b =>
      `<button type="button" class="ba-followup-btn">${this.escapeHtml(b.label)}</button>`
    ).join("")

    el.querySelectorAll(".ba-followup-btn").forEach((btn, i) => {
      btn.addEventListener("click", async () => {
        el.remove()
        const b = buttons[i]
        if (b.action === "rules") {
          await this.startRulesGeneration()
          // 직전 대화 맥락을 룰 채팅에 전달해 바로 룰 생성
          if (this._rulesChatInput) {
            const lastUserMsg = [...this.chatHistory].reverse().find(m => m.role === "user")
            const topic = lastUserMsg ? lastUserMsg.content : "위 대화 내용"
            this._rulesChatInput.value = `"${typeof topic === "string" ? topic : topic?.[topic.length-1]?.text || "위 내용"}" 주제로 작업 규칙을 생성해줘`
            this.sendRulesChat()
          }
        } else if (b.action === "applyAndSubmit") {
          const form = this.config.formSelector
            ? document.querySelector(this.config.formSelector) : null
          if (!form) return
          if (this.lastFormData) await this.applyFormData(this.lastFormData)
          this._saveChatHistoryToSession()
          await new Promise(r => setTimeout(r, 100))
          form.requestSubmit()
        } else if (b.action === "navigate") {
          window.location.href = b.url
        } else {
          if (this._input) this._input.value = b.message
          this.send()
        }
      })
    })

    if (this._messages) this._messages.appendChild(el)
    this.scrollToBottom()
  }

  _appendImageAssetGenerateButton(aiText) {
    const el = document.createElement("div")
    el.className = "ba-followup-buttons"
    el.innerHTML = `<button type="button" class="ba-followup-btn">${this.escapeHtml(_t("buttons.generate_image_from_suggestion", "이대로 생성하기"))}</button>`

    const btn = el.querySelector(".ba-followup-btn")
    btn?.addEventListener("click", () => this.generateImageAssetsFromSuggestion(aiText, btn))

    if (this._messages) this._messages.appendChild(el)
    this.scrollToBottom()
  }

  async generateImageAssetsFromSuggestion(aiText, button = null) {
    const originalLabel = button?.textContent || _t("buttons.generate_image_from_suggestion", "이대로 생성하기")
    if (button) {
      button.disabled = true
      button.textContent = _t("buttons.preparing_image_generation", "생성 준비 중...")
    }

    try {
      const formData = await this.buildImageAssetPayloadFromSuggestion(aiText)
      if (formData?._action !== "create_image_assets" || !formData.data) {
        throw new Error(_t("system_notes.image_asset_request_parse_failed", "생성 요청을 정리하지 못했어요"))
      }

      this.lastFormData = formData
      const result = await this.createImageAssets(formData.data)
      if (!result || result.succeeded < 1) {
        if (button) {
          button.disabled = false
          button.textContent = originalLabel
        }
        return
      }

      button?.closest(".ba-followup-buttons")?.remove()
    } catch (e) {
      console.error("[KlavyAgentCore] image asset suggestion generation failed", e)
      this.appendSystemNote(e.message || _t("system_notes.image_asset_request_parse_failed", "생성 요청을 정리하지 못했어요"))
      if (button) {
        button.disabled = false
        button.textContent = originalLabel
      }
    }
  }

  async buildImageAssetPayloadFromSuggestion(aiText) {
    const lastUserMsg = [...this.chatHistory].reverse().find(m => m.role === "user")
    const userText = this.messageContentText(lastUserMsg?.content || "")
    const provider = this.getProvider(this.planningTextLlm)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ""

    const responseText = await this.requestAiResponse({
      provider,
      endpointPrefix: this.config.aiBasePath,
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken, "X-Requested-With": "XMLHttpRequest" },
      stream: false,
      payload: {
        model: this.planningTextLlm,
        system: this.imageAssetJsonSystemPrompt(),
        messages: [
          {
            role: "user",
            content: `사용자 요청:\n${userText || "(직전 사용자 요청 없음)"}\n\nAI 제안:\n${String(aiText || "").trim()}\n\n위 내용을 바탕으로 실제 이미지 에셋 생성용 JSON만 반환하세요.`
          }
        ],
        max_tokens: 1600,
        context: this._buildUsageContext({ action: "image_asset_suggestion_to_json" })
      }
    })

    const formData = this.extractFormData(responseText)
    if (formData?._action !== "create_image_assets") {
      throw new Error(_t("system_notes.image_asset_request_parse_failed", "생성 요청을 정리하지 못했어요"))
    }
    return formData
  }

  messageContentText(content) {
    if (Array.isArray(content)) {
      return content
        .map(part => part?.text || part?.content || "")
        .filter(Boolean)
        .join("\n")
        .trim()
    }
    return String(content || "").trim()
  }

  isDirectImageAssetGenerationRequest(text) {
    const source = String(text || "").trim()
    if (!source) return false

    const lower = source.toLowerCase()
    const asksForAdvice = /(어떻게|방법|왜|원인|분석|검색|찾아봐|알려줘|설명|비교|how\s+to|why|analy[sz]e|search|explain|compare)/i.test(lower)
    if (asksForAdvice) return false

    const hasGenerationVerb = /(생성|만들|제작|그려|그리|디자인|create|generate|make|design)/i.test(lower)
    const hasImageTarget = /(이미지|사진|포스터|배너|썸네일|광고|에셋|image|photo|poster|banner|thumbnail|advertisement|ad\s*asset)/i.test(lower)
    return hasGenerationVerb && hasImageTarget
  }

  buildDirectImageAssetFormData(text) {
    const prompt = String(text || "").trim()
    return {
      _action: "create_image_assets",
      data: {
        items: [
          {
            title: this.directImageAssetTitle(prompt),
            alt_text: prompt,
            category: this.directImageAssetCategory(prompt),
            aspect_ratio: this.directImageAssetAspectRatio(prompt),
            prompt
          }
        ]
      }
    }
  }

  directImageAssetAspectRatio(text) {
    const source = String(text || "").toLowerCase()
    if (/(정사각|1\s*:\s*1|square)/i.test(source)) return "1:1"
    if (/(가로|와이드|16\s*:\s*9|landscape|wide|banner|배너)/i.test(source)) return "16:9"
    if (/(광고\s*포스터|광고포스터|인쇄\s*포스터|세로\s*포스터|포스터|poster|portrait)/i.test(source)) return "portrait"
    return ""
  }

  directImageAssetCategory(text) {
    const source = String(text || "").toLowerCase()
    if (/(사람|인물|모델|배우|아이돌|person|people|model|portrait)/i.test(source)) return "people"
    if (/(제품|상품|음료|커피|브랜드|광고|product|drink|coffee|brand|advertisement)/i.test(source)) return "product"
    if (/(공간|인테리어|매장|카페|interior|store|room|office)/i.test(source)) return "interior"
    if (/(교육|강의|학습|education|class|study)/i.test(source)) return "education"
    if (/(기술|앱|대시보드|technology|app|dashboard|software)/i.test(source)) return "technology"
    if (/(비즈니스|회사|오피스|business|office|b2b)/i.test(source)) return "business"
    if (/(일상|라이프스타일|lifestyle)/i.test(source)) return "lifestyle"
    return "abstract"
  }

  directImageAssetTitle(text) {
    const source = String(text || "").replace(/\s+/g, " ").trim()
    if (!source) return _t("field_labels.create_image_assets", "이미지 에셋 생성")

    const firstSentence = source.split(/[.!?。！？\n]/)[0]?.trim() || source
    return firstSentence.length > 48 ? `${firstSentence.slice(0, 48).trim()}...` : firstSentence
  }

  imageAssetJsonSystemPrompt() {
    return `당신은 이미지 에셋 생성 요청을 JSON으로 변환하는 도구입니다.
설명, 마크다운, 코드펜스 없이 JSON 객체 하나만 반환하세요.

반환 형식:
{"_action":"create_image_assets","data":{"items":[{"title":"관리용 이름","alt_text":"한국어 설명","category":"product","aspect_ratio":"portrait","prompt":"사용자 원문 또는 선택된 AI 제안 원문"}]}}

규칙:
- items는 1~4개입니다. 사용자나 AI 제안에 개수 지정이 없으면 1개만 만드세요.
- category는 business, education, people, product, interior, lifestyle, technology, abstract 중 하나만 사용하세요.
- aspect_ratio는 선택 필드입니다. 광고포스터/인쇄 포스터/세로 포스터는 기본 "portrait", 가로 배너는 "16:9", 정사각형 SNS 이미지라고 명시된 경우만 "1:1"을 사용하세요.
- title과 alt_text는 한국어로 짧고 명확하게 작성하세요.
- prompt는 사용자 원문 또는 선택된 AI 제안 원문을 그대로 보존하세요.
- prompt를 영어로 번역하지 마세요. 한국어 요청은 한국어 그대로, 영어 요청은 영어 그대로 유지하세요.
- 임의 레이아웃, 브랜드 컬러, 제품 묘사, 카피라이팅 문구를 추가하지 마세요.
- 광고포스터/포스터의 세로 형식은 prompt에 설명을 덧붙이지 말고 aspect_ratio: "portrait"로만 표현하세요.
- prompt가 비어 있으면 사용자 요청과 AI 제안 중 이미지 생성에 가장 직접적인 문장을 그대로 사용하세요.`
  }

  appendSystemNote(text) {
    const el = document.createElement("div")
    el.className = "ba-msg-system"
    this.updateSystemNote(el, text)
    if (this._messages) this._messages.appendChild(el)
    this.scrollToBottom()
    return el
  }

  updateSystemNote(el, text) {
    if (!el) return

    el.innerHTML = `<span style="display: inline-flex; align-items: center; gap: 4px;">
      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
      ${this.escapeHtml(text)}
    </span>`
  }

  setLoading(loading) {
    this.isLoading = loading
    const sendBtn = this._sendBtn
    if (sendBtn) sendBtn.disabled = loading

    if (loading) {
      const typing = document.createElement("div")
      typing.className = "ba-typing ba-ai-thinking"
      typing.id = "ba-typing-indicator"
      typing.innerHTML = `
        <div class="ba-thinking-bubble">
          <div class="ba-thinking-text">${this.escapeHtml(this._loadingMessage || _t("status.thinking", "작업 중이에요"))}</div>
          <div class="ba-thinking-meta">
            <span class="ba-thinking-time">0${_t("status.seconds", "%{sec}초").replace("%{sec}", "")}</span>
          </div>
          <div class="ba-thinking-bar"><div class="ba-thinking-bar-inner"></div></div>
        </div>
      `
      if (this._messages) this._messages.appendChild(typing)
      this._startThinkingTimer(typing.querySelector(".ba-thinking-time"))
      this.scrollToBottom()
    } else {
      this._stopThinkingTimer()
      const typing = document.getElementById("ba-typing-indicator")
      if (typing) typing.remove()
    }
  }

  setLoadingMessage(message) {
    this._loadingMessage = message || ""
    const textEl = document.querySelector("#ba-typing-indicator .ba-thinking-text")
    if (textEl && this._loadingMessage) textEl.textContent = this._loadingMessage
  }

  _startThinkingTimer(timeEl) {
    this._stopThinkingTimer()
    if (!timeEl) return
    this._thinkingStart = Date.now()
    this._thinkingTimer = setInterval(() => {
      const sec = Math.floor((Date.now() - this._thinkingStart) / 1000)
      timeEl.textContent = _t("status.seconds", "%{sec}초").replace("%{sec}", sec)
    }, 1000)
  }

  _stopThinkingTimer() {
    if (this._thinkingTimer) {
      clearInterval(this._thinkingTimer)
      this._thinkingTimer = null
    }
  }

  scrollToBottom() {
    const el = this._messages
    if (el) el.scrollTop = el.scrollHeight
  }

  isEnglishSpeakingTopicWriterAgent() {
    return this.config.agentSlug === "english-speaking-topic-writer"
  }

  showWelcomeMessage() {
    // 사이트에서 이동해서 페이지를 만드는 경우 사이트 인지 웰컴
    if (this.config.contextType === "page" && this.config.parentSiteId > 0
        && (!this.config.resourceId || this.config.resourceId === 0)) {
      this._showSiteAwareWelcome()
      return
    }

    if (this._messages && (this.chatHistory.length === 0 || this.isEnglishSpeakingTopicWriterAgent())) {
      this._messages.querySelectorAll(".ba-welcome-message").forEach((node) => node.remove())
    }

    const msgEl = document.createElement("div")
    msgEl.className = "ba-msg-assistant ba-welcome-message"

    if (this.config.contextType === "ui_planning_prototype") {
      const quickBtns = this.config.quickStarts.length > 0
        ? this.config.quickStarts
        : [
            { text: "최소: 순서대로 자동 연결", prompt: "순서대로 자동 연결" },
            { text: "중간: 요소 단위로 연결", prompt: "요소 단위로 정밀 연결" },
            { text: "고급: 100% 요소 단위로 전체 연결", prompt: "100% 요소 단위로 전체 연결" },
            { text: "선택: 원하는 요소만 골라 연결", prompt: "요소 선택 연결" }
          ]
      const welcomeMsg = this.config.welcomeMessage || "어떻게 화면을 연결할까요?"
      msgEl.innerHTML = `<p>${this.escapeHtml(welcomeMsg)}</p><p style="margin-top:8px; font-size:13px; color:#6B7280;">프레임 순서대로 빠르게 잇거나, 버튼/아이콘 위치를 분석해 정밀하게 연결할 수 있어요.</p><div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:10px;">${quickBtns.map(t => this.quickButtonHtml(t)).join("")}</div>`
    } else if (this.isPromptDesignerContext()) {
      const quickBtns = this.config.quickStarts.length > 0
        ? this.config.quickStarts
        : [
            "스터디카페 좌석 예약 서비스",
            "네일·헤어샵 예약 서비스",
            "캠핑장·글램핑 예약 서비스",
            "웨딩·스냅 촬영 예약 서비스",
            "카페·디저트 브랜드 홈페이지"
          ]
      msgEl.innerHTML = `<p>어떤 화면을 설계하고 싶으신가요?</p><p style="margin-top:8px; font-size:13px; color:#6B7280;">예시를 고르거나 만들고 싶은 서비스를 적어주시면 제목, 설명, 본문까지 채워드립니다.</p><div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:10px;">${quickBtns.map(t => this.quickButtonHtml(t)).join("")}</div>`
    } else if (this.config.contextType === "ia_project") {
      const hasExistingNodes = Array.isArray(this.config.existingSections) && this.config.existingSections.length > 0
      const activeRules = (this.projectRules || []).filter(r => r.active !== false)
      const rulesHint = activeRules.length === 0
        ? `<div style="margin-top:12px; padding:6px 0 6px 12px; border-left:3px solid #6366F1; font-size:12px; color:#6B7280;">작업 규칙을 정리하면 메뉴/페이지 명칭을 더 일관되게 맞출 수 있어요. <button type="button" class="ba-rules-hint-btn" style="background:none; border:none; color:#6366F1; font-weight:600; cursor:pointer; font-size:12px; font-family:inherit; padding:0;">열기</button></div>`
        : `<div style="margin-top:12px; padding:6px 0 6px 12px; border-left:3px solid #6366F1; font-size:12px; color:#6B7280;"><span class="ba-rules-active-hint" style="cursor:pointer;">작업 규칙 <strong style="color:#6366F1;">${activeRules.length}개</strong> 적용 중</span></div>`

      if (hasExistingNodes) {
        const wbsBtns = [
          { text: "우선순위 자동 배정", prompt: "현재 IA의 모든 항목에 우선순위를 1~7순위로 자동 배정해줘. 1순위=홈페이지, 2순위=로그인·회원가입·프로필, 3순위=핵심 서비스(목록·상세), 4순위=게시판·커뮤니티, 5순위=정적 페이지(소개·약관), 6순위=부가 기능, 7순위=기타" },
          { text: "URL 자동 설정", prompt: "현재 IA의 모든 항목에 URL 경로를 자동 설정해줘. 예: 홈=/, 소개=/about, 상품목록=/products, 상품상세=/products/:id, 로그인=/login" },
          { text: "메모 자동 작성", prompt: "현재 IA의 모든 항목에 페이지 목적과 핵심 기능을 purpose(메모)로 자동 작성해줘" }
        ]
        const welcomeMsg = this.config.welcomeMessage || _t("welcome.ia_default", "안녕하세요! IA 구조를 함께 설계해드릴게요.")
        msgEl.innerHTML = `<p>${this.escapeHtml(welcomeMsg)}</p><p style="margin-top:8px; font-size:13px; color:#6B7280;">IA 구조가 준비되어 있어요. 우선순위, URL, 메모를 설정해볼까요?</p><div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:6px;">${wbsBtns.map(t => this.quickButtonHtml(t)).join("")}</div>`
      } else if (!hasExistingNodes && this.config.fromPlanning) {
        const welcomeMsg = this.config.welcomeMessage || _t("welcome.ia_default2", "안녕하세요! IA 구조를 도와드릴게요.")
        const templateBtns = ["홈페이지 IA", "커머스 IA", "SaaS IA", "배달앱 IA", "온보딩 IA", "예약 서비스 IA"]
        msgEl.innerHTML = `<p>${this.escapeHtml(welcomeMsg)}</p><p style="margin-top:8px; font-size:13px; color:#6B7280;">${_t("context_guide.planning_ia_helper", "기획 워크스페이스에서 정리한 아이디어 메모, 린 캔버스, 포지셔닝, 메인 타겟, PRD를 바탕으로 IA 구조를 생성할 수 있어요.")}</p><div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:10px;">${this.quickButtonHtml({ text: "기획 내용 기반으로 IA 구조 생성", prompt: "기획 워크스페이스에서 가져온 브리핑(아이디어 메모, 린 캔버스, 포지셔닝, 메인 타겟 유저, PRD)을 모두 참고해서 IA 구조 초안을 만들어줘." })}</div><p style="margin-top:14px; font-size:12px; color:#9CA3AF;">${_t("context_guide.or_start_template", "또는 기본 템플릿으로 시작할 수도 있어요.")}</p><div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:6px;">${templateBtns.map(t => this.quickButtonHtml(t)).join("")}</div>`
      } else {
        const quickBtns = this.config.quickStarts.length > 0
          ? this.config.quickStarts
          : ["홈페이지 IA 초안", "배달앱 IA 초안", "커머스 IA 초안", "SaaS IA 초안", "온보딩 IA 초안", "예약 서비스 IA 초안", "구독 서비스 IA 초안", "교육/강의 IA 초안", "포트폴리오 IA 초안", "예약+커머스 IA 초안", "뉴스레터/미디어 IA 초안", "체험단 플랫폼 IA 초안"]
        const welcomeMsg = this.config.welcomeMessage || _t("welcome.ia_default", "안녕하세요! IA 구조를 함께 설계해드릴게요.")
        msgEl.innerHTML = `<p>${this.escapeHtml(welcomeMsg)}</p><p style="margin-top:8px; font-size:13px; color:#6B7280;">서비스 목표와 핵심 메뉴를 알려주시면, IA 구조 초안과 수정안을 제안해드릴게요.</p><div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:6px;">${quickBtns.map(t => this.quickButtonHtml(t)).join("")}</div>`
      }
    } else if (this.config.contextType === "site") {
      const quickBtns = this.config.quickStarts.length > 0
        ? this.config.quickStarts
        : ["웹기획자 포트폴리오", "개발자 포트폴리오", "디자이너 포트폴리오", "프리랜서 소개 사이트", "스타트업 소개"]
      const activeRules = (this.projectRules || []).filter(r => r.active !== false)
      const rulesHint = activeRules.length === 0
        ? `<div style="margin-top:12px; padding:6px 0 6px 12px; border-left:3px solid #6366F1; font-size:12px; color:#6B7280;">작업 규칙을 등록하면 더 맞춤형 콘텐츠를 생성할 수 있어요. <button type="button" class="ba-rules-hint-btn" style="background:none; border:none; color:#6366F1; font-weight:600; cursor:pointer; font-size:12px; font-family:inherit; padding:0;">등록하기</button></div>`
        : `<div style="margin-top:12px; padding:6px 0 6px 12px; border-left:3px solid #6366F1; font-size:12px; color:#6B7280;"><span class="ba-rules-active-hint" style="cursor:pointer;">작업 규칙 <strong style="color:#6366F1;">${activeRules.length}개</strong> 적용 중</span></div>`
      const welcomeMsg = this.config.welcomeMessage || _t("welcome.site_default", "안녕하세요! 프로필 사이트를 구성해드릴게요.")
      msgEl.innerHTML = `<p>${this.escapeHtml(welcomeMsg)}</p><p style="margin-top:8px; font-size:13px; color:#6B7280;">어떤 사이트를 만드시나요?</p><div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:6px;">${quickBtns.map(t => this.quickButtonHtml(t)).join("")}</div>${rulesHint}`
    } else if (this.config.contextType === "page") {
      const quickBtns = this.config.quickStarts.length > 0
        ? this.config.quickStarts
        : [
            "SaaS 플랫폼 랜딩 페이지",
            "필라테스 스튜디오 소개 페이지",
            "교육 플랫폼 랜딩 페이지",
            "B2B 솔루션 소개",
            "모바일 앱 출시 페이지"
          ]
      const activeRules = (this.projectRules || []).filter(r => r.active !== false)
      const rulesHint = activeRules.length === 0
        ? `<div style="margin-top:12px; padding:6px 0 6px 12px; border-left:3px solid #6366F1; font-size:12px; color:#6B7280;">작업 규칙을 등록하면 더 맞춤형 콘텐츠를 생성할 수 있어요. <button type="button" class="ba-rules-hint-btn" style="background:none; border:none; color:#6366F1; font-weight:600; cursor:pointer; font-size:12px; font-family:inherit; text-decoration:none; padding:0;">등록하기</button></div>`
        : `<div style="margin-top:12px; padding:6px 0 6px 12px; border-left:3px solid #6366F1; font-size:12px; color:#6B7280;"><span class="ba-rules-active-hint" style="cursor:pointer;">작업 규칙 <strong style="color:#6366F1;">${activeRules.length}개</strong> 적용 중</span></div>`
      const welcomeMsg = this.config.welcomeMessage || _t("welcome.page_default", "안녕하세요! 어떤 페이지를 만들고 싶으신가요?")
      const pageActiveRules = this.activeProjectRules()
      const fullModifyBtn = this.config.designerBridge && pageActiveRules.length > 0
        ? `<div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:10px;"><button type="button" class="ba-quick-btn ba-context-action-btn" data-action-type="rule-based-full-modify">작업 규칙 기반 전체 수정</button></div>`
        : ""
      msgEl.innerHTML = `<p>${this.escapeHtml(welcomeMsg)}</p><p style="margin-top:8px; font-size:13px; color:#6B7280;">빠른 시작:</p><div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:6px;">${quickBtns.map(t => this.quickButtonHtml(t)).join("")}</div>${fullModifyBtn}${rulesHint}`
    } else if (this.config.contextType === "ia_project") {
      const quickBtns = this.config.quickStarts.length > 0
        ? this.config.quickStarts
        : [
            "메뉴 구조를 정리해줘",
            "핵심 페이지를 추천해줘",
            "사용자 흐름을 만들어줘",
            "UI기획용 규칙을 정리해줘",
            "용어를 통일해줘"
          ]
      const activeRules = (this.projectRules || []).filter(r => r.active !== false)
      const rulesHint = activeRules.length === 0
        ? `<div style="margin-top:12px; padding:6px 0 6px 12px; border-left:3px solid #6366F1; font-size:12px; color:#6B7280;">IA 작업 규칙을 등록하면 구조 추천이 더 일관돼요. <button type="button" class="ba-rules-hint-btn" style="background:none; border:none; color:#6366F1; font-weight:600; cursor:pointer; font-size:12px; font-family:inherit; padding:0;">등록하기</button></div>`
        : `<div style="margin-top:12px; padding:6px 0 6px 12px; border-left:3px solid #6366F1; font-size:12px; color:#6B7280;"><span class="ba-rules-active-hint" style="cursor:pointer;">작업 규칙 <strong style="color:#6366F1;">${activeRules.length}개</strong> 적용 중</span></div>`
      const welcomeMsg = this.config.welcomeMessage || _t("welcome.ia_project_default", "안녕하세요! IA 구조를 함께 정리해볼게요.")
      msgEl.innerHTML = `<p>${this.escapeHtml(welcomeMsg)}</p><p style="margin-top:8px; font-size:13px; color:#6B7280;">빠른 시작:</p><div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:6px;">${quickBtns.map(t => this.quickButtonHtml(t)).join("")}</div>${rulesHint}`
    } else if (this.config.designerBridge) {
      // 디자이너 브릿지가 활성화된 섹션 편집에서는 context guide가 단독 카드로 역할
      // welcome message를 별도로 렌더링하지 않고 renderContextGuide()에 위임
      this.renderContextGuide()
      this.scrollToBottom()
      return
    } else if (this.config.contextType === "email_compose") {
      const quickBtns = this.config.quickStarts.length > 0
        ? this.config.quickStarts
        : [
            { text: "업무 협조 요청", prompt: "업무 협조를 요청하는 이메일을 작성해줘" },
            { text: "미팅 일정 조율", prompt: "미팅 일정을 조율하는 이메일을 작성해줘" },
            { text: "프로젝트 진행 보고", prompt: "프로젝트 진행 상황을 보고하는 이메일을 작성해줘" },
            { text: "감사 인사", prompt: "도움에 감사 인사를 전하는 이메일을 작성해줘" },
            { text: "사과 이메일", prompt: "실수에 대해 사과하는 이메일을 작성해줘" },
            { text: "제휴 제안", prompt: "새로운 제휴를 제안하는 이메일을 작성해줘" }
          ]
      const welcomeMsg = this.config.welcomeMessage || _t("welcome.email_default", "안녕하세요! 어떤 이메일을 작성해드릴까요?")
      msgEl.innerHTML = `<p>${this.escapeHtml(welcomeMsg)}</p><p style="margin-top:8px; font-size:13px; color:#6B7280;">어떤 이메일을 작성할까요?</p><div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:6px;">${quickBtns.map(t => this.quickButtonHtml(t)).join("")}</div>`
    } else if (this.config.contextType === "coldmail_compose") {
      const quickBtns = this.config.quickStarts.length > 0
        ? this.config.quickStarts
        : [
            { text: "첫 홍보 메일 작성", prompt: "잠재고객에게 보내는 첫 홍보 메일을 작성해줘. 짧고 개인화된 톤으로." },
            { text: "후속 메일 작성", prompt: "회신이 없는 잠재고객에게 보내는 후속 메일을 작성해줘. 부담스럽지 않게." },
            { text: "시퀀스 3단계 생성", prompt: "홍보 메일 시퀀스 3단계(첫 연락, 후속, 마지막)를 한번에 작성해줘." },
            { text: "제목 최적화", prompt: "오픈율을 높이기 위한 홍보 메일 제목 5개를 제안해줘." },
            { text: "답장 작성", prompt: "잠재고객의 회신에 대한 답장을 작성해줘." }
          ]
      const welcomeMsg = this.config.welcomeMessage || _t("welcome.coldmail_default", "홍보 메일 전문가입니다. 개인화된 아웃바운드 이메일을 작성해드릴게요.")
      msgEl.innerHTML = `<p>${this.escapeHtml(welcomeMsg)}</p><p style="margin-top:8px; font-size:13px; color:#6B7280;">어떤 홍보 메일을 작성할까요?</p><div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:6px;">${quickBtns.map(t => this.quickButtonHtml(t)).join("")}</div>`
    } else if (this.isQuotationWorkspaceContext()) {
      const quickBtns = this.config.quickStarts.length > 0
        ? this.config.quickStarts
        : [
            { text: "홈페이지 제작", prompt: "홈페이지 제작 견적서를 샘플 양식처럼 기획/디자인/개발, 국문/영문, 투입 인원, 투입 월, 월단가, VAT 제외 금액 기준으로 작성해줘" },
            { text: "랜딩페이지", prompt: "랜딩페이지 제작 견적서를 샘플 양식처럼 투입 인원, 투입 월, 월단가, VAT 제외 금액 기준으로 작성해줘" },
            { text: "커머스", prompt: "커머스 구축 견적서를 샘플 양식처럼 투입 인원, 투입 월, 월단가, VAT 제외 금액 기준으로 작성해줘" },
            { text: "SaaS", prompt: "SaaS MVP 개발 견적서를 샘플 양식처럼 투입 인원, 투입 월, 월단가, VAT 제외 금액 기준으로 작성해줘" },
            { text: "UI/UX", prompt: "UI/UX 디자인 견적서를 샘플 양식처럼 투입 인원, 투입 월, 월단가, VAT 제외 금액 기준으로 작성해줘" },
            { text: "유지보수", prompt: "월 유지보수 견적서를 샘플 양식처럼 투입 인원, 투입 월, 월단가, VAT 제외 금액 기준으로 작성해줘" },
            { text: "마케팅 캠페인", prompt: "마케팅 캠페인 운영 견적서를 샘플 양식처럼 투입 인원, 투입 월, 월단가, VAT 제외 금액 기준으로 작성해줘" }
          ]
      const welcomeMsg = this.config.welcomeMessage || "요청사항을 VAT 제외 기준의 홈페이지/개발 견적서 양식으로 정리해드릴게요."
      msgEl.innerHTML = `<p><strong>어떤 견적서를 작성할까요?</strong></p><p style="margin-top:8px; font-size:13px; color:#6B7280;">${this.escapeHtml(welcomeMsg)}</p><p style="margin-top:6px; font-size:13px; color:#6B7280;">서비스 유형을 고르거나 요청사항을 적어주시면 내역, 기간, 투입 인원, 투입 월, 월단가, VAT 제외 금액 기준으로 견적서를 정리합니다.</p><div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:10px;">${quickBtns.map(t => this.quickButtonHtml(t)).join("")}</div>`
    } else if (this.isPlanningWorkspaceContext()) {
      const stageKey = this.config.contextType.replace(/^planning_/, "")
      const stageConfigs = {
        idea_interview: {
          welcome: "아이디어 인터뷰 시작하기",
          helper: "어떤 서비스·사이트를 만들고 싶은지 한 줄로 적어주세요. 도메인에 맞는 질문을 자동으로 준비해드려요.",
          quickBtns: [
            { text: "홈페이지", prompt: "홈페이지를 만들고 싶어요." },
            { text: "커머스", prompt: "커머스 사이트를 만들고 싶어요." },
            { text: "SaaS", prompt: "SaaS 서비스를 만들고 싶어요." },
            { text: "커뮤니티", prompt: "커뮤니티 서비스를 만들고 싶어요." },
            { text: "온보딩", prompt: "온보딩 흐름을 만들고 싶어요." },
            { text: "배달앱", prompt: "배달앱 서비스를 만들고 싶어요." },
            { text: "예약 서비스", prompt: "예약 서비스를 만들고 싶어요." },
            { text: "구독 서비스", prompt: "구독 서비스를 만들고 싶어요." },
            { text: "교육/강의", prompt: "교육/강의 서비스를 만들고 싶어요." },
            { text: "포트폴리오", prompt: "포트폴리오 사이트를 만들고 싶어요." },
            { text: "예약+커머스", prompt: "예약과 커머스를 함께 제공하는 서비스를 만들고 싶어요." },
            { text: "뉴스레터/미디어", prompt: "뉴스레터/미디어 서비스를 만들고 싶어요." },
            { text: "체험단 플랫폼", prompt: "체험단 플랫폼을 만들고 싶어요." }
          ]
        },
        idea_memo: {
          welcome: "아이디어를 메모로 정리해드릴게요.",
          helper: "문제, 대상 고객, 해결 방식, 기대 효과 4가지 항목을 정리할 수 있어요.",
          quickBtns: [
            { text: "아이디어 메모 작성", prompt: "이 아이디어에 대한 메모를 작성해줘. 문제, 대상 고객, 해결 방식, 기대 효과 4가지 항목을 모두 포함해서 저장해줘." },
            { text: "인터뷰 기반 정리", prompt: "아이디어 인터뷰 답변을 바탕으로 아이디어 메모를 완성해줘. 문제, 대상 고객, 해결 방식, 기대 효과 4가지를 모두 채워서 저장해줘." },
            { text: "문제 정의 보완", prompt: "현재 아이디어 메모의 문제 항목을 더 구체적으로 보완해줘." }
          ]
        },
        lean_canvas: {
          welcome: "린 캔버스를 작성해드릴게요.",
          helper: "9개 항목을 채우면 비즈니스 가설을 한눈에 정리할 수 있어요.",
          quickBtns: [
            { text: "린 캔버스 초안 작성", prompt: "프로젝트 정보를 바탕으로 린 캔버스 초안을 작성해줘." },
            { text: "문제/고객 세그먼트 정리", prompt: "린 캔버스의 문제와 고객 세그먼트 항목을 구체적으로 작성해줘." },
            { text: "가치 제안 작성", prompt: "린 캔버스의 고유한 가치 제안(UVP)을 작성해줘." },
            { text: "수익 구조 정리", prompt: "린 캔버스의 수익 흐름과 비용 구조를 정리해줘." }
          ]
        },
        positioning: {
          welcome: "서비스 포지셔닝을 정리해드릴게요.",
          helper: "경쟁사와의 차별점과 타겟 포지션을 명확히 할 수 있어요.",
          quickBtns: [
            { text: "포지셔닝 초안 작성", prompt: "이 서비스의 포지셔닝 초안을 작성해줘." },
            { text: "경쟁사 분석", prompt: "주요 경쟁사와 차별점을 분석해줘." },
            { text: "포지셔닝 한 줄 정리", prompt: "이 서비스의 포지셔닝을 한 문장으로 정리해줘." }
          ]
        },
        service_name: {
          welcome: "서비스 네이밍을 도와드릴게요.",
          helper: "Fiverr, Spotify, Airbnb처럼 조어 브랜드를 만듭니다. Together나 House 같은 설명형은 쓰지 않아요.",
          quickBtns: [
            { text: "네이밍 대화 시작", prompt: "서비스 네이밍을 시작하겠습니다. 이전 단계에서 정리한 내용을 확인했어요. 기본은 Spotify/Fiverr형 조어입니다. 그 방향이 맞는지 한 가지만 여쭤볼게요." },
            { text: "바로 후보 제안받기", prompt: "지금까지 정리된 아이디어, 포지셔닝, 타겟 정보를 바탕으로 바로 서비스명 후보를 제안하고 탭에 저장해줘. Spotify, Fiverr, Airbnb, Qobuz처럼 사전에 없는 조어/합성/스펠링 변형을 5개 이상 내고, Together, Live+, House, Hall, Foyer, Encore Room 같은 설명형은 폐기해. 추천 가칭 하나를 최종 서비스명에 넣어." },
            { text: "다른 스타일로 제안해줘", prompt: "기존 후보와 완전히 다른 조어로 새로운 서비스명 후보를 5개 제안하고 저장해줘. 설명형 이름으로는 후퇴하지 마." }
          ]
        },
        main_target: {
          welcome: "메인 타겟 유저를 만들어드릴게요.",
          helper: "세그먼트가 아니라, 미팅에 들고 갈 사람 3명의 정의서를 만듭니다.",
          quickBtns: [
            { text: "메인 타겟 유저 생성", prompt: "이 서비스의 메인 타겟 유저 페르소나 3명을 정의서 수준으로 만들어 저장해줘. 같은 서울 30대 변주를 만들지 말고, 각자 다른 이용 목적을 갖게 해줘. 써본 기존 제품은 고유명으로 넣고, 각 항목은 장면으로 4개 이상 써줘." },
            { text: "페르소나 추가", prompt: "지금 있는 사람들과 겹치지 않는 서브 타겟 유저 페르소나를 한 명 더 만들어 저장해줘." },
            { text: "유저 니즈 분석", prompt: "메인 타겟 유저의 핵심 니즈와 페인 포인트를 분석해줘." }
          ]
        },
        prd: {
          welcome: "PRD(제품 요구 사항 문서)를 작성해드릴게요.",
          helper: "기능 목록, 우선순위, 범위를 체계적으로 정리할 수 있어요.",
          quickBtns: [
            { text: "PRD 초안 작성", prompt: "지금까지의 기획 내용을 바탕으로 PRD 초안을 작성해줘." },
            { text: "핵심 기능 목록 정리", prompt: "MVP에 포함할 핵심 기능 목록을 우선순위와 함께 정리해줘." },
            { text: "사용자 스토리 작성", prompt: "메인 기능에 대한 사용자 스토리를 작성해줘." }
          ]
        }
      }
      const stage = stageConfigs[stageKey] || {
        welcome: "안녕하세요! 기획을 도와드릴게요.",
        helper: "무엇을 도와드릴까요?",
        quickBtns: []
      }
      // 모든 탭: stageConfigs의 quickBtns 직접 표시
      {
        const welcomeMsg = stage.welcome || this.config.welcomeMessage
        const btns = stage.quickBtns
        const btnHtml = btns.length > 0
          ? `<div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:10px;">${btns.map(t => this.quickButtonHtml(t)).join("")}</div>`
          : ""
        msgEl.innerHTML = `<p>${this.escapeHtml(welcomeMsg)}</p><p style="margin-top:8px; font-size:13px; color:#6B7280;">${this.escapeHtml(stage.helper)}</p>${btnHtml}`
      }
    } else if (typeof this.isKmongServiceDraftAgent === "function" && this.isKmongServiceDraftAgent()) {
      const quickBtns = this.kmongWelcomeQuickButtons()
      if (this.isKmongComparisonReportAgent()) {
        const welcomeMsg = this.config.welcomeMessage || "내 상품과 경쟁상품을 AI 경쟁력 기준으로 평가합니다."
        msgEl.innerHTML = `<p><strong>AI 경쟁력 리포트를 생성할까요?</strong></p><p style="margin-top:8px; font-size:13px; color:#6B7280;">${this.escapeHtml(welcomeMsg)}</p><p style="margin-top:6px; font-size:13px; color:#6B7280;">서비스 생성 모델은 평가 리포트 품질에 반영됩니다. 대표 이미지 생성 모델은 선택하지 않아도 이미지 포함 리포트 분석이 가능하며, 새 대표 이미지를 만들 때만 사용됩니다.</p><div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:10px;">${quickBtns.map(t => this.quickButtonHtml(t)).join("")}</div>`
      } else if (this.threadsAgentMode === "profile") {
        msgEl.innerHTML = `<p><strong>전문가 프로필을 어떻게 정리할까요?</strong></p><p style="margin-top:8px; font-size:13px; color:#6B7280;">크몽 상세 페이지와 등록 초안에 공통으로 쓰는 판매자 정보를 정리합니다. 경력, 전문분야, 보유 기술, 신뢰 포인트를 사실 기반으로 보내주세요.</p><p style="margin-top:6px; font-size:13px; color:#6B7280;">생성된 내용은 전문가 프로필 탭에 저장되고 현재 초안의 판매자 프로필에도 반영됩니다.</p><div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:10px;">${quickBtns.map(t => this.quickButtonHtml(t)).join("")}</div>`
      } else {
        const welcomeMsg = this.config.welcomeMessage || "크몽 서비스 등록 초안을 만들어드릴게요."
        msgEl.innerHTML = `<p><strong>어떤 서비스를 등록할까요?</strong></p><p style="margin-top:8px; font-size:13px; color:#6B7280;">${this.escapeHtml(welcomeMsg)}</p><p style="margin-top:6px; font-size:13px; color:#6B7280;">생성된 내용은 서비스 등록 초안으로 저장됩니다. 이미지 모델을 선택하면 첫 번째 이미지 방향으로 대표 이미지도 생성합니다.</p><div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:10px;">${quickBtns.map(t => this.quickButtonHtml(t)).join("")}</div>`
      }
    } else if (this.config.contextType === "threads_campaign" || (typeof this.isThreadsDraftAgent === "function" && this.isThreadsDraftAgent())) {
      const quickBtns = this.config.quickStarts.length > 0
        ? this.config.quickStarts
        : [
            { text: "오픈 알리기", prompt: "Klavy 서비스 오픈을 알리는 대고객 공개용 Threads 초안을 작성해줘." },
            { text: "다음 홍보글", prompt: "직전 회차와 캠페인 전략을 참고해 Klavy의 다음 홍보용 Threads 초안을 이어서 작성해줘." },
            { text: "사용 사례 소개", prompt: "Klavy가 어떤 상황에서 도움이 되는지 사용 사례 중심으로 Threads 초안을 작성해줘." },
            { text: "무료 체험 안내", prompt: "카드 입력 없이 무료 프로젝트로 Klavy를 먼저 체험할 수 있다는 내용을 중심으로 Threads 초안을 작성해줘." }
          ]
      const welcomeMsg = this.config.welcomeMessage || "Klavy 서비스 홍보용 Threads 초안을 만들어드릴게요."
      msgEl.innerHTML = `<p><strong>어떤 Threads 초안을 만들까요?</strong></p><p style="margin-top:8px; font-size:13px; color:#6B7280;">${this.escapeHtml(welcomeMsg)}</p><p style="margin-top:6px; font-size:13px; color:#6B7280;">생성된 글은 초안으로 저장되고, 실제 발행은 카드의 발행 버튼에서 승인합니다. “핵심 전략을 수정해줘”처럼 요청하면 새 초안 대신 상단 전략 문서만 갱신합니다.</p><div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:10px;">${quickBtns.map(t => this.quickButtonHtml(t)).join("")}</div>`
    } else if (this.config.contextType === "facebook_ads_campaign" || (typeof this.isFacebookAdsDraftAgent === "function" && this.isFacebookAdsDraftAgent())) {
      const quickBtns = this.config.quickStarts.length > 0
        ? this.config.quickStarts
        : [
            { text: "Klavy 문의 유도", prompt: "Klavy 상담 문의를 유도하는 Facebook 단일 이미지 광고 소재를 만들어줘." },
            { text: "Klavy 무료 체험", prompt: "Klavy 무료 체험을 알리는 Facebook 단일 이미지 광고 소재를 만들어줘." },
            { text: "ValueLabs 상담", prompt: "ValueLabs 상담 문의를 유도하는 Facebook 단일 이미지 광고 소재를 만들어줘." },
            { text: "전환 개선 버전", prompt: "문의 전환을 높이는 방향으로 Facebook 광고 소재를 다시 만들어줘." },
            { text: "상품명형 대표 이미지", prompt: "현재 광고 소재의 카피는 유지하고 Klavy 서비스와 산출물이 한눈에 보이는 상품명형 Facebook 대표 이미지만 새로 생성해줘.", actionType: "facebook-ads-main-image", facebookImageMode: "product" },
            { text: "문제제기형 대표 이미지", prompt: "현재 광고 소재의 카피는 유지하고 타겟 고객의 기획 병목 문제가 먼저 느껴지는 문제제기형 Facebook 대표 이미지만 새로 생성해줘.", actionType: "facebook-ads-main-image", facebookImageMode: "problem" }
          ]
      const welcomeMsg = this.config.welcomeMessage || "Facebook Ads Manager에 수동 등록할 광고 소재를 만들어드릴게요."
      msgEl.innerHTML = `<p><strong>어떤 Facebook 광고 소재를 만들까요?</strong></p><p style="margin-top:8px; font-size:13px; color:#6B7280;">${this.escapeHtml(welcomeMsg)}</p><p style="margin-top:6px; font-size:13px; color:#6B7280;">생성된 소재는 카드로 저장됩니다. 이미지 모델을 선택하면 정방형 단일 이미지 광고용 이미지를 함께 만들고, 카드는 Ads Manager 등록용 복사 항목을 제공합니다.</p><div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:10px;">${quickBtns.map(t => this.quickButtonHtml(t)).join("")}</div>`
    } else if (this.isColdmailCampaignContext()) {
      const isPersistedColdmailCampaign = Number(this.config.resourceId || 0) > 0
      const quickBtns = this.config.quickStarts.length > 0
        ? this.config.quickStarts
        : isPersistedColdmailCampaign
          ? [
              { text: "첫 메일 작성", prompt: "한국 에이전시 200곳에 보낼 첫 번째 홍보 메일을 작성해줘. Klavy가 아이디어를 화면으로 만드는 서비스라는 점을 자연스럽게 소개해줘" },
              { text: "3단계 시퀀스", prompt: "한국 에이전시 200곳에 보낼 첫 메일과 후속 2개를 포함한 3단계 콜드 메일 시퀀스를 만들어줘" },
              { text: "에이전시 후속", prompt: "에이전시 대표가 회신하지 않았을 때 보낼 부담 없는 후속 메일 2개를 만들어줘" },
              { text: "발송 준비 보완", prompt: "이 캠페인의 발송 준비 상태를 보고 부족한 항목과 다음 행동을 정리해줘" }
            ]
          : [
            { text: "에이전시 200곳", prompt: "한국에 있는 에이전시 200곳에 먼저 보낼 콜드 메일 캠페인 기본 설정을 채워줘. 안전한 발송량과 업무 시간 기준으로 설정해줘" },
            { text: "제작사 대표", prompt: "웹/앱 제작사 대표에게 보낼 홍보 메일 캠페인 설정을 채워줘. 첫 발송은 부담 없는 소개 톤으로 잡아줘" },
            { text: "디자인 스튜디오", prompt: "브랜딩·UI 디자인 스튜디오 대상 콜드 메일 캠페인 설정을 채워줘" },
            { text: "파트너 제안", prompt: "국내 에이전시와 협업 파트너십을 제안하는 홍보 메일 캠페인 설정을 채워줘" }
          ]
      const welcomeMsg = this.config.welcomeMessage || "홍보 메일 캠페인 설정을 같이 채워드릴게요."
      const coldmailTitle = isPersistedColdmailCampaign ? "어떤 이메일 시퀀스를 만들까요?" : "어떤 캠페인을 만들까요?"
      const coldmailHelp = isPersistedColdmailCampaign
        ? "생성된 메일 단계는 현재 캠페인 상세 화면에 바로 추가됩니다. 대상 리스트가 아직 없으면 먼저 리스트를 만든 뒤 연결할 수 있어요."
        : "생성된 설정은 왼쪽 캠페인 생성 폼에 바로 적용됩니다. 리드 리스트가 아직 없으면 먼저 리스트를 만든 뒤 연결할 수 있어요."
      msgEl.innerHTML = `<p><strong>${this.escapeHtml(coldmailTitle)}</strong></p><p style="margin-top:8px; font-size:13px; color:#6B7280;">${this.escapeHtml(welcomeMsg)}</p><p style="margin-top:6px; font-size:13px; color:#6B7280;">${this.escapeHtml(coldmailHelp)}</p><div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:10px;">${quickBtns.map(t => this.quickButtonHtml(t)).join("")}</div>`
    } else if (this.isEnglishSpeakingTopicWriterAgent()) {
      const quickBtns = this.config.quickStarts.length > 0
        ? this.config.quickStarts
        : [
            { text: "기초 회화 주제 만들기", prompt: "기초 영어 회화 초보자가 바로 연습할 수 있는 주제를 만들어줘. 제목, 카테고리, 시험 목표, 레벨, AI 코치 시나리오, 예시 질문 3개, 성공 기준 3개를 모두 채워줘." },
            { text: "OPIC 고득점 주제", prompt: "OPIC 고득점을 목표로 하는 영어 스피킹 주제를 만들어줘. 후속 질문이 자연스럽게 이어지고 과거/현재 비교, 구체적 사례, 감정 표현을 유도하도록 구성해줘." },
            { text: "TOEIC Speaking 주제", prompt: "TOEIC Speaking 대비용 영어 스피킹 주제를 만들어줘. 답변 구조, 세부 묘사, 이유 제시를 연습할 수 있게 구성해줘." },
            { text: "현재 주제 보강", prompt: "현재 입력된 영어 스피킹 주제를 더 실전적으로 보강해줘. AI 코치 시나리오, 예시 질문, 성공 기준을 더 명확하게 다듬어줘." }
          ]
      const pageTitle = this.config.pageTitle || "영어 스피킹 주제"
      const welcomeMsg = this.config.welcomeMessage || "영어 스피킹 주제, AI 코치 시나리오, 예시 질문, 성공 기준을 함께 만들 수 있어요."
      msgEl.innerHTML = `<p><strong>${this.escapeHtml(pageTitle)}</strong> 주제를 어떻게 작성할까요?</p><p style="margin-top:8px; font-size:13px; color:#6B7280;">${this.escapeHtml(welcomeMsg)}</p><div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:10px;">${quickBtns.map(t => this.quickButtonHtml(t)).join("")}</div>`
    } else {
      const sectionTypeLabels = {
        hero: _t("section_types.hero", "히어로"),
        problem: _t("section_types.problem", "문제/고민 제시"),
        solution: _t("section_types.solution", "솔루션/기능"),
        benefits: _t("section_types.benefits", "혜택/장점"),
        social_proof: _t("section_types.social_proof", "사회적 증거/후기"),
        pricing: _t("section_types.pricing", "가격표"),
        faq: _t("section_types.faq", "자주 묻는 질문"),
        cta: "CTA",
        lead_form: _t("section_types.lead_form", "리드 수집 폼"),
        custom_html: _t("section_types.custom_html", "커스텀 HTML"),
        ai_block: _t("section_types.ai_block", "AI 블록")
      }
      const sectionLabel = this.config.sectionTitle || sectionTypeLabels[this.config.sectionType] || this.config.sectionType || _t("designer.no_section_selected_fallback", "섹션")
      const sectionHeading = /섹션$/.test(sectionLabel) ? sectionLabel : `${sectionLabel} 섹션`
      const pageTitle = this.config.pageTitle || "페이지"
      const aiBlockQuickPrompts = this.config.sectionType === "ai_block"
        ? { prompts: ["서비스 소개 섹션 만들어줘", "이용방법 안내 섹션 만들어줘", "회사 연혁/소개 섹션 만들어줘", "쇼핑몰 상품 상세 섹션 만들어줘", "팀 소개 섹션 만들어줘", "CTA 섹션 만들어줘", "프로모션 배너 섹션 만들어줘"], helperText: "어떤 HTML 섹션을 만들까요? 직접 설명해도 됩니다." }
        : null
      const sectionQuickActions = aiBlockQuickPrompts || (this.config.quickStarts.length > 0
        ? { prompts: this.config.quickStarts, helperText: "" }
        : this.sectionQuickPrompts(sectionLabel))
      const helperText = sectionQuickActions.helperText
        ? `<p style="margin-top:8px; font-size:13px; color:#6B7280;">${this.escapeHtml(sectionQuickActions.helperText)}</p>`
        : ""
      msgEl.innerHTML = `<p>이 <strong>${this.escapeHtml(sectionHeading)}</strong>에 어떤 내용을 넣을까요? (${this.escapeHtml(pageTitle)})</p>${helperText}<div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:10px;">${sectionQuickActions.prompts.map(t => this.quickButtonHtml(t)).join("")}</div>`
    }

    if (this._messages) {
      if (this.isEnglishSpeakingTopicWriterAgent()) {
        this._messages.prepend(msgEl)
      } else {
        this._messages.appendChild(msgEl)
      }
    }

    // 퀵 버튼 이벤트 바인딩
    msgEl.querySelectorAll(".ba-quick-btn").forEach(btn => {
      btn.addEventListener("click", () => {
        if (btn.dataset.actionType === "kmong-new-service-brief-start") {
          this.startKmongNewServiceBrief({ echo: true }).catch(error => console.error(error))
          return
        }
        if (btn.dataset.actionType === "kmong-comparison-report-create") {
          const prompt = btn.dataset.text || "개선 리포트 생성"
          this.pendingKmongComparisonAnalysisMode = btn.dataset.comparisonAnalysisMode || null
          if (this._input) this._input.value = prompt
          this.send().catch(error => console.error(error))
          return
        }
        if (btn.dataset.actionType === "kmong-comparison-preview") {
          this.previewKmongComparisonPatchFromButton(btn)
          return
        }
        if (btn.dataset.actionType === "kmong-comparison-main-image") {
          this.createKmongComparisonMainImage({ main_image_mode: btn.dataset.mainImageMode || "product" })
          return
        }
        if (btn.dataset.actionType === "kmong-comparison-detail-images") {
          this.requestKmongComparisonDetailImages()
          return
        }
        if (btn.dataset.actionType === "kmong-seller-profile-image") {
          this.createKmongSellerProfileImage(btn.dataset.text)
          return
        }
        if (btn.dataset.actionType === "kmong-seller-profile-generate") {
          this.updateKmongSellerProfile(btn.dataset.text, { comparisonReportId: this.kmongComparisonReportId() })
          return
        }
        if (btn.dataset.actionType === "facebook-ads-main-image") {
          this.createFacebookAdsAgentDraft(btn.dataset.text || "Facebook 대표 이미지 생성", {
            imageOnly: true,
            imageMode: btn.dataset.facebookImageMode || "problem"
          }).catch(error => console.error(error))
          return
        }
        if (btn.dataset.actionType === "rule-based-full-modify") {
          this.sendDesignerFullModifyPrompt()
          return
        }
        if (this._input) this._input.value = btn.dataset.text
        this.send()
      })
    })

    // 작업 규칙 등록하기 버튼
    const rulesHintBtn = msgEl.querySelector(".ba-rules-hint-btn")
    if (rulesHintBtn) {
      rulesHintBtn.addEventListener("click", () => this.toggleRules())
    }

    // 작업 규칙 N개 적용 중 클릭 → 룰 패널 열기
    const rulesActiveHint = msgEl.querySelector(".ba-rules-active-hint")
    if (rulesActiveHint) {
      rulesActiveHint.addEventListener("click", () => this.toggleRules())
    }

    this.renderContextGuide()
    this.scrollToBottom()
  }

  buildDesignerSectionGuideHtml() {
    if (!this.config.designerBridge) return ""

    const selection = this.designerSelection
    const pageTitle = this.config.pageTitle || "페이지"

    if (!this.designerSelectionActivated) {
      const canActivateSection = selection?.selectedSurface === "section" && !!selection?.clientKey
      const activeRules = this.activeProjectRules()
      const actions = [
        `<button type="button" class="ba-quick-btn ba-context-action-btn" data-action-type="page-overall-refine">현재 페이지 전체 톤에 맞게 다듬기</button>`
      ]
      if (canActivateSection) {
        actions.push(`<button type="button" class="ba-quick-btn ba-context-action-btn" data-action-type="activate-first-section">첫 번째 섹션부터 수정하기</button>`)
      }
      return `<p>페이지 전체 방향을 먼저 다듬거나, 바로 첫 번째 섹션부터 수정할 수 있어요. (${this.escapeHtml(pageTitle)})</p><p style="margin-top:8px; font-size:13px; color:#6B7280;">페이지 전체를 먼저 정리한 뒤 개별 섹션으로 내려가도 되고, 바로 섹션 편집부터 시작해도 됩니다.</p><div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:10px;">${actions.join("")}</div>`
    }

    if (!selection) return ""
    if (selection.selectedSurface === "section") {
      const sectionLabel = selection.label || selection.sectionType || "섹션"
      const isHero = selection.sectionType === "hero"
      const isPageContext = this.config.contextType === "page"

      let quickBtnsHtml
      if (isHero && !isPageContext) {
        // 섹션 편집 페이지: 히어로 전용 (섹션 단위)
        const contentOnlyPrompt = "이 섹션 내용 생성해줘"
        const contentAndStylePrompt = "히어로 섹션의 내용을 페이지에 맞게 생성하고, 가장 어울리는 히어로 스타일도 함께 선택해줘. style_variant 필드를 JSON에 반드시 포함해."
        const refinePrompt = "현재 페이지 톤에 맞게 다듬어줘"
        quickBtnsHtml = [
          this.quickButtonHtml(contentOnlyPrompt),
          `<button type="button" class="ba-quick-btn" data-text="${this.escapeHtml(contentAndStylePrompt)}">내용 + 스타일 한번에</button>`,
          this.quickButtonHtml(refinePrompt)
        ].join("")
      } else if (isPageContext) {
        // 페이지 디자이너 모드: 전체 섹션 대상
        const sectionQuickActions = this.sectionQuickPrompts(sectionLabel, { designer: true })
        quickBtnsHtml = sectionQuickActions.prompts.map(t => this.quickButtonHtml(t)).join("")
      } else {
        const sectionQuickActions = this.sectionQuickPrompts(sectionLabel, { designer: true })
        quickBtnsHtml = sectionQuickActions.prompts.map(t => this.quickButtonHtml(t)).join("")
      }

      const helperText = isHero && !isPageContext
        ? `<p style="margin-top:8px; font-size:13px; color:#6B7280;">선택한 섹션에 어떤 내용을 넣을까요?</p>`
        : (() => {
            const sectionQuickActions = this.sectionQuickPrompts(sectionLabel, { designer: true })
            return sectionQuickActions.helperText
              ? `<p style="margin-top:8px; font-size:13px; color:#6B7280;">${this.escapeHtml(sectionQuickActions.helperText)}</p>`
              : `<p style="margin-top:8px; font-size:13px; color:#6B7280;">선택한 섹션에 어떤 내용을 넣을까요?</p>`
          })()
      return `<p><strong>${this.escapeHtml(sectionLabel)}</strong> 섹션을 편집 중이에요. (${this.escapeHtml(pageTitle)})</p>${helperText}<div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:6px;">${quickBtnsHtml}</div><div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:10px;"><button type="button" class="ba-quick-btn ba-context-action-btn" data-action-type="page-overall-refine">페이지 전체 수정하기</button></div>`
    }

    const surfaceLabel = selection.selectedSurface === "header" ? _t("designer.header", "헤더") : _t("designer.footer", "푸터")
    return `<p><strong>${this.escapeHtml(surfaceLabel)}</strong>가 선택되어 있어요. (${this.escapeHtml(pageTitle)})</p><p style="margin-top:8px; font-size:13px; color:#6B7280;">섹션을 선택하면 그 섹션 기준으로 바로 도와드릴게요. 페이지 전체 방향을 먼저 다듬는 것도 가능합니다.</p><div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:10px;"><button type="button" class="ba-quick-btn ba-context-action-btn" data-action-type="page-overall-refine">페이지 전체 수정하기</button></div>`
  }

  upsertContextGuide(html) {
    if (!this._messages) return

    let guide = this._messages.querySelector(".ba-context-guide")
    if (!html) {
      guide?.remove()
      return
    }

    if (!guide) {
      guide = document.createElement("div")
      guide.className = "ba-msg-assistant ba-context-guide"
    }

    if (this.chatHistory.length === 0) {
      const welcome = this._messages.querySelector(".ba-welcome-message")
      if (welcome?.nextSibling) {
        this._messages.insertBefore(guide, welcome.nextSibling)
      } else if (welcome) {
        this._messages.appendChild(guide)
      } else {
        this._messages.prepend(guide)
      }
    } else {
      this._messages.appendChild(guide)
    }

    guide.innerHTML = html
    guide.querySelectorAll(".ba-quick-btn").forEach(btn => {
      btn.addEventListener("click", () => {
        if (btn.dataset.actionType === "kmong-new-service-brief-start") {
          this.startKmongNewServiceBrief({ echo: true }).catch(error => console.error(error))
          return
        }
        if (btn.dataset.actionType === "kmong-comparison-preview") {
          this.previewKmongComparisonPatchFromButton(btn)
          return
        }
        if (btn.dataset.actionType === "kmong-comparison-main-image") {
          this.createKmongComparisonMainImage({ main_image_mode: btn.dataset.mainImageMode || "product" })
          return
        }
        if (btn.dataset.actionType === "kmong-comparison-detail-images") {
          this.requestKmongComparisonDetailImages()
          return
        }
        if (btn.dataset.actionType === "kmong-seller-profile-image") {
          this.createKmongSellerProfileImage(btn.dataset.text)
          return
        }
        if (btn.dataset.actionType === "kmong-seller-profile-generate") {
          this.updateKmongSellerProfile(btn.dataset.text, { comparisonReportId: this.kmongComparisonReportId() })
          return
        }
        if (btn.dataset.actionType === "facebook-ads-main-image") {
          this.createFacebookAdsAgentDraft(btn.dataset.text || "Facebook 대표 이미지 생성", {
            imageOnly: true,
            imageMode: btn.dataset.facebookImageMode || "problem"
          }).catch(error => console.error(error))
          return
        }
        if (btn.dataset.actionType === "activate-first-section") {
          this.activateFirstDesignerSection()
          return
        }
        if (btn.dataset.actionType === "page-overall-refine") {
          this.sendDesignerPageLevelPrompt("현재 페이지 전체 톤에 맞게 다듬어줘")
          return
        }
        if (btn.dataset.actionType === "rule-based-full-modify") {
          this.sendDesignerFullModifyPrompt()
          return
        }
        if (this._input) this._input.value = btn.dataset.text
        this.send()
      })
    })
  }

  sendDesignerPageLevelPrompt(message) {
    if (!message) return
    if (this._input) this._input.value = message
    this.designerPromptScope = "page"
    return Promise.resolve(this.send()).finally(() => {
      this.designerPromptScope = null
    })
  }

  sendDesignerFullModifyPrompt() {
    const sectionTypeLabels = {
      hero: _t("profile_section_types.hero", "히어로"), about: _t("profile_section_types.about", "소개"), experience: _t("profile_section_types.experience", "경력"), projects: _t("profile_section_types.projects", "프로젝트"),
      skills: _t("profile_section_types.skills", "스킬"), contact: _t("profile_section_types.contact", "연락처"), education: _t("profile_section_types.education", "학력"), services: _t("profile_section_types.services", "서비스"),
      testimonials: _t("profile_section_types.testimonials", "후기"), stats: _t("profile_section_types.stats", "성과"), gallery: _t("profile_section_types.gallery", "갤러리"), clients: _t("profile_section_types.clients", "클라이언트"),
      awards: _t("profile_section_types.awards", "수상"), faq: "FAQ", cta: "CTA", custom_html: _t("profile_section_types.custom_html", "커스텀"),
      block_text: _t("profile_section_types.block_text", "텍스트"), block_image: _t("profile_section_types.block_image", "이미지"), block_image_text: _t("profile_section_types.block_image_text", "이미지+텍스트"),
      block_columns: _t("profile_section_types.block_columns", "컬럼"), block_cards: _t("profile_section_types.block_cards", "카드"), block_banner: _t("profile_section_types.block_banner", "배너")
    }
    let sections
    if (Array.isArray(this.config.existingSections)) {
      sections = this.config.existingSections
    } else {
      try { sections = JSON.parse(this.config.existingSections || "[]") } catch(e) { sections = [] }
    }
    if (sections.length === 0) {
      this.appendSystemNote(_t("system_notes.no_section_to_edit", "수정할 섹션이 없습니다."))
      return
    }
    const sectionsList = sections.map((s, i) => {
      const label = sectionTypeLabels[s.type] || s.type
      const extra = s.type === "hero" ? " → style_variant도 함께 선택" : ""
      return `${i+1}. ${s.title || label} (타입: ${s.type})${extra}`
    }).join("\n")

    const prompt = `작업 규칙을 기반으로 이 페이지의 모든 섹션 내용을 한번에 생성해줘.

페이지 섹션 목록:
${sectionsList}

각 섹션의 내용을 작업 규칙에 맞게 작성하되, 히어로 섹션은 가장 어울리는 style_variant도 포함해줘.`

    if (this._input) this._input.value = prompt
    this.designerPromptScope = "all_sections"
    return Promise.resolve(this.send()).finally(() => {
      this.designerPromptScope = null
    })
  }

  activateFirstDesignerSection() {
    if (!this.config.designerBridge) return

    if (this.designerSelection?.selectedSurface === "section" && this.designerSelection?.clientKey) {
      this.designerSelectionActivated = true
      this.renderContextGuide()
    }

    this.activateDesignerSection(this.designerSelection?.clientKey || null)
  }

  activateDesignerSection(clientKey) {
    if (!this.config.designerBridge) return

    const controllerElement = document.querySelector('[data-controller~="profile-page-designer"]')
    const designerController = window.Stimulus?.getControllerForElementAndIdentifier(
      controllerElement,
      "profile-page-designer"
    )

    if (designerController) {
      const targetSection = clientKey
        ? designerController.sectionByKey(clientKey)
        : designerController.sections.find((section) => !section._destroy)
      if (!targetSection) return

      this.designerSelectionActivated = true
      this.designerSelection = this.normalizeDesignerSelection({
        selectedSurface: "section",
        clientKey: targetSection.client_key,
        id: targetSection.id,
        sectionType: targetSection.section_type,
        title: targetSection.title,
        contentData: targetSection.content_data || {},
        fieldSchema: designerController.fieldSchemaFor(targetSection.section_type),
        label: designerController.labelFor(targetSection.section_type),
        visible: !!targetSection.visible,
        interactionSource: "user"
      })
      this.renderContextGuide()

      designerController.selectedSurface = "section"
      designerController.selectedKey = targetSection.client_key
      designerController.renderInspector()
      designerController.applySelectionState()
      designerController.scrollSelectionIntoView()
      designerController.emitSelectionChanged("user")
      this.scheduleMessagesScrollToBottom()
      return
    }

    window.dispatchEvent(new CustomEvent("profile-page-designer:activate-section", {
      detail: { clientKey }
    }))
    this.scheduleMessagesScrollToBottom()
  }

  renderContextGuide() {
    if (!this.config.designerBridge) return
    this.upsertContextGuide(this.buildDesignerSectionGuideHtml())
  }

  scheduleMessagesScrollToBottom() {
    if (!this._messages) return

    this.scrollToBottom()
    window.requestAnimationFrame(() => this.scrollToBottom())
    window.setTimeout(() => this.scrollToBottom(), 120)
  }

  _showSiteAwareWelcome() {
    const siteTitle = this.config.parentSiteTitle || "프로필 사이트"
    const siteId = this.config.parentSiteId

    const msgEl = document.createElement("div")
    msgEl.className = "ba-msg-assistant"

    const quickPages = ["홈 페이지", "소개 페이지", "프로젝트 페이지", "스킬 페이지", "연락처 페이지"]
    const activeRules = (this.projectRules || []).filter(r => r.active !== false)
    const rulesHint = activeRules.length > 0
      ? `<div style="margin-top:12px; padding:6px 0 6px 12px; border-left:3px solid #6366F1; font-size:12px; color:#6B7280;"><span class="ba-rules-active-hint" style="cursor:pointer;">작업 규칙 <strong style="color:#6366F1;">${activeRules.length}개</strong> 적용 중</span></div>`
      : ""

    msgEl.innerHTML = `<p><strong>${this.escapeHtml(siteTitle)}</strong>에 새 페이지를 추가합니다.</p>` +
      `<p style="margin-top:8px; font-size:13px; color:#6B7280;">어떤 페이지를 만들까요?</p>` +
      `<div style="display:flex; flex-wrap:wrap; gap:6px; margin-top:6px;">${quickPages.map(t =>
        `<button type="button" class="ba-quick-btn" data-text="${this.escapeHtml(t)}">${this.escapeHtml(t)}</button>`
      ).join("")}</div>` +
      rulesHint +
      (siteId > 0 ? `<div style="padding:4px 0; margin-top:8px;"><a href="/admin/profile_sites/${siteId}/edit" style="font-size:12px; color:#6366F1; text-decoration:none;">&larr; 사이트로 돌아가기</a></div>` : "")

    if (this._messages) this._messages.appendChild(msgEl)

    msgEl.querySelectorAll(".ba-quick-btn").forEach(btn => {
      btn.addEventListener("click", () => {
        if (this._input) this._input.value = btn.dataset.text
        this.send()
      })
    })

    this.scrollToBottom()
  }

  // ── 작업 규칙 ──────────────────────────────────────────────────────────

  toggleRules() {
    this.rulesOpen = !this.rulesOpen
    localStorage.setItem(`${this.config.storagePrefix}-rules-open`, this.rulesOpen ? "1" : "0")
    if (this._rulesBtn) {
      if (this.rulesOpen) {
        this._rulesBtn.style.borderColor = "#6366F1"
        this._rulesBtn.style.color = "#6366F1"
      } else {
        this.updateRulesIndicator()
        this.exitRulesSplitView()
      }
    }
    this.applyRulesPanelLayout()
  }

  exitRulesSplitView() {
    this.rulesHistoryOpen = false
    if (this._rulesHistoryPanel) this._rulesHistoryPanel.style.display = "none"
    this.setRulesPanelMode("list")
  }

  setRulesPanelMode(mode) {
    const normalizedMode = ["list", "edit_split", "chat_split"].includes(mode) ? mode : "list"
    this.rulesPanelMode = normalizedMode
    this.rulesGenerationMode = normalizedMode === "chat_split"
    if (this.rulesGenerationMode) {
      localStorage.setItem(`${this.config.storagePrefix}-rules-gen`, "1")
    } else {
      localStorage.removeItem(`${this.config.storagePrefix}-rules-gen`)
    }
    this.applyRulesPanelLayout()
  }

  applyRulesPanelLayout() {
    const isChatSplit = this.rulesOpen && this.rulesPanelMode === "chat_split"
    const isEditSplit = this.rulesOpen && this.rulesPanelMode === "edit_split"
    const isList = this.rulesOpen && this.rulesPanelMode === "list"
    const isSplit = isChatSplit || isEditSplit

    if (this._wrapper) this._wrapper.style.width = isSplit ? "760px" : "380px"

    if (this._rulesPanel) {
      this._rulesPanel.style.display = this.rulesOpen ? "flex" : "none"
      this._rulesPanel.style.flexDirection = isSplit ? "row" : "column"
    }

    if (this._rulesListView) {
      this._rulesListView.style.display = (isList || isSplit) ? "flex" : "none"
      this._rulesListView.style.width = isSplit ? "50%" : ""
      this._rulesListView.style.borderRight = isSplit ? "1px solid #E5E7EB" : ""
    }

    if (this._rulesEditView) {
      this._rulesEditView.style.display = isEditSplit ? "flex" : "none"
      this._rulesEditView.style.width = isEditSplit ? "50%" : ""
    }

    if (this._rulesChatView) {
      this._rulesChatView.style.display = isChatSplit ? "flex" : "none"
      this._rulesChatView.style.width = isChatSplit ? "50%" : ""
    }
  }

  async loadProjectRules() {
    if (!this.config.rulesApiPath) {
      this.projectRules = []
      this.renderRulesList()
      this.updateRulesIndicator()
      return
    }
    try {
      const res = await fetch(this.config.rulesApiPath, {
        headers: { "X-Requested-With": "XMLHttpRequest" }
      })
      if (!res.ok) return
      const contentType = res.headers.get("content-type") || ""
      if (!contentType.includes("application/json")) {
        this.projectRules = []
        this.renderRulesList()
        this.updateRulesIndicator()
        return
      }
      const json = await res.json()
      this.projectRules = Array.isArray(json.data) ? json.data : []
      this.renderRulesList()
      this.updateRulesIndicator()
    } catch (e) {
      console.error("[KlavyAgentCore] loadProjectRules failed:", e)
    }
  }

  renderRulesList() {
    if (!this._rulesList) return
    const list = this._rulesList
    const total = this.projectRules.length

    if (total === 0) {
      list.innerHTML = '<div class="ba-rule-empty"><svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg><span>등록된 룰이 없습니다</span><button type="button" class="ba-empty-ai-btn" style="margin-top:10px; height:30px; padding:0 12px; font-size:12px; font-weight:600; background:#6366F1; border:none; color:#fff; cursor:pointer; font-family:inherit; display:flex; align-items:center; gap:5px;"><svg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'><path d=\'m21.64 3.64-1.28-1.28a1.21 1.21 0 0 0-1.72 0L2.36 18.64a1.21 1.21 0 0 0 0 1.72l1.28 1.28a1.2 1.2 0 0 0 1.72 0L21.64 5.36a1.2 1.2 0 0 0 0-1.72\'/><path d=\'m14 7 3 3\'/><path d=\'M5 6v4\'/><path d=\'M19 14v4\'/><path d=\'M10 2v2\'/><path d=\'M7 8H3\'/><path d=\'M21 16h-4\'/><path d=\'M11 3H9\'/></svg>AI로 생성하기</button></div>'
      list.querySelector(".ba-empty-ai-btn")?.addEventListener("click", () => this.startRulesGeneration())
      this.updateBatchBar()
      this.renderRulesPagination()
      return
    }

    const perPage = 10
    const totalPages = Math.ceil(total / perPage)
    if (this.rulesCurrentPage > totalPages) this.rulesCurrentPage = totalPages
    const start = (this.rulesCurrentPage - 1) * perPage
    const end = Math.min(start + perPage, total)
    const pageRules = this.projectRules.slice(start, end)

    list.innerHTML = pageRules.map((rule, localIdx) => {
      const globalIdx = start + localIdx
      const isActive = rule.active !== false
      const isChecked = this.selectedRuleIndices.has(globalIdx)
      return `
        <div class="ba-rule-item${!isActive ? ' ba-rule-item--inactive' : ''}" data-index="${globalIdx}">
          <div style="flex-shrink:0;display:flex;align-items:center;padding-right:8px;" onclick="event.stopPropagation()">
            <label class="ba-chk-label">
              <input type="checkbox" class="ba-chk-input ba-rule-checkbox" data-index="${globalIdx}" ${isChecked ? 'checked' : ''}>
              <span class="ba-chk-box"></span>
            </label>
          </div>
          <div style="min-width:0;flex:1;overflow:hidden;">
            <div class="ba-rule-item-title">${this.escHtml(rule.title)}</div>
            <div class="ba-rule-item-preview">${this.escHtml((rule.content || "").replace(/\n/g, " "))}</div>
          </div>
          <span style="flex-shrink:0;font-size:11px;font-weight:500;padding:2px 6px;margin-right:4px;border:1px solid ${isActive ? '#C7D2FE' : '#E5E7EB'};color:${isActive ? '#4F46E5' : '#9CA3AF'};background:${isActive ? '#EEF2FF' : '#F9FAFB'};">${isActive ? '활성' : '비활성'}</span>
          <button class="ba-rule-delete-btn" data-index="${globalIdx}" type="button">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
          </button>
        </div>
      `
    }).join("")

    // 이벤트 바인딩
    list.querySelectorAll(".ba-rule-item").forEach(item => {
      item.addEventListener("click", (e) => {
        if (e.target.type === 'checkbox') return
        this.editRule(parseInt(item.dataset.index))
      })
    })
    list.querySelectorAll(".ba-rule-checkbox").forEach(cb => {
      cb.addEventListener("change", (e) => {
        e.stopPropagation()
        const idx = parseInt(cb.dataset.index)
        if (cb.checked) this.selectedRuleIndices.add(idx)
        else this.selectedRuleIndices.delete(idx)
        this.updateBatchBar()
      })
    })
    list.querySelectorAll(".ba-rule-delete-btn").forEach(btn => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation()
        const idx = parseInt(btn.dataset.index)
        this.projectRules.splice(idx, 1)
        this.selectedRuleIndices.clear()
        this.renderRulesList()
        this.updateRulesIndicator()
        this.persistRules()
      })
    })

    this.updateBatchBar()
    this.renderRulesPagination()
  }

  updateBatchBar() {
    if (!this.selectedRuleIndices) return
    const count = this.selectedRuleIndices.size
    if (this._rulesBatchBar) {
      this._rulesBatchBar.style.display = count > 0 ? "flex" : "none"
    }
    if (this._rulesBatchCount) {
      this._rulesBatchCount.textContent = _t("status.selected_count", "%{count}개 선택됨").replace("%{count}", count)
    }
    if (this._rulesSelectAll) {
      const total = this.projectRules.length
      const perPage = 10
      const start = (this.rulesCurrentPage - 1) * perPage
      const end = Math.min(start + perPage, total)
      const pageCount = end - start
      let checkedCount = 0
      for (let i = start; i < end; i++) {
        if (this.selectedRuleIndices.has(i)) checkedCount++
      }
      this._rulesSelectAll.checked = pageCount > 0 && checkedCount === pageCount
      this._rulesSelectAll.indeterminate = checkedCount > 0 && checkedCount < pageCount
    }
  }

  renderRulesPagination() {
    if (!this._rulesPaginationEl) return
    const total = this.projectRules.length
    const perPage = 10
    const totalPages = Math.ceil(total / perPage)

    if (total <= perPage) {
      this._rulesPaginationEl.style.display = "none"
      return
    }

    this._rulesPaginationEl.style.display = "flex"
    const cur = this.rulesCurrentPage
    let html = `<button class="ba-rules-page-btn" data-page="${cur - 1}" ${cur <= 1 ? 'disabled' : ''}>&lt;</button>`
    for (let p = 1; p <= totalPages; p++) {
      html += `<button class="ba-rules-page-btn${p === cur ? ' active' : ''}" data-page="${p}">${p}</button>`
    }
    html += `<button class="ba-rules-page-btn" data-page="${cur + 1}" ${cur >= totalPages ? 'disabled' : ''}>&gt;</button>`
    this._rulesPaginationEl.innerHTML = html

    this._rulesPaginationEl.querySelectorAll(".ba-rules-page-btn").forEach(btn => {
      btn.addEventListener("click", (e) => {
        if (e.currentTarget.disabled) return
        const page = parseInt(e.currentTarget.dataset.page)
        const tp = Math.ceil(this.projectRules.length / 10)
        if (isNaN(page) || page < 1 || page > tp) return
        this.selectedRuleIndices.clear()
        this.rulesCurrentPage = page
        this.renderRulesList()
      })
    })
  }

  toggleAllRules(e) {
    const total = this.projectRules.length
    const perPage = 10
    const start = (this.rulesCurrentPage - 1) * perPage
    const end = Math.min(start + perPage, total)
    if (e.target.checked) {
      for (let i = start; i < end; i++) this.selectedRuleIndices.add(i)
    } else {
      for (let i = start; i < end; i++) this.selectedRuleIndices.delete(i)
    }
    this.renderRulesList()
  }

  async applyBatchRuleAction() {
    const action = this._t("rulesBatchSelect")?.value
    const indices = Array.from(this.selectedRuleIndices).sort((a, b) => b - a)
    if (indices.length === 0) return

    if (action === "delete") {
      if (!confirm(_t("confirms.delete_selected_rules", "선택한 %{count}개의 룰을 삭제하시겠습니까?").replace("%{count}", indices.length))) return
      indices.forEach(i => this.projectRules.splice(i, 1))
    } else if (action === "activate") {
      indices.forEach(i => { if (this.projectRules[i]) this.projectRules[i].active = true })
    } else if (action === "deactivate") {
      indices.forEach(i => { if (this.projectRules[i]) this.projectRules[i].active = false })
    }

    this.selectedRuleIndices.clear()
    this.rulesCurrentPage = 1
    this.renderRulesList()
    this.updateRulesIndicator()
    await this.persistRules()
  }

  addRule() {
    this.editingRuleIndex = -1
    if (this._rulesEditLabel) this._rulesEditLabel.textContent = _t("status.add_rule_label", "룰 추가")
    if (this._ruleEditTitle) this._ruleEditTitle.value = ""
    if (this._ruleEditContent) this._ruleEditContent.value = ""
    if (this._ruleEditActive) this._ruleEditActive.checked = true
    if (this._rulesDeleteBtn) this._rulesDeleteBtn.style.display = "none"
    this.showEditView()
  }

  fillSampleRule() {
    const samples = [
      { title: "브랜드 정보", content: "회사명: (회사명 입력)\n업종: (업종 입력)\n핵심 가치: (핵심 가치 입력)\n타깃 고객: (타깃 고객 입력)" },
      { title: "톤 & 보이스", content: "전반적인 톤: 친근하고 전문적\n어조: 존댓말 사용\n금지 표현: 과도한 기술 용어, 영어 약어 남용\n강조할 점: 신뢰감, 따뜻함" },
      { title: "서비스 소개", content: "서비스명: (서비스명 입력)\n핵심 기능: (핵심 기능 입력)\n주요 혜택: (주요 혜택 입력)\n차별점: (경쟁사 대비 차별점 입력)" },
    ]
    const sample = samples[Math.floor(Math.random() * samples.length)]
    if (this._ruleEditTitle && !this._ruleEditTitle.value) this._ruleEditTitle.value = sample.title
    if (this._ruleEditContent && !this._ruleEditContent.value) this._ruleEditContent.value = sample.content
  }

  editRule(idx) {
    const rule = this.projectRules[idx]
    if (!rule) return
    this.editingRuleIndex = idx
    if (this._rulesEditLabel) this._rulesEditLabel.textContent = _t("status.edit_rule_label", "룰 편집")
    if (this._ruleEditTitle) this._ruleEditTitle.value = rule.title || ""
    if (this._ruleEditContent) this._ruleEditContent.value = rule.content || ""
    if (this._ruleEditActive) this._ruleEditActive.checked = rule.active !== false
    if (this._rulesDeleteBtn) this._rulesDeleteBtn.style.display = "flex"
    this.showEditView()
  }

  async deleteCurrentRule() {
    if (this.editingRuleIndex < 0) return
    if (!confirm(_t("confirms.delete_rule", "이 룰을 삭제하시겠습니까?"))) return
    this.projectRules.splice(this.editingRuleIndex, 1)
    this.selectedRuleIndices.clear()
    this.renderRulesList()
    this.updateRulesIndicator()
    this.showListView()
    await this.persistRules()
  }

  backToList() {
    this.showListView()
  }

  showListView() {
    this.setRulesPanelMode("list")
  }

  showEditView() {
    this.setRulesPanelMode("edit_split")
    if (this._ruleEditTitle) this._ruleEditTitle.focus()
  }

  async saveRule() {
    const title = this._ruleEditTitle ? this._ruleEditTitle.value.trim() : ""
    const content = this._ruleEditContent ? this._ruleEditContent.value.trim() : ""
    const active = this._ruleEditActive ? this._ruleEditActive.checked : true
    if (!title) { if (this._ruleEditTitle) this._ruleEditTitle.focus(); return }

    if (this.editingRuleIndex >= 0) {
      this.projectRules[this.editingRuleIndex] = { title, content, active }
    } else {
      this.projectRules.push({ title, content, active })
    }

    this.renderRulesList()
    this.updateRulesIndicator()
    this.showListView()
    await this.persistRules()
  }

  async persistRules() {
    if (!this.config.rulesApiPath) return
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ""
      await fetch(this.config.rulesApiPath, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({ project_rules: this.projectRules })
      })
      // 헤더 팝업에 변경 알림
      document.dispatchEvent(new CustomEvent('project-rules-changed', { detail: { source: 'klavy-agent' } }))
    } catch (e) {
      console.error("[KlavyAgentCore] persistRules failed:", e)
    }
  }

  updateRulesIndicator() {
    const isOn = this.rulesEnabled
    if (this._rulesBtn) {
      this._rulesBtn.style.borderColor = isOn ? "#6366F1" : "#E5E7EB"
      this._rulesBtn.style.background = isOn ? "#EEF2FF" : "transparent"
      const innerBtn = this._rulesBtn.querySelector("button")
      if (innerBtn) innerBtn.style.color = isOn ? "#6366F1" : "#9CA3AF"
    }
    if (this._rulesToggleBtn) {
      if (isOn) {
        this._rulesToggleBtn.textContent = "ON"
        this._rulesToggleBtn.style.color = "#6366F1"
        this._rulesToggleBtn.style.borderColor = "#6366F1"
        this._rulesToggleBtn.style.background = "#EEF2FF"
      } else {
        this._rulesToggleBtn.textContent = "OFF"
        this._rulesToggleBtn.style.color = "#9CA3AF"
        this._rulesToggleBtn.style.borderColor = "#E5E7EB"
        this._rulesToggleBtn.style.background = "transparent"
      }
    }
  }

  toggleRulesEnabled(e) {
    if (e) e.preventDefault()
    this.rulesEnabled = !this.rulesEnabled
    localStorage.setItem(`${this.config.storagePrefix}-rules-enabled`, this.rulesEnabled ? "1" : "0")
    this.updateRulesIndicator()
  }

  // ── 룰 생성 채팅 (RC) ─────────────────────────────────────────────────────

  async startRulesGeneration() {
    if (!this.isOpen) this.toggle()

    if (!this.rulesOpen) {
      this.rulesOpen = true
      localStorage.setItem(`${this.config.storagePrefix}-rules-open`, "1")
      if (this._rulesBtn) {
        this._rulesBtn.style.borderColor = "#6366F1"
        this._rulesBtn.style.color = "#6366F1"
      }
    }

    this.setRulesPanelMode("chat_split")

    if (this.currentRulesSessionId) {
      await this.loadRulesSession(this.currentRulesSessionId)
    } else {
      this.currentRulesSessionId = this.generateSessionId()
      localStorage.setItem(`${this.config.storagePrefix}-rules-session`, this.currentRulesSessionId)
      this.rulesChatHistory = []
      this.showRulesChatGuide()
    }

    if (this._rcModelLabel) this._rcModelLabel.textContent = this._modelLabel?.textContent || "Claude Sonnet 4.6"
    if (this._rcModeLabel) this._rcModeLabel.textContent = this._modeLabel?.textContent || _t("modes.make", "생성")
    if (this._rcModelDropdown) {
      this._rcModelDropdown.querySelectorAll(".ba-model-opt").forEach(el => {
        el.style.background = el.dataset.value === this.selectedModel ? "#EEF2FF" : ""
      })
    }

    if (this._rulesChatInput) this._rulesChatInput.focus()
  }

  showRulesChatGuide() {
    if (!this._rulesChatMessages) return
    this._rulesChatMessages.innerHTML = ""
    const isIaProjectContext = this.config.contextType === "ia_project" || this.config.resourceType === "IaProject"
    const isPlanningWorkspaceContext = this.isPlanningWorkspaceContext()
    const isSectionTemplateContext = this.config.agentSlug === "section-template"
    let quickBtns, guideHtml
    if (isIaProjectContext) {
      quickBtns = ["메뉴 구조 정리", "핵심 페이지 추천", "사용자 흐름 정리", "UI기획용 규칙", "용어 통일"]
      guideHtml = `<p>IA 프로젝트에 맞는 룰을 정리해드릴게요. 메뉴, 페이지, 흐름 기준으로 체계적인 규칙을 만들어보세요.</p><p style="margin-top:8px; font-size:12px; color:#6B7280;">기존 활성 룰이 있으면 적용 전에 교체/삭제/병합 방식을 선택할 수 있어요. IA를 UI기획으로 넘길 때 일관된 용어를 유지하는 것이 핵심입니다.</p>`
    } else if (isPlanningWorkspaceContext) {
      quickBtns = ["인터뷰 반영 기준", "PRD 작성 기준", "타겟 고객 구체화", "문체 통일", "산출물 누락 방지"]
      guideHtml = `<p>기획 워크스페이스에 맞는 작업 규칙을 정리해드릴게요. 인터뷰 답변부터 PRD까지 일관되게 반영할 기준을 만들어보세요.</p><p style="margin-top:8px; font-size:12px; color:#6B7280;">등록된 규칙은 이후 아이디어 메모, 린 캔버스, 포지셔닝, 메인 타겟 유저, PRD 생성 프롬프트에 함께 반영됩니다.</p>`
    } else if (isSectionTemplateContext) {
      quickBtns = ["미니멀 모던", "따뜻한 감성 톤", "다크 테마", "비즈니스/기업용", "강렬한 CTA 중심"]
      guideHtml = `<p>AI 블록 섹션을 어떤 스타일로 생성할지 기준을 등록해주세요.</p><p style="margin-top:8px; font-size:12px; color:#6B7280;">디자인 톤, 레이아웃 선호, 색상 팔레트, 카피 스타일 등을 룰로 등록하면 더 일관된 섹션을 생성할 수 있어요.</p>`
    } else {
      quickBtns = ["필라테스 스튜디오", "SaaS 플랫폼", "온라인 교육", "뷰티/헤어살롱", "카페/레스토랑"]
      guideHtml = `<p>어떤 비즈니스/서비스인지 알려주세요. 맞춤형 작업 규칙을 자동으로 생성해드릴게요.</p><p style="margin-top:8px; font-size:12px; color:#6B7280;">기존 활성 룰이 있으면 적용 전에 교체/삭제/병합 방식을 선택할 수 있어요. 업종이 바뀌는 경우에는 교체가 기본입니다.</p>`
    }
    const guideEl = document.createElement("div")
    guideEl.className = "ba-rchat-msg ba-rchat-msg--assistant"
    guideEl.innerHTML = `${guideHtml}<div style="display:flex;flex-wrap:wrap;gap:5px;margin-top:8px;">${quickBtns.map(t => `<button type="button" class="ba-quick-btn" style="font-size:11px;" data-text="${this.escHtml(t)}">${this.escHtml(t)}</button>`).join("")}</div>`
    this._rulesChatMessages.appendChild(guideEl)

    guideEl.querySelectorAll(".ba-quick-btn").forEach(btn => {
      btn.addEventListener("click", () => {
        if (this._rulesChatInput) {
          this._rulesChatInput.value = btn.dataset.text
          this.sendRulesChat()
        }
      })
    })
  }

  async loadRulesSession(sessionId) {
    this.currentRulesSessionId = sessionId
    localStorage.setItem(`${this.config.storagePrefix}-rules-session`, sessionId)
    this.rulesChatHistory = []

    if (!this._rulesChatMessages) return
    this._rulesChatMessages.innerHTML = ""

    const id = this.config.resourceId
    if (!id || id === 0 || !this.config.chatApiPath) { this.showRulesChatGuide(); return }

    try {
      const res = await fetch(withSearchParams(this.config.chatApiPath, {
        context_type: "rules",
        session_id: sessionId
      }))
      const data = await res.json()
      if (data.data && data.data.length > 0) {
        data.data.forEach(msg => {
          this.rulesChatHistory.push({ role: msg.role, content: msg.content })
          const el = document.createElement("div")
          el.className = `ba-rchat-msg ba-rchat-msg--${msg.role}`
          el.style.whiteSpace = "pre-wrap"
          el.textContent = msg.content
          this._rulesChatMessages.appendChild(el)
        })
        this._rulesChatMessages.scrollTop = this._rulesChatMessages.scrollHeight
      } else {
        this.showRulesChatGuide()
      }
    } catch (e) {
      console.error("[KlavyAgentCore] loadRulesSession error:", e)
      this.showRulesChatGuide()
    }
  }

  async persistRulesMessage(role, content) {
    const id = this.config.resourceId
    if (!id || id === 0 || !this.currentRulesSessionId || !this.config.chatApiPath) return
    try {
      await fetch(this.config.chatApiPath, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({
          role,
          content: typeof content === 'string' ? content : JSON.stringify(content),
          context_type: "rules",
          session_id: this.currentRulesSessionId,
          resource_type: this.config.resourceType,
          resource_id: this.config.resourceId
        })
      })
    } catch (e) {
      console.error("[KlavyAgentCore] persistRulesMessage error:", e)
    }
  }

  newRulesSession() {
    this.currentRulesSessionId = this.generateSessionId()
    localStorage.setItem(`${this.config.storagePrefix}-rules-session`, this.currentRulesSessionId)
    this.rulesChatHistory = []
    this.showRulesChatGuide()
    if (this._rulesChatInput) this._rulesChatInput.focus()
  }

  async toggleRulesHistory(e) {
    if (e) e.stopPropagation()
    this.rulesHistoryOpen = !this.rulesHistoryOpen
    if (!this._rulesHistoryPanel) return

    if (!this.rulesHistoryOpen) {
      this._rulesHistoryPanel.style.display = "none"
      return
    }

    const id = this.config.resourceId
    if (!id || id === 0 || !this.config.chatApiPath) {
      this._rulesHistoryPanel.innerHTML = `<div style="padding: 16px; text-align: center; color: #9CA3AF; font-size: 12px;">${_t("history.empty_after_save", "저장 후 대화 내역을 확인할 수 있습니다")}</div>`
      this._rulesHistoryPanel.style.display = "block"
      return
    }

    this._rulesHistoryPanel.innerHTML = `<div style="padding: 12px; text-align: center; color: #9CA3AF; font-size: 12px;">${_t("history.loading", "로딩 중...")}</div>`
    this._rulesHistoryPanel.style.display = "block"

    try {
      const res = await fetch(withPathSuffix(this.config.chatApiPath, "/rules_sessions"))
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()

      if (!data.data || data.data.length === 0) {
        this._rulesHistoryPanel.innerHTML = `<div style="padding: 16px; text-align: center; color: #9CA3AF; font-size: 12px;">${_t("history.no_history", "대화 내역이 없습니다")}</div>`
        return
      }

      this._rulesHistoryPanel.innerHTML = data.data.map(s => {
        const isActive = s.session_id === this.currentRulesSessionId
        const date = new Date(s.created_at)
        const dateStr = `${date.getMonth()+1}/${date.getDate()} ${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`
        return `<div class="ba-history-item${isActive ? ' ba-history-item--active' : ''}"
                     data-session-id="${s.session_id}"
                     style="padding: 10px 14px; cursor: pointer; border-bottom: 1px solid #F3F4F6; font-size: 12px;${isActive ? ' background: #EEF2FF;' : ''}">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <span style="color: #111827; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 200px;">${this.escapeHtml(s.first_message)}</span>
            <span style="display:flex; align-items:center; gap:8px; flex-shrink:0; margin-left:8px;">
              <span style="color: #9CA3AF; font-size: 11px;">${dateStr}</span>
              <button type="button" class="ba-history-delete-btn" data-session-id="${s.session_id}" title="${_t("history.delete_title", "대화 삭제")}" style="width:18px;height:18px;display:inline-flex;align-items:center;justify-content:center;border:1px solid #E5E7EB;background:#fff;color:#9CA3AF;cursor:pointer;">×</button>
            </span>
          </div>
          <div style="color: #6B7280; font-size: 11px; margin-top: 2px;">${_t("history.message_count", "%{count}개 메시지").replace("%{count}", s.message_count)}</div>
        </div>`
      }).join("")

      this._rulesHistoryPanel.querySelectorAll(".ba-history-item").forEach(el => {
        el.addEventListener("click", () => {
          const sid = el.dataset.sessionId
          this.rulesHistoryOpen = false
          this._rulesHistoryPanel.style.display = "none"
          this.loadRulesSession(sid)
        })
      })
      this._rulesHistoryPanel.querySelectorAll(".ba-history-delete-btn").forEach(btn => {
        btn.addEventListener("click", async (event) => {
          event.stopPropagation()
          const sid = btn.dataset.sessionId
          if (!sid) return
          btn.disabled = true
          const deleted = await this.handleRulesSessionDelete(sid)
          if (!deleted) {
            btn.disabled = false
            return
          }
          this._rulesHistoryPanel.innerHTML = `<div style="padding: 12px; text-align: center; color: #9CA3AF; font-size: 12px;">${_t("history.loading", "로딩 중...")}</div>`
          const res = await fetch(withPathSuffix(this.config.chatApiPath, "/rules_sessions"))
          const refreshed = await res.json()
          if (!refreshed.data || refreshed.data.length === 0) {
            this._rulesHistoryPanel.innerHTML = `<div style="padding: 16px; text-align: center; color: #9CA3AF; font-size: 12px;">${_t("history.no_history", "대화 내역이 없습니다")}</div>`
            return
          }
          this.rulesHistoryOpen = false
          await this.toggleRulesHistory()
        })
      })
    } catch (e) {
      this._rulesHistoryPanel.innerHTML = `<div style="padding: 16px; text-align: center; color: #EF4444; font-size: 12px;">${_t("history.load_failed", "로딩 실패")}</div>`
    }
  }

  async sendRulesChat() {
    if (!this._rulesChatInput) return
    const msg = this._rulesChatInput.value.trim()
    if (!msg || this.rulesChatLoading) return
    this._rulesChatInput.value = ""
    this.rulesChatLoading = true

    const userContent = this.rcImageBase64
      ? [
          { type: "image", source: { type: "base64", media_type: this.rcImageBase64.substring(5, this.rcImageBase64.indexOf(";base64,")), data: this.rcImageBase64.replace(/^data:image\/[^;]+;base64,/, "") } },
          { type: "text", text: msg }
        ]
      : msg

    const userEl = document.createElement("div")
    userEl.className = "ba-rchat-msg ba-rchat-msg--user"
    if (Array.isArray(userContent)) {
      const imagePart = userContent.find(p => p.type === "image")
      const textPart = userContent.find(p => p.type === "text")
      if (imagePart?.source?.data) {
        const img = document.createElement("img")
        img.src = `data:${imagePart.source.media_type || "image/png"};base64,${imagePart.source.data}`
        img.alt = _t("image_upload.attached_image_alt", "첨부 이미지")
        img.style.cssText = "max-width:100%;max-height:120px;object-fit:contain;margin-bottom:6px;display:block;border:1px solid rgba(255,255,255,0.2);"
        userEl.appendChild(img)
      }
      if (textPart?.text) {
        const span = document.createElement("span")
        span.textContent = textPart.text
        userEl.appendChild(span)
      }
    } else {
      userEl.textContent = msg
    }
    if (this._rulesChatMessages) this._rulesChatMessages.appendChild(userEl)

    const typingEl = document.createElement("div")
    typingEl.className = "ba-rchat-msg ba-rchat-msg--assistant"
    typingEl.innerHTML = `<span class="ba-typing" style="display:inline-flex;background:none;border:none;padding:4px 0;"><span></span><span></span><span></span></span>`
    if (this._rulesChatMessages) this._rulesChatMessages.appendChild(typingEl)
    if (this._rulesChatMessages) this._rulesChatMessages.scrollTop = this._rulesChatMessages.scrollHeight

    this.rulesChatHistory.push({ role: "user", content: userContent })
    this.persistRulesMessage("user", msg)

    if (this.rcImageBase64) this.removeRcImage()

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ""
      const model = this.selectedModel
      const provider = this.getProvider(model)
      const aiText = await this.requestRulesPreviewResponse({
        provider,
        model,
        csrfToken,
        typingEl,
        userMessage: msg
      })

      this.rulesChatHistory.push({ role: "assistant", content: aiText })
      // 이전 메시지에서 이미지 데이터 제거 (메모리 최적화)
      this.rulesChatHistory.forEach((msg, idx) => {
        if (idx >= this.rulesChatHistory.length - 2) return
        if (Array.isArray(msg.content)) {
          msg.content = msg.content.filter(p => p.type === "text")
        }
      })
      this.persistRulesMessage("assistant", aiText)
      typingEl.remove()

      const formData = this.extractRulesFormData(aiText)
      // JSON 블록 제거: 코드 펜스 + 펜스 없는 raw JSON 모두 제거
      let displayText = aiText
        .replace(/```json[\s\S]*?```/g, "")
        .replace(/```[\s\S]*?```/g, "")
      // formData가 있으면 raw JSON 객체도 제거
      if (formData) {
        displayText = displayText.replace(/\{[\s\S]*"_action"[\s\S]*\}/g, "")
      }
      displayText = this.cleanRulesAssistantDisplayText(displayText)

      const assistantEl = document.createElement("div")
      assistantEl.className = "ba-rchat-msg ba-rchat-msg--assistant"

      if (formData?._action === "preview_rules") {
        assistantEl.innerHTML = this._renderMarkdown(this.rulesPreviewSummaryText(formData.data, aiText))
        if (this._rulesChatMessages) this._rulesChatMessages.appendChild(assistantEl)
        this.renderRulesPreview(formData.data, this._rulesChatMessages)
      } else if (formData?._action === "fill_rules") {
        assistantEl.innerHTML = this._renderMarkdown(this.rulesPreviewSummaryText(formData.data, aiText))
        if (this._rulesChatMessages) this._rulesChatMessages.appendChild(assistantEl)
        this.renderRulesPreview(formData.data, this._rulesChatMessages)
      } else {
        assistantEl.innerHTML = this._renderMarkdown(displayText)
        if (this._rulesChatMessages) this._rulesChatMessages.appendChild(assistantEl)
        this.appendFillExampleBtn(this._rulesChatMessages)
      }
    } catch (e) {
      typingEl.remove()
      const errEl = document.createElement("div")
      errEl.className = "ba-rchat-msg ba-rchat-msg--assistant"
      errEl.textContent = e.message || _t("system_notes.apply_error", "오류가 발생했습니다. 다시 시도해주세요.")
      if (this._rulesChatMessages) this._rulesChatMessages.appendChild(errEl)
    }

    this.rulesChatLoading = false
    if (this._rulesChatMessages) this._rulesChatMessages.scrollTop = this._rulesChatMessages.scrollHeight
  }

  rulesChatKeydown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      this.sendRulesChat()
    }
  }

  rcAutoResize() {
    const el = this._rulesChatInput
    if (!el) return
    el.style.height = "auto"
    el.style.height = Math.min(el.scrollHeight, 120) + "px"
  }

  focusRcInput() {
    if (this._rcInputWrapper) {
      this._rcInputWrapper.style.borderColor = "#6366F1"
      this._rcInputWrapper.style.boxShadow = "0 0 0 2px rgba(99,102,241,0.1)"
    }
  }

  blurRcInput() {
    if (this._rcInputWrapper) {
      this._rcInputWrapper.style.borderColor = "#e0e0e0"
      this._rcInputWrapper.style.boxShadow = "none"
    }
  }

  toggleRcModelDropdown(e) {
    e.stopPropagation()
    this.rcModelDropdownOpen = !this.rcModelDropdownOpen
    this.modelDropdownOpen = false
    if (this._modelDropdown) this._modelDropdown.style.display = "none"
    if (this._rcModelDropdown) {
      this._rcModelDropdown.style.display = this.rcModelDropdownOpen ? "block" : "none"
      this._rcModelDropdown.style.zIndex = "2000"
    }
    if (this.rcModelDropdownOpen) this.refreshCredentialStatus({ force: true, rerenderModels: true })
  }

  selectRcModel(e) {
    const opt = e.currentTarget
    if (opt.dataset.locked === "true") {
      if (this._rcModelDropdown) this._rcModelDropdown.style.display = "none"
      this.rcModelDropdownOpen = false
      window.dispatchEvent(new CustomEvent("klavy:upgrade-required", {
        detail: {
          reason: "model_not_available",
          provider: opt.dataset.provider,
          model: opt.dataset.value
        }
      }))
      return
    }

    this.selectedModel = opt.dataset.value
    if (this.isImageAssetAgent) this.imageAssetModelExplicitlySelected = true
    localStorage.setItem(`${this.config.storagePrefix}-model`, this.selectedModel)
    const label = opt.querySelector(".ba-model-opt__name")?.textContent.trim() || opt.dataset.value
    const weight = opt.querySelector(".ka-model-badge--weight")?.textContent.trim()
    if (this._rcModelLabel) this._rcModelLabel.textContent = label
    if (this._modelLabel) this._modelLabel.textContent = label
    if (weight && this._rcModelWeightChip) this._rcModelWeightChip.textContent = weight
    if (weight && this._modelWeightChip) this._modelWeightChip.textContent = weight
    this.updateSelectedModelBadgeChips()

    if (this._rcModelDropdown) {
      this._rcModelDropdown.querySelectorAll(".ba-model-opt").forEach(el => {
        el.style.background = el.dataset.value === this.selectedModel ? "#EEF2FF" : ""
      })
    }
    if (this._modelDropdown) {
      this._modelDropdown.querySelectorAll(".ba-model-opt").forEach(el => {
        el.style.background = el.dataset.value === this.selectedModel ? "#EEF2FF" : ""
      })
    }

    if (this._rcModelDropdown) this._rcModelDropdown.style.display = "none"
    this.rcModelDropdownOpen = false
    this.updateCredentialSourceUI()
  }

  toggleRcModeDropdown(e) {
    e.stopPropagation()
    this.rcModeDropdownOpen = !this.rcModeDropdownOpen
    if (this._rcModeDropdown) this._rcModeDropdown.style.display = this.rcModeDropdownOpen ? "block" : "none"
  }

  selectRcMode(e) {
    const mode = e.currentTarget.dataset.mode
    const label = e.currentTarget.dataset.label || mode
    this.selectedMode = mode
    localStorage.setItem(`${this.config.storagePrefix}-mode`, mode)
    if (this._rcModeLabel) this._rcModeLabel.textContent = label
    if (this._modeLabel) this._modeLabel.textContent = label
    if (this._rcModeDropdown) this._rcModeDropdown.style.display = "none"
    this.rcModeDropdownOpen = false
  }

  triggerRcImageUpload() {
    if (this._rcImageInput) this._rcImageInput.click()
  }

  handleRcImageUpload(e) {
    const file = e.target.files[0]
    if (!file) return
    // 파일 크기 체크 (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert(_t("alerts.image_too_large", "이미지 크기는 5MB 이하여야 합니다."))
      e.target.value = ""
      return
    }
    const reader = new FileReader()
    reader.onload = (event) => {
      this.rcImageBase64 = event.target.result
      if (this._rcImagePreviewImg) this._rcImagePreviewImg.src = this.rcImageBase64
      if (this._rcImagePreview) this._rcImagePreview.style.display = "block"
      if (this._rcImageFileName) this._rcImageFileName.textContent = file.name
    }
    reader.readAsDataURL(file)
    e.target.value = ""
  }

  removeRcImage() {
    this.rcImageBase64 = null
    if (this._rcImagePreview) this._rcImagePreview.style.display = "none"
    if (this._rcImagePreviewImg) this._rcImagePreviewImg.src = ""
    if (this._rcImageFileName) this._rcImageFileName.textContent = _t("image_upload.attached", "이미지 첨부됨")
  }

  handleRcPaste(e) {
    const items = e.clipboardData?.items
    if (!items) return
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        e.preventDefault()
        const file = item.getAsFile()
        if (!file) return
        if (file.size > 5 * 1024 * 1024) {
          alert(_t("alerts.image_too_large", "이미지 크기는 5MB 이하여야 합니다."))
          return
        }
        const reader = new FileReader()
        reader.onload = (event) => {
          this.rcImageBase64 = event.target.result
          if (this._rcImagePreviewImg) this._rcImagePreviewImg.src = this.rcImageBase64
          if (this._rcImagePreview) this._rcImagePreview.style.display = "block"
          if (this._rcImageFileName) this._rcImageFileName.textContent = _t("image_upload.clipboard", "클립보드 이미지")
        }
        reader.readAsDataURL(file)
        return
      }
    }
  }

  showRulesListFromChat() {
    this.exitRulesSplitView()
  }

  // ── 룰 미리보기 렌더링 ────────────────────────────────────────────────────

  renderRulesPreview(data, container) {
    if (!Array.isArray(data) || data.length === 0) return

    const activeRulesCount = (this.projectRules || []).filter((rule) => rule?.active !== false).length
    const previewEl = document.createElement("div")
    previewEl.className = "ba-rchat-msg ba-rchat-msg--assistant"
    previewEl.style.padding = "0"
    previewEl.style.background = "none"
    previewEl.style.border = "none"

    const card = document.createElement("div")
    card.style.cssText = "background:#F9FAFB; border:1px solid #E5E7EB; overflow:hidden; margin-top:4px;"

    const header = document.createElement("div")
    header.style.cssText = "padding:10px 14px; border-bottom:1px solid #E5E7EB; font-size:12px; font-weight:600; color:#374151;"
    header.textContent = _t("status.generating_rules_preview", "생성된 룰 미리보기 (%{count}개)").replace("%{count}", data.length)
    card.appendChild(header)

    const list = document.createElement("div")
    list.style.cssText = "padding:8px 0; max-height:220px; overflow-y:auto;"
    data.forEach((rule) => {
      const item = document.createElement("div")
      item.style.cssText = "padding:8px 14px; border-bottom:1px solid #F3F4F6;"
      const previewMarkdown = this.buildRulePreviewMarkdown(rule.content || "")
      item.innerHTML = `
        <div style="font-size:12px; font-weight:600; color:#111827;">${this.escapeHtml(rule.title)}</div>
        <div class="ba-rules-preview-content" style="font-size:11px; color:#6B7280; margin-top:4px; line-height:1.55; display:-webkit-box; -webkit-line-clamp:4; -webkit-box-orient:vertical; overflow:hidden;">
          ${this._renderMarkdown(previewMarkdown)}
        </div>
      `
      list.appendChild(item)
    })
    card.appendChild(list)

    const footer = document.createElement("div")
    footer.style.cssText = "padding:10px 14px;"
    if (activeRulesCount > 0) {
      const info = document.createElement("div")
      info.style.cssText = "margin-bottom:10px; padding:8px 10px; background:#fff; border:1px solid #E5E7EB; font-size:12px; color:#4B5563; line-height:1.5;"
      info.innerHTML = `<div style="font-weight:600; color:#111827; margin-bottom:4px;">기존 활성 룰 ${activeRulesCount}개가 있습니다.</div><div>새 업종/사이트로 전환하는 경우 내용이 섞이지 않도록 <strong>교체(비활성 보관)</strong>가 기본입니다.</div>`
      footer.appendChild(info)

      const select = document.createElement("select")
      select.className = "ba-rules-apply-mode"
      select.style.cssText = "width:100%; height:34px; margin-bottom:10px; padding:0 10px; background:#fff; border:1px solid #D1D5DB; font-size:12px; color:#111827; font-family:inherit;"
      select.innerHTML = `
        <option value="replace_active_keep_inactive">기존 활성 룰 교체 (비활성 보관)</option>
        <option value="replace_all_delete">기존 룰 전체 삭제 후 새로 등록</option>
        <option value="merge_by_title">기존 룰과 병합 (같은 제목만 덮어쓰기)</option>
      `
      footer.appendChild(select)
    }

    const btn = document.createElement("button")
    btn.type = "button"
    btn.style.cssText = "width:100%; padding:8px 0; background:#6366F1; color:#fff; border:none; font-size:13px; font-weight:600; cursor:pointer; font-family:inherit;"
    btn.textContent = _t("buttons.generate_rules", "이걸로 생성하기")
    btn.addEventListener("click", async () => {
      btn.disabled = true
      btn.textContent = _t("buttons.registering", "등록 중...")
      const mode = footer.querySelector(".ba-rules-apply-mode")?.value || "replace_active_keep_inactive"
      const result = await this.fillRules(data, mode)
      footer.innerHTML = `<div style="text-align:center; font-size:12px; color:#6366F1; font-weight:600; padding:4px 0;">${this.escapeHtml(this.rulesApplyResultMessage(result))}</div>`
    })
    footer.appendChild(btn)
    card.appendChild(footer)

    previewEl.appendChild(card)
    container.appendChild(previewEl)
    container.scrollTop = container.scrollHeight
  }

  appendFillExampleBtn(container) {
    const wrapEl = document.createElement("div")
    wrapEl.className = "ba-rchat-msg ba-rchat-msg--assistant"
    wrapEl.style.cssText = "background:none; border:none; padding:4px 0;"

    const btn = document.createElement("button")
    btn.type = "button"
    btn.style.cssText = "padding:6px 14px; background:none; border:1px solid #6366F1; color:#6366F1; font-size:12px; font-weight:600; cursor:pointer; font-family:inherit;"
    btn.textContent = (this.config.contextType === "ia_project" || this.config.resourceType === "IaProject")
      ? _t("fill_example.ia_button", "IA 예시로 채워보기")
      : _t("fill_example.button", "예시로 채워보기")
    btn.addEventListener("click", () => {
      btn.disabled = true
      btn.style.opacity = "0.5"
      if (this._rulesChatInput) {
        this._rulesChatInput.value = (this.config.contextType === "ia_project" || this.config.resourceType === "IaProject")
          ? "위 질문들을 예시 데이터로 간단히 채워 6개 IA 작업 규칙을 생성해주세요. 설명 없이 preview_rules JSON만 반환해주세요."
          : "위 질문들을 예시 데이터로 간단히 채워 6개 작업 규칙을 생성해주세요. 설명 없이 preview_rules JSON만 반환해주세요."
        this.sendRulesChat()
      }
    })

    wrapEl.appendChild(btn)
    container.appendChild(wrapEl)
    container.scrollTop = container.scrollHeight
  }

  normalizeGeneratedRules(data) {
    if (!Array.isArray(data)) return []

    const deduped = new Map()
    data.forEach((rule) => {
      const title = rule?.title?.toString().trim()
      if (!title) return

      deduped.set(title, {
        title,
        content: rule?.content?.toString().trim() || "",
        active: rule?.active !== false
      })
    })

    return Array.from(deduped.values())
  }

  rulesApplyResultMessage(result) {
    if (!result) return _t("rules_result.applied", "룰을 적용했습니다.")
    if (result.appliedCount === 0) return _t("rules_result.nothing_new", "적용할 새 룰이 없습니다.")

    if (result.mode === "replace_active_keep_inactive") {
      return _t("rules_result.replace_active", "기존 활성 룰 %{deactivated}개를 비활성 보관하고 새 룰 %{applied}개를 적용했습니다.").replace("%{deactivated}", result.deactivatedCount).replace("%{applied}", result.appliedCount)
    }
    if (result.mode === "replace_all_delete") {
      return _t("rules_result.replace_all", "기존 룰 %{deleted}개를 삭제하고 새 룰 %{applied}개를 적용했습니다.").replace("%{deleted}", result.deletedCount).replace("%{applied}", result.appliedCount)
    }
    if (result.mode === "merge_by_title") {
      return _t("rules_result.merged", "새 룰 %{applied}개를 병합했습니다. (덮어쓴 룰 %{updated}개, 새로 추가 %{added}개)").replace("%{applied}", result.appliedCount).replace("%{updated}", result.updatedCount).replace("%{added}", result.addedCount)
    }
    return _t("rules_result.applied_count", "새 룰 %{applied}개를 적용했습니다.").replace("%{applied}", result.appliedCount)
  }

  async refreshBaroMainPanel() {
    const mainEl = document.getElementById("ba-main")
    if (!mainEl) return

    try {
      const res = await fetch(window.location.href, { headers: { "Accept": "text/html" } })
      const html = await res.text()
      const doc = new DOMParser().parseFromString(html, "text/html")
      const newMain = doc.getElementById("ba-main")
      if (newMain) mainEl.innerHTML = newMain.innerHTML
    } catch (e) {
      console.warn("[KlavyAgentCore] left panel refresh failed", e)
    }
  }

  async fillRules(data, mode = "replace_active_keep_inactive") {
    const incomingRules = this.normalizeGeneratedRules(data)
    if (incomingRules.length === 0) {
      return {
        mode,
        appliedCount: 0,
        deactivatedCount: 0,
        deletedCount: 0,
        updatedCount: 0,
        addedCount: 0
      }
    }

    const existingRules = Array.isArray(this.projectRules) ? this.projectRules.map((rule) => ({
      title: rule?.title?.toString().trim() || "",
      content: rule?.content?.toString().trim() || "",
      active: rule?.active !== false
    })).filter((rule) => rule.title) : []
    const activeExistingRules = existingRules.filter((rule) => rule.active !== false)
    const inactiveExistingRules = existingRules.filter((rule) => rule.active === false)

    let nextRules = existingRules
    let result = {
      mode,
      appliedCount: incomingRules.length,
      deactivatedCount: 0,
      deletedCount: 0,
      updatedCount: 0,
      addedCount: 0
    }

    if (mode === "replace_all_delete") {
      nextRules = incomingRules.map((rule) => ({ ...rule, active: true }))
      result.deletedCount = existingRules.length
      result.addedCount = incomingRules.length
    } else if (mode === "merge_by_title") {
      const byTitle = new Map()
      existingRules.forEach((rule) => byTitle.set(rule.title, { ...rule }))
      incomingRules.forEach((rule) => {
        if (byTitle.has(rule.title)) {
          result.updatedCount += 1
        } else {
          result.addedCount += 1
        }
        byTitle.set(rule.title, { ...rule, active: rule.active !== false })
      })
      nextRules = Array.from(byTitle.values())
    } else {
      const preservedInactive = [
        ...activeExistingRules.map((rule) => ({ ...rule, active: false })),
        ...inactiveExistingRules.map((rule) => ({ ...rule, active: false }))
      ]
      nextRules = [
        ...incomingRules.map((rule) => ({ ...rule, active: true })),
        ...preservedInactive
      ]
      result.mode = "replace_active_keep_inactive"
      result.deactivatedCount = activeExistingRules.length
      result.addedCount = incomingRules.length
    }

    this.projectRules = nextRules
    this.rulesCurrentPage = 1
    this.selectedRuleIndices.clear()
    this.renderRulesList()
    this.updateRulesIndicator()
    await this.persistRules()
    await this.refreshBaroMainPanel()
    return result
  }

  // ── 폼 데이터 파싱/적용 ───────────────────────────────────────────────────

  extractFormData(text) {
    // section-template: 여러 create_ai_block_template 블록을 배치로 묶음
    if (this.config.agentSlug === "section-template") {
      const allPayloads = this.parseAllAiJsonPayloads(text)
        .filter(p => p._action === "create_ai_block_template")
      this._bdLog("extractFormData: allPayloads.length =", allPayloads.length, allPayloads.map(p => p.data?.name))
      if (allPayloads.length > 1) {
        return { _action: "create_ai_block_templates_batch", data: { templates: allPayloads.map(p => p.data) } }
      }
      if (allPayloads.length === 1) return allPayloads[0]
    }

    const parsed = this.parseAiJsonPayload(text)
    if (parsed) return parsed

    if (this.isColdmailCampaignFormPage()) {
      const coldmailFallback = this.buildColdmailCampaignFallback(text)
      if (coldmailFallback) return coldmailFallback
    }

    if (this.isColdmailCampaignContext() && !this.isColdmailCampaignFormPage()) {
      const coldmailSequenceFallback = this.buildColdmailSequenceFallback(text)
      if (coldmailSequenceFallback) return coldmailSequenceFallback
    }

    // UI Planning 프로토타입은 모델이 _action 없이 { summary, actions }만 반환해도 즉시 적용한다.
    if (this.config.contextType === "ui_planning_prototype" && this.config.resourceType === "UiPlanningProject") {
      const candidates = this.extractJsonCandidates(text)
      for (const candidate of candidates) {
        try {
          const obj = JSON.parse(candidate.trim())
          if (obj && Array.isArray(obj.actions)) {
            return { _action: "apply_prototype_actions", data: { summary: obj.summary || "프로토타입 연결 반영", actions: obj.actions } }
          }
          if (obj && obj.data && Array.isArray(obj.data.actions)) {
            return { _action: "apply_prototype_actions", data: obj.data }
          }
        } catch (_) {}
      }
    }

    if (this.isPlanningWorkspaceContext()) {
      const candidates = this.extractJsonCandidates(text)
      for (const candidate of candidates) {
        try {
          const obj = JSON.parse(candidate.trim())
          if (obj && Array.isArray(obj.actions)) {
            return { _action: "apply_planning_workspace_actions", data: { summary: obj.summary || "기획 워크스페이스 반영", actions: obj.actions } }
          }
          if (obj && obj.data && Array.isArray(obj.data.actions)) {
            return { _action: "apply_planning_workspace_actions", data: obj.data }
          }
        } catch (_) {}
      }

      const markdownFallback = this.buildPlanningWorkspaceMarkdownFallback(text)
      if (markdownFallback) return markdownFallback
    }

    // IA 컨텍스트에서 _action 없이 actions 배열만 있는 JSON을 처리
    if (this.config.contextType === "ia_project") {
      const candidates = this.extractJsonCandidates(text)
      for (const candidate of candidates) {
        try {
          const obj = JSON.parse(candidate.trim())
          if (obj && Array.isArray(obj.actions)) {
            return { _action: "apply_ia_actions", data: { summary: obj.summary || "IA 구조 반영", actions: obj.actions } }
          }
          if (obj && obj.data && Array.isArray(obj.data.actions)) {
            return { _action: "apply_ia_actions", data: obj.data }
          }
        } catch (_) {}
      }
    }

    // 유저 스토리 컨텍스트에서 _action 없이 actions 배열만 있는 JSON을 처리
    if (this.config.contextType === "user_story_project") {
      const candidates = this.extractJsonCandidates(text)
      for (const candidate of candidates) {
        try {
          const obj = JSON.parse(candidate.trim())
          if (obj && Array.isArray(obj.actions)) {
            return { _action: "apply_user_story_actions", data: { summary: obj.summary || "유저 스토리 반영", actions: obj.actions } }
          }
          if (obj && obj.data && Array.isArray(obj.data.actions)) {
            return { _action: "apply_user_story_actions", data: obj.data }
          }
        } catch (_) {}
      }
    }
    return null
  }

  buildColdmailCampaignFallback(text) {
    const raw = String(text || "")
    if (!raw.trim()) return null
    if (!/(coldmail_lead_list_id|from_name|from_email|daily_limit|send_interval|timezone|send_days|send_hours|발송|캠페인|아웃바운드|에이전시)/i.test(raw)) return null

    const data = {}
    const knownFields = [
      "name",
      "coldmail_lead_list_id",
      "from_name",
      "from_email",
      "daily_limit",
      "send_interval",
      "timezone",
      "send_hours"
    ]

    knownFields.forEach((field) => {
      const quoted = raw.match(new RegExp(`["']${field}["']\\s*:\\s*["']([^"']*)["']`, "i"))
      if (quoted) {
        data[field] = quoted[1]
        return
      }
      const number = raw.match(new RegExp(`["']${field}["']\\s*:\\s*(\\d+)`, "i"))
      if (number) data[field] = Number(number[1])
    })

    const sendDaysArray = raw.match(/["']send_days["']\s*:\s*\[([^\]]*)\]/i)
    if (sendDaysArray) {
      data.send_days = sendDaysArray[1]
        .split(",")
        .map(day => day.replace(/["']/g, "").trim())
        .filter(Boolean)
    } else {
      const sendDaysString = raw.match(/["']send_days["']\s*:\s*["']([^"']*)["']/i)
      if (sendDaysString) {
        data.send_days = sendDaysString[1].split(",").map(day => day.trim()).filter(Boolean)
      }
    }

    if (!data.name) {
      const nameLine = raw.match(/["']name["']\s*:\s*["']([^"']*)/i)
      if (nameLine?.[1]) data.name = nameLine[1].trim()
    }
    if (!data.name && /에이전시/.test(raw)) {
      data.name = /파트너|제휴/.test(raw) ? "한국 에이전시 파트너 제안 1차 아웃바운드" : "한국 에이전시 1차 아웃바운드"
    }
    if (!data.send_days && /평일|업무\s*시간/.test(raw)) data.send_days = ["mon", "tue", "wed", "thu", "fri"]
    if (!data.timezone && /(서울|한국|KST|Asia\/Seoul)/i.test(raw)) data.timezone = "Asia/Seoul"
    if (!data.send_hours && /업무\s*시간|09:00|18:00/.test(raw)) data.send_hours = "09:00-18:00"

    if (data.daily_limit !== undefined) data.daily_limit = Number(data.daily_limit)
    if (data.send_interval !== undefined) data.send_interval = Number(data.send_interval)

    const validFields = Object.keys(data).filter((key) => data[key] !== undefined && data[key] !== null && String(data[key]).trim?.() !== "")
    if (validFields.length === 0) return null

    return { _action: "fill_coldmail_campaign", data }
  }

  buildColdmailSequenceFallback(text) {
    const raw = String(text || "")
    if (!raw.trim()) return null
    if (!/(create_coldmail_sequences|메일\s*시퀀스|후속\s*\d|단계)/i.test(raw)) return null
    if (!/["']subject["']\s*:/i.test(raw) || !/["']body["']\s*:/i.test(raw)) return null

    const segments = raw
      .split(/(?=["']subject["']\s*:)/i)
      .filter(segment => /["']subject["']\s*:/i.test(segment))

    const sequences = segments.map((segment, index) => {
      const subject = this.extractLooseJsonStringField(segment, "subject")
      const body = this.cleanRecoveredColdmailBody(this.extractLooseJsonStringField(segment, "body"))
      if (!subject || !body) return null

      const delay = this.extractLooseJsonNumberField(segment, "delay_days")
      const condition = this.extractLooseJsonStringField(segment, "condition")
      const imagePrompt = this.extractLooseJsonStringField(segment, "image_prompt")

      return {
        subject,
        body,
        image_prompt: imagePrompt,
        delay_days: Number.isFinite(delay) ? delay : (index === 0 ? 0 : index === 1 ? 3 : 5),
        condition: condition || (index === 0 ? "all" : "no_reply"),
        ai_generated: true
      }
    }).filter(Boolean)

    if (sequences.length === 0) return null

    return { _action: "create_coldmail_sequences", data: { sequences } }
  }

  extractLooseJsonStringField(text, field) {
    const raw = String(text || "")
    const escapedField = field.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    const match = new RegExp(`["']${escapedField}["']\\s*:\\s*`, "i").exec(raw)
    if (!match) return ""

    let index = match.index + match[0].length
    while (/\s/.test(raw[index] || "")) index += 1
    const quote = raw[index]
    if (quote !== "\"" && quote !== "'") return ""

    index += 1
    let value = ""
    let escaped = false
    for (; index < raw.length; index += 1) {
      const char = raw[index]
      if (escaped) {
        value += `\\${char}`
        escaped = false
        continue
      }
      if (char === "\\") {
        escaped = true
        continue
      }
      if (char === quote) {
        const rest = raw.slice(index + 1, index + 120)
        if (/^\s*(,|\}|\])/.test(rest)) break
      }
      value += char
    }

    const boundary = value.search(/["']?\s*,\s*["'](?:image_prompt|delay_days|condition|ai_generated)["']\s*:/i)
    if (boundary > 0) value = value.slice(0, boundary)

    return this.unescapeLooseJsonString(value).trim()
  }

  extractLooseJsonNumberField(text, field) {
    const raw = String(text || "")
    const escapedField = field.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    const match = new RegExp(`["']${escapedField}["']\\s*:\\s*(-?\\d+)`, "i").exec(raw)
    return match ? Number(match[1]) : NaN
  }

  unescapeLooseJsonString(value) {
    return String(value || "")
      .replace(/\\r/g, "\r")
      .replace(/\\n/g, "\n")
      .replace(/\\t/g, "\t")
      .replace(/\\"/g, "\"")
      .replace(/\\'/g, "'")
      .replace(/\\\\/g, "\\")
  }

  cleanRecoveredColdmailBody(body) {
    let cleaned = String(body || "").trim()
    const fieldBoundary = cleaned.search(/["']?\s*,\s*["'](?:image_prompt|delay_days|condition|ai_generated)["']\s*:/i)
    if (fieldBoundary > 0) cleaned = cleaned.slice(0, fieldBoundary).trim()

    const lastParagraphClose = cleaned.lastIndexOf("</p>")
    if (lastParagraphClose !== -1) {
      const trailing = cleaned.slice(lastParagraphClose + 4).trim()
      if (trailing) cleaned = cleaned.slice(0, lastParagraphClose + 4)
    }

    if (!/<(p|div|ul|ol|li|blockquote|table|h[1-6])\b/i.test(cleaned)) {
      const paragraphs = cleaned
        .replace(/\r\n?/g, "\n")
        .split(/\n{2,}/)
        .map(paragraph => paragraph.trim())
        .filter(Boolean)
      cleaned = paragraphs.map(paragraph => `<p>${paragraph}</p>`).join("")
    }

    return cleaned.trim()
  }

  async ensureColdmailSequenceQuality({ aiText, formData, requestOptions, userMessage }) {
    if (formData?._action !== "create_coldmail_sequences") {
      return { aiText, formData, blocked: false, issues: [] }
    }

    let quality = this.validateColdmailSequenceQuality(formData)
    if (quality.valid) return { aiText, formData, blocked: false, issues: [] }

    this.appendSystemNote(`콜드메일 문안이 너무 약해서 제안서형으로 다시 작성하고 있습니다: ${quality.issues.slice(0, 2).join(", ")}`)
    let rewrittenText = ""
    try {
      rewrittenText = await this.rewriteColdmailSequencesForQuality({ aiText, requestOptions, userMessage, issues: quality.issues })
    } catch (error) {
      return {
        aiText,
        formData: null,
        blocked: true,
        issues: [...quality.issues, error?.message || "자동 재작성 요청에 실패했습니다."]
      }
    }
    const rewrittenFormData = this.extractFormData(rewrittenText)
    quality = this.validateColdmailSequenceQuality(rewrittenFormData)

    if (rewrittenFormData?._action === "create_coldmail_sequences" && quality.hardIssues.length === 0) {
      if (quality.softIssues.length > 0) {
        this.appendSystemNote(`콜드메일 품질 보완 후 적용합니다. 남은 보완점: ${quality.softIssues.slice(0, 2).join(", ")}`)
      }
      return { aiText: rewrittenText, formData: rewrittenFormData, blocked: false, issues: quality.softIssues }
    }

    return {
      aiText: rewrittenText || aiText,
      formData: null,
      blocked: true,
      issues: quality.issues.length > 0 ? quality.issues : ["제안서형 콜드메일 JSON을 복구하지 못했습니다."]
    }
  }

  validateColdmailSequenceQuality(formData) {
    if (formData?._action !== "create_coldmail_sequences") {
      return { valid: true, issues: [], hardIssues: [], softIssues: [] }
    }

    const sequences = this.normalizeColdmailSequencePayload(formData.data)
    const hardIssues = []
    const softIssues = []
    if (sequences.length === 0) {
      return {
        valid: false,
        issues: ["생성된 메일 단계가 없습니다."],
        hardIssues: ["생성된 메일 단계가 없습니다."],
        softIssues: []
      }
    }

    const deliverables = [
      "기획서 초안",
      "기획서",
      "IA",
      "메뉴구조",
      "화면설계",
      "주요 화면 흐름",
      "화면 흐름",
      "프로토타입",
      "Figma",
      "피그마",
      "클라이언트 제안용 화면",
      "제안용 화면",
      "내부 검토 자료",
      "요구사항",
      "WBS"
    ]
    const forbidden = /(무료|특가|한정|이벤트|지금\s*바로|클릭|100%|보장|매출\s*상승|대박|혁신|업계\s*최고|무조건|수익)/i

    sequences.forEach((sequence, index) => {
      const subject = String(sequence.subject || "").trim()
      const plain = this.plainTextFromHtml(sequence.body)
      const compactLength = plain.replace(/\s/g, "").length
      const plainLength = plain.length
      const deliverableCount = deliverables.filter(term => new RegExp(term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i").test(plain)).length

      if (!subject || !plain) {
        hardIssues.push(`${index + 1}단계에는 제목과 본문이 모두 필요합니다.`)
      }
      if (subject.length < 22 || subject.length > 45) {
        softIssues.push(`${index + 1}단계 제목은 22~45자를 권장합니다.`)
      }
      if (index === 0 && compactLength < 450 && plainLength < 520) {
        hardIssues.push("1차 메일은 최소 450자 이상의 제안서형 본문이어야 합니다.")
      } else if (index === 0 && (compactLength < 650 || compactLength > 1100)) {
        softIssues.push("1차 메일은 650~950자 수준을 권장합니다.")
      }
      if (index > 0 && compactLength < 280 && plainLength < 330) {
        hardIssues.push(`${index + 1}단계 후속 메일은 최소 280자 이상의 본문이어야 합니다.`)
      } else if (index > 0 && (compactLength < 350 || compactLength > 850)) {
        softIssues.push(`${index + 1}단계 후속 메일은 350~650자 수준을 권장합니다.`)
      }
      if (deliverableCount < 3) {
        hardIssues.push(`${index + 1}단계에는 구체 산출물이 3개 이상 필요합니다.`)
      }
      if (!/(샘플|실제\s*제안|내부\s*검토|협업|방식|범위|선택|중\s*하나)/.test(plain)) {
        softIssues.push(`${index + 1}단계에는 선택 가능한 협업 방식이 있으면 더 좋습니다.`)
      }
      if (!/(담당자님|대표님|PM|기획|에이전시|제작사|스튜디오|\{\{company\}\})/.test(plain)) {
        softIssues.push(`${index + 1}단계에는 대상 명명이 필요합니다.`)
      }
      if (!/(병목|반복|검토|제안|시안|런칭|요구사항|화면|합의|설명|재작업|일정)/.test(plain)) {
        softIssues.push(`${index + 1}단계에는 프로젝트/제안 병목이 필요합니다.`)
      }
      if (!/(10분|짧게|예시|공유드려도|보내드려도|말씀드려도|회신|답장|통화|미팅)/.test(plain)) {
        softIssues.push(`${index + 1}단계에는 낮은 부담의 직접 CTA가 필요합니다.`)
      }
      if (forbidden.test(`${subject} ${plain}`)) {
        hardIssues.push(`${index + 1}단계에 금지된 광고성 표현이 포함되어 있습니다.`)
      }
      if (!this.hasColdmailRichHtmlStructure(sequence.body, index)) {
        softIssues.push(`${index + 1}단계에는 제목/소제목, 굵게, 목록, 문단 줄바꿈을 포함한 리치 HTML 구조가 필요합니다.`)
      }
    })

    const dedupedHardIssues = [...new Set(hardIssues)]
    const dedupedSoftIssues = [...new Set(softIssues)]
    return {
      valid: dedupedHardIssues.length === 0 && dedupedSoftIssues.length === 0,
      issues: [...dedupedHardIssues, ...dedupedSoftIssues],
      hardIssues: dedupedHardIssues,
      softIssues: dedupedSoftIssues
    }
  }

  async rewriteColdmailSequencesForQuality({ aiText, requestOptions, userMessage, issues = [] }) {
    const payload = requestOptions?.payload || {}
    const rewritePrompt = [
      "방금 콜드메일 시퀀스는 품질 기준에 미달했습니다.",
      `미달 사유: ${issues.slice(0, 8).join(", ")}`,
      "같은 요청과 캠페인 맥락을 유지하되, 회신을 유도하는 제안서형 영업 메일로 다시 작성하세요.",
      "1차 메일은 650~950자 수준, 후속 메일은 350~650자 수준으로 작성하세요.",
      "각 메일에는 대상 명명, 프로젝트/제안 병목, 구체 업무 범위/산출물 3개 이상, 선택 가능한 협업 방식, 낮은 부담 CTA를 포함하세요.",
      "제목/소제목, 굵게, 목록, 문단 줄바꿈을 포함한 이메일 안전형 HTML로 작성하세요.",
      "1차 메일은 <p> 인사, <h3> 소제목, 병목 설명 <p>, <ul><li> 산출물/업무 범위, 선택 가능한 협업 방식 <p>, CTA <p> 구조를 사용하세요.",
      "후속 메일도 <p>, <strong>, 필요 시 <ul><li> 1개를 사용하고 과한 컬러, 큰 배너, 전체 굵게, 인라인 스타일은 쓰지 마세요.",
      "가격, 성과 수치, 고객사명은 사용자가 제공하지 않았다면 만들지 마세요.",
      "설명은 짧게만 쓰고 응답 마지막에는 완전한 create_coldmail_sequences JSON 하나를 포함하세요."
    ].join("\n")

    return this.requestAiResponse({
      ...requestOptions,
      stream: false,
      payload: {
        ...payload,
        max_tokens: Math.max(Number(payload.max_tokens || 0), 4096),
        messages: [
          ...(Array.isArray(payload.messages) ? payload.messages : []),
          { role: "assistant", content: aiText },
          { role: "user", content: `${userMessage}\n\n${rewritePrompt}` }
        ]
      }
    })
  }

  plainTextFromHtml(value) {
    const div = document.createElement("div")
    div.innerHTML = String(value || "")
    return (div.textContent || div.innerText || "")
      .replace(/\s+/g, " ")
      .trim()
  }

  hasColdmailRichHtmlStructure(value, index = 0) {
    const html = String(value || "")
    const paragraphCount = (html.match(/<p\b/gi) || []).length
    const hasEmphasis = /<(strong|b)\b/i.test(html)
    const hasList = /<(ul|ol)\b[\s\S]*<li\b/i.test(html)
    const hasHeading = /<h[1-6]\b/i.test(html)

    if (index === 0) {
      return paragraphCount >= 3 && hasEmphasis && hasList && hasHeading
    }

    return paragraphCount >= 2 && hasEmphasis
  }

  buildPlanningWorkspaceMarkdownFallback(text) {
    const tab = this.planningWorkspaceCurrentTab()
    if (!tab || tab === "idea_interview" || tab === "main_target" || tab === "service_name") return null

    const lastUser = [...(this.chatHistory || [])].reverse().find(m => m.role === "user")
    const lastUserText = typeof lastUser?.content === "string"
      ? lastUser.content
      : Array.isArray(lastUser?.content)
        ? lastUser.content.map(item => item?.text || "").join(" ")
        : ""
    if (!/(저장|작성|만들|생성|정리|채워|반영|초안)/.test(lastUserText)) return null

    const content = String(text || "").trim()
    if (!content) return null

    const headingSets = {
      idea_memo: ["문제", "대상 고객", "해결 방식", "기대 효과"],
      lean_canvas: ["문제", "고객군", "고유 가치", "해결책", "채널", "수익 구조", "비용 구조", "핵심 지표"],
      positioning: ["한 줄 포지셔닝", "문제 정의", "기존 대안", "타겟 고객", "고유 가치 제안", "출시 메시지"],
      prd: ["문제", "목표", "사용자 시나리오", "핵심 기능", "비기능 요구사항", "출시 범위"]
    }
    const headings = headingSets[tab]
    if (!headings) return null

    const foundCount = headings.filter(heading => {
      const escaped = heading.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
      return new RegExp(`(^|\\n)\\s*#{1,3}\\s*${escaped}(\\s|$)`, "m").test(content)
    }).length
    const minHeadings = tab === "lean_canvas" ? 4 : Math.min(3, headings.length)
    if (foundCount < minHeadings) return null

    return {
      _action: "apply_planning_workspace_actions",
      data: {
        summary: `${this.planningWorkspaceTabLabel(tab)} 저장`,
        actions: [{ type: "set_stage_document", tab, content }]
      }
    }
  }

  planningWorkspaceCurrentTab() {
    const context = String(this.config.contextType || "")
    if (context.startsWith("planning_")) return context.replace(/^planning_/, "")
    return window.PW_CURRENT_TAB || new URLSearchParams(window.location.search).get("tab")
  }

  planningWorkspaceMaxTokens() {
    const context = String(this.config.contextType || "")
    if (context === "planning_service_name" || context === "planning_prd") return 16384
    if (context.startsWith("planning_")) return 8192
    return 4096
  }

  planningWorkspaceTabLabel(tab) {
    return {
      idea_memo: "아이디어 메모",
      lean_canvas: "린 캔버스",
      positioning: "포지셔닝",
      prd: "PRD"
    }[tab] || "단계 문서"
  }

  extractRulesFormData(text) {
    const parsed = this.parseAiJsonPayload(text)
    if (parsed?._action === "preview_rules" || parsed?._action === "fill_rules") return parsed
    return null
  }

  cleanRulesAssistantDisplayText(text) {
    if (!text) return ""
    return text
      .replace(/^\s*["']?(?:_action|data|title|content|active)["']?\s*:\s*/gim, "")
      .replace(/^\s*#+\s*/gm, "")
      .replace(/^\s*preview_rules\s*$/gim, "")
      .replace(/^\s*[{}\[\],]+\s*$/gm, "")
      .trim()
  }

  cleanRulePreviewSnippet(text) {
    return this.cleanRulesAssistantDisplayText((text || "").replace(/\\n/g, "\n"))
      .split("\n")
      .map((line) => line.trim())
      .find(Boolean) || ""
  }

  buildRulePreviewMarkdown(text) {
    const normalized = this.cleanRulesAssistantDisplayText((text || "").replace(/\\n/g, "\n"))
    if (!normalized) return ""

    const lines = normalized
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .filter((line) => !/^#+\s*$/.test(line))

    return lines.slice(0, 4).join("\n")
  }

  rulesPreviewSummaryText(data, aiText) {
    const count = Array.isArray(data) ? data.length : 0
    const retried = /JSON 문법이 깨졌습니다/.test(aiText)
    if (retried) {
      return `형식을 다시 정리해 작업 규칙 ${count}개를 생성했어요.\n아래 미리보기는 아직 저장 전 상태이며, '이걸로 생성하기'를 눌러야 실제 작업 규칙에 반영됩니다.`
    }
    return `작업 규칙 ${count}개를 생성했어요.\n아래 미리보기는 아직 저장 전 상태이며, '이걸로 생성하기'를 눌러야 실제 작업 규칙에 반영됩니다.`
  }

  parseAiJsonPayload(text) {
    const candidates = this.extractJsonCandidates(text)
    for (const candidate of candidates) {
      try {
        const parsed = JSON.parse(candidate.trim())
        if (parsed && parsed._action && parsed.data !== undefined) return parsed
      } catch (e) {
        const repairedCandidate = this.repairJsonCandidate(candidate)
        if (repairedCandidate) {
          try {
            const parsed = JSON.parse(repairedCandidate)
            if (parsed && parsed._action && parsed.data !== undefined) return parsed
          } catch (_) {
            // fall through to warning below
          }
        }
        console.warn("[KlavyAgentCore] JSON parse failed:", e)
      }
    }
    return null
  }

  parseAllAiJsonPayloads(text) {
    const results = []
    const seen = new Set()
    const candidates = this.extractJsonCandidates(text)
    for (const candidate of candidates) {
      let parsed = null
      try {
        parsed = JSON.parse(candidate.trim())
      } catch (e) {
        const repaired = this.repairJsonCandidate(candidate)
        if (repaired) {
          try { parsed = JSON.parse(repaired) } catch (_) {}
        }
      }
      if (parsed && parsed._action && parsed.data !== undefined) {
        const key = JSON.stringify(parsed)
        if (!seen.has(key)) {
          seen.add(key)
          results.push(parsed)
        }
      }
    }
    return results
  }

  extractJsonCandidates(text) {
    const candidates = []
    const fencedMatches = text.matchAll(/```json\s*([\s\S]*?)```/g)
    for (const match of fencedMatches) {
      if (match[1]) candidates.push(match[1])
    }
    const openFenceIndex = text.indexOf("```json")
    if (openFenceIndex !== -1) {
      const fenceBody = text.slice(openFenceIndex + 7).trim()
      if (fenceBody) candidates.push(fenceBody)
    }
    const firstBrace = text.indexOf("{")
    const lastBrace = text.lastIndexOf("}")
    if (firstBrace !== -1) {
      if (lastBrace !== -1 && lastBrace > firstBrace) {
        candidates.push(text.slice(firstBrace, lastBrace + 1))
      } else {
        candidates.push(text.slice(firstBrace))
      }
    }
    return [...new Set(candidates.map(c => c.trim()).filter(Boolean))]
  }

  repairJsonCandidate(candidate) {
    if (!candidate) return null

    let text = candidate.trim()
    if (!text.startsWith("{")) return null

    // 문자열 값 안의 리터럴 줄바꿈 이스케이프 (AI가 JSON 문자열 안에 개행을 삽입할 때)
    text = text.replace(/"((?:[^"\\]|\\.)*)"/g, (match, inner) => {
      const escaped = inner.replace(/\n/g, "\\n").replace(/\r/g, "\\r")
      return `"${escaped}"`
    })

    // AI가 JSON 안에 // 라인 주석을 삽입할 때 제거 (문자열 내 // 는 보존)
    text = text.replace(/"(?:[^"\\]|\\.)*"|\/\/[^\n]*/g, m => m.startsWith('"') ? m : '')

    // AI가 JSON 배열/객체 안에 ... 줄임표를 사용할 때 제거
    text = text
      .replace(/,\s*\.{3}\s*(?=[}\]])/g, "")
      .replace(/,\s*\.{3}\s*,/g, ",")
      .replace(/\[\s*\.{3}\s*,/g, "[")
      .replace(/\{\s*\.{3}\s*\}/g, "{}")

    text = text
      .replace(/,\s*([}\]])/g, "$1")
      .replace(/[\u0000-\u001F]+$/g, "")
      .trim()

    const closed = this.closeJsonDelimiters(text)
    return closed && closed !== candidate ? closed : null
  }

  closeJsonDelimiters(text) {
    let inString = false
    let escape = false
    const stack = []
    let out = ""

    for (const char of text) {
      if (escape) {
        escape = false
        out += char
        continue
      }
      if (inString) {
        if (char === "\\") escape = true
        else if (char === "\"") inString = false
        out += char
        continue
      }
      if (char === "\\") {
        escape = true
        out += char
        continue
      }
      if (char === "\"") {
        inString = true
        out += char
        continue
      }
      if (char === "{") {
        stack.push("}")
        out += char
        continue
      }
      if (char === "[") {
        stack.push("]")
        out += char
        continue
      }
      if (char === "}" || char === "]") {
        while (stack.length && stack[stack.length - 1] !== char) {
          out += stack.pop()
        }
        if (stack.length && stack[stack.length - 1] === char) stack.pop()
        out += char
        continue
      }
      out += char
    }

    if (inString) out += "\""
    while (stack.length) out += stack.pop()
    return out
  }

  async requestRulesPreviewResponse({ provider, model, csrfToken, typingEl, userMessage }) {
    const payload = {
      model,
      system: this.buildSystemPrompt(),
      messages: this.rulesChatHistory,
      max_tokens: 2400,
      context: this._buildUsageContext({ action: 'ia_rules' })
    }
    const requestOptions = {
      provider,
      endpointPrefix: this.config.aiBasePath,
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken, "X-Requested-With": "XMLHttpRequest" },
      payload,
      stream: false
    }

    let aiText = await this.requestAiResponse(requestOptions)
    let formData = this.extractRulesFormData(aiText)
    if (formData) return aiText

    if (typingEl) {
      typingEl.innerHTML = '<span style="color:#6366F1;font-size:12px;">룰 생성 형식이 깨져서 다시 정리 중...</span>'
      if (this._rulesChatMessages) this._rulesChatMessages.scrollTop = this._rulesChatMessages.scrollHeight
    }

    const repairedText = await this.requestAiResponse({
      ...requestOptions,
      payload: {
        ...payload,
        max_tokens: 2400,
        messages: [
          { role: "user", content: userMessage },
          { role: "assistant", content: aiText },
          {
            role: "user",
            content: "방금 응답은 JSON 문법이 깨졌습니다. 같은 내용을 유지하되 설명 없이 완전한 preview_rules JSON 하나만 다시 반환해주세요. 5~6개 룰만, 각 content는 짧게 유지하세요."
          }
        ]
      }
    })

    formData = this.extractRulesFormData(repairedText)
    if (formData) return repairedText

    return aiText
  }

  async applyFormData(parsed) {
    const normalized = this.normalizeApplyActionPayload(parsed)
    this.debugLog("applyFormData entry", {
      originalAction: parsed?._action,
      normalizedAction: normalized?._action,
      contextType: this.config.contextType,
      designerBridge: this.config.designerBridge
    })

    if (normalized._action === "fill_page") {
      this.debugLog("applying page form payload", normalized.data || {})
      if (this.shouldCreateAdminPromptTemplate()) await this.createAdminPromptTemplate(normalized.data)
      else if (this.isPromptDesignerContext()) this.fillPromptForm(normalized.data)
      else if (this.isColdmailCampaignContext()) return this.fillColdmailCampaignForm(normalized.data)
      else this.fillPageForm(normalized.data)
    } else if (normalized._action === "fill_prompt") {
      this.debugLog("applying prompt form payload", normalized.data || {})
      if (this.shouldCreateAdminPromptTemplate()) await this.createAdminPromptTemplate(normalized.data)
      else this.fillPromptForm(normalized.data)
    } else if (normalized._action === "fill_designer_section") {
      const designerPayload = this.normalizeDesignerSectionPayload(normalized.data)
      this.debugLog("normalized designer payload", designerPayload)
      this.fillDesignerSection(designerPayload)
      if (designerPayload.style_variant) {
        const templates = this.config.heroTemplates || []
        const match = templates.find(t => t.style_variant === designerPayload.style_variant)
        const displayName = match?.label || match?.name || designerPayload.style_variant
        this.appendSystemNote(_t("system_notes.style_applied", "%{name} 스타일이 적용됐어요. 다른 스타일로 변경하려면 우측 패널의 \"히어로 스타일 변경\"을 클릭하세요.").replace("%{name}", displayName))
      }
    } else if (normalized._action === "fill_designer_all_sections") {
      this.debugLog("applying all designer sections", normalized.data || {})
      this.fillDesignerAllSections(normalized.data)
    } else if (normalized._action === "restructure_designer_page") {
      this.debugLog("restructuring designer page", normalized.data || {})
      this.restructureDesignerPage(normalized.data)
    } else if (normalized._action === "fill_section") {
      this.debugLog("applying section form payload", normalized.data || {})
      this.fillSectionForm(normalized.data)
    } else if (normalized._action === "create_profile_page_sections") {
      this.debugLog("creating profile page sections", normalized.data || {})
      await this.createProfilePageSections(normalized.data)
    } else if (normalized._action === "fill_rules") {
      this.fillRules(normalized.data)
    } else if (normalized._action === "fill_ia_project") {
      this.fillIaProjectForm(normalized.data)
    } else if (normalized._action === "fill_coldmail_campaign") {
      return this.fillColdmailCampaignForm(normalized.data)
    } else if (normalized._action === "create_coldmail_sequences") {
      return await this.createColdmailSequences(normalized.data)
    } else if (normalized._action === "apply_ia_actions") {
      await this.applyIaActions(normalized.data)
    } else if (normalized._action === "apply_prototype_actions") {
      await this.applyPrototypeActions(normalized.data)
    } else if (normalized._action === "apply_image_studio_actions") {
      await this.applyImageStudioActions(normalized.data)
    } else if (normalized._action === "apply_planning_workspace_actions") {
      await this.applyPlanningWorkspaceActions(normalized.data)
    } else if (normalized._action === "apply_quotation_workspace_actions") {
      await this.applyQuotationWorkspaceActions(normalized.data)
    } else if (normalized._action === "apply_user_story_actions") {
      await this.applyUserStoryActions(normalized.data)
    } else if (normalized._action === "create_image_assets") {
      await this.createImageAssets(normalized.data)
    } else if (normalized._action === "create_ai_block_template") {
      await this.createAiBlockTemplate(normalized.data)
    } else if (normalized._action === "create_ai_block_templates_batch") {
      await this.createAiBlockTemplatesBatch(normalized.data)
    }
  }

  _pickSectionTemplateVariants() {
    return new Promise((resolve) => {
      const ALL_VARIANTS = [
        { key: "dark",  label: _t("variant_picker.dark_label", "다크"),      desc: "#0A0A0A 배경" },
        { key: "image", label: _t("variant_picker.image_label", "배경 이미지"), desc: "AI 사진 생성" },
        { key: "pattern", label: _t("variant_picker.pattern_label", "패턴"),    desc: "CSS 도트/격자" },
        { key: "light", label: _t("variant_picker.light_label", "라이트"),    desc: "#FFFFFF 배경" },
      ]

      const el = document.createElement("div")
      el.className = "ba-msg-system"
      el.style.cssText = "flex-direction:column;gap:8px;"

      const selected = new Set(ALL_VARIANTS.map(v => v.key)) // 기본 전체 선택

      const render = () => {
        el.innerHTML = `
          <span style="font-size:12px;font-weight:600;">${_t("variant_picker.question", "어떤 시안을 생성할까요?")}</span>
          <div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:2px;">
            ${ALL_VARIANTS.map(v => `
              <button type="button" data-key="${v.key}" style="
                padding:5px 11px;
                border:1.5px solid ${selected.has(v.key) ? "#6366F1" : "#D1D5DB"};
                background:${selected.has(v.key) ? "#EEF2FF" : "#fff"};
                color:${selected.has(v.key) ? "#6366F1" : "#6B7280"};
                cursor:pointer;font-size:12px;font-weight:500;
              ">${v.label}</button>
            `).join("")}
          </div>
          <div style="display:flex;gap:6px;margin-top:4px;">
            <button type="button" id="ba-variant-confirm" style="
              padding:6px 14px;background:#6366F1;color:#fff;
              border:none;font-size:12px;font-weight:600;cursor:pointer;
            ">${_t("variant_picker.confirm", "생성 시작 (%{count}개)").replace("%{count}", selected.size)}</button>
            <button type="button" id="ba-variant-cancel" style="
              padding:6px 10px;background:transparent;color:#9CA3AF;
              border:1px solid #D1D5DB;font-size:12px;cursor:pointer;
            ">${_t("variant_picker.cancel", "취소")}</button>
          </div>
        `

        el.querySelectorAll("button[data-key]").forEach(btn => {
          btn.addEventListener("click", () => {
            const key = btn.dataset.key
            if (selected.has(key)) { if (selected.size > 1) selected.delete(key) }
            else selected.add(key)
            render()
          })
        })

        el.querySelector("#ba-variant-confirm")?.addEventListener("click", () => {
          el.remove()
          resolve(ALL_VARIANTS.filter(v => selected.has(v.key)))
        })

        el.querySelector("#ba-variant-cancel")?.addEventListener("click", () => {
          el.remove()
          resolve(null)
        })
      }

      render()
      if (this._messages) this._messages.appendChild(el)
      this.scrollToBottom()
    })
  }

  async _sendSectionTemplateVariants(msg, userContent, selectedVariants) {
    this.setLoading(true)
    const model = this._resolveModel(msg)
    const provider = this.getProvider(model)
    const baseSystem = this.buildSystemPrompt()
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ""

    const VARIANT_HINTS = {
      dark:    "다크 배경(#0A0A0A/#111827) 시안 1개만 생성하세요. JSON 블록 1개만 출력하세요. name은 \"섹션명 - 다크\".",
      image:   `배경 이미지(__generate__) 시안 1개만 생성하세요. JSON 블록 1개만 출력하세요. name은 "섹션명 - 배경 이미지".
## 배경 이미지 HTML 필수 구조 (반드시 따를 것)
배경 이미지 div는 반드시 min-height를 지정하고, 오버레이와 콘텐츠를 형제로 배치하세요:
<div style="position:relative; min-height:520px; background:url('__generate__') center/cover no-repeat;" data-image-prompt="영어 프롬프트">
  <div style="position:absolute;inset:0;background:rgba(0,0,0,0.55);"></div>
  <div style="position:relative;z-index:1;max-width:1160px;margin:0 auto;padding:80px 24px;">
    콘텐츠 여기
  </div>
</div>
절대 금지: 콘텐츠를 오버레이 div 안에 넣지 마세요. 오버레이는 빈 div여야 합니다.`,
      pattern: "CSS 도트/선격자 패턴 배경 시안 1개만 생성하세요. JSON 블록 1개만 출력하세요. name은 \"섹션명 - 패턴\".",
      light:   "흰/크림 배경(#FFFFFF/#F5F4EF) 시안 1개만 생성하세요. JSON 블록 1개만 출력하세요. name은 \"섹션명 - 라이트\".",
    }
    const VARIANTS = selectedVariants.map(v => ({ label: v.label, hint: VARIANT_HINTS[v.key] || v.label }))

    // 진행 상황 UI
    const progressEl = document.createElement("div")
    progressEl.className = "ba-msg-system"
    progressEl.style.cssText = "display:flex;flex-direction:column;gap:6px;"
    const updateProgress = (statuses) => {
      progressEl.innerHTML = statuses.map(s =>
        `<span style="font-size:12px;">${s.done ? "✓ " : ""}${s.done ? _t("variant_picker.progress_done", "%{label} 시안 완료").replace("%{label}", s.label) : _t("variant_picker.progress", "%{label} 시안 생성 중...").replace("%{label}", s.label)}</span>`
      ).join("")
    }
    const statuses = VARIANTS.map(v => ({ label: v.label, done: false }))
    updateProgress(statuses)
    if (this._messages) this._messages.appendChild(progressEl)
    this.scrollToBottom()

    // 선택한 시안만 병렬 API 호출
    const templateResults = await Promise.all(VARIANTS.map(async (variant, idx) => {
      const variantSystem = `${baseSystem}\n\n## 이번 응답 규칙\n${variant.hint}\n- HTML은 2500자 이내.\n- 사용자가 요청한 요소(카드, 멤버, 항목 등) 수를 반드시 모두 포함하세요. 잘라서 생략 금지.\n- 모든 텍스트는 순수 한국어로 작성하세요. 한자, 영어 단어를 한국어 문장 안에 섞지 마세요.`
      try {
        const aiText = await this.requestAiResponse({
          provider,
          endpointPrefix: this.config.aiBasePath,
          headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
          payload: {
            model,
            system: variantSystem,
            messages: [...this.chatHistory],
            max_tokens: 4096
          },
          stream: false
        })
        statuses[idx].done = true
        updateProgress(statuses)
        // 단일 JSON 블록 파싱
        const parsed = this.parseAiJsonPayload(aiText)
        if (parsed?._action === "create_ai_block_template") return parsed.data
        // fallback: parseAllAiJsonPayloads
        const all = this.parseAllAiJsonPayloads(aiText).filter(p => p._action === "create_ai_block_template")
        if (all.length) return all[0].data
        return null
      } catch (e) {
        statuses[idx].done = true
        updateProgress(statuses)
        this._bdLog(`variant ${variant.label} 오류: ${e?.message}`)
        return null
      }
    }))

    this.setLoading(false)
    progressEl.remove()

    const templates = templateResults.filter(Boolean)
    if (!templates.length) {
      this.appendSystemNote(_t("system_notes.draft_failed", "시안 생성에 실패했습니다."))
      return
    }

    await this.createAiBlockTemplatesBatch({ templates })

    // 저장 완료 후 요약
    const summary = templates.map(t => `- ${t.name}`).join("\n")
    const summaryText = `${templates.length}가지 시안이 등록되었습니다:\n${summary}`
    this.chatHistory.push({ role: "assistant", content: summaryText })
    this.appendMessage("assistant", summaryText)
    await this.persistMessage("assistant", summaryText)
    this._appendFollowUpButtons(summaryText)
  }

  async createAiBlockTemplatesBatch(data) {
    let templates = data?.templates || []
    this._bdLog("createAiBlockTemplatesBatch: templates.length =", templates.length, templates.map(t => t?.name))
    if (!templates.length) return

    // 라이트 시안이 없으면 다크 시안에서 자동 생성 (AI 토큰 잘림 대비)
    const hasLight = templates.some(t => (t?.name || "").includes("라이트"))
    if (!hasLight) {
      const darkTpl = templates.find(t => (t?.name || "").includes("다크"))
      if (darkTpl) {
        const lightHtml = darkTpl.html_content
          .replace(/bg-\[#0[Aa]0[Aa]0[Aa]\]/g, "bg-white")
          .replace(/bg-\[#111827\]/g, "bg-[#F5F4EF]")
          .replace(/background:\s*#0[Aa]0[Aa]0[Aa]/g, "background:#F5F4EF")
          .replace(/background:\s*#111827/g, "background:#F5F4EF")
          .replace(/text-white/g, "text-[#111827]")
          .replace(/color:\s*#fff(fff)?/gi, "color:#111827")
          .replace(/color:\s*white/gi, "color:#111827")
        const baseName = darkTpl.name.replace(/\s*-\s*다크$/, "")
        templates = [...templates, { name: `${baseName} - 라이트`, html_content: lightHtml, description: "라이트 버전 (자동 생성)", category: darkTpl.category || "콘텐츠" }]
        this._bdLog(`라이트 시안 자동 생성: ${baseName} - 라이트`)
      }
    }

    const total = templates.length
    let created = 0

    // 단일 진행 엘리먼트 — 업데이트하며 사용
    const statusEl = document.createElement("div")
    statusEl.className = "ba-msg-system"
    statusEl.style.cssText = "flex-direction:column;gap:4px;"
    const setStatus = (text) => { statusEl.innerHTML = `<span style="font-size:12px;">${text}</span>` }
    setStatus(_t("loading.saving_draft_start", "저장 중... (0/%{total})").replace("%{total}", total))
    if (this._messages) this._messages.appendChild(statusEl)
    this.scrollToBottom()

    for (let i = 0; i < templates.length; i++) {
      const tplData = templates[i]
      this._bdLog(`시작 (${i+1}/${total}): ${tplData?.name} html_len=${tplData?.html_content?.length} has__generate__=${tplData?.html_content?.includes("__generate__")}`)
      setStatus(_t("loading.saving_draft", "저장 중... (%{done}/%{total})").replace("%{done}", i).replace("%{total}", total) + ` — ${tplData.name || _t("variant_picker.dark_label", "시안")}`)
      try {
        const ok = await this._createSingleAiBlockTemplate(tplData, { reload: false, silent: true })
        this._bdLog(`완료 (${i+1}/${total}): ${tplData?.name} ok=${ok}`)
        created++
        setStatus(_t("loading.saving_draft_done", "저장 중... (%{done}/%{total}) — %{name} 완료").replace("%{done}", created).replace("%{total}", total).replace("%{name}", tplData.name || _t("variant_picker.dark_label", "시안")))
      } catch (e) {
        this._bdLog(`오류 (${i+1}/${total}): ${tplData?.name} ${e?.message}`)
      }
    }
    this._bdLog(`배치 완료. 생성된 수: ${created} / 전체: ${total}`)
    statusEl.remove()
    setTimeout(() => window.location.reload(), 1500)
  }

  async createAiBlockTemplate(data) {
    await this._createSingleAiBlockTemplate(data, { reload: true })
  }

  async _createSingleAiBlockTemplate(data, { reload = true, silent = false } = {}) {
    let htmlContent = data?.html_content || data?.content_data?.html_content
    if (!htmlContent) {
      this._bdLog("[WARN] html_content 없음: " + JSON.stringify(Object.keys(data || {})))
      this.appendSystemNote(_t("system_notes.no_html_content", "HTML 콘텐츠가 없어 템플릿을 생성할 수 없습니다."))
      return false
    }
    const name = data?.name || "AI 블록"
    const description = data?.description || ""
    const category = data?.category || "콘텐츠"
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ""

    try {
      // 이미지 생성을 먼저 처리한 뒤 완성된 HTML로 템플릿 저장
      if (htmlContent.includes("__generate__")) {
        this._bdLog(`이미지 생성 시작: ${name}`)
        const tempTA = document.createElement("textarea")
        tempTA.value = htmlContent
        await this.processAiBlockImageGeneration(tempTA, { silent })
        htmlContent = tempTA.value
        this._bdLog(`이미지 생성 후 __generate__ 잔존: ${htmlContent.includes("__generate__")}`)
      }

      this._bdLog(`fetch 시작: ${name} html_len=${htmlContent.length}`)
      const resp = await fetch("/admin/section_templates/create_from_ai", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
        body: JSON.stringify({ name, html_content: htmlContent, description, category })
      })
      this._bdLog(`fetch status=${resp.status} name=${name}`)
      const result = await resp.json()
      this._bdLog(`fetch result: ${name} success=${result.success} error=${result.error}`)
      if (result.success) {
        if (reload) {
          this.appendSystemNote(_t("system_notes.template_created", "템플릿 \"%{name}\" 생성 완료").replace("%{name}", result.name))
          setTimeout(() => window.location.reload(), 1500)
        }
        return true
      } else {
        this.appendSystemNote(_t("system_notes.template_create_failed", "템플릿 생성 실패: %{error}").replace("%{error}", result.error))
        return false
      }
    } catch (e) {
      this._bdLog(`[ERR] catch: ${name} ${e?.message}`)
      this.appendSystemNote(_t("system_notes.template_create_error", "템플릿 생성 오류: %{error}").replace("%{error}", e.message))
      return false
    }
  }

  async _openUiPlanningImportWizard() {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content || ""
    const headers = { "Content-Type": "application/json", "X-CSRF-Token": csrf }

    // 페이지 레벨 모달 오버레이
    const overlay = document.createElement("div")
    overlay.style.cssText = "position:fixed;inset:0;z-index:10000;background:rgba(0,0,0,0.6);display:flex;align-items:center;justify-content:center;"
    overlay.onclick = (e) => { if (e.target === overlay) overlay.remove() }

    const modal = document.createElement("div")
    modal.style.cssText = "width:860px;max-width:95vw;height:88vh;background:#fff;display:flex;flex-direction:column;box-shadow:0 25px 50px -12px rgba(0,0,0,0.4);overflow:hidden;"
    overlay.appendChild(modal)
    document.body.appendChild(overlay)

    // 모달 헤더 빌더
    const buildHeader = (title, onBack) => {
      const h = document.createElement("div")
      h.style.cssText = "display:flex;align-items:center;gap:10px;padding:16px 24px;background:#1E1E2E;flex-shrink:0;"
      if (onBack) {
        const back = document.createElement("button")
        back.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m15 18-6-6 6-6"/></svg>`
        back.style.cssText = "background:none;border:1px solid #374151;cursor:pointer;color:#9CA3AF;padding:5px;display:flex;align-items:center;"
        back.onclick = onBack
        h.appendChild(back)
      }
      const t = document.createElement("span")
      t.textContent = title
      t.style.cssText = "font-weight:600;font-size:14px;color:#F9FAFB;flex:1;"
      h.appendChild(t)
      const close = document.createElement("button")
      close.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>`
      close.style.cssText = "background:none;border:1px solid #374151;cursor:pointer;padding:5px;color:#9CA3AF;display:flex;align-items:center;"
      close.onclick = () => overlay.remove()
      h.appendChild(close)
      return h
    }

    const setLoading = (msg = _t("loading.fetching", "불러오는 중...")) => {
      modal.innerHTML = ""
      const el = document.createElement("div")
      el.style.cssText = "padding:48px;text-align:center;color:#9CA3AF;font-size:13px;"
      el.textContent = msg
      modal.appendChild(el)
    }

    const listItem = (label, sub, onClick) => {
      const el = document.createElement("div")
      el.style.cssText = "background:#fff;border:1px solid #E5E7EB;padding:16px 20px;cursor:pointer;display:flex;align-items:center;justify-content:space-between;transition:border-color 0.15s,box-shadow 0.15s;"
      el.onmouseenter = () => { el.style.borderColor = "#6366F1"; el.style.boxShadow = "0 1px 4px rgba(99,102,241,0.12)" }
      el.onmouseleave = () => { el.style.borderColor = "#E5E7EB"; el.style.boxShadow = "none" }
      el.onclick = onClick
      const left = document.createElement("div")
      left.style.cssText = "min-width:0;flex:1;"
      const nameEl = document.createElement("div")
      nameEl.textContent = label
      nameEl.style.cssText = "font-weight:600;font-size:14px;color:#111827;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;"
      left.appendChild(nameEl)
      if (sub) {
        const subEl = document.createElement("div")
        subEl.textContent = sub
        subEl.style.cssText = "font-size:12px;color:#9CA3AF;margin-top:4px;"
        left.appendChild(subEl)
      }
      el.appendChild(left)
      const arrow = document.createElement("span")
      arrow.style.cssText = "color:#6366F1;font-size:18px;flex-shrink:0;margin-left:12px;"
      arrow.textContent = "›"
      el.appendChild(arrow)
      return el
    }

    // Step 1: 프로젝트 목록
    const showProjects = async () => {
      setLoading()
      try {
        const res = await fetch("/admin/section_templates/ui_planning_projects", { headers })
        const data = await res.json()
        modal.innerHTML = ""
        modal.appendChild(buildHeader("UI 기획 프로젝트 선택"))
        const body = document.createElement("div")
        body.style.cssText = "flex:1;min-height:0;overflow-y:auto;padding:16px 24px;display:flex;flex-direction:column;gap:10px;"
        if (!data.projects?.length) {
          body.innerHTML = `<div style="padding:48px;text-align:center;color:#9CA3AF;font-size:13px;">프로젝트가 없습니다.</div>`
        } else {
          data.projects.forEach(p => body.appendChild(
            listItem(p.name, `프레임 ${p.frames_count}개 · ${p.updated_at?.slice(0,10) || ""}`, () => showFrames(p.id, p.name))
          ))
        }
        modal.appendChild(body)
      } catch (e) {
        modal.innerHTML = `<div style="padding:24px;color:#EF4444;font-size:13px;">오류: ${e.message}</div>`
      }
    }

    // Step 2: 프레임 목록
    const showFrames = async (projectId, projectName) => {
      setLoading()
      try {
        const res = await fetch(`/admin/section_templates/ui_planning_sections?project_id=${projectId}`, { headers })
        const data = await res.json()
        modal.innerHTML = ""
        modal.appendChild(buildHeader(projectName, showProjects))
        const body = document.createElement("div")
        body.style.cssText = "flex:1;min-height:0;overflow-y:auto;padding:16px 24px;display:flex;flex-direction:column;gap:10px;"
        if (!data.frames?.length) {
          body.innerHTML = `<div style="padding:48px;text-align:center;color:#9CA3AF;font-size:13px;">프레임이 없습니다.</div>`
        } else {
          data.frames.forEach(f => {
            const dim = f.width && f.height ? `${f.width}×${f.height}px` : ""
            body.appendChild(listItem(f.name || "이름 없음", dim, () => showSections(projectId, projectName, f.id, f.name)))
          })
        }
        modal.appendChild(body)
      } catch (e) {
        modal.innerHTML = `<div style="padding:24px;color:#EF4444;font-size:13px;">오류: ${e.message}</div>`
      }
    }

    // Step 3: 섹션 선택 (카드 그리드)
    const showSections = async (projectId, projectName, frameId, frameName) => {
      setLoading()
      try {
        const res = await fetch(`/admin/section_templates/ui_planning_sections?project_id=${projectId}&frame_id=${frameId}`, { headers })
        const data = await res.json()
        modal.innerHTML = ""
        modal.appendChild(buildHeader(frameName || "섹션 선택", () => showFrames(projectId, projectName)))

        const sections = data.sections || []
        const headCss = data.head_css || ""
        const checked = new Set(sections.map(s => s.id))

        const bodyWrap = document.createElement("div")
        bodyWrap.style.cssText = "flex:1;min-height:0;overflow-y:auto;display:flex;flex-direction:column;"

        // 전체 선택 바
        const selectAllBar = document.createElement("div")
        selectAllBar.style.cssText = "display:flex;align-items:center;gap:8px;padding:10px 24px;border-bottom:1px solid #E5E7EB;background:#F9FAFB;flex-shrink:0;"

        const selectAllLabel = document.createElement("label")
        selectAllLabel.className = "admin-checkbox-wrapper"
        selectAllLabel.style.cssText = "display:inline-flex;align-items:center;gap:8px;cursor:pointer;margin:0;"

        const selectAllCb = document.createElement("input")
        selectAllCb.type = "checkbox"
        selectAllCb.className = "admin-checkbox-input"
        selectAllCb.checked = true

        const selectAllCustom = document.createElement("span")
        selectAllCustom.className = "admin-checkbox-custom"
        selectAllCustom.innerHTML = `<svg viewBox="0 0 12 10" fill="none" style="width:12px;height:10px;"><path d="M1 5L4.5 8.5L11 1" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`

        const selectAllText = document.createElement("span")
        selectAllText.style.cssText = "font-size:13px;font-weight:600;color:#374151;"

        selectAllLabel.appendChild(selectAllCb)
        selectAllLabel.appendChild(selectAllCustom)
        selectAllLabel.appendChild(selectAllText)
        selectAllBar.appendChild(selectAllLabel)
        bodyWrap.appendChild(selectAllBar)

        const body = document.createElement("div")
        body.style.cssText = "padding:20px 24px;display:grid;grid-template-columns:repeat(2,1fr);gap:16px;align-content:start;"

        const cardCbMap = new Map() // card → {cb, cbInput}

        const ICON_CHECK = `<svg viewBox="0 0 12 10" fill="none" style="width:12px;height:10px;"><path d="M1 5L4.5 8.5L11 1" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`
        const ICON_DASH  = `<svg viewBox="0 0 12 2" fill="none" style="width:12px;height:2px;"><path d="M1 1H11" stroke="white" stroke-width="2" stroke-linecap="round"/></svg>`

        const syncSelectAll = () => {
          const total = sections.length
          const n = checked.size
          selectAllCb.classList.remove("indeterminate")
          if (n === 0) {
            selectAllCb.checked = false
            selectAllCustom.innerHTML = ICON_CHECK
          } else if (n === total) {
            selectAllCb.checked = true
            selectAllCustom.innerHTML = ICON_CHECK
          } else {
            selectAllCb.checked = false
            selectAllCb.classList.add("indeterminate")
            selectAllCustom.innerHTML = ICON_DASH
          }
          selectAllText.textContent = _t("status.select_all", "전체 선택 (%{n}/%{total})").replace("%{n}", n).replace("%{total}", total)
        }

        selectAllCb.onchange = () => {
          const toCheck = selectAllCb.checked
          sections.forEach(s => {
            const { cbInput, card } = cardCbMap.get(s.id)
            cbInput.checked = toCheck
            toCheck ? checked.add(s.id) : checked.delete(s.id)
            card.style.borderColor = toCheck ? "#6366F1" : "#E5E7EB"
          })
          selectAllCb.classList.remove("indeterminate")
          syncSelectAll()
          updateBtn()
        }

        sections.forEach(s => {
          const card = document.createElement("div")
          card.style.cssText = "border:2px solid #6366F1;background:#fff;cursor:pointer;display:flex;flex-direction:column;"

          // 이름 + 커스텀 체크박스 행
          const topRow = document.createElement("div")
          topRow.style.cssText = "display:flex;align-items:center;gap:8px;padding:10px 12px;border-bottom:1px solid #E5E7EB;"

          const cbLabel = document.createElement("label")
          cbLabel.className = "admin-checkbox-wrapper"
          cbLabel.style.cssText = "display:inline-flex;align-items:center;cursor:pointer;margin:0;flex-shrink:0;"
          cbLabel.onclick = (e) => e.stopPropagation()

          const cbInput = document.createElement("input")
          cbInput.type = "checkbox"
          cbInput.className = "admin-checkbox-input"
          cbInput.checked = true

          const cbCustom = document.createElement("span")
          cbCustom.className = "admin-checkbox-custom"
          cbCustom.innerHTML = `<svg viewBox="0 0 12 10" fill="none" style="width:12px;height:10px;"><path d="M1 5L4.5 8.5L11 1" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`

          cbInput.onchange = () => { cbInput.checked ? checked.add(s.id) : checked.delete(s.id); card.style.borderColor = cbInput.checked ? "#6366F1" : "#E5E7EB"; syncSelectAll(); updateBtn() }

          cbLabel.appendChild(cbInput)
          cbLabel.appendChild(cbCustom)

          const sLabel = document.createElement("span")
          sLabel.textContent = s.name || s.type
          sLabel.style.cssText = "font-size:13px;font-weight:600;color:#111827;"

          topRow.appendChild(cbLabel)
          topRow.appendChild(sLabel)
          card.appendChild(topRow)

          card.onclick = () => { cbInput.checked = !cbInput.checked; cbInput.checked ? checked.add(s.id) : checked.delete(s.id); card.style.borderColor = cbInput.checked ? "#6366F1" : "#E5E7EB"; syncSelectAll(); updateBtn() }

          cardCbMap.set(s.id, { cbInput, card })

          // iframe 미리보기 — 프레임 원본 <head> CSS를 사용하여 정확히 렌더링
          const previewWrap = document.createElement("div")
          previewWrap.style.cssText = "width:100%;height:200px;overflow:hidden;background:#F9FAFB;position:relative;"
          const ifr = document.createElement("iframe")
          const srcdoc = `<!DOCTYPE html><html><head>
<link href="https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@400;500;700&display=swap" rel="stylesheet">
${headCss}
<style>*{margin:0;padding:0;box-sizing:border-box;font-family:"Noto Sans KR",sans-serif;}html,body{background:#fff;line-height:1.6;width:100%;overflow:hidden;}img{max-width:100%;height:auto;}ul,ol{padding:0;margin:0;}</style>
</head><body>${s.html}<script>if(typeof lucide!=="undefined")lucide.createIcons()<\/script></body></html>`
          ifr.setAttribute("srcdoc", srcdoc)
          ifr.setAttribute("sandbox", "allow-scripts")
          // 1160px → 카드 너비에 맞게 스케일
          ifr.style.cssText = "position:absolute;top:0;left:0;width:1160px;height:700px;transform:scale(0.34);transform-origin:top left;pointer-events:none;border:none;"
          previewWrap.appendChild(ifr)
          card.appendChild(previewWrap)
          body.appendChild(card)
        })
        bodyWrap.appendChild(body)
        modal.appendChild(bodyWrap)
        syncSelectAll()

        // 하단 버튼
        const footer = document.createElement("div")
        footer.style.cssText = "display:flex;gap:8px;padding:16px 24px;border-top:1px solid #E5E7EB;background:#F9FAFB;flex-shrink:0;"
        const confirmBtn = document.createElement("button")
        confirmBtn.style.cssText = "flex:1;background:#111827;color:#fff;border:none;padding:11px 0;font-size:14px;font-weight:600;cursor:pointer;"
        const cancelBtn = document.createElement("button")
        cancelBtn.textContent = _t("buttons.cancel", "취소")
        cancelBtn.style.cssText = "padding:11px 20px;background:#fff;border:1px solid #E5E7EB;font-size:14px;cursor:pointer;color:#374151;"
        cancelBtn.onclick = () => overlay.remove()
        footer.appendChild(confirmBtn)
        footer.appendChild(cancelBtn)
        modal.appendChild(footer)

        const updateBtn = () => {
          const n = checked.size
          confirmBtn.textContent = `${_t("buttons.apply_to_section", "섹션 템플릿으로 가져오기")} (${n}개)`
          confirmBtn.disabled = n === 0
          confirmBtn.style.opacity = n === 0 ? "0.4" : "1"
        }
        updateBtn()

        confirmBtn.onclick = async () => {
          const toImport = sections.filter(s => checked.has(s.id)).map(s => ({ name: s.name || s.type, html: s.html }))
          overlay.remove()
          await this._redesignAndImportSections(toImport)
        }
      } catch (e) {
        modal.innerHTML = `<div style="padding:24px;color:#EF4444;font-size:13px;">오류: ${e.message}</div>`
      }
    }

    showProjects()
  }

  async _redesignAndImportSections(wireframeSections) {
    if (!wireframeSections?.length) return

    // 시안 스타일 선택
    const selectedVariants = await this._pickSectionTemplateVariants()
    if (!selectedVariants?.length) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ""
    const model = this._resolveModel()
    const provider = this.getProvider(model)
    const baseSystem = this.buildSystemPrompt()

    const VARIANT_HINTS = {
      dark:    "다크 배경(#0A0A0A/#111827) 시안 1개만 생성하세요. JSON 블록 1개만 출력하세요. name은 \"섹션명 - 다크\".",
      image:   `배경 이미지(__generate__) 시안 1개만 생성하세요. JSON 블록 1개만 출력하세요. name은 "섹션명 - 배경 이미지".
## 배경 이미지 HTML 필수 구조 (반드시 따를 것)
배경 이미지 div는 반드시 min-height를 지정하고, 오버레이와 콘텐츠를 형제로 배치하세요:
<div style="position:relative; min-height:520px; background:url('__generate__') center/cover no-repeat;" data-image-prompt="영어 프롬프트">
  <div style="position:absolute;inset:0;background:rgba(0,0,0,0.55);"></div>
  <div style="position:relative;z-index:1;max-width:1160px;margin:0 auto;padding:80px 24px;">
    콘텐츠 여기
  </div>
</div>
절대 금지: 콘텐츠를 오버레이 div 안에 넣지 마세요. 오버레이는 빈 div여야 합니다.`,
      pattern: "CSS 도트/선격자 패턴 배경 시안 1개만 생성하세요. JSON 블록 1개만 출력하세요. name은 \"섹션명 - 패턴\".",
      light:   "흰/크림 배경(#FFFFFF/#F5F4EF) 시안 1개만 생성하세요. JSON 블록 1개만 출력하세요. name은 \"섹션명 - 라이트\".",
    }

    // 전체 태스크: 섹션 × 시안
    const allTasks = wireframeSections.flatMap(s =>
      selectedVariants.map(v => ({ section: s, variant: v }))
    )
    const total = allTasks.length

    // 진행 UI
    const progressEl = document.createElement("div")
    progressEl.className = "ba-msg-system"
    progressEl.style.cssText = "display:flex;flex-direction:column;gap:4px;"
    const statuses = allTasks.map(t => ({ label: `${t.section.name} - ${t.variant.label}`, done: false, error: false }))
    const updateProgress = () => {
      const done = statuses.filter(s => s.done).length
      progressEl.innerHTML = `<span style="font-size:12px;font-weight:600;">AI 디자인 생성 중... (${done}/${total})</span>` +
        statuses.map(s => `<span style="font-size:11px;color:${s.error ? "#EF4444" : s.done ? "#10B981" : "#9CA3AF"};">${s.done ? "✓ " : s.error ? "✕ " : "· "}${s.label}</span>`).join("")
    }
    updateProgress()
    if (this._messages) this._messages.appendChild(progressEl)
    this.scrollToBottom()

    // 순차 AI 호출 (섹션 × 시안)
    const templates = []
    for (let i = 0; i < allTasks.length; i++) {
      const { section, variant } = allTasks[i]
      const variantSystem = `${baseSystem}\n\n## 이번 응답 규칙\n${VARIANT_HINTS[variant.key] || variant.label}\n- HTML은 2500자 이내.\n- 사용자가 요청한 요소(카드, 멤버, 항목 등) 수를 반드시 모두 포함하세요. 잘라서 생략 금지.\n- 모든 텍스트는 순수 한국어로 작성하세요. 한자, 영어 단어를 한국어 문장 안에 섞지 마세요.`
      const userMsg = `아래 UI 기획 와이어프레임을 참고하여 동일한 구조와 콘텐츠의 프로덕션 품질 Tailwind CSS 섹션을 만들어주세요.\n섹션명: ${section.name}\n\n## 참고 와이어프레임 HTML:\n\`\`\`html\n${section.html}\n\`\`\``
      try {
        const aiText = await this.requestAiResponse({
          provider,
          endpointPrefix: this.config.aiBasePath,
          headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
          payload: {
            model,
            system: variantSystem,
            messages: [{ role: "user", content: userMsg }],
            max_tokens: 4096
          },
          stream: false
        })
        statuses[i].done = true
        updateProgress()
        const parsed = this.parseAiJsonPayload(aiText)
        if (parsed?._action === "create_ai_block_template") { templates.push(parsed.data); continue }
        const all = this.parseAllAiJsonPayloads(aiText).filter(p => p._action === "create_ai_block_template")
        if (all.length) templates.push(all[0].data)
      } catch (e) {
        statuses[i].done = true
        statuses[i].error = true
        updateProgress()
        this._bdLog(`redesign 오류 ${section.name} ${variant.label}: ${e?.message}`)
      }
    }

    progressEl.remove()

    if (!templates.length) {
      this.appendSystemNote(_t("system_notes.design_failed", "디자인 생성에 실패했습니다."))
      return
    }

    await this.createAiBlockTemplatesBatch({ templates })

    const summary = templates.map(t => `- ${t.name}`).join("\n")
    const summaryText = `${templates.length}가지 시안이 등록되었습니다:\n${summary}`
    this.chatHistory.push({ role: "assistant", content: summaryText })
    this.appendMessage("assistant", summaryText)
    await this.persistMessage("assistant", summaryText)
    this._appendFollowUpButtons(summaryText)
  }

  fillDesignerAllSections(data) {
    const sections = data?.sections
    if (!Array.isArray(sections) || sections.length === 0) {
      this.appendSystemNote(_t("system_notes.no_apply_data", "적용할 섹션 데이터가 없습니다."))
      return
    }
    this.debugLog("dispatching profile-page-designer:apply-all-sections-content", sections)
    window.dispatchEvent(new CustomEvent("profile-page-designer:apply-all-sections-content", {
      detail: { sections }
    }))

    const applied = sections.map(s => {
      const label = s.title || s.section_type || "섹션"
      return s.style_variant ? `${label} (${s.style_variant})` : label
    })
    this.appendSystemNote(_t("system_notes.sections_applied", "%{count}개 섹션이 일괄 적용됐어요: %{list}").replace("%{count}", applied.length).replace("%{list}", applied.join(", ")))
  }

  restructureDesignerPage(data) {
    const sections = data?.sections
    if (!Array.isArray(sections) || sections.length === 0) {
      this.appendSystemNote(_t("system_notes.no_restructure_data", "적용할 구조 변경 데이터가 없습니다."))
      return
    }

    this.debugLog("dispatching profile-page-designer:restructure-page", sections)
    window.dispatchEvent(new CustomEvent("profile-page-designer:restructure-page", {
      detail: { sections, header: data?.header, footer: data?.footer }
    }))

    const summary = sections.map((section) => {
      if (section.op === "add") {
        return `추가:${section.title || section.section_type || "섹션"}`
      }
      if (section.op === "replace") {
        return `교체:${section.client_key || "unknown"}→${section.new_section_type || "unknown"}`
      }
      if (section.op === "remove") {
        return `삭제:${section.client_key || "unknown"}`
      }
      return `유지:${section.client_key || section.section_type || "섹션"}`
    })

    this.appendSystemNote(_t("system_notes.page_restructured", "페이지 구조가 재편성됐어요: %{summary}").replace("%{summary}", summary.join(", ")))

    // 이미지 생성 요청 처리 (비동기)
    this.processImageGeneration(sections)
  }

  async processImageGeneration(sections) {
    const IMAGE_FIELDS = ["image_url", "photo_url", "bg_image_url"]
    const tasks = []

    for (const section of sections) {
      const cd = section.content_data
      if (!cd || typeof cd !== "object") continue
      for (const field of IMAGE_FIELDS) {
        if (cd[field] === "__generate__" && cd.image_prompt) {
          tasks.push({ cd, field, prompt: cd.image_prompt, clientKey: section.client_key, arrayKey: null, arrayIndex: null })
        }
      }
      for (const [arrKey, arrVal] of Object.entries(cd)) {
        if (!Array.isArray(arrVal)) continue
        arrVal.forEach((item, idx) => {
          if (!item || typeof item !== "object") return
          for (const field of IMAGE_FIELDS) {
            if (item[field] === "__generate__" && item.image_prompt) {
              tasks.push({ cd: item, field, prompt: item.image_prompt, clientKey: section.client_key, arrayKey: arrKey, arrayIndex: idx })
            }
          }
        })
      }
    }

    if (tasks.length === 0) return

    // 플로팅 배너 + CSS 주입
    this._showImageGenBanner(0, tasks.length)

    // 이미지 필드에 스피너 표시
    this._showImageFieldSpinners(tasks)

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ""
    const basePath = this.config.aiBasePath || "/admin/ai"
    let succeeded = 0
    const updates = []

    for (const task of tasks) {
      try {
        console.log("[ImageGen] Fetching:", `${basePath}/generate_image`, "prompt:", task.prompt.substring(0, 50))
        const controller = new AbortController()
        const timeout = setTimeout(() => controller.abort(), 120000)
        const resp = await fetch(`${basePath}/generate_image`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
          body: JSON.stringify({
            prompt: task.prompt,
            category: "abstract",
            model: this.selectedThreadsImageModel || undefined,
            context: this._buildUsageContext?.() || undefined
          }),
          signal: controller.signal
        })
        clearTimeout(timeout)
        console.log("[ImageGen] Response status:", resp.status)
        const result = await resp.json()
        if (result.success && result.imageUrl) {
          task.cd[task.field] = result.imageUrl
          delete task.cd.image_prompt
          updates.push({ clientKey: task.clientKey, field: task.field, value: result.imageUrl, arrayKey: task.arrayKey, index: task.arrayIndex })
          succeeded++
          this._showImageGenBanner(succeeded, tasks.length)
          this._clearImageFieldSpinner(task)
        } else {
          console.warn("[ImageGen] 실패 응답:", result)
          this.appendSystemNote(_t("system_notes.image_gen_failed", "이미지 생성 실패: %{error}").replace("%{error}", result.error || "알 수 없는 오류"))
          this._clearImageFieldSpinner(task)
        }
      } catch (e) {
        console.error("[ImageGen] 에러:", e)
        this.appendSystemNote(_t("system_notes.image_gen_error", "이미지 생성 오류: %{error}").replace("%{error}", e.message || e))
        this._clearImageFieldSpinner(task)
      }
    }

    this._showImageGenBanner(succeeded, tasks.length, true)

    if (succeeded > 0) {
      // client_key 기반으로 designer controller에 이미지 URL 직접 전달 ("keep" op deep-clone 우회)
      window.dispatchEvent(new CustomEvent("profile-page-designer:update-images", {
        detail: { updates }
      }))

      this.appendSystemNote(_t("system_notes.images_generated_autosave", "%{count}개 이미지 생성 완료 — 자동 저장 중...").replace("%{count}", succeeded))

      // 자동 저장 (프로필 페이지 디자이너 또는 섹션 편집 페이지)
      const form = document.getElementById("profile-page-designer-form") || document.getElementById("profile-page-section-designer-form")
      if (form) {
        form.addEventListener("turbo:submit-end", (e) => {
          if (e.detail?.success) {
            this.appendSystemNote(_t("system_notes.autosave_done", "자동 저장 완료 — 이미지가 페이지에 적용됐어요"))
          }
        }, { once: true })
        form.requestSubmit()
      }
    }
  }

  async processAiBlockImageGeneration(textarea, { silent = false } = {}) {
    const html = textarea.value

    // 1) <img src="__generate__" data-image-prompt="..."> 태그
    const imgRegex = /<img\s[^>]*src=['"]__generate__['"][^>]*>/gi
    const imgMatches = [...html.matchAll(imgRegex)]
    this._bdLog(`imgMatches=${imgMatches.length}`)
    const imgTasks = imgMatches.map(m => {
      const tag = m[0]
      const promptMatch = tag.match(/data-image-prompt=['"]([^'"]+)['"]/i)
      return {
        type: "img",
        tag,
        prompt: promptMatch?.[1] || "professional photo, high quality",
        replace: (html, url) => html.replace(tag, tag.replace(/__generate__/, url))
      }
    })

    // 2) style 내 background url('__generate__') + data-image-prompt 속성이 있는 태그
    // 여는 태그 전체를 잡아서 url(__generate__) 부분만 교체
    const bgRegex = /<[a-zA-Z][^>]*url\(['"]?__generate__['"]?\)[^>]*data-image-prompt=['"]([^'"]+)['"][^>]*>|<[a-zA-Z][^>]*data-image-prompt=['"]([^'"]+)['"][^>]*url\(['"]?__generate__['"]?\)[^>]*>/gi
    const bgMatches = [...html.matchAll(bgRegex)]
    this._bdLog(`bgMatches=${bgMatches.length}`)
    const bgTasks = bgMatches.map(m => {
      const tag = m[0]
      const promptMatch = tag.match(/data-image-prompt=['"]([^'"]+)['"]/i)
      return {
        type: "bg",
        tag,
        prompt: promptMatch?.[1] || "professional background photo, high quality",
        replace: (html, url) => html.replace(tag, tag.replace(/url\(['"]?__generate__['"]?\)/i, `url('${url}')`))
      }
    })

    const tasks = [...imgTasks, ...bgTasks]
    if (tasks.length === 0) return

    this._showImageGenBanner(0, tasks.length)

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ""
    const basePath = this.config.aiBasePath || "/admin/ai"
    let succeeded = 0
    let updatedHtml = textarea.value

    for (const task of tasks) {
      try {
        const controller = new AbortController()
        const timeout = setTimeout(() => controller.abort(), 120000)
        const resp = await fetch(`${basePath}/generate_image`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
          body: JSON.stringify({
            prompt: task.prompt,
            category: "abstract",
            model: this.selectedThreadsImageModel || undefined,
            context: this._buildUsageContext?.() || undefined
          }),
          signal: controller.signal
        })
        clearTimeout(timeout)
        const result = await resp.json()
        if (result.success && result.imageUrl) {
          updatedHtml = task.replace(updatedHtml, result.imageUrl)
          succeeded++
          this._showImageGenBanner(succeeded, tasks.length)
        } else {
          if (!silent) this.appendSystemNote(_t("system_notes.image_gen_failed", "이미지 생성 실패: %{error}").replace("%{error}", result.error || "알 수 없는 오류"))
        }
      } catch (e) {
        if (!silent) this.appendSystemNote(_t("system_notes.image_gen_error", "이미지 생성 오류: %{error}").replace("%{error}", e.message || e))
      }
    }

    this._showImageGenBanner(succeeded, tasks.length, true)

    if (succeeded > 0) {
      textarea.value = updatedHtml
      textarea.dispatchEvent(new Event("input", { bubbles: true }))
      textarea.dispatchEvent(new Event("change", { bubbles: true }))
      if (!silent) this.appendSystemNote(_t("system_notes.images_generated_count", "%{count}개 이미지 생성 완료").replace("%{count}", succeeded))
    }
  }

  _showImageGenBanner(done, total, complete = false) {
    if (!document.getElementById("ba-img-spin-style")) {
      const s = document.createElement("style")
      s.id = "ba-img-spin-style"
      s.textContent = `
        @keyframes ba-spin { to { transform: rotate(360deg); } }
        .ba-img-spinner { width: 22px; height: 22px; flex: 0 0 22px; display: inline-block; animation: ba-spin 0.8s linear infinite; }
      `
      document.head.appendChild(s)
    }

    let banner = document.getElementById("ba-image-gen-banner")
    if (!banner) {
      banner = document.createElement("div")
      banner.id = "ba-image-gen-banner"
      banner.style.cssText = `
        position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
        background: #111827; color: #fff; padding: 12px 20px;
        font-size: 13px; font-weight: 600; letter-spacing: -0.01em;
        display: flex; align-items: center; gap: 10px;
        box-shadow: 0 4px 20px rgba(0,0,0,0.3); z-index: 9999;
        min-width: 260px; justify-content: center;
      `
      document.body.appendChild(banner)
    }

    if (complete) {
      banner.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6366F1" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        이미지 생성 완료
      `
      setTimeout(() => banner.remove(), 2000)
    } else {
      const pct = total > 0 ? Math.round((done / total) * 100) : 0
      banner.innerHTML = `
        <svg class="ba-img-spinner" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="9" stroke="#C7D2FE" stroke-width="3"></circle><path d="M12 3a9 9 0 0 1 9 9" stroke="#4F46E5" stroke-width="3" stroke-linecap="round"></path></svg>
        이미지 생성 중... ${done}/${total}
        <span style="color:#6366F1;font-weight:700;">${pct}%</span>
      `
    }
  }

  async _runSequentialConnectFlow() {
    const app = window.__prototypeApp
    if (!app || !Array.isArray(app.frames) || app.frames.length === 0) {
      this.appendSystemNote("프레임 정보를 가져올 수 없어요. 페이지를 새로고침 후 다시 시도해주세요.")
      return
    }

    const sorted = [...app.frames].sort((a, b) => (a.canvasX || 0) - (b.canvasX || 0))
    if (sorted.length < 2) {
      this.appendSystemNote("연결할 프레임이 2개 이상 필요해요.")
      return
    }

    const total = sorted.length - 1
    let done = 0

    let banner = document.getElementById("ba-seq-connect-banner")
    if (!banner) {
      banner = document.createElement("div")
      banner.id = "ba-seq-connect-banner"
      banner.style.cssText = `
        position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
        background: #111827; color: #fff; padding: 12px 20px;
        font-size: 13px; font-weight: 600; letter-spacing: -0.01em;
        display: flex; align-items: center; gap: 10px;
        box-shadow: 0 4px 20px rgba(0,0,0,0.3); z-index: 9999;
        min-width: 260px; justify-content: center;
      `
      document.body.appendChild(banner)
    }
    banner.innerHTML = `<span>순서대로 연결 중… 0/${total}</span>`

    for (let i = 0; i < sorted.length - 1; i++) {
      const src = sorted[i]
      const tgt = sorted[i + 1]
      let created = null
      try {
        created = await app.createHotspot(src.id, { x: 0, y: 0, width: src.width, height: src.height })
        if (created?.id) {
          await app.updateHotspot(created.id, { target_frame_id: tgt.id })
          done++
        }
      } catch (e) {
        console.warn(`[sequential] 프레임 ${src.id}→${tgt.id} 연결 실패`, e)
        if (created?.id) { try { await app.deleteHotspot(created.id) } catch (_) {} }
      }
      banner.innerHTML = `<span>순서대로 연결 중… ${done}/${total}</span>`
    }

    banner.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6366F1" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
      ${done}개 연결 완료
    `
    setTimeout(() => banner.remove(), 1500)
    this.appendSystemNote(`프레임 ${done}개를 순서대로 연결했어요.`)
    if (done > 0) await this._autoAssignStartFrameAfterConnect()
  }

  // 연결을 1건 이상 만들었고 아직 시작 화면이 없으면, 가장 왼쪽(첫) 프레임을 홈으로 자동 지정.
  // 사용자가 이미 시작 화면을 지정했으면 절대 덮어쓰지 않는다.
  async _autoAssignStartFrameAfterConnect() {
    const app = window.__prototypeApp
    if (!app || app.startFrameId) return
    const sorted = [...(app.frames || [])].sort((a, b) => (a.canvasX || 0) - (b.canvasX || 0))
    const first = sorted[0]
    if (!first || typeof app._setStartFrame !== "function") return
    try {
      await app._setStartFrame(first.id)
      this.appendSystemNote(`'${first.name}' 화면을 시작 화면으로 자동 지정했어요.`)
    } catch (e) {
      console.warn("[prototype] 시작 화면 자동 지정 실패", e)
    }
  }

  _showFullConnectBanner(done, total, complete = false) {
    if (!document.getElementById("ba-img-spin-style")) {
      const s = document.createElement("style")
      s.id = "ba-img-spin-style"
      s.textContent = `
        @keyframes ba-spin { to { transform: rotate(360deg); } }
        .ba-img-spinner { width: 22px; height: 22px; flex: 0 0 22px; display: inline-block; animation: ba-spin 0.8s linear infinite; }
      `
      document.head.appendChild(s)
    }

    let banner = document.getElementById("ba-full-connect-banner")
    if (!banner) {
      banner = document.createElement("div")
      banner.id = "ba-full-connect-banner"
      banner.style.cssText = `
        position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
        background: #111827; color: #fff; padding: 12px 20px;
        font-size: 13px; font-weight: 600; letter-spacing: -0.01em;
        display: flex; align-items: center; gap: 10px;
        box-shadow: 0 4px 20px rgba(0,0,0,0.3); z-index: 9999;
        min-width: 280px; justify-content: center;
      `
      document.body.appendChild(banner)
    }

    if (complete) {
      banner.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6366F1" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        전체 연결 완료
      `
      setTimeout(() => banner.remove(), 1500)
    } else {
      const pct = total > 0 ? Math.round((done / total) * 100) : 0
      banner.innerHTML = `
        <svg class="ba-img-spinner" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="9" stroke="#C7D2FE" stroke-width="3"></circle><path d="M12 3a9 9 0 0 1 9 9" stroke="#4F46E5" stroke-width="3" stroke-linecap="round"></path></svg>
        전체 요소 연결 중… 시간이 다소 걸릴 수 있어요 — ${done}/${total}
        <span style="color:#6366F1;font-weight:700;">${pct}%</span>
      `
    }
  }

  async _runFullConnectFlow() {
    const app = window.__prototypeApp
    if (!app || !Array.isArray(app.frames) || app.frames.length === 0) {
      this.appendSystemNote("프레임 정보를 가져올 수 없어요. 페이지를 새로고침 후 다시 시도해주세요.")
      return
    }

    const frames = app.frames
    if (frames.length > 30) {
      const ok = window.confirm(`프레임이 ${frames.length}개로 많아 시간과 AI 비용이 더 걸려요. 계속할까요?`)
      if (!ok) return
    }

    const total = frames.length
    let done = 0
    this._showFullConnectBanner(0, total)

    const projectId = this.config.resourceId
    const model = this._resolveModel("100% 요소 단위로 전체 연결")
    const provider = this.getProvider(model)
    const systemPrompt = this.buildSystemPrompt()

    // Build the FRAMES_JSON context (same as injected into system prompt)
    const framesJson = JSON.stringify(
      frames.map(f => ({ id: f.id, name: f.name || `프레임 ${f.id}`, width: f.width, height: f.height }))
    )

    // Worker pool: N=3 concurrent workers stealing from frame queue
    const CONCURRENCY = 3
    let nextFrameIndex = 0
    const prevParallelFlag = window.__uiPlanningParallelGeneration
    window.__uiPlanningParallelGeneration = true

    const processFrame = async (frame) => {
      // Build single-frame ELEMENTS_CONTEXT
      let elementsCtx = null
      try {
        if (typeof window.buildPrototypeElementsContext === "function") {
          const singleFrameApp = { frames: [frame], zoom: app.zoom }
          elementsCtx = window.buildPrototypeElementsContext(singleFrameApp)
        }
      } catch (e) {
        console.warn("[fullConnect] ELEMENTS_CONTEXT build error for frame", frame.id, e)
      }

      const userMsg = "100% 요소 단위로 전체 연결"
      let payload = userMsg
      // 단일메시지 경로와 동일하게 실제 요소가 1개 이상일 때만 컨텍스트 주입.
      // 빈 elements:[] 를 주입하면 AI가 "연결할 요소 없음"으로 판단해 버린다.
      const totalElements = (elementsCtx?.frames || []).reduce((s, f) => s + (f.elements?.length || 0), 0)
      if (elementsCtx && totalElements > 0) {
        payload = `${userMsg}\n\n<ELEMENTS_CONTEXT>\n${JSON.stringify(elementsCtx)}\n</ELEMENTS_CONTEXT>`
      }

      try {
        const aiText = await this.requestAiResponse({
          provider,
          endpointPrefix: this.config.aiBasePath,
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
          },
          payload: {
            model: model,
            system: systemPrompt,
            messages: [{ role: "user", content: payload }],
            max_tokens: 8192,
            context: this._buildUsageContext()
          },
          stream: false
        })

        // 단일메시지 경로와 동일한 견고한 추출기 재사용 (잘린 JSON 복구 + bare {actions} 폴백)
        const formData = this.extractFormData(aiText)
        if (formData?._action === "apply_prototype_actions" && Array.isArray(formData.data?.actions)) {
          return await this.applyPrototypeActions(formData.data) || 0
        }
        console.warn("[fullConnect] no applicable actions for frame", frame.id)
      } catch (e) {
        console.warn("[fullConnect] frame", frame.id, "error:", e.message)
      }
      return 0
    }

    let created = 0
    const workerLoop = async () => {
      while (true) {
        const idx = nextFrameIndex++
        if (idx >= total) break
        // await를 먼저 끝낸 뒤 동기 증가 — 동시 워커 간 read-modify-write 경쟁 방지
        const n = await processFrame(frames[idx])
        created += n
        done++
        this._showFullConnectBanner(done, total)
      }
    }

    await Promise.all(Array.from({ length: CONCURRENCY }, () => workerLoop()))

    window.__uiPlanningParallelGeneration = prevParallelFlag
    this._showFullConnectBanner(total, total, true)
    if (created > 0) {
      this.appendSystemNote(`전체 연결 완료 — ${created}개 연결을 만들었어요.`)
      await this._autoAssignStartFrameAfterConnect()
    } else {
      this.appendSystemNote("연결을 만들지 못했어요. 화면 속 버튼·아이콘을 자동 인식하지 못했을 수 있어요. '최소: 순서대로 자동 연결'로 빠르게 잇거나, 더 강력한 모델로 다시 시도해보세요.")
    }
  }

  // 프레임 HTML 문자열에서 클릭 가능한 요소의 라벨/태그를 추출한다(렌더 불필요).
  // ElementInventory.normalizeLabel 과 동일 규칙: textContent → aria-label → title → icon:<lucide>.
  // 좌표는 안 뽑는다 — 렌더 시점에 PrototypeApp 이 라벨로 자동 계산하기 때문.
  _deterministicExtractClickables(frame) {
    const html = frame?.content
    if (!html || typeof html !== "string") return []
    let doc
    try {
      doc = new DOMParser().parseFromString(html, "text/html")
    } catch (e) {
      console.warn("[detConnect] DOMParser 실패 frame", frame?.id, e)
      return []
    }

    const normalize = (el) => {
      const text = (el.textContent || "").trim().replace(/\s+/g, " ")
      if (text) return text.slice(0, PROTO_MAX_LABEL_LEN)
      const aria = (el.getAttribute("aria-label") || "").trim()
      if (aria) return aria.slice(0, PROTO_MAX_LABEL_LEN)
      const title = (el.getAttribute("title") || "").trim()
      if (title) return title.slice(0, PROTO_MAX_LABEL_LEN)
      const iconEl = el.querySelector("[data-lucide]")
      const name = iconEl?.getAttribute("data-lucide")
      if (name) return `icon:${name}`.slice(0, PROTO_MAX_LABEL_LEN)
      return ""
    }

    const out = []
    const seen = new Set()
    const nodes = doc.querySelectorAll(PROTO_CLICKABLE_SELECTOR)
    for (const el of nodes) {
      // 중첩 클릭요소가 있으면 가장 안쪽(leaf) 것만 사용
      if (el.querySelector(PROTO_CLICKABLE_SELECTOR)) continue
      const label = normalize(el)
      if (!label) continue
      if (seen.has(label)) continue
      seen.add(label)
      out.push({ label, tag: el.tagName })
      if (out.length >= PROTO_MAX_ELEMENTS_PER_FRAME) break
    }
    return out
  }

  // 고급: 100% 요소 단위로 전체 연결 — 결정론 경로.
  async _runDeterministicElementConnectFlow() {
    const app = window.__prototypeApp
    if (!app || !Array.isArray(app.frames) || app.frames.length === 0) {
      this.appendSystemNote("프레임 정보를 가져올 수 없어요. 페이지를 새로고침 후 다시 시도해주세요.")
      return
    }
    const frames = app.frames
    if (frames.length < 2) {
      this.appendSystemNote("연결할 프레임이 2개 이상 필요해요.")
      return
    }

    // 캔버스 좌→우 흐름 순으로 정렬 — '뒤로→직전' 의미와 첫 프레임(홈) 자동 시작에 맞춘다
    const ordered = [...frames].sort((a, b) => (a.canvasX || 0) - (b.canvasX || 0))
    const frameRefs = ordered.map((f) => ({ id: f.id, name: f.name || `프레임 ${f.id}` }))
    const total = ordered.length

    this._showFullConnectBanner(0, total)

    // 각 프레임의 클릭 요소 추출
    const clickablesByIndex = []
    for (let i = 0; i < ordered.length; i++) {
      clickablesByIndex[i] = this._deterministicExtractClickables(ordered[i])
      this._showFullConnectBanner(i + 1, total)
    }

    const actions = buildConnectionActions(frameRefs, clickablesByIndex)

    let created = 0
    if (actions.length > 0) {
      created = await this.applyPrototypeActions({
        actions,
        auto_start_from_first_connection: true,
        summary: "요소 단위 전체 연결"
      }) || 0
    }

    this._showFullConnectBanner(total, total, true)

    if (created > 0) {
      let note = `전체 연결 완료 — ${created}개 연결을 만들었어요.`
      if (actions.length > created) note += ` (대상 요소를 못 찾은 ${actions.length - created}개는 건너뛰었어요.)`
      this.appendSystemNote(note)
      await this._autoAssignStartFrameAfterConnect()
    } else {
      this.appendSystemNote("연결할 대상을 자동으로 찾지 못했어요. 화면 이름과 버튼 이름이 비슷할수록 잘 연결돼요. '최소: 순서대로 자동 연결'로 빠르게 이어볼 수도 있어요.")
    }
  }

  _classifyClickableElements(ctx) {
    const flat = []
    const labelFrameCounts = new Map()
    for (const frame of (ctx.frames || [])) {
      const fid = frame.frame_id ?? frame.id
      const fname = frame.frame_name || frame.name || `프레임 ${fid}`
      const seen = new Set()
      for (const el of (frame.elements || [])) {
        const key = el.label
        if (seen.has(key)) continue
        seen.add(key)
        flat.push({ frameId: fid, frameName: fname, label: el.label, tag: el.tag || "" })
        labelFrameCounts.set(key, (labelFrameCounts.get(key) || 0) + 1)
      }
    }

    const result = { menu: [], button: [], iconBtn: [], textLink: [], other: [] }
    for (const item of flat) {
      const { label, tag } = item
      const repeatNav = (labelFrameCounts.get(label) || 0) >= 2 && label.length >= 2
      if (label === "≡" || label === "☰" || label.startsWith("icon:menu") || repeatNav) {
        result.menu.push(item)
      } else if (label.startsWith("icon:") || label.length <= 2) {
        result.iconBtn.push(item)
      } else if (tag === "BUTTON") {
        result.button.push(item)
      } else if (tag === "A") {
        result.textLink.push(item)
      } else {
        result.other.push(item)
      }
    }
    return result
  }

  _showSelectiveConnectModal(categorized) {
    return new Promise((resolve) => {
      const CATEGORY_LABELS = {
        menu: "메뉴/내비게이션",
        button: "일반 버튼",
        iconBtn: "아이콘 버튼",
        textLink: "텍스트 링크",
        other: "기타"
      }
      const CATEGORY_ORDER = ["menu", "button", "iconBtn", "textLink", "other"]

      if (!document.getElementById("ba-sel-modal-style")) {
        const styleEl = document.createElement("style")
        styleEl.id = "ba-sel-modal-style"
        styleEl.textContent = `
          .sel-checkbox {
            appearance: none;
            -webkit-appearance: none;
            -moz-appearance: none;
            width: 16px;
            height: 16px;
            border: 1.5px solid #D1D5DB;
            background: #fff;
            cursor: pointer;
            position: relative;
            flex-shrink: 0;
            margin: 0;
            transition: border-color 0.12s ease, background-color 0.12s ease;
            border-radius: 0;
          }
          .sel-checkbox:hover { border-color: #6366F1; }
          .sel-checkbox:focus-visible { outline: 2px solid #C7D2FE; outline-offset: 1px; }
          .sel-checkbox:checked {
            background-color: #6366F1;
            border-color: #6366F1;
            background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none'><path d='M3.5 8.5l3 3 6-7' stroke='white' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'/></svg>");
            background-size: 12px 12px;
            background-position: center;
            background-repeat: no-repeat;
          }
          .sel-checkbox.is-indeterminate {
            background-color: #6366F1;
            border-color: #6366F1;
            background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none'><path d='M3 8h10' stroke='white' stroke-width='2.2' stroke-linecap='round'/></svg>");
            background-size: 12px 12px;
            background-position: center;
            background-repeat: no-repeat;
          }
          .sel-row:hover { background: #F9FAFB; }
        `
        document.head.appendChild(styleEl)
      }

      const overlay = document.createElement("div")
      overlay.style.cssText = `
        position: fixed; inset: 0; background: rgba(17,24,39,0.6);
        z-index: 10000; display: flex; align-items: center; justify-content: center;
        font-family: 'Pretendard Variable', Pretendard, sans-serif;
      `

      const modal = document.createElement("div")
      modal.style.cssText = `
        background: #fff; width: 580px; max-width: 95vw; max-height: 80vh;
        display: flex; flex-direction: column; box-shadow: 0 8px 40px rgba(0,0,0,0.25);
      `

      const selectedKeys = new Set()
      const collapsed = {}
      let searchQuery = ""

      const makeKey = (item) => `${item.frameId}:${item.label}:${item.tag}`

      const totalAll = CATEGORY_ORDER.reduce((s, c) => s + (categorized[c]?.length || 0), 0)

      const renderModal = () => {
        const allChecked = totalAll > 0 && selectedKeys.size === totalAll
        const someChecked = selectedKeys.size > 0 && selectedKeys.size < totalAll
        modal.innerHTML = `
          <div style="padding: 20px 24px 16px; border-bottom: 1px solid #E5E7EB;">
            <div style="display: flex; align-items: center; justify-content: space-between;">
              <h2 style="margin: 0; font-size: 16px; font-weight: 700; color: #111827; letter-spacing: -0.01em;">어떤 요소를 연결할지 골라주세요</h2>
              <button id="sel-modal-close" type="button" style="background: none; border: none; cursor: pointer; padding: 4px; color: #6B7280; font-size: 20px; line-height: 1;">&times;</button>
            </div>
            <p style="margin: 8px 0 0; font-size: 13px; color: #6B7280; line-height: 1.5;">시뮬레이션에 필요한 흐름만 골라 연결할 수 있어요. 메뉴 + 핵심 버튼만 체크해도 전체 시연이 가능해요.</p>
          </div>
          <div style="padding: 12px 24px; border-bottom: 1px solid #E5E7EB;">
            <input id="sel-modal-search" type="text" placeholder="라벨 검색…" style="
              width: 100%; box-sizing: border-box; padding: 9px 12px;
              border: 1px solid #E5E7EB; font-size: 13px; outline: none;
              font-family: inherit; color: #111827; border-radius: 0;
            " value="${this.escapeHtml(searchQuery)}" />
          </div>
          <label style="
            display: flex; align-items: center; gap: 10px;
            padding: 10px 24px; border-bottom: 1px solid #E5E7EB; background: #F9FAFB;
            cursor: pointer; user-select: none;
          ">
            <input id="sel-modal-all" type="checkbox" class="sel-checkbox${someChecked ? " is-indeterminate" : ""}" ${allChecked ? "checked" : ""} />
            <span style="flex: 1; font-size: 13px; font-weight: 700; color: #374151;">모든 항목 선택</span>
            <span style="font-size: 12px; color: #6B7280;">총 ${totalAll}개 / 선택 <span style="color: #6366F1; font-weight: 700;">${selectedKeys.size}</span>개</span>
          </label>
          <div id="sel-modal-body" style="flex: 1; overflow-y: auto; padding: 0;">
            ${CATEGORY_ORDER.map(cat => {
              const items = categorized[cat] || []
              if (items.length === 0) return ""
              const isCollapsed = collapsed[cat]
              const catLabel = CATEGORY_LABELS[cat]
              const filtered = items.filter(item =>
                !searchQuery || item.label.toLowerCase().includes(searchQuery.toLowerCase())
              )
              const catCheckedCount = items.filter(item => selectedKeys.has(makeKey(item))).length
              const allCatChecked = items.length > 0 && catCheckedCount === items.length
              const someCatChecked = catCheckedCount > 0 && catCheckedCount < items.length
              const chevronPath = isCollapsed ? "9 18 15 12 9 6" : "6 9 12 15 18 9"
              return `
                <div style="border-bottom: 1px solid #F3F4F6;">
                  <div class="sel-cat-header" data-cat="${cat}" style="
                    display: flex; align-items: center; gap: 10px;
                    padding: 11px 24px; cursor: pointer; background: #fff;
                    user-select: none;
                  ">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6B7280" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="${chevronPath}"/></svg>
                    <span style="font-size: 13px; font-weight: 700; color: #374151; flex: 1;">
                      ${this.escapeHtml(catLabel)} <span style="color: #9CA3AF; font-weight: 500; margin-left: 4px;">${items.length}개</span>
                    </span>
                    <label class="sel-cat-all-label" data-cat="${cat}" style="display: flex; align-items: center; gap: 6px; font-size: 12px; color: #6366F1; cursor: pointer; font-weight: 600;">
                      <input type="checkbox" class="sel-checkbox sel-cat-all${someCatChecked ? " is-indeterminate" : ""}" data-cat="${cat}" ${allCatChecked ? "checked" : ""} />
                      전체 선택
                    </label>
                  </div>
                  ${!isCollapsed ? `
                    <div class="sel-cat-items">
                      ${filtered.length === 0 ? `<div style="padding: 10px 24px; font-size: 12px; color: #9CA3AF;">검색 결과 없음</div>` : filtered.map(item => {
                        const key = makeKey(item)
                        const checked = selectedKeys.has(key)
                        return `
                          <label class="sel-row" style="
                            display: flex; align-items: center; gap: 12px;
                            padding: 8px 24px; cursor: pointer; font-size: 13px; color: #374151;
                          ">
                            <input type="checkbox" class="sel-checkbox sel-item-cb" data-key="${this.escapeHtml(key)}" ${checked ? "checked" : ""} />
                            <span style="flex: 1;">${this.escapeHtml(item.label)}</span>
                            <span style="font-size: 11px; color: #9CA3AF;">${this.escapeHtml(item.frameName)}</span>
                          </label>
                        `
                      }).join("")}
                    </div>
                  ` : ""}
                </div>
              `
            }).join("")}
          </div>
          <div style="padding: 14px 24px; border-top: 1px solid #E5E7EB; display: flex; justify-content: flex-end; gap: 10px;">
            <button id="sel-modal-cancel" type="button" style="
              padding: 10px 18px; border: 1px solid #E5E7EB; background: #fff;
              font-size: 13px; font-weight: 700; cursor: pointer; color: #374151;
              font-family: inherit; border-radius: 0;
            ">취소</button>
            <button id="sel-modal-confirm" type="button" ${selectedKeys.size === 0 ? "disabled" : ""} style="
              padding: 10px 20px; background: ${selectedKeys.size === 0 ? "#E5E7EB" : "linear-gradient(90deg, #4F46E5 31%, #6366F1 100%)"};
              color: ${selectedKeys.size === 0 ? "#9CA3AF" : "#fff"}; border: none;
              font-size: 13px; font-weight: 700; cursor: ${selectedKeys.size === 0 ? "not-allowed" : "pointer"};
              font-family: inherit; border-radius: 0;
            ">선택한 ${selectedKeys.size}개 연결</button>
          </div>
        `

        modal.querySelector("#sel-modal-close").addEventListener("click", () => {
          overlay.remove()
          resolve(new Set())
        })
        modal.querySelector("#sel-modal-cancel").addEventListener("click", () => {
          overlay.remove()
          resolve(new Set())
        })

        const confirmBtn = modal.querySelector("#sel-modal-confirm")
        if (confirmBtn && !confirmBtn.disabled) {
          confirmBtn.addEventListener("click", () => {
            overlay.remove()
            resolve(new Set(selectedKeys))
          })
        }

        modal.querySelector("#sel-modal-search").addEventListener("input", (e) => {
          searchQuery = e.target.value
          renderModal()
        })

        const allCb = modal.querySelector("#sel-modal-all")
        allCb.addEventListener("change", (e) => {
          e.stopPropagation()
          if (allCb.checked) {
            CATEGORY_ORDER.forEach(cat => (categorized[cat] || []).forEach(item => selectedKeys.add(makeKey(item))))
          } else {
            selectedKeys.clear()
          }
          renderModal()
        })

        modal.querySelectorAll(".sel-cat-header").forEach(header => {
          header.addEventListener("click", (e) => {
            if (e.target.closest(".sel-cat-all-label")) return
            const cat = header.dataset.cat
            collapsed[cat] = !collapsed[cat]
            renderModal()
          })
        })

        modal.querySelectorAll(".sel-cat-all").forEach(cb => {
          cb.addEventListener("click", (e) => e.stopPropagation())
          cb.addEventListener("change", (e) => {
            e.stopPropagation()
            const cat = cb.dataset.cat
            const items = categorized[cat] || []
            if (cb.checked) {
              items.forEach(item => selectedKeys.add(makeKey(item)))
            } else {
              items.forEach(item => selectedKeys.delete(makeKey(item)))
            }
            renderModal()
          })
        })

        modal.querySelectorAll(".sel-item-cb").forEach(cb => {
          cb.addEventListener("change", () => {
            if (cb.checked) selectedKeys.add(cb.dataset.key)
            else selectedKeys.delete(cb.dataset.key)
            renderModal()
          })
        })
      }

      renderModal()
      overlay.appendChild(modal)
      document.body.appendChild(overlay)

      overlay.addEventListener("click", (e) => {
        if (e.target === overlay) {
          overlay.remove()
          resolve(new Set())
        }
      })

      const onKey = (e) => {
        if (e.key === "Escape") {
          document.removeEventListener("keydown", onKey)
          overlay.remove()
          resolve(new Set())
        }
      }
      document.addEventListener("keydown", onKey)
    })
  }

  _showSelectiveConnectBanner(done, total, complete = false) {
    if (!document.getElementById("ba-img-spin-style")) {
      const s = document.createElement("style")
      s.id = "ba-img-spin-style"
      s.textContent = `
        @keyframes ba-spin { to { transform: rotate(360deg); } }
        .ba-img-spinner { width: 22px; height: 22px; flex: 0 0 22px; display: inline-block; animation: ba-spin 0.8s linear infinite; }
      `
      document.head.appendChild(s)
    }

    let banner = document.getElementById("ba-selective-connect-banner")
    if (!banner) {
      banner = document.createElement("div")
      banner.id = "ba-selective-connect-banner"
      banner.style.cssText = `
        position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
        background: #111827; color: #fff; padding: 12px 20px;
        font-size: 13px; font-weight: 600; letter-spacing: -0.01em;
        display: flex; align-items: center; gap: 10px;
        box-shadow: 0 4px 20px rgba(0,0,0,0.3); z-index: 9999;
        min-width: 280px; justify-content: center;
      `
      document.body.appendChild(banner)
    }

    if (complete) {
      banner.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6366F1" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        선택 연결 완료
      `
      setTimeout(() => banner.remove(), 1500)
    } else {
      const pct = total > 0 ? Math.round((done / total) * 100) : 0
      banner.innerHTML = `
        <svg class="ba-img-spinner" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="9" stroke="#C7D2FE" stroke-width="3"></circle><path d="M12 3a9 9 0 0 1 9 9" stroke="#4F46E5" stroke-width="3" stroke-linecap="round"></path></svg>
        선택 요소 연결 중… — ${done}/${total}
        <span style="color:#6366F1;font-weight:700;">${pct}%</span>
      `
    }
  }

  async _runSelectiveConnectFlow() {
    const app = window.__prototypeApp
    if (!app || !Array.isArray(app.frames) || app.frames.length === 0) {
      this.appendSystemNote("프레임 정보를 가져올 수 없어요. 페이지를 새로고침 후 다시 시도해주세요.")
      return
    }

    if (typeof window.buildPrototypeElementsContext !== "function") {
      this.appendSystemNote("요소 정보를 불러올 수 없어요. 페이지를 새로고침 후 다시 시도해주세요.")
      return
    }

    const ctx = window.buildPrototypeElementsContext(app)
    if (!ctx || !Array.isArray(ctx.frames) || ctx.frames.length === 0) {
      this.appendSystemNote("클릭 가능한 요소가 없어요. 먼저 화면을 로드해주세요.")
      return
    }

    const categorized = this._classifyClickableElements(ctx)
    const selectedKeys = await this._showSelectiveConnectModal(categorized)

    if (selectedKeys.size === 0) {
      this.appendSystemNote("선택 취소")
      return
    }

    // Rebuild selectedKeys as Map<frameId, Set<labelKey>> for fast lookup
    // key format: `${frameId}:${label}:${tag}`
    const frameKeyMap = new Map()
    for (const key of selectedKeys) {
      const [frameId] = key.split(":")
      if (!frameKeyMap.has(frameId)) frameKeyMap.set(frameId, new Set())
      frameKeyMap.get(frameId).add(key)
    }

    const frames = app.frames
    const total = frames.length
    let done = 0
    this._showSelectiveConnectBanner(0, total)

    const model = this._resolveModel("요소 선택 연결")
    const provider = this.getProvider(model)
    const systemPrompt = this.buildSystemPrompt()

    const CONCURRENCY = 3
    let nextFrameIndex = 0
    const prevParallelFlag = window.__uiPlanningParallelGeneration
    window.__uiPlanningParallelGeneration = true

    const processFrame = async (frame) => {
      let elementsCtx = null
      try {
        const singleFrameApp = { frames: [frame], zoom: app.zoom }
        const rawCtx = window.buildPrototypeElementsContext(singleFrameApp)
        if (rawCtx && Array.isArray(rawCtx.frames)) {
          // Filter elements to only selectedKeys for this frame
          const frameKeys = frameKeyMap.get(String(frame.id))
          const filteredFrames = rawCtx.frames.map(f => {
            const fid = f.frame_id ?? f.id
            return {
              ...f,
              elements: (f.elements || []).filter(el => {
                const k = `${fid}:${el.label}:${el.tag || ""}`
                return frameKeys && frameKeys.has(k)
              })
            }
          })
          if (filteredFrames.some(f => f.elements.length > 0)) {
            elementsCtx = { ...rawCtx, frames: filteredFrames }
          }
        }
      } catch (e) {
        console.warn("[selectiveConnect] ELEMENTS_CONTEXT build error for frame", frame.id, e)
      }

      if (!elementsCtx) return 0 // no selected elements in this frame, skip

      const userMsg = "요소 선택 연결"
      const payload = `${userMsg}\n\n<ELEMENTS_CONTEXT>\n${JSON.stringify(elementsCtx)}\n</ELEMENTS_CONTEXT>`

      try {
        const aiText = await this.requestAiResponse({
          provider,
          endpointPrefix: this.config.aiBasePath,
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
          },
          payload: {
            model: model,
            system: systemPrompt,
            messages: [{ role: "user", content: payload }],
            max_tokens: 8192,
            context: this._buildUsageContext()
          },
          stream: false
        })

        // 단일메시지 경로와 동일한 견고한 추출기 재사용
        const formData = this.extractFormData(aiText)
        if (formData?._action === "apply_prototype_actions" && Array.isArray(formData.data?.actions)) {
          return await this.applyPrototypeActions(formData.data) || 0
        }
        console.warn("[selectiveConnect] no applicable actions for frame", frame.id)
      } catch (e) {
        console.warn("[selectiveConnect] frame", frame.id, "error:", e.message)
      }
      return 0
    }

    let created = 0
    const workerLoop = async () => {
      while (true) {
        const idx = nextFrameIndex++
        if (idx >= total) break
        // await를 먼저 끝낸 뒤 동기 증가 — 동시 워커 간 read-modify-write 경쟁 방지
        const n = await processFrame(frames[idx])
        created += n
        done++
        this._showSelectiveConnectBanner(done, total)
      }
    }

    await Promise.all(Array.from({ length: CONCURRENCY }, () => workerLoop()))

    window.__uiPlanningParallelGeneration = prevParallelFlag
    this._showSelectiveConnectBanner(total, total, true)
    if (created > 0) {
      this.appendSystemNote(`선택 연결 완료 — ${created}개 연결을 만들었어요.`)
      await this._autoAssignStartFrameAfterConnect()
    } else {
      this.appendSystemNote("선택한 요소로 연결을 만들지 못했어요. 다른 요소를 고르거나 더 강력한 모델로 다시 시도해보세요.")
    }
  }

  _showImageFieldSpinners(tasks) {
    tasks.forEach(task => {
      // 이미지 URL 입력 필드 찾기
      const selector = `input[name*="${task.field}"], input[name*="bg_image_url"], input[name*="image_url"], input[name*="photo_url"]`
      const inputs = document.querySelectorAll(selector)
      inputs.forEach(input => {
        if (input.value === "__generate__" || input.dataset.generating) {
          input.dataset.generating = "true"
          const wrapper = input.closest("div") || input.parentElement
          if (wrapper && !wrapper.querySelector(".ba-field-spinner")) {
            wrapper.style.position = "relative"
            const spinner = document.createElementNS("http://www.w3.org/2000/svg", "svg")
            spinner.setAttribute("class", "ba-field-spinner")
            spinner.setAttribute("viewBox", "0 0 24 24")
            spinner.setAttribute("fill", "none")
            spinner.setAttribute("aria-hidden", "true")
            spinner.innerHTML = `<circle cx="12" cy="12" r="9" stroke="#C7D2FE" stroke-width="3"></circle><path d="M12 3a9 9 0 0 1 9 9" stroke="#4F46E5" stroke-width="3" stroke-linecap="round"></path>`
            spinner.style.cssText = `
              position: absolute; right: 10px; top: 50%; margin-top: -9px;
              width: 18px; height: 18px;
              animation: ba-spin 0.8s linear infinite;
            `
            wrapper.appendChild(spinner)
          }
        }
      })
    })
  }

  _clearImageFieldSpinner(task) {
    document.querySelectorAll(".ba-field-spinner").forEach(el => el.remove())
    document.querySelectorAll("[data-generating]").forEach(el => delete el.dataset.generating)
  }

  async createImageAssets(data) {
    const rawItems = Array.isArray(data?.items) ? data.items : (data ? [data] : [])
    const items = rawItems
      .filter((item) => item && typeof item === "object")
      .map((item) => ({
        title: String(item.title || "").trim(),
        alt_text: String(item.alt_text || item.title || "").trim(),
        category: String(item.category || "abstract").trim() || "abstract",
        prompt: String(item.prompt || item.image_prompt || "").trim(),
        aspect_ratio: String(item.aspect_ratio || item.size || "").trim()
      }))
      .filter((item) => item.prompt.length > 0)

    if (items.length === 0) {
      this.appendSystemNote(_t("system_notes.no_image_prompt", "생성할 이미지 프롬프트가 없습니다."))
      return { succeeded: 0, total: 0 }
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ""
    const basePath = this.config.aiBasePath || "/admin/ai"
    let succeeded = 0
    const createdTitles = []

    this._showImageGenBanner(0, items.length)

    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      try {
        const controller = new AbortController()
        const timeout = setTimeout(() => controller.abort(), 360000)
        const requestedModel = this.isImageAssetAgent &&
          this.imageAssetModelExplicitlySelected &&
          this.selectedModel &&
          this.selectedModel !== this.imageAssetServerDefaultModel
          ? this.selectedModel
          : undefined
        const resp = await fetch(`${basePath}/generate_image`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
          body: JSON.stringify({
            title: item.title,
            prompt: item.prompt,
            category: item.category,
            alt_text: item.alt_text || item.title || item.prompt,
            aspect_ratio: item.aspect_ratio || undefined,
            // 기본은 planning workspace처럼 서버 기본 이미지 생성 경로를 사용한다.
            // 사용자가 드롭다운에서 기본값이 아닌 모델을 직접 고른 경우만 명시적으로 전달한다.
            model: requestedModel
          }),
          signal: controller.signal
        })
        clearTimeout(timeout)

        const result = await resp.json().catch(() => ({}))
        if (resp.ok && result.success) {
          succeeded += 1
          createdTitles.push(item.title || item.alt_text || _t("system_notes.image_item_fallback", "이미지 %{index}").replace("%{index}", i + 1))
          this._showImageGenBanner(succeeded, items.length)
        } else {
          this.appendSystemNote(_t("system_notes.image_gen_failed", "이미지 생성 실패: %{error}").replace("%{error}", result.error || item.title || item.alt_text || "요청 이미지"))
        }
      } catch (e) {
        this.appendSystemNote(_t("system_notes.image_gen_error", "이미지 생성 오류: %{error}").replace("%{error}", e.message || e))
      }
    }

    this._showImageGenBanner(succeeded, items.length, true)

    if (succeeded > 0) {
      const previewTitles = createdTitles.slice(0, 3).join(", ")
      const extraCount = createdTitles.length - 3
      const suffix = extraCount > 0 ? ` 외 ${extraCount}개` : ""
      this.appendSystemNote(previewTitles ? _t("system_notes.image_assets_generated_detail", "%{count}개 이미지 에셋이 생성됐어요: %{titles}").replace("%{count}", succeeded).replace("%{titles}", `${previewTitles}${suffix}`) : _t("system_notes.image_assets_generated_simple", "%{count}개 이미지 에셋이 생성됐어요").replace("%{count}", succeeded))
      setTimeout(() => window.location.reload(), 800)
    }

    return { succeeded, total: items.length }
  }

  applyIaActions(data) {
    return new Promise((resolve, reject) => {
      let settled = false
      const timeoutId = setTimeout(() => {
        safeReject(new Error("IA 워크스페이스와 연결되지 않았습니다."))
      }, 30000)
      const safeResolve = (value) => {
        if (settled) return
        settled = true
        clearTimeout(timeoutId)
        resolve(value)
      }
      const safeReject = (error) => {
        if (settled) return
        settled = true
        clearTimeout(timeoutId)
        reject(error)
      }

      window.dispatchEvent(new CustomEvent("ia-designer:apply-actions", {
        detail: { data, resolve: safeResolve, reject: safeReject }
      }))
    })
  }

  async applyPrototypeActions(data) {
    if (!data || !Array.isArray(data.actions) || data.actions.length === 0) return 0
    const projectId = this.config.resourceId
    if (!projectId) {
      this.debugLog("[applyPrototypeActions] no projectId")
      return 0
    }
    // 자동 적용 시에는 시연 자동 시작을 건너뜀 — 사용자가 결과를 먼저 확인하고 시연 시작을 누르게 함
    const rawActions = data.actions.filter(a => a?.type !== "start_play")
    if (rawActions.length === 0) return 0
    // add_hotspot 액션에서 target_label / target_tag / target_dom_id 를 명시적으로 전달 (라벨 기반 매칭 + DOM 앵커 핫스팟 스키마)
    const actions = rawActions.map(a => {
      if (a.type !== "add_hotspot") return a
      return {
        type: a.type,
        frame_id: a.frame_id,
        target_frame_id: a.target_frame_id,
        label: a.label || "",
        target_label: a.target_label || null,
        target_tag: a.target_tag || null,
        target_dom_id: a.target_dom_id || null,
        x: a.x || 0,
        y: a.y || 0,
        width: a.width || 0,
        height: a.height || 0
      }
    })
    const url = `/ui-planning/projects/${projectId}/klavy_agent_actions`
    const payload = { actions, summary: data.summary || "" }
    if (data.auto_start_from_first_connection === true) {
      payload.auto_start_from_first_connection = true
    }
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify(payload)
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        this.debugLog("[applyPrototypeActions] error", err)
        return 0
      }
      const body = await res.json()
      this.debugLog("[applyPrototypeActions] applied", body.data)
      window.dispatchEvent(new CustomEvent("prototype:apply-actions", { detail: body.data }))
      // 실제 생성된 연결(핫스팟) 수만 센다 — applied_count는 set_start_frame 등을 포함해 과대평가됨
      return body.data?.hotspots?.length || 0
    } catch (e) {
      this.debugLog("[applyPrototypeActions] exception", e)
      return 0
    }
  }

  applyImageStudioActions(data) {
    return new Promise((resolve, reject) => {
      let settled = false
      const timeoutId = setTimeout(() => {
        safeReject(new Error(_t("system_notes.image_studio_disconnected", "이미지스튜디오와 연결되지 않았습니다.")))
      }, 30000)
      const safeResolve = (value) => {
        if (settled) return
        settled = true
        clearTimeout(timeoutId)
        resolve(value)
      }
      const safeReject = (error) => {
        if (settled) return
        settled = true
        clearTimeout(timeoutId)
        reject(error)
      }

      window.dispatchEvent(new CustomEvent("image-studio-designer:apply-actions", {
        detail: { data, resolve: safeResolve, reject: safeReject }
      }))
    })
  }

  applyPlanningWorkspaceActions(data) {
    return new Promise((resolve, reject) => {
      let settled = false
      const timeoutId = setTimeout(() => {
        if (settled) return
        settled = true
        reject(new Error(_t("system_notes.planning_workspace_disconnected", "기획 워크스페이스와 연결되지 않았습니다.")))
      }, 30000)
      const safeResolve = (value) => {
        if (settled) return
        settled = true
        clearTimeout(timeoutId)
        resolve(value)
      }
      const safeReject = (error) => {
        if (settled) return
        settled = true
        clearTimeout(timeoutId)
        reject(error)
      }

      window.dispatchEvent(new CustomEvent("planning-workspace-designer:apply-actions", {
        detail: { data, resourceId: this.config.resourceId, resolve: safeResolve, reject: safeReject }
      }))
    })
  }

  applyQuotationWorkspaceActions(data) {
    return new Promise((resolve, reject) => {
      let settled = false
      const timeoutId = setTimeout(() => {
        if (settled) return
        settled = true
        reject(new Error("견적서 워크스페이스와 연결되지 않았습니다."))
      }, 30000)
      const safeResolve = (value) => {
        if (settled) return
        settled = true
        clearTimeout(timeoutId)
        resolve(value)
      }
      const safeReject = (error) => {
        if (settled) return
        settled = true
        clearTimeout(timeoutId)
        reject(error)
      }

      window.dispatchEvent(new CustomEvent("quotation-workspace-designer:apply-actions", {
        detail: { data, resolve: safeResolve, reject: safeReject }
      }))
    })
  }

  applyUserStoryActions(data) {
    return new Promise((resolve, reject) => {
      let settled = false
      const timeoutId = setTimeout(() => {
        if (settled) return
        settled = true
        reject(new Error(_t("system_notes.user_story_disconnected", "유저 스토리 워크스페이스와 연결되지 않았습니다.")))
      }, 30000)
      const safeResolve = (value) => {
        if (settled) return
        settled = true
        clearTimeout(timeoutId)
        resolve(value)
      }
      const safeReject = (error) => {
        if (settled) return
        settled = true
        clearTimeout(timeoutId)
        reject(error)
      }

      window.dispatchEvent(new CustomEvent("user-story-designer:apply-actions", {
        detail: { data, resolve: safeResolve, reject: safeReject }
      }))
    })
  }

  normalizeApplyActionPayload(parsed) {
    const payload = parsed ? JSON.parse(JSON.stringify(parsed)) : { _action: null, data: {} }
    if (this.isPlanningWorkspaceContext() && payload._action === "apply_user_story_actions") {
      payload._action = "apply_planning_workspace_actions"
    }
    if (this.isQuotationWorkspaceContext() && payload._action == null && (payload?.data?.items || payload?.items)) {
      payload._action = "apply_quotation_workspace_actions"
      payload.data = payload.data || { cells: payload.cells || {}, items: payload.items || [] }
    }
    if (this.config.contextType === "page_sections" && payload._action === "fill_page" && Array.isArray(payload?.data?.sections)) {
      payload._action = "create_profile_page_sections"
      this.debugLog("coerced fill_page sections into create_profile_page_sections", payload)
      return payload
    }
    const sectionContext = this.config.contextType === "section" && this.config.designerBridge
    if (!sectionContext) return payload

    if (payload._action === "fill_page") {
      payload._action = "fill_designer_section"
      if (!payload.data || typeof payload.data !== "object" || Array.isArray(payload.data)) {
        payload.data = {}
      }
      const styleVariantFp = payload.data.style_variant || payload.style_variant
      if (!payload.data.content_data || typeof payload.data.content_data !== "object" || Array.isArray(payload.data.content_data)) {
        const promoted = { ...payload.data }
        delete promoted.title
        delete promoted.style_variant
        payload.data = {
          title: payload.data.title,
          content_data: promoted
        }
      }
      if (styleVariantFp) payload.data.style_variant = styleVariantFp
      this.debugLog("coerced fill_page into fill_designer_section", payload)
      return payload
    }

    if (payload._action === "fill_section") {
      payload._action = "fill_designer_section"
      if (!payload.data || typeof payload.data !== "object" || Array.isArray(payload.data)) {
        payload.data = {}
      }
      const styleVariantFs = payload.data.style_variant || payload.style_variant
      if (!payload.data.content_data || typeof payload.data.content_data !== "object" || Array.isArray(payload.data.content_data)) {
        const promoted = { ...payload.data }
        delete promoted.title
        delete promoted.style_variant
        payload.data = {
          title: payload.data.title,
          content_data: promoted
        }
      }
      if (styleVariantFs) payload.data.style_variant = styleVariantFs
      this.debugLog("coerced fill_section into fill_designer_section", payload)
      return payload
    }

    if (payload._action == null && payload?.data?.content_data) {
      payload._action = "fill_designer_section"
      this.debugLog("filled missing action as fill_designer_section", payload)
    }

    if (payload._action == null && Array.isArray(payload?.data?.sections)) {
      const hasStructuredOps = payload.data.sections.some((section) => ["keep", "replace", "remove", "add"].includes(section?.op))
      if (hasStructuredOps) {
        payload._action = "restructure_designer_page"
        this.debugLog("filled missing action as restructure_designer_page", payload)
      }
    }

    return payload
  }

  fillDesignerSection(data) {
    if (!this.hasDesignerSectionSelection()) {
      this.appendSystemNote(_t("system_notes.no_section_selected", "선택된 섹션이 없어 적용할 수 없습니다."))
      return
    }

    this.debugLog("dispatching profile-page-designer:apply-section-content", data)

    window.dispatchEvent(new CustomEvent("profile-page-designer:apply-section-content", {
      detail: { data }
    }))

    // 단일 섹션 업데이트에서도 __generate__ 이미지 생성 처리
    if (data.content_data) {
      const clientKey = this.designerSelection?.clientKey
      this.processImageGeneration([{ content_data: data.content_data, client_key: clientKey }])
    }
  }

  normalizeDesignerSectionPayload(data) {
    const payload = JSON.parse(JSON.stringify(data || {}))
    const contentData = payload.content_data && typeof payload.content_data === "object" && !Array.isArray(payload.content_data)
      ? JSON.parse(JSON.stringify(payload.content_data))
      : {}

    const normalized = { ...contentData }
    const availableFieldPaths = new Set(
      Array.from(document.querySelectorAll("[data-path]")).map((node) => node.dataset.path).filter(Boolean)
    )

    this.debugLog("normalizing designer payload", {
      before: contentData,
      availableFieldPaths: Array.from(availableFieldPaths)
    })

    if (availableFieldPaths.has("tagline")) {
      if (normalized.subtitle !== undefined && normalized.tagline === undefined) normalized.tagline = normalized.subtitle
      if (normalized.subheadline !== undefined && normalized.tagline === undefined) normalized.tagline = normalized.subheadline
      if (normalized.headline !== undefined && normalized.tagline === undefined) normalized.tagline = normalized.headline
      if (normalized.site_title !== undefined && normalized.tagline === undefined) normalized.tagline = normalized.site_title
      if (normalized.title_text !== undefined && normalized.tagline === undefined) normalized.tagline = normalized.title_text
    }

    if (availableFieldPaths.has("bio_short")) {
      if (normalized.description !== undefined && normalized.bio_short === undefined) normalized.bio_short = normalized.description
      if (normalized.bio !== undefined && normalized.bio_short === undefined) normalized.bio_short = normalized.bio
      if (normalized.subtitle !== undefined && normalized.bio_short === undefined && !availableFieldPaths.has("tagline")) normalized.bio_short = normalized.subtitle
    }

    if (availableFieldPaths.has("secondary_cta_text") && normalized.cta_secondary_text !== undefined && normalized.secondary_cta_text === undefined) {
      normalized.secondary_cta_text = normalized.cta_secondary_text
    }

    if (availableFieldPaths.has("secondary_cta_url") && normalized.cta_secondary_url !== undefined && normalized.secondary_cta_url === undefined) {
      normalized.secondary_cta_url = normalized.cta_secondary_url
    }

    if (availableFieldPaths.has("badge_text")) {
      if (normalized.badge !== undefined && normalized.badge_text === undefined) normalized.badge_text = normalized.badge
      if (normalized.badge_label !== undefined && normalized.badge_text === undefined) normalized.badge_text = normalized.badge_label
    }

    payload.content_data = normalized
    this.debugLog("normalized designer payload result", payload)
    return payload
  }

  async createProfilePageSections(data) {
    const sections = Array.isArray(data?.sections) ? data.sections : []
    if (!sections.length) {
      this.appendSystemNote("생성할 섹션 데이터가 없어 좌측에 반영하지 못했습니다.")
      throw new Error("No profile page sections to create")
    }

    const match = window.location.pathname.match(/\/dashboard\/sites\/(\d+)\/pages\/(\d+)/)
    if (!match) {
      this.appendSystemNote("현재 페이지 경로에서 섹션 생성 위치를 찾지 못했습니다.")
      throw new Error("Profile page route not found")
    }

    const [, siteId, pageId] = match
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const response = await fetch(`/dashboard/sites/${siteId}/pages/${pageId}/sections/ai_bulk_create`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": token || ""
      },
      credentials: "same-origin",
      body: JSON.stringify({ sections })
    })

    const result = await response.json().catch(() => ({}))
    if (!response.ok || result.success === false) {
      const message = result.error || "섹션 생성에 실패했습니다."
      this.appendSystemNote(message)
      throw new Error(message)
    }

    this.appendSystemNote(`${result.created_count || sections.length}개 섹션을 생성했습니다. 화면을 새로고침합니다.`)
    window.location.href = result.redirect_url || window.location.href
    return result
  }

  fillPageForm(data) {
    const form = this.config.formSelector ? document.querySelector(this.config.formSelector) : null
    if (!form) return
    const prefix = this.config.fieldPrefix || ""
    const fieldValue = (...keys) => {
      for (const key of keys) {
        if (Object.prototype.hasOwnProperty.call(data, key)) return data[key]
      }
      return undefined
    }

    const set = (name, value) => {
      const candidates = Array.from(form.querySelectorAll(`[name='${name}']`))
      const el = candidates.find(field => field.type === "checkbox") || candidates.find(field => field.type !== "hidden") || candidates[0]
      if (!el) return
      if (el.type === "checkbox") el.checked = !!value
      else el.value = value || ""
      el.dispatchEvent(new Event("input", { bubbles: true }))
      el.dispatchEvent(new Event("change", { bubbles: true }))
    }

    const title = fieldValue("title", "site_title", "name")
    const slug = fieldValue("slug", "url_slug")
    const metaDescription = fieldValue("meta_description", "metaDescription", "meta description", "description", "intro", "summary")
    const theme = fieldValue("theme")
    const active = fieldValue("active", "is_published", "published", "publish", "is_public")
    const showHeader = fieldValue("show_header", "showHeader", "header", "has_header")
    const headerStyle = fieldValue("header_style", "headerStyle")
    const showFooter = fieldValue("show_footer", "showFooter", "footer", "has_footer")
    const footerStyle = fieldValue("footer_style", "footerStyle")
    const stickyCta = fieldValue("sticky_cta", "stickyCta")
    const stickyCtaText = fieldValue("sticky_cta_text", "stickyCtaText")
    const stickyCtaUrl = fieldValue("sticky_cta_url", "stickyCtaUrl")
    const stickyCtaColor = fieldValue("sticky_cta_color", "stickyCtaColor")

    if (title !== undefined) set(`${prefix}[title]`, title)
    if (slug !== undefined) set(`${prefix}[slug]`, slug)
    if (metaDescription !== undefined) set(`${prefix}[meta_description]`, metaDescription)
    if (theme !== undefined) set(`${prefix}[theme]`, theme)
    if (active !== undefined) set(`${prefix}[active]`, active)
    if (showHeader !== undefined) set(`${prefix}[show_header]`, showHeader)
    if (headerStyle !== undefined) set(`${prefix}[header_style]`, headerStyle)
    if (showFooter !== undefined) set(`${prefix}[show_footer]`, showFooter)
    if (footerStyle !== undefined) set(`${prefix}[footer_style]`, footerStyle)
    if (stickyCta !== undefined) set(`${prefix}[sticky_cta]`, stickyCta)
    if (stickyCtaText !== undefined) set(`${prefix}[sticky_cta_text]`, stickyCtaText)
    if (stickyCtaUrl !== undefined) set(`${prefix}[sticky_cta_url]`, stickyCtaUrl)
    if (stickyCtaColor !== undefined) set(`${prefix}[sticky_cta_color]`, stickyCtaColor)

    Object.entries(data || {}).forEach(([key, value]) => {
      if (!key || key.startsWith("_")) return
      const formattedValue = Array.isArray(value) ? value.join("\n") : value
      set(`${prefix}[${key}]`, formattedValue)
    })
  }

  configuredFormElement() {
    return (this.config.formSelector ? document.querySelector(this.config.formSelector) : null)
      || document.getElementById("dsec-form")
      || document.getElementById("section-form")
  }

  snapshotConfiguredForm() {
    const form = this.config.contextType === "section"
      ? this.configuredFormElement()
      : (this.config.formSelector ? document.querySelector(this.config.formSelector) : null)
    if (!form) return []
    return Array.from(form.querySelectorAll("input, select, textarea"))
      .filter((el) => el.type !== "file")
      .map((el) => ({
        el,
        name: el.name,
        value: el.value,
        checked: el.checked,
        type: el.type
      }))
  }

  restoreConfiguredFormSnapshot(snapshot = []) {
    const sectionForm = this.config.contextType === "section" ? this.configuredFormElement() : null
    sectionForm?.querySelectorAll("[data-klavy-ai-generated='true']").forEach((el) => el.remove())
    snapshot.forEach((item) => {
      const el = item?.el
      if (!el || !el.isConnected) return
      if (item.type === "checkbox" || item.type === "radio") el.checked = !!item.checked
      else el.value = item.value || ""
      el.dispatchEvent(new Event("input", { bubbles: true }))
      el.dispatchEvent(new Event("change", { bubbles: true }))
      if (item.name === "profile_page_section[section_type]" && typeof window.dsecSelectType === "function") {
        window.dsecSelectType(el.value)
      }
    })
  }

  fillColdmailCampaignForm(data = {}) {
    const form = this.coldmailCampaignFormElement()
    if (!form) throw new Error("홍보 메일 캠페인 입력 폼을 찾을 수 없습니다.")
    const prefix = this.config.fieldPrefix || "campaign"
    const fallbackPrefix = prefix === "campaign" ? "coldmail_campaign" : "campaign"
    const result = { appliedCount: 0, changedCount: 0, skippedFields: [] }

    const dispatchChange = (el) => {
      el.dispatchEvent(new Event("input", { bubbles: true }))
      el.dispatchEvent(new Event("change", { bubbles: true }))
    }

    const findField = (field) => (
      form.querySelector(`[name='${prefix}[${field}]']`)
      || form.querySelector(`[name='${fallbackPrefix}[${field}]']`)
    )

    const set = (name, value, options = {}) => {
      const match = String(name).match(/\[([^\]]+)\]$/)
      const field = match ? match[1] : name
      const el = match ? findField(match[1]) : form.querySelector(`[name='${name}']`)
      if (!el || value === undefined) {
        result.skippedFields.push(field)
        return
      }
      const incoming = value !== null ? String(value) : ""
      if (options.preserveExistingOnBlank && incoming.trim() === "" && String(el.value || "").trim() !== "") {
        return
      }
      if (el.tagName === "SELECT" && incoming !== "") {
        const hasOption = Array.from(el.options || []).some((option) => option.value === incoming)
        if (!hasOption) {
          result.skippedFields.push(field)
          return
        }
      }
      const before = el.type === "checkbox" ? el.checked : el.value
      if (el.type === "checkbox") el.checked = !!value
      else el.value = incoming
      dispatchChange(el)
      const after = el.type === "checkbox" ? el.checked : el.value
      result.appliedCount += 1
      if (before !== after) result.changedCount += 1
    }

    if (data.name !== undefined) set(`${prefix}[name]`, data.name)
    if (data.coldmail_lead_list_id !== undefined) set(`${prefix}[coldmail_lead_list_id]`, data.coldmail_lead_list_id)
    if (data.from_name !== undefined) set(`${prefix}[from_name]`, data.from_name, { preserveExistingOnBlank: true })
    if (data.from_email !== undefined) set(`${prefix}[from_email]`, data.from_email, { preserveExistingOnBlank: true })
    if (data.daily_limit !== undefined) set(`${prefix}[daily_limit]`, data.daily_limit)
    if (data.send_interval !== undefined) set(`${prefix}[send_interval]`, data.send_interval)
    if (data.timezone !== undefined) set(`${prefix}[timezone]`, data.timezone)
    if (data.send_hours !== undefined) set(`${prefix}[send_hours]`, data.send_hours)

    if (data.send_days !== undefined) {
      const days = Array.isArray(data.send_days)
        ? data.send_days
        : String(data.send_days).split(",")
      const normalizedDays = days.map(day => String(day).trim()).filter(Boolean)
      const daySet = new Set(normalizedDays)
      const dayBoxes = [
        ...form.querySelectorAll(`input[name='${prefix}[send_days_arr][]']`),
        ...form.querySelectorAll(`input[name='${fallbackPrefix}[send_days_arr][]']`)
      ]
      dayBoxes.forEach((checkbox) => {
        checkbox.checked = daySet.has(checkbox.value)
        dispatchChange(checkbox)
      })
      const hidden = form.querySelector(`#cm_send_days_hidden, input[name='${prefix}[send_days]'], input[name='${fallbackPrefix}[send_days]']`)
      if (hidden) {
        const before = hidden.value
        hidden.value = normalizedDays.join(",")
        dispatchChange(hidden)
        result.appliedCount += 1
        if (before !== hidden.value) result.changedCount += 1
      }
    }

    return result
  }

  async createColdmailSequences(data = {}) {
    if (!this.isColdmailCampaignContext()) {
      throw new Error("홍보 메일 캠페인 화면에서만 시퀀스를 생성할 수 있습니다.")
    }

    const campaignId = this.resolveColdmailCampaignId()
    if (!campaignId) {
      throw new Error("저장된 캠페인에서만 이메일 시퀀스를 생성할 수 있습니다. 먼저 캠페인을 생성해주세요.")
    }

    const sequences = this.normalizeColdmailSequencePayload(data)
    if (sequences.length === 0) {
      throw new Error("생성할 이메일 단계가 없습니다. 제목과 본문이 포함된 시퀀스를 다시 요청해주세요.")
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ""
    const errors = []
    const createdSequences = []
    const isCampaignDetailPage = !!window.location.pathname.match(/\/coldmail\/campaigns\/\d+/)
    const requestedCount = sequences.length
    let created = 0

    for (const sequence of sequences) {
      try {
        const res = await this.fetchColdmailSequenceWithTimeout(`/coldmail/campaigns/${campaignId}/sequences`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
            "X-Requested-With": "XMLHttpRequest"
          },
          body: JSON.stringify({
            sequence,
            image_model_id: this.selectedThreadsImageModel || "",
            image_prompt: sequence.image_prompt || ""
          })
        })
        const json = await res.json().catch(() => ({}))
        if (!res.ok || json.success === false) {
          const message = Array.isArray(json.errors) ? json.errors.join(", ") : `HTTP ${res.status}`
          errors.push(message)
          continue
        }
        created += 1
        if (json.sequence) {
          createdSequences.push(json.sequence)
          if (isCampaignDetailPage) {
            window.dispatchEvent(new CustomEvent("coldmail:sequences-created", {
              detail: { sequences: [json.sequence] }
            }))
          }
        }
      } catch (error) {
        const message = error?.name === "AbortError"
          ? "시퀀스 생성 요청 시간이 초과되었습니다."
          : (error?.message || "시퀀스 생성 중 오류가 발생했습니다.")
        errors.push(message)
      }
    }

    if (created === 0) {
      throw new Error(errors.join("\n") || "생성된 이메일 단계가 없습니다.")
    }

    const failedCount = requestedCount - created
    if (errors.length > 0) {
      this.appendSystemNote(`${created}개 단계가 생성됐지만 ${failedCount}개 단계는 실패했습니다: ${errors.join(", ")}`)
    }

    if (!isCampaignDetailPage) {
      window.location.href = `/coldmail/campaigns/${campaignId}`
    }

    return {
      status: failedCount > 0 ? "partial" : "success",
      requestedCount,
      appliedCount: created,
      changedCount: created,
      failedCount,
      errors,
      createdSequences
    }
  }

  fetchColdmailSequenceWithTimeout(url, options = {}) {
    const timeoutMs = Number(this.config.coldmailSequenceTimeoutMs || 90000)
    if (typeof AbortController === "undefined" || !Number.isFinite(timeoutMs) || timeoutMs <= 0) {
      return fetch(url, options)
    }

    const controller = new AbortController()
    const timer = window.setTimeout(() => controller.abort(), timeoutMs)
    return fetch(url, { ...options, signal: controller.signal }).finally(() => {
      window.clearTimeout(timer)
    })
  }

  appendColdmailSequenceUndoButton(container, createdSequences = []) {
    const ids = (createdSequences || []).map((sequence) => sequence?.id).filter(Boolean)
    if (ids.length === 0) return

    const undoWrap = document.createElement("div")
    undoWrap.className = "ba-followup-buttons"
    undoWrap.style.cssText = "display:flex; gap:8px; margin-top:6px;"

    const undoBtn = document.createElement("button")
    undoBtn.type = "button"
    undoBtn.className = "ba-followup-btn"
    undoBtn.style.cssText = "color:#DC2626; border-color:#FECACA;"
    undoBtn.textContent = _t("buttons.undo", "적용 취소")
    undoBtn.addEventListener("click", async () => {
      undoBtn.disabled = true
      undoBtn.textContent = _t("buttons.undoing", "취소 중...")
      try {
        await this.deleteColdmailSequences(ids)
        window.dispatchEvent(new CustomEvent("coldmail:sequences-removed", { detail: { ids } }))
        undoBtn.remove()
        const notice = container.querySelector("span[style*='6366F1']")
        if (notice) { notice.textContent = _t("buttons.undone", "적용 취소됨"); notice.style.color = "#6B7280" }
      } catch (error) {
        undoBtn.disabled = false
        undoBtn.textContent = _t("buttons.undo", "적용 취소")
        this.appendSystemNote(error?.message || "메일 단계 적용 취소에 실패했습니다.")
      }
    })

    undoWrap.appendChild(undoBtn)
    container.querySelector("div")?.appendChild(undoWrap)
  }

  async deleteColdmailSequences(ids = []) {
    const campaignId = this.resolveColdmailCampaignId()
    if (!campaignId) throw new Error("캠페인을 찾을 수 없어 적용을 취소하지 못했습니다.")

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ""
    const errors = []
    for (const id of ids) {
      try {
        const res = await fetch(`/coldmail/campaigns/${campaignId}/sequences/${id}`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-CSRF-Token": csrfToken,
            "X-Requested-With": "XMLHttpRequest"
          }
        })
        const json = await res.json().catch(() => ({}))
        if (!res.ok || json.success === false) {
          errors.push(Array.isArray(json.errors) ? json.errors.join(", ") : `HTTP ${res.status}`)
        }
      } catch (error) {
        errors.push(error?.message || "삭제 중 오류가 발생했습니다.")
      }
    }

    if (errors.length > 0) throw new Error(errors.join("\n"))
  }

  resolveColdmailCampaignId() {
    const configured = Number(this.config.resourceId || 0)
    if (configured > 0) return configured
    const match = window.location.pathname.match(/\/coldmail\/campaigns\/(\d+)/)
    return match ? Number(match[1]) : 0
  }

  normalizeColdmailSequencePayload(data = {}) {
    let rawItems = []

    if (Array.isArray(data)) {
      rawItems = data
    } else if (Array.isArray(data?.sequences)) {
      rawItems = data.sequences
    } else if (Array.isArray(data?.items)) {
      rawItems = data.items
    } else if (data?.sequence && typeof data.sequence === "object") {
      rawItems = [data.sequence]
    } else if (data && typeof data === "object" && (data.subject || data.body)) {
      rawItems = [data]
    }

    const allowedConditions = new Set(["no_reply", "no_open", "all"])
    return rawItems.map((item, index) => {
      const subject = String(item?.subject || "").trim()
      const body = this.normalizeGeneratedEmailBodyHtml(item?.body || "")
      if (!subject || !body) return null

      const delayDays = Number.parseInt(item?.delay_days ?? item?.delayDays ?? (index === 0 ? 0 : 3), 10)
      const condition = String(item?.condition || (index === 0 ? "all" : "no_reply")).trim()

      return {
        subject,
        body,
        image_prompt: String(item?.image_prompt || item?.imagePrompt || "").trim(),
        delay_days: Number.isFinite(delayDays) && delayDays >= 0 ? delayDays : (index === 0 ? 0 : 3),
        condition: allowedConditions.has(condition) ? condition : (index === 0 ? "all" : "no_reply"),
        ai_generated: item?.ai_generated === false ? false : true
      }
    }).filter(Boolean)
  }

  normalizeGeneratedEmailBodyHtml(value) {
    const raw = String(value || "").trim()
    if (!raw) return ""

    const withoutArtifacts = raw
      .replace(/alt=(['"])Cold mail image\1/gi, 'alt=""')
      .replace(/<figcaption[^>]*>\s*Cold mail image\s*<\/figcaption>/gi, "")
      .replace(/<p[^>]*>\s*Cold mail image\s*<\/p>/gi, "")
      .replace(/^\s*Cold mail image\s*$/gim, "")
      .trim()

    if (/<(p|br|div|ul|ol|li|blockquote|table|h[1-6]|figure|img)\b/i.test(withoutArtifacts)) return withoutArtifacts

    return this.plainTextToEmailSafeHtml(withoutArtifacts)
  }

  plainTextToEmailSafeHtml(value = "") {
    return String(value || "")
      .replace(/\r\n?/g, "\n")
      .split(/\n{2,}/)
      .map(block => block.trim())
      .filter(Boolean)
      .map(block => {
        const lines = block.split("\n").map(line => line.trim()).filter(Boolean)
        const isSimpleList = lines.length > 1 && lines.every(line => /^(?:[-*•]|\d+[.)])\s+/.test(line))
        if (isSimpleList) {
          const items = lines
            .map(line => line.replace(/^(?:[-*•]|\d+[.)])\s+/, ""))
            .map(line => `<li>${this.escapeHtml(line)}</li>`)
            .join("")
          return `<ul>${items}</ul>`
        }
        return `<p>${this.escapeHtml(block.replace(/\n+/g, " "))}</p>`
      })
      .join("\n")
  }

  isColdmailCampaignContext() {
    return this.config.contextType === "coldmail_campaign"
      || this.config.resourceType === "ColdmailCampaign"
      || this.config.agentSlug === "coldmail-campaign-writer"
  }

  coldmailCampaignFormElement() {
    return (this.config.formSelector ? document.querySelector(this.config.formSelector) : null)
      || document.querySelector("#coldmail-campaign-form")
  }

  isColdmailCampaignFormPage() {
    return this.isColdmailCampaignContext() && !!this.coldmailCampaignFormElement()
  }

  fillPromptForm(data) {
    const form = this.config.formSelector ? document.querySelector(this.config.formSelector) : null
    if (!form) throw new Error("프롬프트 입력 폼을 찾을 수 없습니다.")
    const prefix = this.config.fieldPrefix || "prompt"

    const set = (name, value) => {
      const el = form.querySelector(`[name='${name}']`)
      if (!el) return
      if (el.type === "checkbox") el.checked = !!value
      else el.value = value !== null && value !== undefined ? String(value) : ""
      el.dispatchEvent(new Event("input", { bubbles: true }))
      el.dispatchEvent(new Event("change", { bubbles: true }))
    }

    if (data.title !== undefined) set(`${prefix}[title]`, data.title)
    if (data.body !== undefined) set(`${prefix}[body]`, data.body)
    if (data.category !== undefined) set(`${prefix}[category]`, data.category)
    if (data.active !== undefined) set(`${prefix}[active]`, data.active)
    if (data.is_public !== undefined) set(`${prefix}[is_public]`, data.is_public)
  }

  shouldCreateAdminPromptTemplate() {
    return this.config.agentSlug === "prompt-designer"
      && this.config.contextType === "page"
      && !document.querySelector(this.config.formSelector || "#prompt-form")
      && window.location.pathname.startsWith("/admin/prompt_templates")
  }

  async createAdminPromptTemplate(data = {}) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ""
    const promptPayload = {
      title: data.title,
      body: data.body,
      category: data.category || "ui_planning",
      is_public: true,
      is_official: true,
      active: true
    }

    if (!promptPayload.title || !promptPayload.body) {
      throw new Error("프롬프트 템플릿으로 등록할 제목과 본문이 필요합니다.")
    }

    const res = await fetch("/admin/prompt_templates", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": csrfToken,
        "X-Requested-With": "XMLHttpRequest"
      },
      body: JSON.stringify({ prompt: promptPayload })
    })
    const payload = await res.json().catch(() => ({}))
    if (!res.ok) {
      const message = Array.isArray(payload.errors) ? payload.errors.join(", ") : (payload.error || "프롬프트 템플릿 등록에 실패했습니다.")
      throw new Error(message)
    }

    const title = payload.data?.title || promptPayload.title
    this.appendSystemNote(`프롬프트 템플릿 "${title}"이 등록되었습니다. 목록을 새로고침합니다.`)
    window.setTimeout(() => {
      window.location.href = payload.redirect_url || "/admin/prompt_templates"
    }, 350)
  }

  fillIaProjectForm(data) {
    const form = this.config.formSelector ? document.querySelector(this.config.formSelector) : document.getElementById("ia-project-form")
    if (!form) return
    const prefix = this.config.fieldPrefix || "ia_project"

    const set = (name, value) => {
      const el = form.querySelector(`[name='${name}']`)
      if (!el) return
      if (el.type === "checkbox") el.checked = !!value
      else el.value = value !== null && value !== undefined ? String(value) : ""
      el.dispatchEvent(new Event("input", { bubbles: true }))
      el.dispatchEvent(new Event("change", { bubbles: true }))
    }

    if (data.name !== undefined) set(`${prefix}[name]`, data.name)
    if (data.description !== undefined) set(`${prefix}[description]`, data.description)
    if (data.workspace_mode !== undefined) set(`${prefix}[workspace_mode]`, data.workspace_mode)
    if (data.source_type !== undefined) set(`${prefix}[source_type]`, data.source_type)
    if (data.status !== undefined) set(`${prefix}[status]`, data.status)
    if (data.linked_ui_planning_project_id !== undefined) set(`${prefix}[linked_ui_planning_project_id]`, data.linked_ui_planning_project_id)
    if (data.ai_bootstrap_prompt !== undefined) set(`${prefix}[ai_bootstrap_prompt]`, data.ai_bootstrap_prompt)
    if (data.project_rules !== undefined) set(`${prefix}[project_rules]`, data.project_rules)
  }

  fillSectionForm(data) {
    const form = this.configuredFormElement()
    if (!form) return

    const set = (name, value) => {
      const el = form.querySelector(`[name='${name}']`)
      if (!el) return
      if (el.type === "checkbox") el.checked = !!value
      else el.value = value !== null && value !== undefined ? String(value) : ""
      el.dispatchEvent(new Event("input", { bubbles: true }))
      el.dispatchEvent(new Event("change", { bubbles: true }))
    }

    // ai_block: content_data.html_content를 textarea에 직접 적용
    if (this.config.sectionType === "ai_block" && data.content_data?.html_content !== undefined) {
      const ta = Array.from(form.querySelectorAll("textarea:not([disabled])")).find(t => t.name === "profile_page_section[content_data][html_content]") || null
      if (ta) {
        ta.value = String(data.content_data.html_content)
        ta.dispatchEvent(new Event("input", { bubbles: true }))
        ta.dispatchEvent(new Event("change", { bubbles: true }))
      }
      // 섹션 제목이 비어 있으면 기본값 채우기 (validate :title, presence: true)
      const titleInput = form.querySelector("[name='profile_page_section[title]']")
      if (titleInput && !titleInput.value.trim()) {
        titleInput.value = "AI 블록"
        titleInput.dispatchEvent(new Event("input", { bubbles: true }))
      }
      return
    }

    if (data.section_type) {
      const hiddenType = form.querySelector("[name='profile_page_section[section_type]']")
        || form.querySelector("[name='landing_page_section[section_type]']")
      if (hiddenType) {
        hiddenType.value = data.section_type
        hiddenType.dispatchEvent(new Event("input", { bubbles: true }))
        hiddenType.dispatchEvent(new Event("change", { bubbles: true }))
        if (typeof window.dsecSelectType === "function") window.dsecSelectType(data.section_type)
      }
      const typeSelect = form.querySelector("[name='landing_page_section[section_type]']")
      if (typeSelect && typeSelect.tagName === "SELECT") {
        typeSelect.value = data.section_type
        typeSelect.dispatchEvent(new Event("change", { bubbles: true }))
        setTimeout(() => this.fillSectionContent(form, data), 100)
        return
      }
    }

    this.fillSectionContent(form, data)
  }

  fillSectionContent(form, data) {
    const activeSection = form.querySelector(`[data-${this.config.sectionTypeAttr}-target="fields"]:not(.hidden)`)
    const scope = activeSection || form

    // 폼 프리픽스 자동 감지 (profile_page_section vs landing_page_section)
    const formModel = form.querySelector("[name^='profile_page_section']")
      ? "profile_page_section"
      : "landing_page_section"
    const cdPrefix = `${formModel}[content_data]`

    const set = (name, value) => {
      const searchIn = name.startsWith(cdPrefix) ? scope : form
      let el = searchIn.querySelector(`[name='${name}']`) || form.querySelector(`[name='${name}']`)
      if (!el && name.startsWith(cdPrefix)) {
        el = document.createElement("input")
        el.type = "hidden"
        el.name = name
        el.dataset.klavyAiGenerated = "true"
        form.appendChild(el)
      }
      if (!el) return
      if (el.type === "checkbox") el.checked = !!value
      else el.value = value !== null && value !== undefined ? String(value) : ""
      el.dispatchEvent(new Event("input", { bubbles: true }))
      el.dispatchEvent(new Event("change", { bubbles: true }))
    }

    const setContentValue = (keyPath, value) => {
      if (Array.isArray(value)) {
        value.forEach((item, index) => {
          if (item && typeof item === "object" && !Array.isArray(item)) {
            Object.entries(item).forEach(([childKey, childValue]) => setContentValue(`${keyPath}[${index}][${childKey}]`, childValue))
          } else {
            set(`${prefix}[${keyPath}][${index}]`, item)
          }
        })
        return
      }
      if (value && typeof value === "object") {
        Object.entries(value).forEach(([childKey, childValue]) => setContentValue(`${keyPath}[${childKey}]`, childValue))
        return
      }
      set(`${prefix}[${keyPath}]`, value)
    }

    if (data.title !== undefined) set(`${formModel}[title]`, data.title)
    if (data.bg_color !== undefined) set(`${formModel}[bg_color]`, data.bg_color)
    if (data.padding_class !== undefined) set(`${formModel}[padding_class]`, data.padding_class)
    if (data.style_variant !== undefined) set(`${formModel}[style_variant]`, data.style_variant)
    if (data.visible !== undefined) set(`${formModel}[visible]`, data.visible)

    if (!data.content_data) return
    const cd = data.content_data
    const prefix = cdPrefix

    const arrayDefs = {
      social_links: ["platform", "url", "icon"],
      pain_points: ["icon", "title", "description"],
      features: ["icon", "title", "description", "image_url"],
      items: cd.items?.[0]?.question !== undefined
        ? ["question", "answer"]
        : ["icon", "feature", "benefit"],
      testimonials: ["name", "role", "company", "quote", "rating"],
      logos: ["name", "image_url"],
      tiers: ["name", "price", "price_period", "description", "features", "cta_text", "cta_url", "highlighted"],
      fields: ["label", "field_type", "required"]
    }

    Object.entries(cd).forEach(([key, value]) => {
      if (Array.isArray(value) && arrayDefs[key]) return
      setContentValue(key, value)
    })

    Object.entries(arrayDefs).forEach(([arrayKey, subFields]) => {
      if (!cd[arrayKey] || !Array.isArray(cd[arrayKey])) return
      this.fillRepeaterArray(scope, prefix, arrayKey, subFields, cd[arrayKey])
    })
  }

  fillRepeaterArray(form, prefix, arrayKey, subFields, items) {
    if (!items || items.length === 0) return

    const container = form.querySelector(`[data-${this.config.sectionRepeaterAttr}-target="container"]`)
    const addBtn = form.querySelector(`[data-${this.config.sectionRepeaterAttr}-target="addBtn"]`)

    if (!container) return

    items.forEach((item, idx) => {
      if (idx > 0 && addBtn) {
        addBtn.click()
      }

      subFields.forEach(field => {
        if (item[field] === undefined) return
        const name = `${prefix}[${arrayKey}][${idx}][${field}]`
        const el = form.querySelector(`[name='${name}']`)
        if (!el) return
        if (el.type === "checkbox") el.checked = !!item[field]
        else el.value = item[field] !== null ? String(item[field]) : ""
        el.dispatchEvent(new Event("input", { bubbles: true }))
      })
    })
  }

  // ── HTML 이스케이프 유틸 ──────────────────────────────────────────────────

  escHtml(str) {
    return (str || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }

  escapeHtml(str) {
    return (str || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }

  _renderMarkdown(text) {
    let html = this.escapeHtml(text)

    // 코드 블록 (```lang ... ```)
    html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (_, lang, code) =>
      `<pre class="ba-md-pre"><code>${code.trim()}</code></pre>`)

    // 인라인 코드
    html = html.replace(/`([^`]+)`/g, '<code class="ba-md-code">$1</code>')

    // 테이블
    html = html.replace(/((?:^\|.+\|$\n?)+)/gm, (tableBlock) => {
      const rows = tableBlock.trim().split("\n").filter(r => r.trim())
      if (rows.length < 2) return tableBlock
      const parseRow = r => r.split("|").slice(1, -1).map(c => c.trim())
      const headerCells = parseRow(rows[0])
      // 구분선 행 (|---|---| 패턴) 감지
      const sepIdx = rows.findIndex(r => /^\|[\s\-:]+(\|[\s\-:]+)+\|$/.test(r.trim()))
      if (sepIdx < 0) return tableBlock
      const bodyRows = rows.slice(sepIdx + 1)
      let t = '<div class="ba-md-table-wrap"><table class="ba-md-table"><thead><tr>'
      headerCells.forEach(c => { t += `<th>${c}</th>` })
      t += "</tr></thead><tbody>"
      bodyRows.forEach(r => {
        const cells = parseRow(r)
        t += "<tr>"
        cells.forEach(c => { t += `<td>${c}</td>` })
        t += "</tr>"
      })
      t += "</tbody></table></div>"
      return t
    })

    // 헤딩 (## ~ ####)
    html = html.replace(/^####\s+(.+)$/gm, '<h4 class="ba-md-h4">$1</h4>')
    html = html.replace(/^###\s+(.+)$/gm, '<h3 class="ba-md-h3">$1</h3>')
    html = html.replace(/^##\s+(.+)$/gm, '<h2 class="ba-md-h2">$1</h2>')
    html = html.replace(/^#\s+(.+)$/gm, '<h2 class="ba-md-h2">$1</h2>')

    // 볼드/이탈릭
    html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    html = html.replace(/\*(.+?)\*/g, "<em>$1</em>")

    // 인용 (> ...)
    html = html.replace(/^&gt;\s?(.+)$/gm, '<blockquote class="ba-md-quote">$1</blockquote>')

    // 리스트 (- ...)
    html = html.replace(/^- (.+)$/gm, '<li class="ba-md-li">$1</li>')
    html = html.replace(/((?:<li class="ba-md-li">.*<\/li>\n?)+)/g, '<ul class="ba-md-ul">$1</ul>')

    // 수평선 (---)
    html = html.replace(/^---$/gm, '<hr class="ba-md-hr">')

    // 남은 줄바꿈
    html = html.replace(/\n\n/g, "</p><p>")
    html = html.replace(/\n/g, "<br>")
    html = `<p>${html}</p>`
    // 빈 <p> 제거
    html = html.replace(/<p>\s*<\/p>/g, "")

    return html
  }

  // ── 사용처 컨텍스트 빌더 ─────────────────────────────────────────────────

  _buildUsageContext(override = {}) {
    const resourceType = this.config.resourceType || ''
    const contextType  = this.config.contextType  || ''

    const surfaceMap = {
      'IaProject':                'ia',
      'PlanningWorkspaceProject': 'planning_workspace',
      'UsProject':                'main_target',
      'ImageStudioProject':       'image_studio',
      'KakaoTalkChatRoom':        'kakao_talk'
    }
    const surface = surfaceMap[resourceType] || 'klavy_agent'

    let action = 'default'
    if (surface === 'planning_workspace' && contextType.startsWith('planning_')) {
      action = contextType.replace(/^planning_/, '')
    } else if (surface === 'ia') {
      action = 'ia_generate'
    } else if (surface === 'main_target') {
      action = 'main_target'
    } else if (surface === 'image_studio') {
      action = 'generate'
    } else if (surface === 'kakao_talk') {
      action = 'message'
    }
    return { surface, action, ...override }
  }

  // ── 스타일 주입 ───────────────────────────────────────────────────────────

  injectStyles() {
    if (document.getElementById("ba-styles")) return
    const style = document.createElement("style")
    style.id = "ba-styles"
    style.textContent = `
      #ka-wrapper {
        width: 380px;
        flex-shrink: 0;
        position: sticky;
        top: 20px;
        height: calc(100dvh - 100px);
        max-height: calc(100dvh - 100px);
        align-self: flex-start;
        transition: width 0.2s ease;
      }
      #ka-chat-panel {
        width: 100%;
        height: 100%;
        min-height: 0;
        background: #fff;
        border: 1px solid #E5E7EB;
        display: flex;
        flex-direction: column;
        font-family: 'Pretendard Variable', Pretendard, -apple-system, sans-serif;
        font-size: 14px;
        color: #111827;
        overflow: hidden;
        position: relative;
      }
      #ka-messages {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        padding: 12px;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      @media (max-width: 1200px) {
        #ka-wrapper {
          position: static;
          width: 100%;
          height: min(70dvh, 760px);
          max-height: min(70dvh, 760px);
        }
      }
      .ba-msg-user {
        background: #6366F1;
        color: #fff;
        padding: 8px 12px;
        margin: 4px 8px 4px auto;
        max-width: 80%;
        width: fit-content;
        font-size: 13px;
        line-height: 1.5;
        word-break: break-word;
      }
      .ba-msg-user img {
        max-width: 100%;
        max-height: 120px;
        object-fit: contain;
        margin-bottom: 6px;
        display: block;
        border: 1px solid rgba(255,255,255,0.2);
      }
      .ba-msg-user span {
        display: block;
      }
      .ba-msg-assistant {
        background: #F3F4F6;
        color: #111827;
        padding: 8px 12px;
        margin: 4px auto 4px 8px;
        max-width: 90%;
        width: fit-content;
        font-size: 13px;
        line-height: 1.5;
        word-break: break-word;
      }
      .ba-msg-assistant p { margin: 0 0 6px; }
      .ba-msg-assistant p:last-child { margin-bottom: 0; }
      .ba-md-h2 { font-size: 14px; font-weight: 700; margin: 10px 0 4px; color: #111827; }
      .ba-md-h3 { font-size: 13px; font-weight: 700; margin: 8px 0 3px; color: #111827; }
      .ba-md-h4 { font-size: 13px; font-weight: 600; margin: 6px 0 2px; color: #374151; }
      .ba-md-ul { margin: 4px 0; padding-left: 16px; }
      .ba-md-li { margin: 2px 0; list-style: disc; }
      .ba-md-quote { margin: 6px 0; padding: 6px 10px; border-left: 3px solid #6366F1; background: #EEF2FF; color: #374151; font-size: 12px; }
      .ba-md-hr { border: none; border-top: 1px solid #E5E7EB; margin: 8px 0; }
      .ba-md-code { background: #E5E7EB; padding: 1px 4px; font-family: monospace; font-size: 12px; }
      .ba-md-pre { background: #1F2937; color: #E5E7EB; padding: 8px 10px; margin: 6px 0; overflow-x: auto; font-size: 12px; line-height: 1.4; }
      .ba-md-pre code { background: none; padding: 0; font-family: monospace; font-size: 12px; }
      .ba-md-table-wrap { max-width: 100%; overflow-x: auto; margin: 6px 0; }
      .ba-md-table { width: 100%; min-width: 420px; border-collapse: collapse; font-size: 12px; }
      .ba-md-table th { background: #E5E7EB; padding: 4px 8px; text-align: left; font-weight: 600; border: 1px solid #D1D5DB; }
      .ba-md-table td { padding: 4px 8px; border: 1px solid #E5E7EB; }
      .ba-md-table tr:nth-child(even) { background: #F9FAFB; }
      .ba-msg-system {
        background: #F3F4F6;
        color: #374151;
        border: 1px solid #E5E7EB;
        padding: 8px 12px;
        margin: 4px 8px;
        font-size: 12px;
        display: flex;
        align-items: center;
      }
      .ba-apply-block {
        background: #EEF2FF;
        border: 1px solid #C7D2FE;
        flex-direction: column;
      }
      .ba-apply-btn {
        width: 100%;
        height: 32px;
        background: #6366F1;
        color: #fff;
        border: 1px solid #6366F1;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        font-family: inherit;
        transition: background 0.15s;
      }
      .ba-apply-btn:hover { background: #4F46E5; }
      .ba-followup-buttons {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        padding: 4px 8px 8px;
      }
      .ba-followup-btn {
        padding: 6px 12px;
        background: #fff;
        border: 1px solid #E5E7EB;
        color: #374151;
        font-size: 12px;
        cursor: pointer;
        font-family: inherit;
        transition: border-color 0.15s, color 0.15s;
      }
      .ba-followup-btn:hover {
        border-color: #6366F1;
        color: #6366F1;
      }
      .ba-streaming {
        border-left: 2px solid #6366F1;
      }
      .ba-typing.ba-ai-thinking {
        display: flex;
        align-items: center;
        gap: 10px;
        margin: 8px auto 8px 8px;
        padding: 10px 14px;
        background: #EEF2FF;
        border: 1px solid #E0E7FF;
        max-width: 85%;
        width: fit-content;
        min-width: 220px;
      }
      .ba-thinking-bubble {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        column-gap: 8px;
        row-gap: 4px;
        min-height: 36px;
        flex: 1 1 auto;
        min-width: 0;
      }
      .ba-thinking-text {
        font-size: 13px;
        color: #374151;
        font-weight: 500;
      }
      .ba-thinking-meta {
        display: flex;
        align-items: baseline;
        gap: 6px;
        flex-wrap: nowrap;
        min-width: 0;
      }
      .ba-thinking-time {
        font-size: 11px;
        color: #9CA3AF;
        line-height: 1.35;
      }
      .ba-thinking-bar {
        flex-basis: 100%;
        width: 100%;
        height: 2px;
        background: #E5E7EB;
        margin-top: 4px;
        overflow: hidden;
      }
      .ba-thinking-bar-inner {
        width: 40%;
        height: 100%;
        background: linear-gradient(90deg, #4F46E5 31%, #6366F1 100%);
        animation: ba-bar-slide 1.4s ease-in-out infinite;
      }
      @keyframes ba-bar-slide {
        0% { transform: translateX(-100%); }
        50% { transform: translateX(150%); }
        100% { transform: translateX(350%); }
      }
      .ba-quick-btn {
        padding: 5px 10px;
        background: #fff;
        border: 1px solid #E5E7EB;
        color: #374151;
        font-size: 12px;
        cursor: pointer;
        font-family: inherit;
        transition: border-color 0.15s, color 0.15s;
      }
      .ba-quick-btn--long {
        width: 100%;
        display: flex;
        justify-content: flex-start;
        text-align: left;
        line-height: 1.45;
        white-space: normal;
      }
      .ba-quick-btn:hover {
        border-color: #6366F1;
        color: #6366F1;
      }
      .ba-rule-empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        padding: 24px 16px;
        color: #9CA3AF;
        font-size: 13px;
        text-align: center;
      }
      .ba-rule-item {
        display: flex;
        align-items: center;
        padding: 8px 12px;
        border-bottom: 1px solid #F3F4F6;
        cursor: pointer;
        transition: background 0.1s;
      }
      .ba-rule-item:hover { background: #F9FAFB; }
      .ba-rule-item--inactive { opacity: 0.5; }
      .ba-rule-item-title {
        font-size: 12px;
        font-weight: 600;
        color: #111827;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .ba-rule-item-preview {
        font-size: 11px;
        color: #6B7280;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        margin-top: 2px;
      }
      .ba-rule-delete-btn {
        background: none;
        border: none;
        color: #9CA3AF;
        cursor: pointer;
        padding: 4px;
        display: flex;
        align-items: center;
      }
      .ba-rule-delete-btn:hover { color: #EF4444; }
      .ba-chk-label {
        display: inline-flex;
        align-items: center;
        cursor: pointer;
      }
      .ba-chk-input {
        position: absolute;
        opacity: 0;
        width: 0;
        height: 0;
      }
      .ba-chk-box {
        width: 14px;
        height: 14px;
        border: 1px solid #D1D5DB;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: #fff;
      }
      .ba-chk-input:checked + .ba-chk-box {
        background: #6366F1;
        border-color: #6366F1;
      }
      .ba-chk-input:checked + .ba-chk-box::after {
        content: '';
        width: 8px;
        height: 8px;
        background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'%3E%3C/polyline%3E%3C/svg%3E") center/contain no-repeat;
      }
      .ba-rules-page-btn {
        padding: 4px 8px;
        border: 1px solid #E5E7EB;
        background: #fff;
        font-size: 12px;
        cursor: pointer;
        font-family: inherit;
        color: #374151;
      }
      .ba-rules-page-btn.active {
        background: #6366F1;
        color: #fff;
        border-color: #6366F1;
      }
      .ba-rules-page-btn:disabled {
        opacity: 0.4;
        cursor: default;
      }
      .ba-rule-overlay {
        position: absolute;
        top: 0; left: 0; right: 0; bottom: 0;
        background: #fff;
        z-index: 20;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }
      .ba-rule-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 10px 16px;
        border-bottom: 1px solid #E5E7EB;
        flex-shrink: 0;
      }
      .ba-rule-header-title {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 13px;
        font-weight: 700;
        color: #111827;
      }
      .ba-rule-body {
        flex: 1;
        overflow-y: auto;
      }
      .ba-rule-footer {
        padding: 10px 16px;
        border-top: 1px solid #E5E7EB;
        display: flex;
        gap: 6px;
        flex-shrink: 0;
      }
      .ba-rule-input {
        width: 100%;
        height: 36px;
        padding: 0 10px;
        font-size: 13px;
        border: 1px solid #E5E7EB;
        outline: none;
        color: #111827;
        font-family: inherit;
        box-sizing: border-box;
      }
      .ba-rule-input:focus { border-color: #6366F1; }
      .ba-rule-textarea {
        width: 100%;
        flex: 1;
        padding: 10px;
        font-size: 13px;
        border: 1px solid #E5E7EB;
        outline: none;
        color: #111827;
        font-family: inherit;
        resize: none;
        line-height: 1.5;
        box-sizing: border-box;
      }
      .ba-rule-textarea:focus { border-color: #6366F1; }
      .ba-rule-save-btn {
        height: 36px;
        background: #6366F1;
        color: #fff;
        border: 1px solid #6366F1;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        font-family: inherit;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 5px;
      }
      .ba-rule-save-btn:hover { background: #4F46E5; }
      .ba-rule-active-row {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;
        color: #374151;
        cursor: pointer;
      }
      .ba-rule-hint {
        font-size: 11px;
        color: #9CA3AF;
        text-align: center;
        margin-top: 8px;
      }
      .ba-rules-batch-bar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 6px 12px;
        background: #F3F4F6;
        border-bottom: 1px solid #E5E7EB;
        flex-shrink: 0;
      }
      .ba-rules-pagination {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        padding: 8px;
        border-top: 1px solid #E5E7EB;
        flex-shrink: 0;
      }
      .ba-rchat-msg {
        font-size: 13px;
        line-height: 1.5;
        word-break: break-word;
      }
      .ba-rchat-msg--user {
        background: #6366F1;
        color: #fff;
        padding: 8px 12px;
        margin: 4px 8px 4px auto;
        max-width: 80%;
        width: fit-content;
      }
      .ba-rchat-msg--assistant {
        background: #F3F4F6;
        color: #111827;
        padding: 8px 12px;
        margin: 4px auto 4px 8px;
        max-width: 90%;
        width: fit-content;
      }
      .ba-rchat-msg--assistant p { margin: 0 0 6px; }
      .ba-rchat-msg--assistant p:last-child { margin-bottom: 0; }
      .ba-history-item:hover { background: #F9FAFB !important; }
      .ba-history-item--active { background: #EEF2FF !important; }
      .ba-model-opt { display: flex; align-items: center; justify-content: space-between; gap: 8px; width: 100%; padding: 8px 12px; font-size: 13px; cursor: pointer; color: #111827; text-align: left; border: 0; background: none; font-family: inherit; }
      .ba-model-opt:hover { background: #F9FAFB; }
      .ba-model-opt.selected { background: #EEF2FF; color: #4338CA; font-weight: 600; }
      .ba-model-opt__name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .ba-model-opt__meta { display: flex; align-items: center; gap: 4px; flex-shrink: 0; }
      .ka-model-group-header { padding: 8px 12px 4px; font-size: 11px; font-weight: 700; color: #6B7280; letter-spacing: 0.05em; text-transform: uppercase; border-top: 1px solid #E5E7EB; }
      .ka-model-badge { font-size: 10px; font-weight: 700; line-height: 1; padding: 3px 6px; white-space: nowrap; display: inline-flex; align-items: center; }
      .ka-model-badge--weight { color: #C2410C; border: 1px solid #FDBA74; background: #FFF7ED; }
      .ka-model-badge--recommended { color: #4338CA; border: 1px solid #C7D2FE; background: #EEF2FF; }
      .ka-model-badge--label { color: #4B5563; border: 1px solid #E5E7EB; background: #F9FAFB; }
      .ka-model-badge--image { color: #4B5563; border: 1px solid #E5E7EB; background: #F9FAFB; display: inline-flex; align-items: center; padding: 3px 5px; }
      .ka-model-badge--image svg { width: 11px; height: 11px; }
      .ka-model-badge--credential { border: 1px solid #E5E7EB; color: #6B7280; background: #F9FAFB; }
      .ka-model-badge--credential.is-system { color: #4338CA; border-color: #C7D2FE; background: #EEF2FF; }
      .ka-model-badge--credential.is-user { color: #4338CA; border-color: #C7D2FE; background: #EEF2FF; }
      .ka-model-badge--credential.is-oauth { color: #047857; border-color: #A7F3D0; background: #ECFDF5; }
      .ka-model-badge--credential.is-missing { color: #B45309; border-color: #FCD34D; background: #FFFBEB; }
      .ka-model-badge--locked { color: #6B7280; border: 1px solid #E5E7EB; background: #F3F4F6; }
      .ba-model-opt.is-locked { opacity: 0.65; cursor: not-allowed; }
      .ba-model-opt.is-locked:hover { background: #F9FAFB; }
      .ba-model-opt.is-locked .ba-model-opt__name { color: #6B7280; }
      .ka-threads-mode-tabs {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        border: 1px solid #E5E7EB;
        margin-bottom: 14px;
        background: #F9FAFB;
      }
      .ka-threads-mode-tab {
        height: 46px;
        border: 0;
        border-right: 1px solid #E5E7EB;
        background: transparent;
        color: #6B7280;
        font-size: 13px;
        font-weight: 800;
        font-family: inherit;
        cursor: pointer;
        white-space: nowrap;
      }
      .ka-threads-mode-tab:last-child { border-right: 0; }
      .ka-threads-mode-tab.is-active {
        background: #FFFFFF;
        color: #4F46E5;
        box-shadow: inset 0 -2px 0 #6366F1;
      }
      .ka-input-expand-btn {
        height: 28px;
        padding: 0 9px;
        border: 1px solid #E5E7EB;
        background: #FFFFFF;
        color: #6B7280;
        font-size: 12px;
        font-weight: 700;
        font-family: inherit;
        cursor: pointer;
      }
      .ka-input-expand-btn:hover {
        border-color: #6366F1;
        color: #4F46E5;
      }
      .ka-input-expanded {
        border-color: #A5B4FC !important;
        box-shadow: 0 0 0 2px #EEF2FF;
      }
      #ka-toggle-btn {
        position: fixed;
        bottom: 24px;
        right: 24px;
        width: 48px;
        height: 48px;
        padding: 0;
        background: linear-gradient(135deg, #4F46E5, #6366F1);
        border: none;
        border-radius: 50% !important;
        overflow: hidden !important;
        cursor: pointer;
        display: none;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 16px rgba(99,102,241,0.45);
        z-index: 9998;
      }
      #ka-toggle-btn .ka-toggle-icon {
        border-radius: 50% !important;
      }
      #ka-toggle-btn:hover {
        box-shadow: 0 6px 20px rgba(99,102,241,0.55);
      }
      #ka-toggle-btn.ka-panel-open {
        display: none !important;
      }
    `
    document.head.appendChild(style)
  }
}

// TO BE CONTINUED: project rules CRUD, form filling (별도 파일에서 추가 예정)

// Railway deploy trigger: Threads agent tabs are included in this build.
